import { useState, useEffect, useMemo, useRef, createContext, useContext, Component } from "react";
import { createPortal } from "react-dom";
// IMPORTANTE: usiamo la build "legacy" di pdfjs-dist perché la build moderna
// utilizza sintassi recente (es. `for await ... of readableStream`) che non è
// supportata da tutti i browser. La legacy è transpilata e compatibile.
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs';
import mammoth from 'mammoth';
import { supabase, SUPABASE_ENABLED } from './lib/supabase';
import { useCrediti, COSTI_CREDITI, SOGLIA_AVVISO,
         isPianoTrial, isSezioneBloccataTrial, isDimensioneBloccataTrial } from './lib/credits';
import {
  caricaDaSupabase,
  leggiDatiLocalStorage,
  salvaStudenti,
  salvaScadenze,
  salvaTuttaPeiDatiMap,
  migraLocalStorageASupabase,
  eliminaStudenteDaSupabase,
  salvaDocumentiStudente,
} from './lib/storage';
// Knowledge base traguardi/obiettivi — Indicazioni Nazionali, nelle due versioni
// vigenti durante il rollout (D.M. 254/2012 e D.M. 221/2025). La scelta fra le
// due è fatta dal resolver qui sotto (usa221/versioneKB/infoIndicazioni).
import INDICAZIONI_254 from './data/indicazioni_254_2012.json';
import INDICAZIONI_221 from './data/indicazioni_221_2025.json';
import { AuthFlow, ResetPasswordScreen } from './auth/AuthScreens';

// ─── Resolver versione Indicazioni Nazionali (DM 254/2012 ↔ DM 221/2025) ───
// Le Indicazioni 221/2025 (in vigore 11/2/2026) si applicano in modo PROGRESSIVO
// dall'a.s. 2026/27, partendo da scuola dell'infanzia e CLASSI PRIME del primo
// ciclo, aggiungendo una classe ogni anno fino al regime pieno. Tabella: per ogni
// a.s. l'ULTIMA classe (per ordine) coperta dal 221; classi superiori restano 254.
// ─── Anno scolastico corrente (rollover automatico al 1° settembre) ───────────
// L'a.s. non coincide con l'anno solare: da settembre a dicembre l'anno di avvio
// è quello in corso, da gennaio ad agosto è il precedente. Niente costanti da
// aggiornare a mano: il 1/9 l'app passa da sé all'a.s. nuovo.
const annoAvvioAS = (d = new Date()) =>
  (d.getMonth() >= 8 ? d.getFullYear() : d.getFullYear() - 1);   // getMonth(): 8 = settembre
// Due formati: esteso "2026/2027" per il campo visibile all'insegnante,
// breve "2026/27" per le chiavi di ROLLOUT_221.
const asEsteso = (avvio = annoAvvioAS()) => `${avvio}/${avvio + 1}`;
const asBreve  = (avvio = annoAvvioAS()) => `${avvio}/${String((avvio + 1) % 100).padStart(2, "0")}`;
// A.s. DEL PEI: comanda il campo compilato nella scheda studente (testo libero →
// si cerca il primo anno a 4 cifre plausibile); se manca o è illeggibile si
// ricade sulla data di sistema. Serve soprattutto ai PEI PROVVISORI (Sez. 12),
// redatti a giugno per l'anno SUCCESSIVO: con la sola data prenderebbero la riga
// di rollout sbagliata.
const annoAvvioStudente = (studente) => {
  const m = String(studente?.annoScolastico || "").match(/(20\d{2})/);
  const n = m ? Number(m[1]) : NaN;
  return (n >= 2000 && n <= 2100) ? n : annoAvvioAS();
};

// La scuola dell'infanzia NON è scandita in classi: entra nel 221 per INTERO
// fin dal primo anno di applicazione (nessuna transizione anno per anno) →
// marcatore "tutta" anziché un numero di classe.
const ROLLOUT_221 = {
  "2026/27": { infanzia: "tutta", primaria: 1, secondaria1: 1 },
  "2027/28": { infanzia: "tutta", primaria: 2, secondaria1: 2 },
  "2028/29": { infanzia: "tutta", primaria: 3, secondaria1: 3 },
  "2029/30": { infanzia: "tutta", primaria: 4, secondaria1: 3 },
  "2030/31": { infanzia: "tutta", primaria: 5, secondaria1: 3 },
};
// Dal 2031/32 il rollout è concluso: TUTTE le classi seguono il 221. Senza questo
// clamp, un a.s. oltre l'ultima riga della tabella ricadrebbe sul 254 per tutti —
// una regressione silenziosa che si sarebbe manifestata da sola nel 2031.
const ROLLOUT_221_PIENO = { infanzia: "tutta", primaria: 5, secondaria1: 3 };
const ULTIMO_AS_ROLLOUT = Object.keys(ROLLOUT_221).sort().pop();
// Prima del 2026/27 → null = nessuna classe nel 221 (tutto 254), corretto.
const rollout221 = (asKey) =>
  ROLLOUT_221[asKey] || (asKey > ULTIMO_AS_ROLLOUT ? ROLLOUT_221_PIENO : null);

// Decide la versione in base all'ORDINE e alla CLASSE EFFETTIVAMENTE FREQUENTATA
// (non alla programmazione di riferimento, che serve solo a scegliere la fascia),
// nell'anno scolastico del PEI.
const usa221 = (ordKey, classeAttuale, avvio = annoAvvioAS()) => {
  const cfg = rollout221(asBreve(avvio));
  const max = cfg && cfg[ordKey];
  if (!max) return false;
  if (max === "tutta") return true;   // infanzia: nessun rollout per classe
  const n = Number(classeAttuale);
  return Number.isInteger(n) && n >= 1 && n <= max;
};
const versioneKB = (ordKey, classeAttuale, avvio) =>
  usa221(ordKey, classeAttuale, avvio) ? INDICAZIONI_221 : INDICAZIONI_254;

// ─── Riferimenti alle Indicazioni Nazionali NEI PROMPT (fuori dalla KB Sez.8) ──
// La KB di Sez. 8 inietta il testo ufficiale verbatim ed è già version-aware.
// Le stringhe generiche dei prompt citavano invece il 254/2012 in modo fisso:
// qui si ricava il decreto vigente per l'alunno (ordine + classe frequentata),
// così tutte le generazioni restano coerenti durante il rollout.
const infoIndicazioni = (studente) => {
  const is221 = usa221(
    studente?.ordine,
    parseInt(String(studente?.classe || "").trim(), 10),
    annoAvvioStudente(studente),
  );
  return {
    is221,
    decreto: is221 ? "D.M. 221/2025" : "D.M. 254/2012",
    // Il 221 rinomina i "traguardi per lo sviluppo della competenza" in
    // "competenze attese" (cfr. src/data/indicazioni_221_2025.json).
    etichettaCompetenze: is221
      ? "Competenze attese"
      : "Traguardi per lo sviluppo della competenza",
  };
};
// ─── Secondaria II grado: l'indirizzo NON è noto all'app ──────────────────────
// L'app non raccoglie l'indirizzo di studio (liceo classico/scientifico/linguistico,
// istituto tecnico, professionale e relative articolazioni), eppure i prompt
// chiedevano di riferirsi "alle Linee Guida per l'indirizzo scelto": un'istruzione
// a usare un'informazione mai fornita, che il modello poteva solo inventare.
// Stesso trattamento del DM 221: si dichiara il limite invece di simulare la
// competenza. NB per il PEI la perdita è minima — nel percorso RICONDUCIBILE gli
// obiettivi derivano dagli obiettivi minimi della classe e nel DIFFERENZIATO sono
// personalizzati: in entrambi i casi la fonte è la programmazione dell'insegnante.
const NOTA_INDIRIZZO_SEC2 = `
INDIRIZZO DI STUDIO — LIMITE DA RISPETTARE: non ti è stato comunicato l'indirizzo specifico della scuola (liceo classico, scientifico, linguistico, delle scienze umane, istituto tecnico o professionale e relative articolazioni) e NON puoi dedurlo. Di conseguenza: NON citare Linee Guida, Indicazioni Nazionali o quadri orari di uno specifico indirizzo, e NON attribuire obiettivi, competenze o contenuti a documenti di indirizzo che non ti sono stati forniti. L'ancoraggio disciplinare deve venire ESCLUSIVAMENTE da ciò che è presente nel contesto: la programmazione della classe e i contenuti inseriti dall'insegnante, il profilo di funzionamento, i documenti caricati. Se un riferimento disciplinare specifico manca, NON inventarlo e NON riempire con genericità: usa un segnaposto esplicito, es. "[da personalizzare: indicare gli obiettivi minimi della disciplina previsti dalla programmazione di classe]". Restano invece pienamente utilizzabili, perché non dipendono dall'indirizzo: la distinzione fra percorso riconducibile e differenziato, le competenze chiave europee, i PCTO, la transizione post-diploma e la struttura del modello A4.`;

// Cautela OBBLIGATORIA: il D.M. 221/2025 è successivo all'addestramento del
// modello, che quindi non ne conosce il testo. Citarlo senza questa nota
// produrrebbe traguardi inventati attribuiti a un decreto vigente — esattamente
// il tipo di falsa precisione normativa da evitare in un documento ufficiale.
const NOTA_221 = `
INDICAZIONI NAZIONALI VIGENTI — D.M. 221/2025: per questo alunno si applicano le NUOVE Indicazioni Nazionali per il curricolo della scuola dell'infanzia e del primo ciclo (D.M. 9 dicembre 2025 n. 221), in applicazione progressiva dall'a.s. 2026/27 a partire dalla scuola dell'infanzia e dalle classi prime del primo ciclo; sostituiscono il D.M. 254/2012. NON conosci il testo di questo decreto: non citare a memoria traguardi, competenze od obiettivi attribuendoli alle Indicazioni Nazionali, e non spacciare per vigenti le formulazioni del D.M. 254/2012. Se nel contesto è presente un blocco "RIFERIMENTO UFFICIALE — Indicazioni Nazionali (D.M. 221/2025)", quello è il testo vigente verbatim: usa ESCLUSIVAMENTE quello come base. In sua assenza, formula obiettivi e traguardi a partire dal profilo di funzionamento dell'alunno e dalla programmazione di classe, senza attribuire citazioni ad alcuna versione delle Indicazioni. Terminologia: il D.M. 221/2025 usa "competenze attese" al posto di "traguardi per lo sviluppo della competenza".`;

// ─── Recovery detector a livello di modulo ────────────────────────────────────
// Supabase (con PKCE) processa il token in modo asincrono all'avvio del client,
// PRIMA che React monti i componenti. Se aspettiamo useEffect, il listener arriva
// troppo tardi e ci perdiamo l'evento PASSWORD_RECOVERY.
// Soluzione: registriamo il listener qui, a livello di modulo, così lo catturiamo
// con certezza. AuthGate legge _recoveryPending alla prima render.
let _recoveryPending = false;
let _earlyAuthSub = null;
if (SUPABASE_ENABLED) {
  const { data } = supabase.auth.onAuthStateChange((event) => {
    if (event === 'PASSWORD_RECOVERY') _recoveryPending = true;
  });
  _earlyAuthSub = data.subscription;
}

// ─── Polyfill: ReadableStream[Symbol.asyncIterator] ───────────────────────────
// Safari < 17.4 e Firefox < 124 non implementano nativamente l'async iterator
// su ReadableStream. pdfjs-dist v5 lo usa internamente con `for await ... of`
// e altrimenti lancia: "undefined is not a function (near '...value of readableStream...')".
if (typeof ReadableStream !== 'undefined'
    && typeof Symbol !== 'undefined'
    && !ReadableStream.prototype[Symbol.asyncIterator]) {
  ReadableStream.prototype[Symbol.asyncIterator] = async function* asyncIterator() {
    const reader = this.getReader();
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) return;
        yield value;
      }
    } finally {
      reader.releaseLock();
    }
  };
  // Alcuni motori (Safari) cercano anche `values()` per `for await`
  if (!ReadableStream.prototype.values) {
    ReadableStream.prototype.values = ReadableStream.prototype[Symbol.asyncIterator];
  }
}

// ─── Configurazione worker PDF.js ─────────────────────────────────────────────
// pdfjs-dist v5+ richiede che venga specificato il workerSrc, altrimenti
// la lettura dei PDF lancia: "No 'GlobalWorkerOptions.workerSrc' specified."
// Carichiamo il worker LEGACY (transpilato) dalla CDN unpkg, sincronizzando
// la versione con quella della libreria installata.
if (typeof window !== 'undefined' && pdfjsLib?.GlobalWorkerOptions) {
  pdfjsLib.GlobalWorkerOptions.workerSrc =
    `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/legacy/build/pdf.worker.min.mjs`;
}

// Database ICD-10 in memoria. Parte vuoto; viene riempito (in ordine) da:
//   1) i codici essenziali qui sotto (sincroni, fallback immediato);
//   2) i codici custom dell'utente (localStorage);
//   3) il dataset completo ICD-10 italiano (public/icd10_it.json), caricato in
//      modo lazy alla prima apertura del wizard studente (vedi caricaICD10Completo).
const ICD10_DB = [];

// Fallback minimo (se il database principale è vuoto)
// Aggiungi sempre i codici essenziali in italiano (non dipendenti dal pacchetto)
const codiciEssenziali = [
  { code: "F81.0", description: "Disturbo specifico della lettura (Dislessia)", alt: "Disturbo specifico della lettura" },
  { code: "F81.1", description: "Disturbo specifico dell'ortografia (Disortografia)", alt: "Disturbo specifico della compitazione, disortografia" },
  { code: "F81.2", description: "Disturbo specifico delle abilità aritmetiche (Discalculia)", alt: "Disturbo specifico delle abilità aritmetiche" },
  { code: "F81.8", description: "Altri disturbi dello sviluppo delle abilità scolastiche (Disgrafia)", alt: "Altri disturbi dello sviluppo delle abilità scolastiche" },
  { code: "F90.0", description: "ADHD – tipo prevalentemente disattento", alt: "Disturbo dell’attività e dell’attenzione" },
  { code: "F90.1", description: "ADHD – tipo prevalentemente iperattivo-impulsivo", alt: "Disturbo della condotta ipercinetico" },
  { code: "F90.2", description: "ADHD – tipo combinato", alt: "Disturbo ipercinetico misto, ADHD combinato" },
  { code: "F84.0", description: "Disturbo dello spettro autistico (ASD)", alt: "Autismo infantile, disturbo autistico" },
  { code: "F84.1", description: "Autismo atipico" },
  { code: "F84.2", description: "Sindrome di Rett" },
  { code: "F84.3", description: "Altro disturbo disintegrativo dell'infanzia", alt: "Disturbo disintegrativo dell’infanzia di altro tipo" },
  { code: "F84.4", description: "Disturbo iperattivo associato a disabilità intellettiva e movimenti stereotipati", alt: "Disturbo iperattivo associato a ritardo mentale e a movimenti stereotipati" },
  { code: "F84.5", description: "Sindrome di Asperger" },
  { code: "F84.8", description: "Altri disturbi pervasivi dello sviluppo" },
  { code: "F84.9", description: "Alterazione globale dello sviluppo psicologico (disturbo pervasivo non specificato)", alt: "Disturbo pervasivo dello sviluppo, non specificato" },
  { code: "F70", description: "Disabilità intellettiva lieve", alt: "Ritardo mentale lieve" },
  { code: "F71", description: "Disabilità intellettiva moderata", alt: "Ritardo mentale di media gravità, moderato" },
  { code: "F93.8", description: "Altri disturbi emotivi dell'infanzia", alt: "Altri disturbi emozionali dell’infanzia" },
  { code: "F93.9", description: "Disturbo emotivo dell'infanzia non specificato", alt: "Disturbo emozionale dell’infanzia, non specificato" },
  { code: "F91.3", description: "Disturbo oppositivo provocatorio" },
  { code: "F80.1", description: "Disturbo del linguaggio espressivo" },
  { code: "F80.2", description: "Disturbo della comprensione del linguaggio" },
  { code: "F82", description: "Disturbo della coordinazione motoria (Disprassia/DCD)", alt: "Disturbo specifico dello sviluppo della funzione motoria" },
  { code: "G80", description: "Paralisi cerebrale infantile (PCI)", alt: "Paralisi cerebrale" },
  { code: "Q90", description: "Sindrome di Down (Trisomia 21)", alt: "Sindrome di Down" },
  { code: "F94.0", description: "Mutismo selettivo", alt: "Mutismo elettivo" },
  { code: "F98.5", description: "Balbuzie", alt: "Balbuzie [tartagliamento]" },
  // Codici aggiunti per copertura istituti scolastici italiani
  { code: "F72",   description: "Disabilità intellettiva grave", alt: "Ritardo mentale grave" },
  { code: "F79",   description: "Disabilità intellettiva non specificata (Funzionamento Cognitivo Limite)", alt: "Ritardo mentale non specificato, funzionamento cognitivo limite, borderline" },
  { code: "F80.0", description: "Disturbo fonologico (Dislalia)", alt: "Disturbo specifico dell'articolazione dell'eloquio" },
  { code: "F81.3", description: "Disturbo misto delle abilità scolastiche", alt: "Disturbo misto delle capacità scolastiche" },
  { code: "F83",   description: "Disturbi misti dello sviluppo", alt: "Disturbi specifici misti dello sviluppo" },
  { code: "F88",   description: "Altri disturbi dello sviluppo psicologico" },
  { code: "F89",   description: "Disturbo non specificato dello sviluppo psicologico" },
  { code: "F91",   description: "Disturbi della condotta" },
  { code: "F91.0", description: "Disturbo della condotta in ambito familiare", alt: "Disturbo della condotta limitato al contesto familiare" },
  { code: "F91.1", description: "Disturbo della condotta con scarsa socializzazione", alt: "Disturbo della condotta con ridotta socializzazione" },
  { code: "F91.2", description: "Disturbo della condotta con socializzazione", alt: "Disturbo della condotta con socializzazione normale" },
  { code: "F91.8", description: "Altri disturbi della condotta" },
  { code: "F91.9", description: "Disturbo della condotta non specificato", alt: "Disturbo della condotta, non specificato" },
  { code: "F93",   description: "Disturbi emotivi con esordio nell'infanzia", alt: "Disturbi della sfera emozionale con esordio specifico nell’infanzia" },
  { code: "F93.0", description: "Disturbo d'ansia da separazione dell'infanzia", alt: "Disturbo ansioso da separazione dell’infanzia" },
  { code: "F93.1", description: "Disturbo fobico dell'infanzia", alt: "Disturbo fobico dell’infanzia" },
  { code: "F93.2", description: "Disturbo d'ansia sociale dell'infanzia", alt: "Disturbo di ansia sociale dell’infanzia" },
  { code: "F93.3", description: "Disturbo da rivalità tra fratelli", alt: "Disturbo di rivalità tra fratelli" },
  { code: "F98.9", description: "Disturbo emotivo e comportamentale non specificato dell'infanzia e adolescenza", alt: "Disturbi comportamentali ed emozionali non specificati, con esordio abituale" },
  { code: "G40",   description: "Epilessia" },
  { code: "G40.8", description: "Altre forme di epilessia e sindromi epilettiche", alt: "Altre epilessie" },
  { code: "G91",   description: "Idrocefalo" },
  { code: "H54",   description: "Ipovisione e cecità binoculare o monoculare", alt: "Deficit visivo, inclusa la cecità (binoculare o monoculare)" },
  { code: "H54.0", description: "Cecità binoculare", alt: "Cecità, binoculare" },
  { code: "H54.1", description: "Cecità di un occhio, ipovisione dell'altro", alt: "Deficit visivo grave, binoculare" },
  { code: "H54.2", description: "Ipovisione binoculare", alt: "Deficit visivo moderato, binoculare" },
  { code: "H90",   description: "Perdita dell'udito da causa conduttiva e neurosensoriale", alt: "Sordità da difetto di trasmissione e/o neurosensoriale" },
  { code: "H90.0", description: "Perdita dell'udito trasmissiva, bilaterale", alt: "Sordità bilaterale da difetto di trasmissione" },
  { code: "H90.3", description: "Perdita dell'udito neurosensoriale, bilaterale", alt: "Sordità neurosensoriale bilaterale" },
  { code: "H90.5", description: "Perdita dell'udito neurosensoriale non specificata", alt: "Sordità neurosensoriale, non specificata" },
  { code: "H90.8", description: "Perdita mista dell'udito, non specificata", alt: "Sordità mista trasmissiva e neurosensoriale, non specificata" },
  { code: "I60",   description: "Emorragia subaracnoidea" },
  { code: "I52",   description: "Altri disturbi cardiaci in malattie classificate altrove" },
  { code: "E23.2", description: "Diabete insipido" },
  { code: "E27.2", description: "Insufficienza corticosurrenale", alt: "Crisi addisoniana" },
  { code: "K71.0", description: "Malattia epatica tossica con colestasi", alt: "Epatopatia tossica con colestasi" },
  { code: "M30.3", description: "Sindrome linfonodale mucocutanea (Kawasaki)", alt: "Sindrome mucocutanea linfonodale [Kawasaki]" },
  { code: "M21.0", description: "Deformità valga (incluse ginocchio valgo e alluce valgo congenito)", alt: "Valgismo, non classificato altrove" },
  { code: "P77",   description: "Enterocolite necrotizzante del feto e del neonato" },
  { code: "Q04",   description: "Altre malformazioni congenite del cervello", alt: "Altre malformazioni congenite dell’encefalo" },
  { code: "Q04.3", description: "Altra riduzione del cervello (agenesia/ipoplasia del corpo calloso)", alt: "Altri difetti, per riduzione, dell’encefalo" },
  { code: "Q07.8", description: "Altre malformazioni congenite specificate del sistema nervoso", alt: "Altre malformazioni congenite del sistema nervoso specificate" },
  { code: "Q87.8", description: "Altre sindromi da malformazioni congenite specificate", alt: "Altre specificate sindromi malformative congenite di apparati multipli, non" },
  { code: "Q93",   description: "Monosomie e delezioni degli autosomi", alt: "Monosomie e delezioni di autosomi, non classificate altrove" },
  { code: "Q93.5", description: "Altre delezioni di parte di un cromosoma (Sindrome di Angelman, Williams)", alt: "Altre delezioni parziali di un cromosoma" },
  { code: "Q99.8", description: "Altre anomalie cromosomiche specificate", alt: "Altre anomalie cromosomiche (congenite) specificate" },
  { code: "R13",   description: "Disfagia" },
  { code: "T90",   description: "Sequele di traumatismo della testa", alt: "Sequele di traumatismi della testa" },
  { code: "Z60",   description: "Problemi correlati all'ambiente sociale", alt: "Problemi legati all’ambiente sociale" },
  { code: "Z61",   description: "Problemi correlati a eventi negativi nell'infanzia", alt: "Problemi legati a un evento negativo della vita durante l’infanzia" }
];

// Unisci i codici essenziali al database esistente (evitando duplicati)
const codiciEsistenti = new Set(ICD10_DB.map(c => c.code));
codiciEssenziali.forEach(c => {
  if (!codiciEsistenti.has(c.code)) {
    ICD10_DB.push({ ...c, ess: true });
  }
});

if (ICD10_DB.length === 0) {
  console.warn('⚠️ Database ICD-10 ancora vuoto dopo l\'aggiunta dei codici essenziali.');
}

// ─── Caricamento lazy del dataset ICD-10 italiano completo ────────────────────
// public/icd10_it.json (~570KB, ~100-150KB gzip) = 10.737 codici ICD-10 ufficiali
// italiani (Elenco Sistematico OMS, trad. it.). Scaricato UNA volta, alla prima
// apertura del wizard studente, e fuso in ICD10_DB. Idempotente. Se il fetch
// fallisce si resta su codici essenziali + ricerca AI (nessun blocco).
let icd10FullLoaded = false;
let icd10LoadPromise = null;
function caricaICD10Completo() {
  if (icd10FullLoaded) return Promise.resolve();
  if (icd10LoadPromise) return icd10LoadPromise;
  icd10LoadPromise = (async () => {
    try {
      const res = await fetch(`${process.env.PUBLIC_URL || ''}/icd10_it.json`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json(); // { "F84.0": "Autismo infantile", ... }
      const perCodice = new Map(ICD10_DB.map(c => [c.code, c]));
      for (const [code, description] of Object.entries(data)) {
        const gia = perCodice.get(code);
        if (!gia) {
          const voce = { code, description };
          ICD10_DB.push(voce); perCodice.set(code, voce);
        } else if (!gia.alt && gia.description !== description) {
          gia.alt = description;
        }
      }
      icd10FullLoaded = true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Caricamento ICD-10 completo non riuscito, uso essenziali + AI:', e);
      icd10LoadPromise = null; // consente un nuovo tentativo più avanti
    }
  })();
  return icd10LoadPromise;
}

// ─── Contesto "Diagnosi" per i prompt AI ──────────────────────────────────────
// Regole (decise con l'utente):
//  - diagnosi testuale dell'insegnante presente → usa QUELLA + i codici nudi.
//    NON si iniettano le descrizioni del dataset ICD-10: sono termini ufficiali
//    spesso datati (es. "Autismo infantile", "Ritardo mentale") che entrerebbero
//    in conflitto con la diagnosi clinica e con la terminologia vigente del PEI.
//  - diagnosi vuota/scarna ma codici presenti → FALLBACK: codice + descrizione
//    (qui non c'è narrazione con cui confliggere); le regole lessicali del PEI
//    penseranno a modernizzare i termini nel testo generato.
//  - nulla → etichetta "vuoto".
function buildDiagnosiContesto(studente, vuotoLabel = 'non specificata') {
  const testo = (studente?.diagnosi || '').trim();
  const codici = Array.isArray(studente?.icd10) ? studente.icd10 : [];
  const SOGLIA_SCARNA = 15; // caratteri: sotto questa soglia consideriamo la diagnosi "scarna"
  if (testo.length >= SOGLIA_SCARNA) {
    const cod = codici.length ? ` (${codici.map(c => c.code).join(', ')})` : '';
    return testo + cod;
  }
  if (codici.length) {
    const elenco = codici.map(c => (c.description ? `${c.code} ${c.description}` : c.code)).join('; ');
    return `${testo ? testo + ' — ' : ''}codici ICD-10: ${elenco}`;
  }
  return testo || vuotoLabel;
}

// Blocco "riferimento tecnico" dei codici ICD-10 da affiancare alla diagnosi nei
// prompt: la diagnosi clinica dell'insegnante resta PREDOMINANTE; le descrizioni
// ufficiali OMS servono solo a chiarire i codici (utili soprattutto sui codici rari).
// Vuoto se la diagnosi è scarna (lì le descrizioni sono già in buildDiagnosiContesto)
// o se non ci sono codici con descrizione.
function buildRiferimentoICD10(studente) {
  const testo = (studente?.diagnosi || '').trim();
  const codici = (Array.isArray(studente?.icd10) ? studente.icd10 : []).filter(c => c.description);
  if (testo.length < 15 || !codici.length) return '';
  const elenco = codici.map(c => `${c.code} = ${c.description}`).join('; ');
  return `\nRiferimento tecnico dei codici ICD-10 (solo per comprensione; la diagnosi clinica sopra resta PREDOMINANTE): ${elenco}. In caso di discordanza prevale sempre la diagnosi dell'insegnante. Le descrizioni OMS possono usare termini datati (es. "ritardo mentale", "autismo infantile"): nel testo del PEI usa SEMPRE la terminologia vigente (es. "disabilità intellettiva", "disturbo dello spettro autistico") e quella della diagnosi clinica.`;
}

// ─── CONTEXT PER IMPOSTAZIONI ─────────────────────────────────────────────────
const SettingsContext = createContext({
  includeICFCodes: false,
  toggleIncludeICFCodes: () => {},
  guidedMode: true,
  setGuidedMode: () => {},
});

const useSettings = () => useContext(SettingsContext);

const SettingsProvider = ({ children }) => {
  const [includeICFCodes, setIncludeICFCodes] = useState(() => {
    try {
      const saved = localStorage.getItem('iep_settings_includeICF');
      return saved !== null ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });

  const [guidedMode, setGuidedModeState] = useState(() => {
    try {
      const saved = localStorage.getItem('iep_settings_guidedMode');
      return saved !== null ? JSON.parse(saved) : true;
    } catch {
      return true;
    }
  });

  const toggleIncludeICFCodes = () => {
    setIncludeICFCodes(prev => {
      const next = !prev;
      localStorage.setItem('iep_settings_includeICF', JSON.stringify(next));
      return next;
    });
  };

  const setGuidedMode = (value) => {
    setGuidedModeState(value);
    localStorage.setItem('iep_settings_guidedMode', JSON.stringify(value));
  };

  return (
    <SettingsContext.Provider value={{ includeICFCodes, toggleIncludeICFCodes, guidedMode, setGuidedMode }}>
      {children}
    </SettingsContext.Provider>
  );
};

// ─── CONTEXT PER CREDITI E UTENTE LOGGATO ────────────────────────────────────
// Espone in tutta l'app:
//   user, profile, balance, plan, transactions, consume(amount, reason, metadata?)
// Quando Supabase non è configurato (modalità demo), consume ritorna sempre
// success e non scala niente — così la UI esistente continua a funzionare.
const CreditsContext = createContext({
  user: null,
  balance: null,
  plan: 'free',
  band: null,
  transactions: [],
  loading: true,
  error: null,
  consume: async () => ({ success: true, demo: true }),
  refresh: () => {},
  refreshTransactions: () => {},
  signOut: async () => {},
});
const useCreditsCtx = () => useContext(CreditsContext);

const CreditsProvider = ({ user, children }) => {
  const credits = useCrediti(user);
  const signOut = async () => {
    // Pulisci la cache locale studenti/PEI al logout, così l'account successivo
    // sullo stesso browser non eredita i dati (isolamento dati tra account).
    try { localStorage.removeItem('iep_assistant_v9'); } catch (_) {}
    if (SUPABASE_ENABLED) await supabase.auth.signOut();
  };
  return (
    <CreditsContext.Provider value={{ user, signOut, ...credits }}>
      {children}
    </CreditsContext.Provider>
  );
};

// ─── TRIAL GATE CONTEXT ──────────────────────────────────────────────────────
// Indica se l'AI deve essere bloccata per la sezione/dimensione corrente.
// Letto da FieldAI per disabilitare i bottoni Genera/Correggi/Adatta.
// Settato dai wrapper di sezione (PeiEditor + SezOsservazioni) quando l'utente
// è in piano free e sta visualizzando una sezione/dimensione bloccata.
const TrialGateContext = createContext({ aiBloccata: false });
const useTrialGate = () => useContext(TrialGateContext);

// ─── PALETTE LIGHT ────────────────────────────────────────────────────────────
// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
// Palette "bento" ispirata a indigo + mint + amber su sfondo crema chiaro.
// Manteniamo i nomi originali (accent, teal, green, orange…) così le centinaia
// di riferimenti sparsi nel codice continuano a funzionare senza modifiche.
const C = {
  // sfondi
  bg:        "#f5f3ee",       // crema warm
  bgAlt:     "#ece8df",
  surface:   "#ffffff",
  card:      "#ffffff",
  cardHov:   "#fafaf7",

  // primario indigo/viola
  accent:    "#6366f1",
  accent2:   "#8b5cf6",
  accentDim: "#eef0ff",
  accentDeep:"#1e1b4b",       // navy profondo per il rail navigazione

  // verde "mint" per completato/successo
  green:     "#10b981",
  greenDim:  "#d1fae5",

  // ambra per "in compilazione" / warnings
  orange:    "#f59e0b",
  orangeDim: "#fef3c7",

  // rosso coral per errori
  red:       "#ef4444",
  redDim:    "#fee2e2",

  // teal per accenti freddi
  teal:      "#14b8a6",
  tealDim:   "#ccfbf1",

  // testi
  text:      "#1c1917",
  textSec:   "#44403c",
  muted:     "#78716c",
  mutedLight:"#a8a29e",

  // bordi
  border:    "#ebe7df",
  borderMed: "#d6d3cc",

  // ombre soft, diffuse
  shadow:    "0 1px 2px rgba(28,25,23,0.04), 0 2px 4px rgba(28,25,23,0.04)",
  shadowMd:  "0 6px 16px rgba(28,25,23,0.06), 0 2px 4px rgba(28,25,23,0.04)",
  shadowLg:  "0 16px 40px rgba(28,25,23,0.08), 0 4px 12px rgba(28,25,23,0.05)",
  shadowGlow:"0 0 0 1px rgba(99,102,241,0.12), 0 8px 24px rgba(99,102,241,0.18)",
};

// Scala di border-radius "bento": dai chip ai grandi container
const R = {
  xs: 8,
  sm: 12,
  md: 18,
  lg: 24,
  xl: 32,
  pill: 999,
};
const hex = (c, a) => {
  if (!c || typeof c !== "string") return "#000000";
  return c + Math.round(Math.max(0, Math.min(1, a)) * 255).toString(16).padStart(2, "0");
};
const grad = (a, b, deg = 135) => `linear-gradient(${deg}deg, ${a}, ${b})`;

// ─── DATE UTILITIES ───────────────────────────────────────────────────────────
const isoToIt = (s) => {
  if (!s) return "";
  if (s.includes("/")) return s;
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return s;
  return `${d}/${m}/${y}`;
};
const itToIso = (s) => {
  if (!s) return "";
  if (s.includes("-")) return s;
  const [d, m, y] = s.split("/");
  if (!d || !m || !y) return s;
  return `${y}-${m}-${d}`;
};

// ─── API ──────────────────────────────────────────────────────────────────────
const LOCAL_TOKEN = process.env.REACT_APP_LOCAL_TOKEN || "";
const ANTHROPIC_HEADERS = {
  "Content-Type": "application/json",
  "x-local-token": LOCAL_TOKEN,
};
// Strategia a 2 modelli per il CONTENUTO del PEI:
//   • Sonnet = default (qualità e coerenza su tutti i campi sostanziali)
//   • Opus   = campi a ragionamento pesante (obiettivi Sez.5/8, barriere/facilitatori Sez.6)
//              + Verifica di coerenza (ragiona sull'intero PEI)
// Haiku resta SOLO per i lookup non creativi (ricerca ICD-10).
const MODELS = {
  haiku:  "claude-haiku-4-5-20251001",
  sonnet: "claude-sonnet-4-6",
  opus:   "claude-opus-4-8",
};
const MODEL = MODELS.sonnet; // default per la generazione di contenuto (usato da pickModel)

// Campi a ragionamento pesante → Opus.
//  - s5_{A|B|C|D}_ob   → obiettivi per dimensione (Sez. 5)
//  - s6_barriere/facilitatori → analisi gap ICF (Sez. 6)
//  - s8_{i}_ob / s8_{i}_c_obiettivi e relativi s8_cx_{j}_… → obiettivi per disciplina (Sez. 8)
const isCampoOpus = (campoId = "") =>
  /^s5_[A-D]_ob$/.test(campoId) ||
  campoId === "s6_barriere" || campoId === "s6_facilitatori" ||
  /^s8_\d+_(ob|c_obiettivi)$/.test(campoId) ||
  /^s8_cx_\d+_(ob|c_obiettivi)$/.test(campoId);

// Selettore centralizzato del modello: unico punto di verità per l'instradamento.
const pickModel = (reason, metadata) => {
  if (reason === "verifica_coerenza") return MODELS.opus;   // ragiona su tutto il PEI
  if (reason === "icd10_search")      return MODELS.haiku;  // lookup non creativo, economico
  if (reason === "ai_field" && isCampoOpus(metadata?.campoId)) return MODELS.opus;
  return MODEL;                                             // default Sonnet (incl. doc_extraction)
};

const SYSTEM_PROMPT_PEI = `Sei un esperto di pedagogia speciale e inclusione scolastica italiana con profonda conoscenza della normativa vigente. Il tuo compito è assistere i docenti nella compilazione del Piano Educativo Individualizzato (PEI).

## NORMATIVA DI RIFERIMENTO

### D.Lgs 66/2017 e D.Lgs 62/2024 (Riforma disabilità)
- Il PEI è elaborato dal GLO (Gruppo di Lavoro Operativo) entro ottobre
- Il GLO comprende: docenti, genitori/tutori, specialisti ASL, dirigente
- Il PEI deve essere coerente con il Profilo di Funzionamento (PF) redatto secondo ICF
- Valutazione basata su criteri definiti nel PEI, non sui programmi ministeriali standard
- Dal 2026: accertamento disabilità tramite INPS con valutazione multidimensionale

### D.I. 153/2023 (Modelli PEI)
- 4 allegati: A1 Infanzia, A2 Primaria, A3 Secondaria I grado, A4 Secondaria II grado
- Struttura in 12 sezioni obbligatorie
- Sezione 4: osservazioni nelle 4 dimensioni ICF
- Sezione 5: obiettivi, attività e strategie per dimensione
- Sezione 8: curricolo con scelta A (classe) o B (personalizzato)
- Verifica intermedia e finale obbligatorie
- PEI provvisorio entro 30 giugno per nuove certificazioni

## MODELLO ICF — 4 DIMENSIONI DEL PEI
- **Dim. A — Relazione/Interazione/Socializzazione**: capacità relazionale, partecipazione sociale, gestione emotiva
- **Dim. B — Comunicazione/Linguaggio**: comprensione, espressione verbale e non verbale
- **Dim. C — Autonomia/Orientamento**: autonomia personale, orientamento spaziale e temporale
- **Dim. D — Cognitiva/Neuropsicologica/Apprendimento**: attenzione, memoria, problem solving, apprendimenti

## DOMINI ICF PRINCIPALI (elenco ESEMPLIFICATIVO, non esaustivo: un Profilo di Funzionamento reale usa molti altri codici, tutti legittimi)
**Funzioni corporee (b)**: b117 intelletto, b140 attenzione, b144 memoria, b152 emozioni, b160 pensiero, b164 funzioni cognitive superiori, b167 funzioni mentali del linguaggio, b172 funzioni di calcolo, b320 articolazione, b760 controllo movimenti
**Attività e partecipazione (d)**: d140 imparare a leggere, d145 imparare a scrivere, d150 imparare a calcolare (blocco d130-d159, ACQUISIZIONE della strumentalità); d166 leggere, d170 scrivere, d172 calcolare (blocco d160-d179, APPLICAZIONE della strumentalità già acquisita); d160 focalizzare attenzione, d163 pensare, d175 risolvere problemi, d310 messaggi verbali, d330 parlare, d350 conversazione, d710-d770 relazioni interpersonali
Nota: leggere e scrivere sono attività, non funzioni corporee — in ICF non esiste un codice "b" per la lettura o la scrittura. Entrambe le terne d140/d145/d150 e d166/d170/d172 sono corrette: la scelta spetta a chi redige il Profilo di Funzionamento, non alla scuola.
**Fattori contestuali (e)**: e130 prodotti istruzione, e310 famiglia, e320 amici, e330 autorità, e340 assistenti, e355 professionisti sanitari, e585 istruzione/formazione

## UTILIZZO DEI CODICI ICF NEL PEI
Le regole che seguono valgono SOLO se la singola richiesta prevede la codifica. Se la richiesta contiene l'istruzione "CODIFICA ICF DISATTIVATA", quella prevale su tutto questo paragrafo e il testo va scritto senza codici.
- **Sezione 2 (Profilo di Funzionamento)**: se nel contesto è presente un Profilo di Funzionamento con codici ICF, riportali con entrambi i qualificatori (performance e capacità), formato "codice.XX" (es. "d160.22"), con breve descrizione. In assenza di un Profilo con codici ICF nel contesto, NON assegnare qualificatori di gravità arbitrari (segui la regola "CODICI ICF SENZA FONTE" eventualmente presente nel contesto).
- **Sezione 4 (Osservazioni)**: includi i codici ICF con un solo qualificatore (quello della performance osservata), nel formato "codice.X" (es. "b140.2"). Il codice va inserito tra parentesi tonde dopo la frase descrittiva.
- **Nelle altre sezioni (5, 6, 7, 8)**, non aggiungere codici ICF a meno che l'utente non lo richieda esplicitamente.

## OBIETTIVI PER IL PEI
Gli obiettivi si formulano come frasi brevi all'infinito (max 10 parole), senza arco temporale.
- Verbi A (Relazione): partecipare, interagire, collaborare, rispettare, condividere
- Verbi B (Comunicazione): esprimere, comunicare, comprendere, descrivere, raccontare
- Verbi C (Autonomia): eseguire, gestire, organizzare, orientarsi, completare
- Verbi D (Cognitiva): leggere, scrivere, calcolare, risolvere, memorizzare, classificare

## CRITERI DI VALUTAZIONE
- Percorso A (classe): stessi criteri, eventualmente obiettivi minimi
- Percorso B (personalizzato): criteri definiti nel PEI per ogni disciplina
- Strumenti inclusivi: prove orali, mappe, calcolatrici, sintesi vocale, tempi aggiuntivi

## REGOLE DI SCRITTURA DEL PEI
1. Usa sempre linguaggio ICF (capacità, attività, partecipazione, fattori contestuali)
2. Descrivi SEMPRE i punti di forza PRIMA delle difficoltà
3. Usa frasi positive: "sa fare", "è in grado di", "partecipa quando", "mostra interesse per"
4. Collega obiettivi al profilo di funzionamento
5. Assicurati che obiettivi, attività e strategie siano coerenti tra loro
6. Non inventare dati: se mancano informazioni necessarie, NON riempire con testo generico ma inserisci un segnaposto esplicito tra parentesi quadre da personalizzare (es. "[da personalizzare: indicare la situazione di partenza]")
7. Rispetta rigorosamente i modelli ministeriali D.I. 153/2023
8. Non utilizzare mai il grassetto (doppi asterischi) nei testi generati. Non scrivere titoli o sottotitoli all'interno dei campi (es. "Obiettivi:", "Attività:", "Barriere:"). Fanno eccezione solo i separatori obbligatori: in Sezione 5, campo "Strategie e Strumenti", usa "STRATEGIE" e "STRUMENTI" (in maiuscolo, senza grassetto) su righe separate. In Sezione 8, campo "Strategie e Metodologie" inizia con "STRATEGIE E METODOLOGIE", campo "Strumenti e Materiali" inizia con "STRUMENTI E MATERIALI".
9. Nelle sezioni narrative (5, 7, 8): quando citi una metodologia o tecnica specialistica, usa prima una descrizione in linguaggio comune e poi il termine tecnico tra parentesi. Es. "pause cognitive brevi (brain break)", "apprendimento tra pari (peer tutoring)", "rinforzo immediato del comportamento (token economy)". Eccezione: nei campi a voce sintetica (es. lista strategie in Sez.5) puoi usare il solo termine tecnico per brevità.
10. PRIVACY OBBLIGATORIA: nei testi generati riferisci allo studente sempre e solo con la sua iniziale (es. "lo studente S." oppure "l'alunno M."). Non scrivere mai nomi completi, cognomi, date di nascita o qualsiasi altro dato anagrafico. I dati sanitari e diagnostici nei documenti caricati sono dati sensibili: non trasmetterli mai a terzi né inserirli integralmente nelle risposte — usali solo come contesto interno per elaborare suggerimenti pertinenti. ECCEZIONE — OBIETTIVI DI APPRENDIMENTO (Sez. 5 e Sez. 8): negli elenchi di obiettivi NON scrivere il soggetto. Usa direttamente il verbo ("Legge semplici testi con supporto visivo", NON "Lo studente S. legge…").`;

// ─── CHIAMATA A CLAUDE ────────────────────────────────────────────────────
// Quando l'utente è loggato e Supabase è configurato → Edge Function
// `claude-proxy` (scala crediti atomicamente, chiave Anthropic mai esposta).
// Altrimenti → fallback al server.js locale (modalità demo / dev).
//
// Errore speciale "InsufficientCredits": il chiamante (FieldAI/VerificaCoerenza)
// lo intercetta per mostrare la CTA "Acquista crediti".

class InsufficientCreditsError extends Error {
  constructor(balance) {
    super('insufficient_credits');
    this.code = 'insufficient_credits';
    this.balance = balance ?? 0;
  }
}

async function callClaude(prompt, maxTokens = 800, reason = 'ai_field', metadata = null) {
  const model = pickModel(reason, metadata);
  // ─── PROD: passa per Edge Function (auth + crediti server-side) ─────
  if (SUPABASE_ENABLED) {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      const { data, error } = await supabase.functions.invoke('claude-proxy', {
        body: {
          model,
          max_tokens: maxTokens,
          system:     SYSTEM_PROMPT_PEI,
          messages:   [{ role: "user", content: prompt }],
          reason,
          metadata,
        },
      });
      if (error) {
        // Stato 402 = crediti insufficienti
        const status = error.context?.status || error.status;
        if (status === 402) throw new InsufficientCreditsError(error.context?.balance);
        // Prova a leggere il detail dall'Edge Function (es. errore Anthropic)
        let detail = error.message || 'edge_function_error';
        try {
          const body = typeof error.context?.json === 'function'
            ? await error.context.json()
            : null;
          if (body?.detail) detail = body.detail;
          else if (body?.error) detail = body.error;
        } catch { /* best-effort */ }
        throw new Error(detail);
      }
      return data?.content?.find(b => b.type === "text")?.text || "";
    }
  }
  // ─── DEMO/DEV: server.js locale ────────────────────────────────────
  const res = await fetch("http://localhost:3001/claude", {
    method: "POST",
    headers: ANTHROPIC_HEADERS,
    body: JSON.stringify({
      model,
      max_tokens: maxTokens,
      system: SYSTEM_PROMPT_PEI,
      messages: [{ role: "user", content: prompt }],
    }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (data.error) throw new Error(data.error.message);
  return data.content?.find(b => b.type === "text")?.text || "";
}

// `imageBase64` può essere una singola stringa base64 OPPURE un array di
// { data, mediaType }: in quest'ultimo caso TUTTE le immagini viaggiano in
// UN'UNICA chiamata (= 1 credito), così l'OCR multi-pagina non moltiplica il costo.
async function callClaudeVision(imageBase64, mimeType, prompt, reason = 'doc_extraction', metadata = null, maxTokens = 2000) {
  const imgs = Array.isArray(imageBase64)
    ? imageBase64
    : [{ data: imageBase64, mediaType: mimeType }];
  const messages = [{
    role: "user",
    content: [
      ...imgs.map(im => ({ type: "image", source: { type: "base64", media_type: im.mediaType, data: im.data } })),
      { type: "text", text: prompt }
    ]
  }];

  const model = pickModel(reason, metadata);
  // ─── PROD: Edge Function ─────────────────────────────────────────────
  if (SUPABASE_ENABLED) {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      const { data, error } = await supabase.functions.invoke('claude-proxy', {
        body: { model, max_tokens: maxTokens, system: SYSTEM_PROMPT_PEI, messages, reason, metadata },
      });
      if (error) {
        const status = error.context?.status || error.status;
        if (status === 402) throw new InsufficientCreditsError(error.context?.balance);
        throw new Error(error.message || 'edge_function_error');
      }
      return data?.content?.find(b => b.type === "text")?.text || "";
    }
  }
  // ─── DEMO/DEV: server.js locale ────────────────────────────────────
  const res = await fetch("http://localhost:3001/claude", {
    method: "POST",
    headers: ANTHROPIC_HEADERS,
    body: JSON.stringify({ model, max_tokens: maxTokens, system: SYSTEM_PROMPT_PEI, messages }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (data.error) throw new Error(data.error.message);
  return data.content?.find(b => b.type === "text")?.text || "";
}

// ─── FUNZIONI DI RICERCA ICD‑10 (ROBUSTE) ─────────────────────────────────────
const normICD = (s) => String(s || '')
  .toLowerCase()
  .replace(/[\u2018\u2019\u02bc\u00b4`]/g, "'")
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .replace(/\s+/g, ' ')
  .trim();

const RE_CODICE_ICD = /^[a-z]\d{2}(\.\d{1,2})?$/;

const SINONIMI_ICD = [
  ['disabilita intellettiva', 'ritardo mentale'],
  ['ritardo mentale', 'disabilita intellettiva'],
  ['disturbo dello spettro autistico', 'autismo'],
  ['spettro autistico', 'autismo'],
  ['autismo', 'disturbo pervasivo dello sviluppo'],
  ['sordita', "perdita dell'udito"],
  ["perdita dell'udito", 'sordita'],
  ['ipovisione', 'deficit visivo'],
  ['deficit visivo', 'ipovisione'],
  ['mutismo selettivo', 'mutismo elettivo'],
  ['mutismo elettivo', 'mutismo selettivo'],
  ['moderato', 'media gravita'],
  ['moderata', 'media gravita'],
  ['emotivo', 'emozionale'],
  ['emotivi', 'emozionali'],
  ['disortografia', 'compitazione'],
  ['paralisi cerebrale infantile', 'paralisi cerebrale'],
];

function espandiQueryICD(ql) {
  const varianti = [ql];
  for (const [da, a] of SINONIMI_ICD) {
    if (ql.includes(da)) {
      const v = ql.split(da).join(a);
      if (!varianti.includes(v)) varianti.push(v);
    }
  }
  return varianti;
}

const tokenNelTesto = (testo, t) =>
  testo.includes(t) || (t.length >= 5 && testo.includes(t.slice(0, -1)));

function punteggioICD(e, ql, tokens) {
  const code = normICD(e.code);
  const desc = normICD(e.description || '');
  let p;
  if (code === ql) p = 0;
  else if (code.startsWith(ql)) p = 1;
  else if (desc.startsWith(ql)) p = 2;
  else if (tokens.every(t => tokenNelTesto(desc, t))) p = 3;
  else p = 4;
  return e.ess ? p - 1.5 : p;
}

function cercaICD10(q) {
  if (!q || q.trim().length < 2) return [];
  const ql = normICD(q);

  if (RE_CODICE_ICD.test(ql)) {
    const base = ql.split('.')[0];
    const gruppo = ICD10_DB.filter(e => {
      const c = normICD(e.code);
      return c === base || c.startsWith(base + '.');
    });
    if (gruppo.length > 0) {
      return gruppo
        .sort((a, b) => {
          const ca = normICD(a.code), cb = normICD(b.code);
          if (ca === ql) return -1;
          if (cb === ql) return 1;
          return ca.localeCompare(cb);
        })
        .slice(0, 12);
    }
  }

  const varianti = espandiQueryICD(ql).map(v => v.split(' ').filter(Boolean));
  const results = ICD10_DB.filter(e => {
    const testo = normICD(`${e.code} ${e.description || ''} ${e.alt || ''}`);
    return varianti.some(tokens => tokens.every(t => tokenNelTesto(testo, t)));
  });
  const tokens = ql.split(' ').filter(Boolean);
  return results
    .sort((a, b) => {
      const d = punteggioICD(a, ql, tokens) - punteggioICD(b, ql, tokens);
      return d !== 0 ? d : normICD(a.code).localeCompare(normICD(b.code));
    })
    .slice(0, 10);
}

function suggerisciICD10(diagnosi) {
  if (!diagnosi || diagnosi.length < 2) return [];
  const d = diagnosi.toLowerCase();
  
  // Mappa COMPLETA parole chiave italiane → codici ICD-10
  const map = {
    // DSA
    "disless": ["F81.0"], "dislessia": ["F81.0"],
    "disortograf": ["F81.1"], "disgrafia": ["F81.8"],
    "discalcul": ["F81.2"], "discalculia": ["F81.2"],
    "dsa": ["F81.0", "F81.1", "F81.8", "F81.2"],
    "disturbo specifico apprendimento": ["F81.0", "F81.1", "F81.2", "F81.8", "F81.3"],
    "disturbi specifici apprendimento": ["F81.0", "F81.1", "F81.2", "F81.8", "F81.3"],
    
    // ADHD e attenzione
    "adhd": ["F90.0", "F90.1", "F90.2", "F90.8", "F90.9"],
    "iperattiv": ["F90.1", "F90.8"],
    "deficit attenzione": ["F90.0"],
    "attenzione": ["F90.0"],
    "disturbo da deficit dell'attenzione": ["F90.0"],
    "disturbo da deficit di attenzione": ["F90.0"],
    
    // Spettro autistico e disturbi pervasivi dello sviluppo
    "autis": ["F84.0"], "autismo": ["F84.0"],
    "asd": ["F84.0"], "spettro": ["F84.0"],
    "asperger": ["F84.5"], "sindrome di asperger": ["F84.5"],
    "alterazione globale": ["F84.9"],
    "sviluppo psicologico": ["F84.9"],
    "disturbo pervasivo": ["F84.0", "F84.8", "F84.9"],
    "pervasivo non specificato": ["F84.9"],
    "disturbo pervasivo non specificato": ["F84.9"],
    "autismo atipico": ["F84.1"],
    "sindrome di rett": ["F84.2"], "rett": ["F84.2"],
    "sindrome di heller": ["F84.3"],
    
    // Disabilità intellettiva / Funzionamento cognitivo
    "disabilità intellettiva": ["F70", "F71", "F72", "F73", "F78", "F79"],
    "ritardo mentale": ["F70", "F71", "F72", "F73", "F78", "F79"],
    "ritardo cognitivo": ["F70", "F71", "F72", "F73", "F78", "F79"],
    "funzionamento cognitivo limite": ["F70", "F79"],
    "funzionamento intellettivo limite": ["F70", "F79"],
    "fil": ["F70", "F79"],
    "cognitivo limite": ["F70", "F79"],
    "ritardo": ["F70", "F71", "F72", "F73", "F78", "F79"],
    
    // Linguaggio e comunicazione
    "linguaggio": ["F80.1", "F80.2"],
    "ritardo del linguaggio": ["F80.1", "F80.2"],
    "disturbo del linguaggio": ["F80.1", "F80.2"],
    "disturbo linguaggio": ["F80.1", "F80.2"],
    "balbuzie": ["F98.5"],
    "farfallismo": ["F98.6"],
    "mutismo": ["F94.0"],
    "mutismo selettivo": ["F94.0"],
    
    // Condotta e oppositività
    "oppositivo": ["F91.3"],
    "condotta": ["F91.0", "F91.1", "F91.2", "F91.3", "F91.8", "F91.9"],
    "comportamento dirompente": ["F91.8"],
    
    // Emotivo e ansia
    "emotivo": ["F93.8", "F93.9"],
    "disturbo emotivo": ["F93.8", "F93.9"],
    "ansia da separazione": ["F93.0"],
    "separazione": ["F93.0"],
    "ansia sociale": ["F93.2"],
    "fobia sociale": ["F93.2"],
    "fobia specifica": ["F93.1"],
    "rivalità tra fratelli": ["F93.3"],
    "ansia": ["F41.1", "F41.0", "F41.8"],
    "ansia generalizzata": ["F41.1"],
    "panico": ["F41.0"],
    
    // Umore
    "depressione": ["F32.0", "F32.1", "F32.2"],
    "depressiv": ["F32.0", "F32.1", "F32.2"],
    
    // Altri disturbi infanzia
    "pica": ["F98.3"],
    "enuresi": ["F98.0"],
    "encopresi": ["F98.1"],
    "tic": ["F95.0", "F95.1", "F95.2", "F95.8", "F95.9"],
    "tourette": ["F95.2"],
    
    // Sindromi genetiche
    "down": ["Q90"],
    "trisomia 21": ["Q90"],
    "angelman": ["Q93.5"],
    "williams": ["Q93.5"],
    "sindrome di williams": ["Q93.5"],
    "prader willi": ["Q93.5"],
    "sindrome di prader": ["Q93.5"],
    "monosomia": ["Q93"],
    "delezione cromosomica": ["Q93", "Q99.8"],
    "anomalia cromosomica": ["Q99.8"],
    "malformazione cerebrale": ["Q04"],
    "agenesia del corpo calloso": ["Q04.3"],
    "corpo calloso": ["Q04.3"],
    "malformazione sistema nervoso": ["Q07.8"],
    "sindrome polimalformativa": ["Q87.8"],
    "kawasaki": ["M30.3"],
    "sindrome di kawasaki": ["M30.3"],

    // Neurologici acquisiti e funzionali
    "epilessia": ["G40", "G40.8"],
    "epilettico": ["G40", "G40.8"],
    "crisi epilettiche": ["G40"],
    "convulsioni": ["G40"],
    "idrocefalo": ["G91"],
    "idrocefalico": ["G91"],
    "emorragia subaracnoidea": ["I60"],
    "emorragia cerebrale": ["I60"],
    "trauma cranico": ["T90"],
    "sequele trauma": ["T90"],
    "sequele traumatismo": ["T90"],

    // Sensoriale e comunicazione
    "disfagia": ["R13"],
    "difficoltà deglutizione": ["R13"],
    "deglutizione": ["R13"],
    "dislalia": ["F80.0"],
    "disturbo fonologico": ["F80.0"],
    "fonologico": ["F80.0"],

    // Disabilità intellettiva estesa
    "fcl": ["F70", "F79"],
    "disabilità intellettiva grave": ["F72"],
    "disabilità intellettiva non specificata": ["F79"],
    "ritardo grave": ["F72"],

    // Disturbi misti dello sviluppo
    "disturbo misto": ["F83", "F81.3"],
    "disturbi misti": ["F83"],
    "misto delle abilità": ["F81.3"],
    "abilità scolastiche miste": ["F81.3"],

    // Endocrini e metabolici
    "diabete insipido": ["E23.2"],
    "insufficienza surrenale": ["E27.2"],
    "surrenale": ["E27.2"],
    "enterocolite necrotizzante": ["P77"],
    "malattia epatica": ["K71.0"],

    // Cardiaci
    "cardiopatia": ["I52"],
    "disturbo cardiaco": ["I52"],

    // Fattori sociali e ambientali
    "disagio sociale": ["Z60"],
    "problemi sociali": ["Z60"],
    "ambiente sociale": ["Z60"],
    "eventi negativi": ["Z61"],
    "trauma infantile": ["Z61"],
    "abuso": ["Z61"],
    "trascuratezza": ["Z61"],
    "neglect": ["Z61"],

    // Motori
    "paralisi cerebrale": ["G80"],
    "pci": ["G80"],
    "distrofia muscolare": ["G71.0"],
    "dcd": ["F82"],
    "disprassia": ["F82"],
    "disturbo della coordinazione motoria": ["F82"],

    // Sensoriale
    "sordità": ["H90.0", "H90.3", "H90.5", "H90.8"],
    "ipoacusia": ["H90.0", "H90.3", "H90.5", "H90.8"],
    "sordocecità": ["H90.3", "H54.0"],
    "ipovisione": ["H54.0", "H54.1", "H54.2"],
    "cecità": ["H54.0", "H54.1"],
    "visus": ["H54.2"],

    // Altro
    "identità di genere": ["F64.2"],
    "attaccamento reattivo": ["F94.1"],
    "attaccamento disinibito": ["F94.2"]
  };

  const found = new Set();
  
  // 1. Ricerca tramite mappa parole chiave
  Object.entries(map).forEach(([k, codes]) => {
    if (d.includes(k)) codes.forEach(c => found.add(c));
  });
  
  // 2. Riconoscimento di codici ICD-10 scritti esplicitamente (es. F93.8, F84.9)
  const codiceMatch = d.match(/[a-zA-Z]\d{2}(?:\.\d{1,2})?/g);
  if (codiceMatch) {
    codiceMatch.forEach(codice => {
      const codiceCercato = codice.toUpperCase();
      // Aggiunge sempre il codice esplicito, anche se non è nel DB locale
      // (il DB ICD-10-CM potrebbe non avere tutti i codici ICD-10 WHO usati in Italia)
      found.add(codiceCercato);
      // Se non è già nel DB, aggiungilo con descrizione generica temporanea
      if (!ICD10_DB.find(e => e.code === codiceCercato)) {
        ICD10_DB.push({ code: codiceCercato, description: `Codice ICD-10: ${codiceCercato}` });
      }
    });
  }
  
  // 3. Se non abbiamo trovato nulla, prova una ricerca testuale nel database
  //    cercando parole significative della diagnosi nelle descrizioni (inglese)
  let results = ICD10_DB.filter(e => found.has(e.code));
  
  if (results.length === 0 && d.length > 3) {
    // Estrai parole significative (lunghezza > 3) ed escludi stopword
    const stopwords = ["della", "dello", "delle", "degli", "dei", "con", "per", "dal", "dal", "nel", "nella", "nello", "il", "la", "i", "le", "un", "una", "uno", "e", "ed", "a", "da", "di", "in", "su", "tra", "fra"];
    const parole = d.split(/[\s,.-]+/).filter(w => w.length > 3 && !stopwords.includes(w));
    
    // Mappa di traduzione approssimativa per parole comuni (italiano -> inglese)
    const traduzioni = {
      "attenzione": "attention",
      "deficit": "deficit",
      "iperattività": "hyperactivity",
      "iperattivo": "hyperactive",
      "lettura": "reading",
      "scrittura": "writing",
      "calcolo": "calculation",
      "matematica": "mathematics",
      "linguaggio": "language",
      "disturbo": "disorder",
      "emotivo": "emotional",
      "relazionale": "relational",
      "cognitivo": "cognitive",
      "funzionamento": "functioning",
      "limite": "borderline",
      "intellettivo": "intellectual",
      "autismo": "autism",
      "spettro": "spectrum",
      "ansia": "anxiety",
      "depressione": "depression",
      "condotta": "conduct",
      "oppositivo": "oppositional",
      "provocatorio": "defiant"
    };
    
    for (let parola of parole) {
      const termine = traduzioni[parola] || parola;
      results = ICD10_DB.filter(e => {
        const desc = String(e.description || '').toLowerCase();
        return desc.includes(termine);
      });
      if (results.length > 0) break; // esci al primo termine che produce risultati
    }
  }
  
  // 4. Fallback finale: restituisci i primi codici che contengono la diagnosi
  if (results.length === 0 && d.length > 3) {
    results = ICD10_DB.filter(e => {
      const desc = String(e.description || '').toLowerCase();
      return desc.includes(d.substring(0, 15));
    }).slice(0, 5);
  }
  
  return results.slice(0, 8);
}

// ─── DATABASE ICD-10 CUSTOM (persistente in localStorage) ────────────────────
// Si popola automaticamente con i codici cercati dagli insegnanti via AI
const ICD10_CUSTOM_KEY = "iep_icd10_custom_db";

function caricaICD10Custom() {
  try {
    const raw = localStorage.getItem(ICD10_CUSTOM_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function salvaICD10Custom(db) {
  try {
    localStorage.setItem(ICD10_CUSTOM_KEY, JSON.stringify(db));
  } catch (e) { console.warn("ICD10 custom DB save error:", e); }
}

// ─── SEGNALAZIONE CODICI MANCANTI ────────────────────────────────────────────
// Quando un docente aggiunge manualmente un codice non presente nel DB locale,
// lo registriamo su Supabase così che lo sviluppatore possa implementarlo.
async function segnalaCodiceMancanteASupabase(code, description) {
  if (!SUPABASE_ENABLED || !supabase) return;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const userEmail = session?.user?.email || null;
    await supabase.from('icd10_segnalazioni').insert({
      code: code.toUpperCase(),
      description: description || null,
      user_email: userEmail,
    });
  } catch (e) {
    // Silenzioso: la segnalazione è best-effort, non deve bloccare il flusso
    console.warn('icd10_segnalazioni insert failed:', e);
  }
}

function aggiungiACodiciCustom(entry) {
  const db = caricaICD10Custom();
  if (!db.find(e => e.code === entry.code)) {
    db.push(entry);
    salvaICD10Custom(db);
    // Aggiunge anche al DB in memoria se non già presente
    if (!ICD10_DB.find(e => e.code === entry.code)) {
      ICD10_DB.push(entry);
    }
  }
}

// Inizializza: carica i codici custom nel DB in-memory all'avvio
(function initCustomDB() {
  const custom = caricaICD10Custom();
  custom.forEach(e => {
    if (!ICD10_DB.find(x => x.code === e.code)) {
      ICD10_DB.push(e);
    }
  });
})();

async function cercaICD10ViaAI(diagnosi) {
  // Chiede a Claude di identificare i codici ICD-10. Costa 1 credito (icd10_search).
  const prompt = `Sei un medico esperto. Per la seguente diagnosi o condizione clinica, fornisci i codici ICD-10 più appropriati.

Diagnosi: "${diagnosi}"

Rispondi ESCLUSIVAMENTE con un array JSON valido (senza markdown, senza spiegazioni) nel formato:
[{"code":"X00.0","description":"Descrizione in italiano"}]

Se la diagnosi non corrisponde a nessun codice ICD-10 noto, rispondi con: []
Massimo 5 codici. Preferisci codici ICD-10 usati in Italia per contesti scolastici/pediatrici.`;

  const testo = await callClaude(prompt, 400, 'icd10_search', { diagnosi });
  const pulito = (testo || "[]").replace(/```json|```/g, "").trim();
  const match = pulito.match(/\[[\s\S]*\]/);
  if (!match) return [];
  return JSON.parse(match[0]);
}

// Ricerca INVERSA: dato un codice ICD-10, restituisce la diagnosi corrispondente
// Salva l'esito nel DB locale custom così da arricchire l'archivio.
async function cercaDiagnosiViaAI(code) {
  const codeTrim = (code || "").trim().toUpperCase();
  if (!codeTrim) return null;

  // ── 1. Consulta la cache condivisa Supabase (gratuita, zero crediti) ──
  if (SUPABASE_ENABLED && supabase) {
    try {
      const { data } = await supabase
        .from('icd10_cache')
        .select('code, description')
        .eq('code', codeTrim)
        .maybeSingle();
      if (data?.description) {
        return { code: data.code, description: data.description };
      }
    } catch (e) {
      console.warn('icd10_cache lookup error:', e);
    }
  }

  // ── 2. Cache miss → chiama l'AI (gratuito: icd10_search costa 0 crediti) ──
  const prompt = `Sei un medico esperto di codifiche cliniche ICD-10.
Per il codice ICD-10 "${codeTrim}" restituisci la descrizione clinica corrispondente.

Rispondi ESCLUSIVAMENTE con un oggetto JSON valido (senza markdown, senza spiegazioni) in questo formato:
{"code":"${codeTrim}","description":"Descrizione in italiano"}

Se il codice non esiste o non è un codice ICD-10 valido, rispondi con: {"code":"${codeTrim}","description":""}
Usa la terminologia clinica italiana standard, breve (massimo 12 parole).`;

  const testo = await callClaude(prompt, 200, 'icd10_search', { code: codeTrim });
  const pulito = (testo || "{}").replace(/```json|```/g, "").trim();
  const match = pulito.match(/\{[\s\S]*\}/);
  if (!match) return null;
  try {
    const obj = JSON.parse(match[0]);
    if (obj && obj.code && obj.description && obj.description.trim()) {
      const risultato = { code: obj.code.toUpperCase(), description: obj.description.trim() };
      // ── 3. Salva nella cache condivisa per tutti gli utenti futuri ──
      if (SUPABASE_ENABLED && supabase) {
        supabase.from('icd10_cache').insert(risultato, { ignoreDuplicates: true })
          .then(() => {}).catch(() => {}); // fire-and-forget, non blocca
      }
      return risultato;
    }
    return null;
  } catch { return null; }
}

// ─── COSTANTI ─────────────────────────────────────────────────────────────────
const ORDINI = {
  infanzia:    { label:"Scuola dell'Infanzia",  allegato:"A1", icon:"🧸", color:C.accent2, haProgDisc:false, haCert:false },
  primaria:    { label:"Scuola Primaria",        allegato:"A2", icon:"📚", color:C.accent,  haProgDisc:false, haCert:true  },
  secondaria1: { label:"Secondaria I Grado",     allegato:"A3", icon:"🎒", color:C.teal,    haProgDisc:true,  haCert:true  },
  secondaria2: { label:"Secondaria II Grado",    allegato:"A4", icon:"🎓", color:C.green,   haProgDisc:true,  haCert:true  },
};

const ETA_ESEMPIO_ORDINE = { infanzia: 3, primaria: 6, secondaria1: 11, secondaria2: 14 };

// Ordini di scuola ammessi per ciascuna fascia (band).
// Mirror lato client di band_consente_ordine() nella migration 0007.
// band null/sconosciuta → tutti gli ordini (utenti senza fascia / modalità demo).
const BAND_ORDINI = {
  inf_pri: ["infanzia", "primaria"],
  sec1:    ["secondaria1"],
  sec2:    ["secondaria2"],
};
function ordiniConsentiti(band) {
  return BAND_ORDINI[band] || Object.keys(ORDINI);
}

const DIMENSIONI = [
  { id:"A", label:"Relazione / Interazione / Socializzazione",       color:C.accent  },
  { id:"B", label:"Comunicazione / Linguaggio",                      color:C.teal    },
  { id:"C", label:"Autonomia / Orientamento",                        color:C.orange  },
  { id:"D", label:"Cognitiva / Neuropsicologica / Apprendimento",    color:C.accent2 },
];

const SEZIONI_PEI_BASE = [
  { id:"glo",  label:"Dati e GLO",                icon:"🏫" },
  { id:"s1",   label:"1. Quadro informativo",      icon:"👨‍👩‍👧" },
  { id:"s2",   label:"2. Profilo Funzionamento",   icon:"📋" },
  { id:"s3",   label:"3. Progetto Individuale",    icon:"🔗" },
  { id:"s4",   label:"4. Osservazioni",            icon:"👁"  },
  { id:"s5",   label:"5. Obiettivi e Strategie",   icon:"🎯"  },
  { id:"s6",   label:"6. Barriere/Facilitatori",   icon:"⚖️"  },
  { id:"s7",   label:"7. Ambiente Inclusivo",      icon:"🌿"  },
  { id:"s8",   label:"8. Curricolo",               icon:"📖"  },
  { id:"s9",   label:"9. Risorse",                 icon:"📦"  },
  { id:"s10",  label:"10. Certificazione",         icon:"🏅", soloOrdini:["primaria","secondaria1","secondaria2"] },
  { id:"s11",  label:"11. Verifica Finale",        icon:"✅"  },
  { id:"s12",  label:"12. PEI Provvisorio",        icon:"🔮"  },
  { id:"s_pdv", label:"Progetto di Vita (integrativo)",          icon:"🌱"  },
];

// ─── VALIDAZIONE SEZIONI PEI (usata dalla modalità guidata) ───────────────────
// Per ogni sezione restituisce { valida, mancanti, opzionale }.
//   valida    = i campi essenziali sono compilati (o la sezione è opzionale)
//   mancanti  = elenco campi obbligatori non ancora compilati
//   opzionale = la sezione può essere saltata senza compilare nulla
// In modalità guidata il bottone "Successivo" è abilitato se valida=true.
const MIN_TESTO = 20;
// Sezioni che il PEI ministeriale considera applicabili solo in casi specifici
// e che quindi possono essere saltate in modalità guidata.
//   s3  → Progetto Individuale (art. 14 L. 328/2000): non sempre redatto
//   s10 → Certificazione delle Competenze: solo per alunni in uscita dalla classe finale
//   s12 → PEI Provvisorio: solo nuove certificazioni / passaggi d'ordine
const SEZIONI_OPZIONALI = new Set(["s3", "s10", "s12"]);

function validaSezionePEI(id, dati) {
  const d = dati || {};
  const ok    = (k, min = MIN_TESTO) => (d[k] || "").trim().length >= min;
  const okAny = (keys, min = MIN_TESTO) => keys.some(k => (d[k] || "").trim().length >= min);
  const mancanti = [];
  const opzionale = SEZIONI_OPZIONALI.has(id);

  switch (id) {
    case "glo":
      if (!d.glo_pi_stato) mancanti.push("Stato del Progetto Individuale (Redatto / Non redatto)");
      break;
    case "s1":
      if (!ok("s1_famiglia")) mancanti.push("Situazione familiare e colloquio con la famiglia");
      break;
    case "s2":
      if (!ok("pf_descrizione")) mancanti.push("Profilo di Funzionamento complessivo");
      break;
    case "s3":
      // Opzionale — non si registra alcun campo come obbligatorio
      break;
    case "s4":
      if (!okAny(["s4_A_obs","s4_B_obs","s4_C_obs","s4_D_obs"]))
        mancanti.push("Osservazioni in almeno una delle 4 dimensioni ICF");
      break;
    case "s5":
      if (!okAny(["s5_A_ob","s5_B_ob","s5_C_ob","s5_D_ob"]))
        mancanti.push("Obiettivi in almeno una delle 4 dimensioni ICF");
      break;
    case "s6":
      if (!ok("s6_barriere"))     mancanti.push("Barriere");
      if (!ok("s6_facilitatori")) mancanti.push("Facilitatori");
      break;
    case "s7":
      if (!ok("s7_interventi"))
        mancanti.push("Interventi sul contesto per un ambiente di apprendimento inclusivo");
      break;
    case "s8":
      if (!ok("s8_1")) mancanti.push("Interventi educativo-didattici (8.1)");
      break;
    case "s9":
      if (!ok("s9_modalita", 10)) mancanti.push("Modalità di sostegno");
      break;
    case "s10":
      // Opzionale — solo per classi finali
      break;
    case "s11":
      if (!ok("s11_valutazione")) mancanti.push("Valutazione globale dei risultati");
      break;
    case "s12":
      // Opzionale — solo nuove certificazioni / passaggi d'ordine
      break;
    case "s_pdv":
      if (!ok("pdv_aspirazioni")) mancanti.push("Interessi e aspirazioni dello studente");
      break;
    default:
      break;
  }
  return { valida: mancanti.length === 0, mancanti, opzionale };
}

const COLORI_AVATAR = [C.accent, C.teal, C.orange, C.accent2, "#e11d48", "#0891b2"];

const annoAvvioScadenze = (d = new Date()) =>
  (d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1);
const scadenzeDefault = (avvio = annoAvvioScadenze()) => [
  {id:1,testo:"PEI definitivo approvato dal GLO",   data:`${avvio}-10-31`,   tipo:"pei",     urgente:false},
  {id:2,testo:"Verifica intermedia",                data:`${avvio+1}-02-28`, tipo:"verifica",urgente:false},
  {id:3,testo:"Verifica finale + PEI provvisorio",  data:`${avvio+1}-06-30`, tipo:"pei",     urgente:false},
];

// ─── STILI ────────────────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Fraunces:opsz,wght@9..144,400;9..144,600;9..144,700;9..144,800&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: 'Inter', system-ui, sans-serif;
    background: ${C.bg};
    color: ${C.text};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    font-feature-settings: 'cv11', 'ss01';
  }
  ::-webkit-scrollbar { width: 8px; height: 8px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: ${hex(C.muted,0.25)}; border-radius: 99px; border: 2px solid transparent; background-clip: padding-box; }
  ::-webkit-scrollbar-thumb:hover { background: ${hex(C.muted,0.45)}; background-clip: padding-box; }
  textarea, input, select { outline: none; }
  textarea:focus, input:focus, select:focus {
    border-color: ${C.accent} !important;
    box-shadow: 0 0 0 4px ${hex(C.accent, 0.12)} !important;
  }
  button { cursor: pointer; font-family: inherit; }
  button:disabled { cursor: not-allowed; opacity: .45; }
  .serif { font-family: 'Fraunces', Georgia, serif; font-feature-settings: 'ss01'; letter-spacing: -0.02em; }

  @keyframes fadeUp  { from{opacity:0;transform:translateY(14px);} to{opacity:1;transform:translateY(0);} }
  @keyframes fadeIn  { from{opacity:0;} to{opacity:1;} }
  @keyframes slideIn { from{opacity:0;transform:translateX(16px);} to{opacity:1;transform:translateX(0);} }
  @keyframes slideInLeft { from{opacity:0;transform:translateX(-16px);} to{opacity:1;transform:translateX(0);} }
  @keyframes spin    { to{transform:rotate(360deg);} }
  @keyframes popIn   { 0%{transform:scale(.4);opacity:0;} 60%{transform:scale(1.15);opacity:1;} 100%{transform:scale(1);} }
  @keyframes shimmer { 0%{background-position:-200% 0;} 100%{background-position:200% 0;} }
  @keyframes pulseGlow { 0%,100%{box-shadow:0 0 0 0 ${hex(C.accent,0.35)};} 50%{box-shadow:0 0 0 8px ${hex(C.accent,0)};} }
  @keyframes floatY  { 0%,100%{transform:translateY(0);} 50%{transform:translateY(-3px);} }
  @keyframes indeterminate { 0%{left:-40%;width:40%;} 50%{width:55%;} 100%{left:100%;width:40%;} }

  .fadeUp  { animation: fadeUp  .4s cubic-bezier(.16,1,.3,1) forwards; }
  .fadeIn  { animation: fadeIn  .25s ease forwards; }
  .slideIn { animation: slideIn .3s cubic-bezier(.16,1,.3,1) forwards; }
  .slideInLeft { animation: slideInLeft .35s cubic-bezier(.16,1,.3,1) forwards; }
  .spin    { animation: spin 1s linear infinite; }
  .popIn   { animation: popIn .5s cubic-bezier(.34,1.56,.64,1) forwards; }
  .floatY  { animation: floatY 3s ease-in-out infinite; }

  .hoverLift { transition: transform .25s cubic-bezier(.16,1,.3,1), box-shadow .25s ease; }
  .hoverLift:hover { transform: translateY(-2px); }

  .progressBar {
    background: linear-gradient(90deg, ${C.accent} 0%, ${C.accent2} 50%, ${C.accent} 100%);
    background-size: 200% 100%;
    animation: shimmer 3s linear infinite;
  }
  .indetTrack { position:relative; height:4px; border-radius:3px; background:${hex(C.accent,0.15)}; overflow:hidden; width:100%; }
  .indetBar { position:absolute; top:0; bottom:0; border-radius:3px; background:linear-gradient(90deg, ${C.accent}, ${C.accent2}); animation: indeterminate 1.15s cubic-bezier(.65,.05,.36,1) infinite; }
`;

// ─── UI PRIMITIVES ────────────────────────────────────────────────────────────
const Badge = ({ color = C.muted, bg, children, small }) => (
  <span style={{
    background: bg || hex(color, 0.10),
    color, border: `1px solid ${hex(color, 0.20)}`,
    borderRadius: R.pill, padding: small ? "2px 9px" : "4px 13px",
    fontSize: small ? 10 : 11, fontWeight: 600,
    whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 4,
    letterSpacing: 0.1
  }}>{children}</span>
);

const Btn = ({ children, onClick, primary, ghost, danger, small, disabled, style = {}, icon, loading: ld, title }) => {
  let bg = C.surface, col = C.textSec, brd = `1px solid ${C.border}`, shadow = "none";
  if (primary) {
    bg = grad(C.accent, C.accent2);
    col = "#fff"; brd = "none";
    shadow = `0 6px 18px ${hex(C.accent, 0.32)}, inset 0 1px 0 ${hex("#ffffff", 0.18)}`;
  }
  if (danger)  { bg = C.redDim; col = C.red; brd = `1px solid ${hex(C.red, 0.25)}`; }
  if (ghost)   { bg = "transparent"; col = C.muted; brd = `1px solid ${C.border}`; }
  return (
    <button disabled={disabled || ld} onClick={onClick} title={title}
      style={{
        background: bg, color: col, border: brd,
        borderRadius: R.pill, boxShadow: shadow,
        padding: small ? "6px 14px" : "11px 22px",
        fontSize: small ? 12 : 13, fontWeight: 600,
        fontFamily: "'Inter',sans-serif",
        transition: "all .2s cubic-bezier(.16,1,.3,1)",
        lineHeight: 1.4,
        display: "inline-flex", alignItems: "center", gap: 6,
        whiteSpace: "nowrap",
        ...style
      }}
      onMouseEnter={e => { if (!disabled && !ld) {
        e.currentTarget.style.transform = "translateY(-1px)";
        if (primary) e.currentTarget.style.boxShadow = `0 10px 24px ${hex(C.accent, 0.4)}, inset 0 1px 0 ${hex("#ffffff", 0.18)}`;
      }}}
      onMouseLeave={e => {
        e.currentTarget.style.transform = "translateY(0)";
        if (primary) e.currentTarget.style.boxShadow = `0 6px 18px ${hex(C.accent, 0.32)}, inset 0 1px 0 ${hex("#ffffff", 0.18)}`;
      }}
    >
      {ld
        ? <span className="spin" style={{ width:12,height:12,border:`2px solid ${hex(col,0.25)}`,borderTop:`2px solid ${col}`,borderRadius:"50%",display:"inline-block" }}/>
        : icon && <span style={{ fontSize: small ? 12 : 14 }}>{icon}</span>}
      {children}
    </button>
  );
};

const Input = ({ value, onChange, placeholder, type = "text", style = {}, onBlur }) => {
  const [local, setLocal] = useState(value || "");
  const prev = useRef(value);
  useEffect(() => {
    if (value !== prev.current) { prev.current = value; setLocal(value || ""); }
  }, [value]);
  return (
    <input type={type} value={local}
      onChange={e => { setLocal(e.target.value); onChange && onChange(e); }}
      onBlur={e => { prev.current = local; onBlur && onBlur(e); }}
      placeholder={placeholder}
      style={{
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: R.md, padding: "12px 16px",
        color: C.text, fontSize: 14, fontWeight: 500,
        fontFamily: "'Inter',sans-serif", width: "100%",
        transition: "all .2s cubic-bezier(.16,1,.3,1)", ...style }} />
  );
};

const Textarea = ({ value, onChange, placeholder, rows = 4, style = {}, onBlur }) => {
  const [local, setLocal] = useState(value || "");
  const prev = useRef(value);
  useEffect(() => {
    if (value !== prev.current) { prev.current = value; setLocal(value || ""); }
  }, [value]);
  return (
    <textarea value={local} rows={rows}
      onChange={e => { setLocal(e.target.value); onChange && onChange(e); }}
      onBlur={e => { prev.current = local; onBlur && onBlur(e); }}
      placeholder={placeholder}
      style={{
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: R.md, padding: "12px 16px",
        color: C.text, fontSize: 14, fontWeight: 400,
        fontFamily: "'Inter',sans-serif", width: "100%",
        resize: "vertical", lineHeight: 1.6,
        transition: "all .2s cubic-bezier(.16,1,.3,1)", ...style }} />
  );
};

const Select = ({ value, onChange, children, style = {} }) => (
  <select value={value} onChange={onChange}
    style={{
      background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: R.md, padding: "12px 16px",
      color: C.text, fontSize: 14, fontWeight: 500,
      fontFamily: "'Inter',sans-serif", width: "100%",
      transition: "all .2s cubic-bezier(.16,1,.3,1)",
      cursor: "pointer", ...style }}>
    {children}
  </select>
);

const Label = ({ children, sub, required }) => (
  <div style={{ marginBottom: 8 }}>
    <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase",
      letterSpacing: .8, display: "flex", alignItems: "center", gap: 4 }}>
      {children}
      {required && <span style={{ color: C.red }}>*</span>}
    </div>
    {sub && <div style={{ fontSize: 12, color: C.mutedLight, marginTop: 3, fontWeight: 400 }}>{sub}</div>}
  </div>
);

const Card = ({ children, style = {}, onClick }) => (
  <div onClick={onClick} className={onClick ? "hoverLift" : ""} style={{
    background: C.surface, border: `1px solid ${C.border}`,
    borderRadius: R.lg, padding: 22, boxShadow: C.shadow,
    cursor: onClick ? "pointer" : "default",
    transition: "all .25s cubic-bezier(.16,1,.3,1)", ...style }}>
    {children}
  </div>
);

const Field = ({ label, sub, required, children, style = {} }) => (
  <div style={{ marginBottom: 18, ...style }}>
    {label && <Label sub={sub} required={required}>{label}</Label>}
    {children}
  </div>
);

const Divider = () => <div style={{ borderTop: `1px solid ${C.border}`, margin: "18px 0" }} />;

const InfoBox = ({ color = C.accent, children, icon = "ℹ️" }) => (
  <div style={{
    padding: "12px 16px", background: hex(color, 0.06),
    border: `1px solid ${hex(color, 0.18)}`, borderRadius: R.md,
    fontSize: 13, color, marginBottom: 14, lineHeight: 1.6,
    display: "flex", gap: 10, alignItems: "flex-start" }}>
    <span style={{ flexShrink: 0, marginTop: 1, fontSize: 15 }}>{icon}</span>
    <div>{children}</div>
  </div>
);

const SezTitle = ({ icon, children, color = C.text }) => (
  <h2 className="serif" style={{
    fontSize: 32, fontWeight: 700, color, marginBottom: 8,
    display: "flex", alignItems: "center", gap: 12,
    letterSpacing: "-0.02em", lineHeight: 1.1
  }}>
    {icon && <span style={{ fontSize: 28 }}>{icon}</span>}{children}
  </h2>
);

// ─── Glossario contestuale (ⓘ accanto ai titoli sezione) ─────────────────────
const GLOSSARIO = {
  "Profilo di Funzionamento": {
    titolo: "Profilo di Funzionamento (ICF-based)",
    testo: "Documento redatto dall'Unità di Valutazione Multidisciplinare (UVM) secondo il modello della Classificazione Internazionale del Funzionamento (ICF-OMS). Descrive il funzionamento dello studente nelle dimensioni di salute, attività, partecipazione, fattori ambientali e personali. Dal 2019 (D.Lgs 66/2017) sostituisce la Diagnosi Funzionale e il Profilo Dinamico Funzionale.",
    normativa: "D.Lgs 66/2017, art. 5 — D.M. 182/2020",
  },
  "Obiettivi e Strategie": {
    titolo: "Obiettivi e Strategie (Sez. 5)",
    testo: "Definisce gli obiettivi educativi e di apprendimento per le quattro dimensioni ICF: A) Relazione/Interazione/Socializzazione, B) Comunicazione/Linguaggio, C) Autonomia/Orientamento, D) Cognitiva/Neuropsicologica/Apprendimento. Gli obiettivi devono essere SMART: Specifici, Misurabili, Raggiungibili, Rilevanti, con Tempi definiti.",
    normativa: "D.M. 182/2020, All. A — Sezione 5",
  },
  "Barriere e Facilitatori": {
    titolo: "Barriere e Facilitatori (ICF)",
    testo: "Concetti chiave del modello ICF. Le barriere sono fattori ambientali che ostacolano il funzionamento e la partecipazione dello studente (es. ambienti non accessibili, atteggiamenti negativi, materiali inadeguati). I facilitatori sono fattori che invece la supportano (es. tecnologie assistive, sostegno familiare, ambiente flessibile).",
    normativa: "ICF-OMS 2001 — D.M. 182/2020, Sez. 6",
  },
  "Progetto di Vita": {
    titolo: "Progetto di Vita",
    testo: "Dimensione trasversale del PEI introdotta dal D.M. 182/2020. Non si limita alla scolarità, ma descrive la visione a lungo termine della persona: chi vuole diventare, quali competenze di vita sta costruendo, come si immagina da adulta. Prevede il coinvolgimento attivo dello studente (secondo età e capacità), della famiglia e dei servizi territoriali.",
    normativa: "D.M. 182/2020, art. 7 — D.Lgs 66/2017, art. 7",
  },
  "Raccordo con Progetto Individuale": {
    titolo: "Progetto Individuale (art. 14, L. 328/2000)",
    testo: "Progetto globale di vita promosso dall'ente locale (Comune) in accordo con la ASL e la scuola. A differenza del PEI (competenza scolastica), il Progetto Individuale riguarda tutti gli aspetti della vita della persona: abitazione, assistenza personale, inserimento lavorativo, tempo libero. Il PEI deve essere raccordato con esso, se esistente.",
    normativa: "L. 328/2000, art. 14 — D.Lgs 66/2017",
  },
  "Curricolo": {
    titolo: "Sezione Curricolo (Sez. 8)",
    testo: "Definisce per ogni disciplina se lo studente segue la programmazione della classe (opzione A), una programmazione personalizzata con obiettivi minimi e prove equipollenti (opzione B — porta al diploma), oppure — solo per la secondaria di II grado, Allegato A4 — un percorso differenziato con obiettivi nettamente ridotti e prove non equipollenti (opzione C — attestato di crediti formativi, non diploma). È cruciale ricordare che anche una sola disciplina impostata su C rende automaticamente differenziato l'intero percorso dello studente. Dal D.I. 153/2023 (correttivo al D.I. 182/2020) l'esonero totale da una disciplina non è più previsto: per ogni disciplina vanno comunque definiti obiettivi disciplinari, anche se nettamente ridotti.",
    normativa: "D.M. 182/2020, All. A, Sez. 8 — OM annuale esami di Stato II ciclo (superiori)",
  },
  "Osservazioni sull'alunno/a": {
    titolo: "Osservazioni sistematiche (Sez. 4)",
    testo: "Descrizione delle caratteristiche dello studente nelle quattro dimensioni ICF, basata sull'osservazione diretta in aula e nei vari contesti scolastici. Diversamente dalla Sezione 2 (documento della ASL), questa sezione è redatta dai docenti e riflette ciò che si osserva quotidianamente. Va corredata, dove possibile, dai codici ICF con qualificatore di performance.",
    normativa: "D.M. 182/2020, Sez. 4 — ICF-OMS 2001",
  },
  "Verifica Finale": {
    titolo: "Verifica Finale (Sez. 11)",
    testo: "Sezione compilata al termine dell'anno scolastico. Riporta il grado di raggiungimento degli obiettivi definiti nella Sezione 5 e nel Curricolo (Sez. 8). Costituisce la base per il PEI dell'anno successivo e per il PEI provvisorio. Deve essere approvata dal GLO (Gruppo di Lavoro Operativo).",
    normativa: "D.M. 182/2020, art. 10 — D.Lgs 66/2017, art. 7",
  },
};

function GlossarioBtn({ voce }) {
  const [aperto, setAperto] = useState(false);
  const info = GLOSSARIO[voce];
  if (!info) return null;
  return (
    <>
      <button onClick={() => setAperto(v => !v)}
        title={`Informazioni su: ${voce}`}
        style={{ background:"none", border:`1px solid ${hex(C.accent, 0.3)}`, borderRadius:"50%",
          width:20, height:20, display:"inline-flex", alignItems:"center", justifyContent:"center",
          cursor:"pointer", fontSize:11, color:C.accent, flexShrink:0,
          fontFamily:"'Inter',sans-serif", fontWeight:700, lineHeight:1, transition:"all .15s",
          marginLeft:6, verticalAlign:"middle" }}
        onMouseEnter={e => { e.currentTarget.style.background = hex(C.accent,0.1); }}
        onMouseLeave={e => { e.currentTarget.style.background = "none"; }}>
        ⓘ
      </button>
      {aperto && (
        <div className="fadeIn" style={{
          position:"fixed", top:0, left:0, right:0, bottom:0, zIndex:9999,
          background:"rgba(0,0,0,0.45)", display:"flex", alignItems:"center", justifyContent:"center",
          padding:24
        }} onClick={() => setAperto(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background:C.surface, borderRadius:16, padding:28, maxWidth:520, width:"100%",
            boxShadow:C.shadowLg, position:"relative"
          }}>
            <button onClick={() => setAperto(false)}
              style={{ position:"absolute", top:14, right:16, background:"none", border:"none",
                fontSize:20, color:C.muted, cursor:"pointer" }}>×</button>
            <div style={{ fontSize:16, fontWeight:700, color:C.text, marginBottom:10 }}>
              📖 {info.titolo}
            </div>
            <p style={{ fontSize:13, color:C.text, lineHeight:1.65, marginBottom:14 }}>
              {info.testo}
            </p>
            <div style={{ fontSize:11, padding:"6px 12px", background:hex(C.accent,0.07),
              border:`1px solid ${hex(C.accent,0.2)}`, borderRadius:6, color:C.accent }}>
              <strong>Riferimento normativo:</strong> {info.normativa}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const Spinner = ({ size = 16, color = C.accent }) => (
  <span className="spin" style={{ width: size, height: size, border: `2px solid ${hex(color, 0.2)}`,
    borderTop: `2px solid ${color}`, borderRadius: "50%", display: "inline-block", flexShrink: 0 }} />
);

// Toggle switch (es. "Segna come completata") — coerente con il prototipo
const Toggle = ({ checked, onChange, label, color = C.accent }) => (
  <label style={{ display: "inline-flex", alignItems: "center", gap: 12, cursor: "pointer", userSelect: "none" }}>
    <span style={{
      position: "relative", width: 44, height: 24,
      background: checked ? color : C.borderMed,
      borderRadius: R.pill,
      transition: "background .25s cubic-bezier(.16,1,.3,1)",
      flexShrink: 0
    }}>
      <span style={{
        position: "absolute", top: 2, left: checked ? 22 : 2,
        width: 20, height: 20, borderRadius: "50%",
        background: "#fff",
        boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
        transition: "left .25s cubic-bezier(.34,1.56,.64,1)"
      }} />
    </span>
    <input type="checkbox" checked={!!checked}
      onChange={e => onChange && onChange(e.target.checked)}
      style={{ position: "absolute", opacity: 0, width: 0, height: 0 }} />
    {label && <span style={{ fontSize: 14, color: C.text, fontWeight: 500 }}>{label}</span>}
  </label>
);

// ─── FUNZIONI DI ESTRAZIONE TESTO ─────────────────────────────────────────────
const estraiTestoDaPDF = async (file, maxPages = 40) => {
  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
  const pdf = await loadingTask.promise;
  const totalPages = pdf.numPages;
  const pagesToRead = Math.min(totalPages, maxPages);
  
  let testoCompleto = '';
  for (let i = 1; i <= pagesToRead; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const pageText = content.items.map(item => item.str).join(' ');
    testoCompleto += pageText + '\n';
  }
  
  if (totalPages > maxPages) {
    testoCompleto += `\n[Nota: documento di ${totalPages} pagine. Estratte solo le prime ${maxPages} pagine per limiti di prestazioni.]`;
  }
  return testoCompleto;
};

const estraiTestoDaDOCX = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result.value;
};

// ─── Redazione PII (safety net per il testo grezzo non passato dall'AI) ───────
// Le immagini sono già redatte via vision e l'estrazione AI (FASE 2) applica
// [OMISSIS]. Questa funzione protegge i PDF/DOCX salvati come testo grezzo:
// il wizard nuovo studente e il fallback dell'Archivio quando i crediti sono
// esauriti. NON sostituisce l'AI: cattura solo pattern ad alta confidenza
// (codice fiscale, date complete, campi nominativi etichettati).
const redactPII = (testo) => {
  if (!testo) return testo;
  return testo
    // Codice fiscale (16 caratteri)
    .replace(/\b[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]\b/gi, "[OMISSIS-CF]")
    // Date complete (gg/mm/aaaa con separatore / . o -)
    .replace(/\b\d{1,2}[/.\-]\d{1,2}[/.\-]\d{2,4}\b/g, "[DATA]")
    // Campi nominativi etichettati: "Alunno: Mario Rossi" → "Alunno: [OMISSIS]"
    .replace(
      /\b(alunn[oa]|student[ei]|alliev[oa]|cognome e nome|nome e cognome|nominativo|cognome|nome)(\s*[:\-]\s*)([A-ZÀ-Ù][a-zà-ù']+(?:\s+[A-ZÀ-Ù][a-zà-ù']+){1,3})/gi,
      (m, lab, sep) => `${lab}${sep}[OMISSIS]`
    );
};

// ─── Estrazione AI strutturata dei documenti (condivisa) ─────────────────────
// Dato il testo grezzo di un documento, ne ricava le sezioni rilevanti per il PEI
// con un focus per tipo. Usata sia dall'Archivio (sempre) sia, opt-in, dal wizard
// nuovo studente. Costa 1 credito (gestito da callClaude). Ritorna il testo
// strutturato, o null se l'AI non produce nulla di utile (il chiamante usa il grezzo).
const REGOLE_ESTRAZIONE_DOC = `Regole:
- Scrivi SOLO le informazioni effettivamente presenti nel documento. Ometti completamente le voci non trovate — nessun "[non rilevato]", nessun placeholder.
- PRIVACY: sostituisci nomi propri con "[OMISSIS]", conserva solo l'iniziale dello studente, ometti date di nascita complete.
- Usa etichette brevi in maiuscolo seguite da ":" per separare i temi (es. "DIAGNOSI:", "ORE DI SOSTEGNO:").
- Sii denso e concreto: ogni riga deve dare informazioni utili. Massimo 500 parole.
- CODICI ICF: se il documento ne contiene, riportali ESATTAMENTE come compaiono, con i qualificatori nella stessa identica forma ("d166.22" resta "d166.22", "b140.2" resta "b140.2"). Non completare i qualificatori mancanti, non dedurne di nuovi, non arrotondare. Se il codice sta in una tabella mal leggibile o disallineata e l'abbinamento fra riga e valori è incerto, riporta il solo codice SENZA qualificatori.
- Non aggiungere titoli del documento, introduzioni o conclusioni.`;

const FOCUS_DOC_PER_TIPO = {
  pf: `Stai analizzando un PROFILO DI FUNZIONAMENTO (documento ICF-based).
Estrai le informazioni più utili per la compilazione del PEI: profilo di funzionamento globale, capacità cognitive e di apprendimento, comunicazione e linguaggio, sfera emotivo-relazionale, motricità e autonomia, codici ICF rilevanti, barriere, facilitatori, punti di forza.`,

  pei_prec: `Stai analizzando un PEI DELL'ANNO PRECEDENTE.
Estrai le informazioni più utili per il nuovo PEI: diagnosi e ore di sostegno, obiettivi previsti e grado di raggiungimento (per ogni disciplina o area se possibile), strategie e interventi efficaci, strumenti e materiali usati, modalità di verifica, indicazioni per l'anno successivo.
Per questo tipo di documento puoi arrivare fino a 900 parole per catturare tutti gli obiettivi e le aree disciplinari.`,

  diagnosi: `Stai analizzando una DIAGNOSI o RELAZIONE CLINICA (neuropsichiatra, psicologo, specialista).
Estrai le informazioni più utili per il PEI: diagnosi principale con codici ICD-10, eventuali comorbidità, profilo neuropsicologico (attenzione, memoria, linguaggio, funzioni esecutive), punti di forza, aree di difficoltà, raccomandazioni per la scuola, terapie in corso.`,

  glho: `Stai analizzando un VERBALE DI RIUNIONE GLHO.
Estrai le informazioni più utili per il PEI: data della riunione, decisioni prese (ore di sostegno, AEC, interventi), obiettivi condivisi, osservazioni della famiglia, indicazioni dell'équipe, prossimi passi.`,

  altro: `Stai analizzando un documento scolastico (relazione pedagogica, relazione di terapia, valutazione specialistica o altro).
Estrai le informazioni più utili per il PEI: tipo e fonte del documento, profilo dello studente, osservazioni principali, progressi rilevati, difficoltà persistenti, tecniche e approcci usati, raccomandazioni operative.`,
};

// Budget di caratteri del testo grezzo dato in pasto all'AI, per tipo. Sostituisce
// il vecchio taglio fisso a 5000 (≈1-2 pagine) che vanificava la lettura fino a 40
// pagine: il PEI precedente è il documento più ricco (obiettivi per disciplina) e
// riceve il budget maggiore. Token in input trascurabili rispetto al contesto del
// modello; l'estrazione resta UNA chiamata = UN credito.
const INPUT_DOC_BUDGET = (tipo) =>
  tipo === 'pei_prec' ? 16000 :
  (tipo === 'pf' || tipo === 'diagnosi') ? 12000 :
  8000;
// Token massimi dell'output dell'estrazione, per tipo (il PEI prec. è più lungo).
const OUTPUT_DOC_TOKENS = (tipo) => tipo === 'pei_prec' ? 1600 : 900;

// Quando il testo supera il budget di input, invece di tagliare alla cieca la coda
// (dove spesso stanno le "indicazioni per l'anno successivo" e le verifiche finali)
// si conserva testa + coda, con un marcatore esplicito della porzione centrale omessa.
const estraiTestaCoda = (testo, budget) => {
  if (!testo || testo.length <= budget) return testo;
  const marcatore = "\n\n[…porzione centrale del documento omessa per limiti di lunghezza…]\n\n";
  const utile = Math.max(0, budget - marcatore.length);
  const testaLen = Math.floor(utile * 0.7); // la maggior parte del contenuto sta nella prima metà
  const codaLen  = utile - testaLen;
  return testo.slice(0, testaLen) + marcatore + testo.slice(testo.length - codaLen);
};

async function analizzaDocumentoAI(testoGrezzo, tipoId, fileName) {
  if (!testoGrezzo || testoGrezzo.trim().length <= 100) return null;
  const focus = FOCUS_DOC_PER_TIPO[tipoId] || FOCUS_DOC_PER_TIPO.altro;
  const maxTokensEstrazione = OUTPUT_DOC_TOKENS(tipoId);
  const testoInput = estraiTestaCoda(testoGrezzo, INPUT_DOC_BUDGET(tipoId));
  const promptEstrazione = `Sei un assistente specializzato nella compilazione del PEI italiano.\n\n${focus}\n\n${REGOLE_ESTRAZIONE_DOC}\n\nTESTO DEL DOCUMENTO:\n${testoInput}`;
  try {
    const testoEstratto = await callClaude(
      promptEstrazione, maxTokensEstrazione, 'doc_extraction', { tipo: tipoId, nome: fileName }
    );
    if (testoEstratto && testoEstratto.trim().length > 50) return testoEstratto.trim();
  } catch (errAI) {
    console.warn('[analizzaDocumentoAI] estrazione AI fallita, uso testo grezzo:', errAI.message);
  }
  return null;
}

// ─── Conversione ArrayBuffer → base64 a blocchi ──────────────────────────────
// `btoa(String.fromCharCode(...new Uint8Array(ab)))` passa l'intero array come
// argomenti a fromCharCode: con una foto da telefono (>~100 KB) supera il limite
// dello stack e lancia "Maximum call stack size exceeded". Convertiamo a blocchi.
const arrayBufferToBase64 = (ab) => {
  const bytes = new Uint8Array(ab);
  let binary = '';
  const CHUNK = 0x8000; // 32k
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
};

// Prompt di sola trascrizione (immagini e PDF scansionati) con privacy obbligatoria.
const PROMPT_OCR = `Sei un assistente per insegnanti di sostegno. Estrai TUTTO il testo visibile da questo documento scolastico, preservando la struttura. PRIVACY OBBLIGATORIA: sostituisci i nomi propri con "[OMISSIS]", conserva solo l'iniziale dello studente, non riportare date di nascita complete.`;

// ─── OCR di un PDF scansionato (senza layer di testo) ────────────────────────
// pdf.js estrae solo il text-layer: un PDF scansionato (immagine, tipico dei
// referti ASL) restituisce testo vuoto. Qui renderizziamo le pagine su canvas e
// le leggiamo via vision. TUTTE le pagine viaggiano in UN'UNICA chiamata vision
// (1 credito complessivo, coerente con "il caricamento costa 1 credito"): cap a
// `maxOcrPages` per contenere dimensione richiesta e token.
async function ocrPDFScansionato(file, tipoId, maxOcrPages = 6) {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const pagine = Math.min(pdf.numPages, maxOcrPages);
  const immagini = [];
  for (let i = 1; i <= pagine; i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale: 1.6 });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
    const b64 = canvas.toDataURL('image/jpeg', 0.8).split(',')[1];
    immagini.push({ data: b64, mediaType: 'image/jpeg' });
  }
  if (immagini.length === 0) return '';
  const promptMulti = `${PROMPT_OCR}\n\nQuesto documento è composto da ${immagini.length} pagina/e fornite in ordine. Trascrivi il testo di tutte le pagine, preservando l'ordine e la struttura.`;
  let out = await callClaudeVision(
    immagini, 'image/jpeg', promptMulti, 'doc_extraction',
    { tipo: tipoId, pdf_ocr_pagine: immagini.length }, 4096
  ) || '';
  if (pdf.numPages > maxOcrPages) {
    out += `\n[Nota: PDF scansionato di ${pdf.numPages} pagine; OCR eseguito sulle prime ${maxOcrPages}.]`;
  }
  return out;
}

// ─── Pipeline unica di caricamento documento (wizard + Archivio) ─────────────
// Estrae il testo dal file e (se serve) lo struttura via AI, restituendo
// { testo, isImg, aiAnalizzato }. Centralizza la logica per evitare divergenze
// tra i due punti di upload e per gestire i crediti in modo coerente:
//  • Immagini      → 1 sola chiamata vision (legge ed eventualmente struttura).
//                    NIENTE seconda analisi: prima costavano 2 crediti (vision +
//                    analizzaDocumentoAI) pur mostrando "1 credito".
//  • PDF testuale  → estrazione locale gratuita; analisi AI opzionale (1 credito).
//  • PDF scansionato (nessun testo) → OCR via vision (richiede crediti).
//  • DOCX          → estrazione locale gratuita; analisi AI opzionale.
// `usaAI` governa SOLO i documenti di testo (PDF/DOCX): immagini e PDF scansionati
// richiedono comunque l'AI per essere leggibili (se non ci sono crediti → throw).
async function caricaDocumento(file, tipoId, { usaAI, haCrediti, onFase = () => {} }) {
  const isImg = (file.type || '').startsWith('image/');
  const nameLower = file.name.toLowerCase();
  let testoGrezzo = '';
  let aiHaLetto = false; // true se il testo proviene già da una chiamata AI (vision/OCR)

  onFase('estrazione');
  if (isImg) {
    if (!haCrediti) throw new InsufficientCreditsError(0);
    onFase('analisi');
    const b64 = arrayBufferToBase64(await file.arrayBuffer());
    const focus = FOCUS_DOC_PER_TIPO[tipoId] || FOCUS_DOC_PER_TIPO.altro;
    const prompt = usaAI
      ? `${PROMPT_OCR}\n\nDopo aver letto il testo, organizza le informazioni più rilevanti per il PEI.\n\n${focus}\n\n${REGOLE_ESTRAZIONE_DOC}`
      : PROMPT_OCR;
    testoGrezzo = await callClaudeVision(b64, file.type, prompt, 'doc_extraction', { tipo: tipoId, nome: file.name });
    aiHaLetto = true;
  } else if (nameLower.endsWith('.pdf')) {
    testoGrezzo = redactPII(await estraiTestoDaPDF(file, 40));
    if (testoGrezzo.replace(/\s/g, '').length < 100) {
      // PDF scansionato: nessun layer di testo → serve OCR via vision (a crediti).
      if (!haCrediti) throw new InsufficientCreditsError(0);
      onFase('analisi');
      testoGrezzo = redactPII(await ocrPDFScansionato(file, tipoId) || '');
      aiHaLetto = true;
    }
  } else if (nameLower.endsWith('.docx')) {
    testoGrezzo = redactPII(await estraiTestoDaDOCX(file));
  } else {
    throw new Error('FORMATO_NON_SUPPORTATO');
  }

  let testoFinale;
  let aiAnalizzato = false;
  if (aiHaLetto) {
    // Già letto/strutturato dall'AI (vision/OCR): conserva testa+coda entro un tetto
    // ampio così un documento scansionato di ~6 pagine (es. Profilo di Funzionamento)
    // entra intero. Il budget di contesto a valle è type-aware e decide quanto iniettare.
    testoFinale = estraiTestaCoda(testoGrezzo, 12000);
    aiAnalizzato = true;
  } else if (usaAI && haCrediti) {
    onFase('analisi');
    const estratto = await analizzaDocumentoAI(testoGrezzo, tipoId, file.name);
    if (estratto) { testoFinale = estratto; aiAnalizzato = true; }
    else { testoFinale = estraiTestaCoda(testoGrezzo, 6000); }
  } else {
    // Grezzo senza AI: testa+coda (non solo l'inizio) per non perdere le
    // indicazioni finali del documento (es. obiettivi anno successivo del PEI).
    testoFinale = estraiTestaCoda(testoGrezzo, 6000);
  }
  return { testo: testoFinale, isImg, aiAnalizzato };
}

// ─── Hint metodologici per diagnosi (usato nei prompt AI) ────────────────────
// Mappa prefisso ICD-10 → pratiche evidence-based sintetiche da iniettare nel prompt
// ─── CLUSTER METODOLOGICI PER DIAGNOSI ───────────────────────────────────────
// Le diagnosi ICD-10 vengono mappate su 6 macro-cluster.
// Ogni cluster ha un hint unico: copertura totale, token stabili, facile manutenzione.

const CLUSTER_METODOLOGIE = {
  autismo: {
    label: "Disturbi dello spettro autistico e dello sviluppo globale",
    hint: "Strutturazione spazio-temporale (TEACCH), comunicazione aumentativa e alternativa (CAA/PECS), analisi del comportamento applicata (ABA), schedule visive, social stories, economia a gettoni (token economy), time-timer. Privilegiare routine prevedibili e transizioni segnalate in anticipo."
  },
  attentivi: {
    label: "Disturbi attentivi, iperattività e comportamentali",
    hint: "Suddivisione dei compiti in segmenti brevi (chunking), timer visivi, rinforzo immediato del comportamento desiderato, contratti comportamentali, pause cognitive programmate (brain break), ambiente a bassa distrazione, de-escalation proattiva, problem solving sociale (PATHS)."
  },
  intellettiva: {
    label: "Disabilità intellettiva",
    hint: "Analisi del compito per step minimi (task analysis), concatenamento in avanti e indietro (forward/backward chaining), apprendimento multisensoriale, guida fisica e gestuale con riduzione progressiva (prompting e fading), rinforzo differenziale, obiettivi funzionali e adattivi, apprendimento tra pari strutturato (peer tutoring)."
  },
  linguaggio: {
    label: "Disturbi del linguaggio, degli apprendimenti e dello sviluppo",
    hint: "Mappe concettuali e organizzatori grafici, sintesi vocale e strumenti compensativi digitali, supporto visivo alle istruzioni verbali (scaffolding), input linguistico semplificato e facilitato, riduzione del carico esecutivo, font ad alta leggibilità (es. OpenDyslexic per dislessia), comunicazione aumentativa come supporto (CAA), routine comunicative strutturate."
  },
  emotivi: {
    label: "Disturbi emotivi, dell'umore e psichiatrici",
    hint: "Ambiente scolastico prevedibile e rassicurante, approccio basato sul trauma (trauma-informed: sicurezza, fiducia, scelta), tecniche di regolazione emotiva adattate (zone di regolazione, respirazione guidata), desensibilizzazione graduale alle situazioni ansiogene, evitare punizioni e approcci coercitivi, coordinamento stretto con psicologo scolastico e famiglia."
  },
  sensoriali_motorie: {
    label: "Disabilità sensoriali, motorie e neurologiche",
    hint: "Ausili tecnologici specifici per il profilo (sistema FM, screen reader, ingranditori, comunicatori), adattamento degli spazi e dei materiali (alto contrasto, font ≥18pt, braille se necessario), posizionamento funzionale in aula, piano di emergenza condiviso con il personale (epilessia), raccordo con l'équipe medica di riferimento, riduzione del carico cognitivo nei periodi critici o post-acuti."
  }
};

const ICD10_TO_CLUSTER = {
  // Spettro autistico e sviluppo globale
  F84: "autismo", F88: "autismo", F89: "autismo",
  // Attentivi, iperattività e comportamentali
  F90: "attentivi", F91: "attentivi", F92: "attentivi", F98: "attentivi",
  // Disabilità intellettiva
  F70: "intellettiva", F71: "intellettiva", F72: "intellettiva",
  F73: "intellettiva", F78: "intellettiva", F79: "intellettiva",
  // Linguaggio e apprendimenti
  F80: "linguaggio", F81: "linguaggio", F82: "linguaggio", F83: "linguaggio",
  // Emotivi, umore, psichiatrici
  F20: "emotivi", F21: "emotivi", F22: "emotivi", F25: "emotivi",
  F31: "emotivi", F32: "emotivi", F33: "emotivi",
  F40: "emotivi", F41: "emotivi", F42: "emotivi", F43: "emotivi",
  F44: "emotivi", F45: "emotivi", F50: "emotivi",
  F93: "emotivi", F94: "emotivi", F95: "emotivi",
  Z60: "emotivi", Z61: "emotivi",
  // Sensoriali, motorie, neurologiche
  G35: "sensoriali_motorie", G40: "sensoriali_motorie",
  G80: "sensoriali_motorie", G91: "sensoriali_motorie",
  H53: "sensoriali_motorie", H54: "sensoriali_motorie",
  H90: "sensoriali_motorie", H91: "sensoriali_motorie",
  I60: "sensoriali_motorie",
  Q04: "sensoriali_motorie", Q93: "sensoriali_motorie",
  R13: "sensoriali_motorie", T90: "sensoriali_motorie",
};

function getMetodologieHint(icd10Array) {
  if (!icd10Array?.length) return "";
  const clustersUsati = new Set();
  for (const entry of icd10Array) {
    const prefix = entry.code?.slice(0, 3);
    const cluster = ICD10_TO_CLUSTER[prefix];
    if (cluster) clustersUsati.add(cluster);
  }
  if (!clustersUsati.size) return "";
  const righe = [...clustersUsati].map(k => {
    const c = CLUSTER_METODOLOGIE[k];
    return `• ${c.label}: ${c.hint}`;
  });
  return `\nMetodologie evidence-based suggerite per il profilo diagnostico dello studente:\n${righe.join("\n")}`;
}

// ─── DIRETTIVE PER ORDINE SCOLASTICO ──────────────────────────────────────────
// Fonte unica delle direttive pedagogico-valutative per ordine, iniettata in
// basePrompt() (e quindi anteposta a OGNI generazione IA). Evita la duplicazione
// inline dei prompt e tiene un solo punto da aggiornare quando cambia la normativa.
function direttiveValutazioneOrdine(studente) {
  const ordine = studente?.ordine;
  // Decreto Indicazioni Nazionali vigente per QUESTO alunno + nota anti-invenzione
  // quando si applica il 221/2025 (testo che il modello non conosce).
  const indNaz = infoIndicazioni(studente);
  const nota221 = indNaz.is221 ? NOTA_221 : "";
  if (ordine === "infanzia") {
    return `\nDIRETTIVE INFANZIA (Allegato A1 D.I. 182/2020 + Linee Guida Allegato B + ${indNaz.decreto}): Alla scuola dell'infanzia gli interventi si articolano in PERCORSI EDUCATIVI E NON DIDATTICI (Linee Guida All. B). Lavora per Campi di Esperienza, non per discipline. Gli obiettivi sono PREVALENTEMENTE EDUCATIVI (Linee Guida All. B) e declinano ${indNaz.is221 ? "le COMPETENZE ATTESE" : "i TRAGUARDI PER LO SVILUPPO DELLA COMPETENZA"} delle Indicazioni Nazionali per il curricolo della scuola dell'infanzia (${indNaz.decreto}) sul singolo bambino/sulla singola bambina; riguardano autonomie, prerequisiti, interazione tra pari, gioco simbolico, routine — non contenuti disciplinari. Per ciascun obiettivo le Linee Guida richiedono di indicare anche gli ESITI ATTESI e i criteri e modalità di verifica per il relativo conseguimento. NON esiste alcuna programmazione A/B (riconducibile/personalizzata): tutti gli interventi sono naturalmente individualizzati nella cornice della pedagogia infantile (art. 8 D.I. 182/2020 — osservazione sistematica come compito dei docenti della sezione). La valutazione si basa ESCLUSIVAMENTE sull'osservazione sistematica ed evolutiva nei contesti di gioco, routine e laboratorio; è riferita agli obiettivi del PEI (D.Lgs 62/2017 art. 11), NON al gruppo sezione. Quando descrivi gli esiti, usa la formula del modello A1: "verifica dei risultati EDUCATIVI conseguiti" — mai "risultati didattici", mai voti, mai livelli, mai percentuali. La valutazione finale degli apprendimenti è di competenza di tutti i docenti della sezione.
REGOLA LESSICALE TASSATIVA — Nei testi generati per la scuola dell'infanzia il soggetto è SEMPRE "il bambino" o "la bambina" (in base al genere desumibile dal contesto, altrimenti alterna o usa "il bambino/la bambina"). NON usare MAI "lo studente", "l'alunno", "l'alunno/a", "l'allievo". NON usare "classe" ma "sezione". I docenti curricolari sono i "docenti della sezione".${nota221}`;
  }
  if (ordine === "primaria") {
    return `\nDIRETTIVE PRIMARIA: La valutazione è descrittiva e narrativa. Trattandosi di un alunno con disabilità, è SEMPRE riferita agli obiettivi del PEI (obiettivi minimi riconducibili a quelli della classe, oppure obiettivi semplificati e personalizzati) e misura il progresso rispetto ai livelli di partenza dell'alunno, NON rispetto agli obiettivi standard della classe (D.Lgs 62/2017 art. 11). Quando esprimi un giudizio usa una forma discorsiva coerente con i GIUDIZI SINTETICI vigenti (L. 1° ottobre 2024, n. 150, a regime dall'a.s. 2024/25): ottimo, distinto, buono, discreto, sufficiente, non sufficiente. Il giudizio esprime il grado di raggiungimento degli obiettivi personalizzati: un alunno che raggiunge pienamente i propri obiettivi minimi o semplificati può legittimamente ottenere un giudizio elevato (anche "ottimo"). NON usare MAI i livelli aboliti dell'OM 172/2020 ("in via di prima acquisizione", "base", "intermedio", "avanzato"). NON usare voti numerici né percentuali.${nota221}`;
  }
  if (ordine === "secondaria1") {
    return `\nDIRETTIVE SECONDARIA I GRADO: La valutazione (espressa in voti in decimi) è SEMPRE riferita agli obiettivi del PEI — obiettivi minimi riconducibili a quelli della classe, oppure obiettivi semplificati e personalizzati — e non agli obiettivi standard della classe (D.Lgs 62/2017 art. 11); un alunno che raggiunge pienamente i propri obiettivi può ottenere legittimamente una valutazione elevata. Integra le misure compensative/dispensative per le prove INVALSI. Indica se l'alunno affronta l'Esame di Stato con prove differenziate ma equipollenti.${nota221}`;
  }
  if (ordine === "secondaria2") {
    const diff = studente?.tipoPercorso === "differenziato";
    return `\nDIRETTIVE SECONDARIA II GRADO: Il percorso è ${diff
      ? "DIFFERENZIATO — porta all'Attestato di credito formativo (le prove NON sono equipollenti)"
      : "PERSONALIZZATO CON PROVE EQUIPOLLENTI — porta al Diploma"}. Includi il raccordo con i PCTO e i facilitatori nel contesto aziendale.${NOTA_INDIRIZZO_SEC2}`;
  }
  return "";
}

// ─── FieldAI (con suggerimenti e correzioni AI) ───────────────────────────────
// Campi descrittivi semplici: la pertinenza è verificabile senza rischio di falsi positivi.
// Per i campi tecnici (ICF, obiettivi, curricolo) il check è disabilitato.
const CAMPI_SEMPLICI = new Set([
  "s1_famiglia", "s1_extra",
  "pi_sintesi", "pi_raccordo",
  "s7_interventi", "s7_revisione", "s7_verifica_conclusiva",
]);

function FieldAI({ label, sub, campoId, value, onChange, placeholder, rows = 3,
                   studente, sezioneLabel, required, contestoGlobale, contestoObiettivi,
                   customPrompt, maxTokens = 800 }) {
  const { includeICFCodes } = useSettings();
  const { balance } = useCreditsCtx();
  const { aiBloccata } = useTrialGate();
  const [loading,    setLoading]    = useState(false);
  const [loadingCorr,setLoadingCorr]= useState(false);
  const [storico,    setStorico]    = useState([]);
  const [idxStorico, setIdxStorico] = useState(-1);
  const [errCrediti,  setErrCrediti]  = useState(false);
  const [errGenerico, setErrGenerico] = useState("");    // errore generico API/rete
  const [avviso,      setAvviso]      = useState(false); // testo non pertinente
  const [avvisoMsg,   setAvvisoMsg]   = useState("");

  // Crediti esauriti = balance esplicito a 0 (in modalità demo balance è null e non blocca)
  const senzaCrediti = balance !== null && balance <= 0;
  // L'AI è bloccata se la sezione/dimensione è premium e l'utente è in trial.
  // In questo stato i bottoni Genera/Correggi/Adatta sono visibili ma non
  // cliccabili e mostrano un tooltip che invita all'upgrade.
  const aiDisabilitata = aiBloccata || senzaCrediti;
  const motivoBlocco = aiBloccata
    ? "Disponibile nel piano completo"
    : senzaCrediti
      ? "Crediti esauriti — acquista per continuare"
      : null;

  const isSezione2 = campoId === "pf_descrizione";
  const isSezione4 = campoId?.startsWith("s4_");

  const basePrompt = () => {
    const ctx = contestoGlobale
      ? `\n\nContesto dalle sezioni precedenti del PEI e documenti:\n${contestoGlobale}`
      : "";

    // Registro linguistico calibrato per ordine scolastico
    const registroOrdine = {
      infanzia:    "Usa linguaggio esperienziale e osservativo (es. 'il bambino mostra…', 'nel gioco strutturato…'). Evita terminologia clinica astratta. Focalizzati su gioco, routine e sviluppo senso-motorio. SEMPLICITÀ LESSICALE OBBLIGATORIA: usa parole italiane semplici e concrete; vietati il gergo tecnico-clinico e gli anglismi non necessari (es. NON scrivere 'compliance attentiva', 'performance', 'coping', 'self-regulation', 'arousal', 'goal setting'). Preferisci equivalenti chiari: 'compliance attentiva' → 'persistenza dell'attenzione'; 'coping' → 'capacità di gestire le difficoltà'; 'self-regulation' → 'autoregolazione'; 'arousal' → 'livello di attivazione'. I codici ICF, dove richiesti, restano invariati.",
      primaria:    "Usa linguaggio descrittivo-comportamentale, concreto e accessibile. Riferisci le competenze a situazioni di apprendimento quotidiane. Bilancia terminologia ICF con esempi pratici di classe. SEMPLICITÀ LESSICALE OBBLIGATORIA: usa parole italiane semplici e concrete; vietati il gergo tecnico-clinico e gli anglismi non necessari (es. NON scrivere 'compliance attentiva', 'performance', 'coping', 'self-regulation', 'arousal', 'goal setting'). Preferisci equivalenti chiari: 'compliance attentiva' → 'persistenza dell'attenzione'; 'coping' → 'capacità di gestire le difficoltà'; 'self-regulation' → 'autoregolazione'; 'arousal' → 'livello di attivazione'. I codici ICF, dove richiesti, restano invariati.",
      secondaria1: `Usa linguaggio pedagogico preciso. Includi riferimenti alle Indicazioni Nazionali per il curricolo vigenti (${infoIndicazioni(studente).decreto}). Considera la dimensione evolutiva adolescenziale e il pre-orientamento.`,
      secondaria2: "Usa linguaggio tecnico-pedagogico formale. Considera le competenze chiave europee e la transizione post-diploma. I riferimenti disciplinari provengono dalla programmazione di classe inserita dall'insegnante: non dedurli da un indirizzo di studio che non ti è stato indicato.",
    }[studente?.ordine] || "";

    // Tipo percorso (solo superiori)
    const percorsoCtx = (studente?.ordine === "secondaria2" && studente?.tipoPercorso)
      ? `\nTipo di percorso: ${studente.tipoPercorso === "differenziato"
          ? "DIFFERENZIATO — obiettivi NON riconducibili ai programmi ministeriali (art. 14 D.M. 182/2020). Lo studente conseguirà un attestato di crediti. Gli obiettivi devono essere chiaramente personalizzati e non equiparabili a quelli del gruppo classe."
          : "RICONDUCIBILE — obiettivi minimi riconducibili ai programmi ministeriali. Lo studente può conseguire il diploma. Adatta la programmazione senza stravolgerne i contenuti disciplinari."}`
      : "";

    const metodHint = getMetodologieHint(studente?.icd10);
    const metodFallback = !metodHint && studente?.diagnosi
      ? `\nNota metodologica: non sono disponibili hint predefiniti per questa diagnosi. Identifica autonomamente le metodologie e pratiche educative evidence-based più appropriate per "${studente.diagnosi}", basandoti sulla letteratura scientifica e sulle linee guida internazionali per l'inclusione scolastica. Privilegia approcci validati empiricamente e coerenti con il profilo ICF dello studente.`
      : "";

    // Gap analysis ICF: si attiva solo se la Sezione 2 è già compilata con codici a doppio qualificatore
    const hasICFGap = contestoGlobale && /[bdse]\d+\.\d{2}/.test(contestoGlobale);
    const gapHint = hasICFGap
      ? `\nANALISI GAP ICF DISPONIBILE: nel Profilo di Funzionamento sono già presenti codici ICF con qualificatori performance.capacità. Dove performance > capacità (es. d415.32) esiste una BARRIERA AMBIENTALE che il PEI deve affrontare (rimuovere, compensare o aggirare). Tienilo presente in questa sezione: privilegia interventi che riducano le barriere identificate e potenzino i facilitatori.`
      : "";

    // Codici ICF SENZA fonte (l'AI genera senza un Profilo di Funzionamento nel
    // contesto): NON inventare i qualificatori clinici. Regola concordata.
    const hasICFCodici = contestoGlobale && /[bdse]\d{3}/.test(contestoGlobale);
    const icfSenzaFonteHint = (includeICFCodes && !hasICFCodici)
      ? `\nCODICI ICF SENZA FONTE: nel contesto NON è presente un Profilo di Funzionamento con codici ICF. Se usi codici ICF, NON inventare i qualificatori clinici di gravità in modo arbitrario:\n- Attività e Partecipazione (d…): indica al massimo la CAPACITÀ stimata (es. "d330 — capacità stimata: lieve"), MAI il qualificatore di performance (richiede osservazione del contesto reale, non desumibile dalla diagnosi); di conseguenza NON dedurre "barriere ambientali" dal confronto performance/capacità;\n- Funzioni corporee (b…): al massimo una gravità orientativa coerente con la diagnosi, oppure solo il dominio;\n- Fattori ambientali (e…): NON assegnare qualificatori né segni "+"; descrivi barriere e facilitatori in linguaggio chiaro;\n- chiudi con: "I codici ICF sono orientativi; performance e codici definitivi vanno desunti dal Profilo di Funzionamento e dalle osservazioni."`
      : "";

    const icfOffHint = !includeICFCodes
      ? `\nCODIFICA ICF DISATTIVATA: il PEI va scritto in forma discorsiva. NON riportare nel testo generato codici ICF (es. "d166", "b140", "e585") né i loro qualificatori numerici.${hasICFCodici ? ` Nel contesto è però presente un Profilo di Funzionamento con codici ICF: USALI per orientare il contenuto — quali capacità e difficoltà descrivere, quali dimensioni privilegiare, e dove il qualificatore di performance è peggiore di quello di capacità un ostacolo del contesto scolastico da segnalare — ma esprimi tutto in linguaggio corrente, senza sigle né numeri.` : ""}`
      : "";

    // Comorbidità (es. 104 + DSA): guida invisibile per l'IA su misure compensative/dispensative
    const comorbiditaCtx = studente?.comorbidita
      ? `\nComorbidità: ${studente.comorbidita}. Oltre alla disabilità certificata, integra misure compensative e dispensative coerenti con il/i disturbo/i indicato/i (per i DSA, ad es.: sintesi vocale, calcolatrice, tabelle e formulari, mappe e schemi; dispensa dalla lettura ad alta voce, riduzione del carico di lavoro, tempi aggiuntivi), raccordandole con gli obiettivi del PEI senza creare un PDP parallelo.`
      : "";

    // Certificazione in iter / diagnosi provvisoria: tono prudente, formule osservative
    const certAttesaCtx = studente?.certInAttesa
      ? `\nStato certificazione: in iter / diagnosi funzionale provvisoria. Adotta cautela: privilegia formule osservative ("si osserva che…", "emerge un profilo orientato a…", "il bambino/la bambina/l'alunno mostra…"); evita affermazioni cliniche definitive sul profilo cognitivo o sulla diagnosi; non sovrainterpretare il quadro diagnostico né attribuire categorie cliniche con certezza. Mantieni un linguaggio descrittivo ed evolutivo.`
      : "";

    // Ancoraggio al profilo: contrasto centralizzato alla genericità. Anteposto
    // a OGNI generazione, obbliga l'IA a USARE i dati specifici dell'alunno già
    // forniti nel contesto soprastante, anziché produrre testo intercambiabile.
    const direttivaAncoraggio = `

ANCORAGGIO AL PROFILO (regola anti-genericità, OBBLIGATORIA):
Il testo che generi deve essere riconoscibilmente riferito a QUESTO alunno, non a un alunno generico.
- Aggancia il contenuto ad almeno 2-3 elementi concreti presenti nel profilo o nel contesto soprastante: diagnosi e codici, punti di forza e difficoltà già osservati, dati del quadro familiare, esiti dei PEI o documenti caricati, ore e risorse, ordine ed età.
- Test dell'intercambiabilità: se una frase resterebbe identica cambiando alunno, riscrivila aggiungendo un riferimento concreto al profilo.
- Evita le formule-riempitivo prive di contenuto: specifica sempre QUALE capacità o autonomia, IN QUALE contesto e CON QUALE appiglio nel profilo.
- Se un'informazione necessaria manca davvero nel profilo, NON inventarla e NON riempire con genericità: inserisci un segnaposto esplicito tra parentesi quadre, es. "[da personalizzare: indicare la situazione di partenza in lettura]".`;

    const etichettaSoggetto = studente?.ordine === "infanzia" ? "Bambino/a" : "Studente";
    return `${etichettaSoggetto}: ${studente?.iniziali || "N.N."}, ${studente?.eta || ""} anni, ${ORDINI[studente?.ordine]?.label || ""}. Anno scolastico: ${studente?.annoScolastico || "n.d."}.
Diagnosi: ${buildDiagnosiContesto(studente, "n.s.")}${certAttesaCtx}${comorbiditaCtx}${percorsoCtx}${metodHint || metodFallback}${buildRiferimentoICD10(studente)}
Registro linguistico richiesto: ${registroOrdine}${direttiveValutazioneOrdine(studente)}${gapHint}${icfSenzaFonteHint}${icfOffHint}${ctx}${direttivaAncoraggio}
Sezione PEI: ${sezioneLabel} — Campo: ${label}`;
  };

  const suggerisci = async () => {
    if (!studente?.diagnosi) return;
    // I crediti vengono scalati server-side dall'Edge Function `claude-proxy`.
    // Qui catchiamo solo l'errore di crediti insufficienti.
    setErrCrediti(false);
    setErrGenerico("");
    setLoading(true);
    try {
      let prompt;
      if (customPrompt) {
        prompt = basePrompt() + `\n\n${customPrompt}`;
      } else {
        const isS5 = sezioneLabel === "Obiettivi e Strategie";
        const isS5AttivStrat = isS5 && (label === "Attività" || label === "Strategie e Strumenti");
        const isS5Ob = isS5 && label === "Obiettivi ed esiti attesi";
        const isS5Verifica = isS5 && label === "Revisione conclusiva degli esiti";
        const dimId = campoId?.match(/s5_([ABCD])_/)?.[1] || "";
        const dimLabel = {
          A: "Relazione, Interazione e Socializzazione",
          B: "Comunicazione e Linguaggio",
          C: "Autonomia e Orientamento",
          D: "Cognitiva, Neuropsicologica e Apprendimento",
        }[dimId] || "";

        // Riferimento curricolare version-aware (rollout DM 254/2012 → DM 221/2025).
        // Sotto il 221 le stringhe restano volutamente generiche: la struttura per
        // fasce/classi del nuovo decreto non è nota al modello e il testo vigente
        // arriva dal blocco "RIFERIMENTO UFFICIALE" della KB (Sez. 8).
        const indNaz = infoIndicazioni(studente);
        const ordineCtx = {
          infanzia:    `Scuola dell'Infanzia — ${indNaz.etichettaCompetenze} delle Indicazioni Nazionali (${indNaz.decreto}), articolate nei cinque campi di esperienza: Il sé e l'altro, Il corpo e il movimento, Immagini suoni colori, I discorsi e le parole, La conoscenza del mondo.`,
          primaria:    indNaz.is221
            ? `Scuola Primaria — Indicazioni Nazionali vigenti (${indNaz.decreto}): obiettivi di apprendimento e competenze attese per disciplina; se nel contesto è presente un blocco "RIFERIMENTO UFFICIALE" usa quel testo come base.`
            : "Scuola Primaria — Indicazioni Nazionali 2012 e Nuovi Scenari 2018: obiettivi di apprendimento al termine della classe 2a e 5a per ogni disciplina.",
          secondaria1: indNaz.is221
            ? `Scuola Secondaria I grado — Indicazioni Nazionali vigenti (${indNaz.decreto}): obiettivi di apprendimento e competenze attese per disciplina; se nel contesto è presente un blocco "RIFERIMENTO UFFICIALE" usa quel testo come base.`
            : "Scuola Secondaria I grado — Indicazioni Nazionali 2012: obiettivi di apprendimento al termine della classe 3a, traguardi di competenza per disciplina.",
          secondaria2: "Scuola Secondaria II grado — la progettazione disciplinare si àncora alla programmazione della classe (biennio o triennio) fornita dall'insegnante e agli obiettivi minimi che ne derivano, non a un quadro di indirizzo predefinito.",
        }[studente?.ordine] || "";

        const correlazione = contestoObiettivi
          ? `\n\nOBIETTIVI GIÀ DEFINITI per questa dimensione (le voci DEVONO essere coerenti):\n${contestoObiettivi}`
          : "";

        const dimContesto = dimId ? `\n\nDIMENSIONE ICF DI RIFERIMENTO: ${dimId} — ${dimLabel}
Gli obiettivi/attività/strategie devono riguardare ESCLUSIVAMENTE questa dimensione.
${isS5Ob ? `Usa verbi d'azione specifici per la Dim.${dimId}:
${dimId === "A" ? "partecipare, interagire, collaborare, rispettare, condividere, gestire emozioni, aspettare il turno, richiedere aiuto" :
  dimId === "B" ? "esprimere, comunicare, comprendere, descrivere, raccontare, denominare, rispondere, produrre, utilizzare CAA" :
  dimId === "C" ? "eseguire, gestire, organizzare, orientarsi, spostarsi, completare, pianificare, prendersi cura" :
  "leggere, scrivere, calcolare, risolvere, memorizzare, classificare, riconoscere, applicare, analizzare"}` : ""}` : "";

        const livelloCtx = {
          infanzia:    `Scuola dell'Infanzia (3-6 anni): usa ${indNaz.is221 ? "le competenze attese" : "i traguardi di sviluppo"}, linguaggio semplice, gioco come strumento, attività senso-motorie e simboliche.`,
          primaria:    `Scuola Primaria (6-11 anni): riferimento alle Indicazioni Nazionali per il curricolo vigenti (${indNaz.decreto}), obiettivi disciplinari per classi, competenze di base.`,
          secondaria1: `Scuola Secondaria I grado (11-14 anni): Indicazioni Nazionali per il curricolo vigenti (${indNaz.decreto}), ${indNaz.is221 ? "competenze attese" : "traguardi di competenza"} per disciplina, orientamento.`,
          secondaria2: "Scuola Secondaria II grado (14-19 anni): competenze chiave europee, transizione al lavoro/università; i riferimenti disciplinari provengono dalla programmazione di classe inserita dall'insegnante.",
        }[studente?.ordine] || "";

        const formatoObiettivi = isS5Ob ? `
Genera un numero adeguato di OBIETTIVI con i relativi ESITI ATTESI nel formato OBBLIGATORIO.
Il numero (di solito 2-5) deve essere determinato in base al profilo dello studente, ai punti di forza,
alle barriere e ai facilitatori. Non fissare un numero predefinito, ma adatta la quantità alla complessità.

OBIETTIVO 1: [verbo all'infinito + oggetto, max 10 parole, NO arco temporale]
→ Esito a: [frase al presente indicativo, soggetto omesso, max 10 parole]
→ Esito b: [frase al presente indicativo, soggetto omesso, max 10 parole] (se applicabile)

OBIETTIVO 2: [verbo all'infinito + oggetto, max 10 parole]
→ Esito a: [frase al presente indicativo, soggetto omesso, max 10 parole]

(continua con gli obiettivi necessari)

Regole TASSATIVE per gli OBIETTIVI:
- Inizia SEMPRE con verbo all'infinito (Sviluppare, Migliorare, Acquisire, Partecipare, Comunicare...)
- NO arco temporale (niente "entro maggio", "nel I quadrimestre", ecc.)
- Massimo 10 parole per obiettivo — sii conciso e diretto
- Specifici per la Dimensione ${dimId} — ${dimLabel}
- Adeguati al livello: ${livelloCtx}
- **NON scrivere "Dimensione A:" o simili, né il titolo della dimensione, nel testo generato**

Regole per gli ESITI ATTESI:
- Massimo 2 esiti per obiettivo
- Scritti al PRESENTE INDICATIVO, terza persona singolare, senza soggetto esplicito
  Es. corretti: "Mostra interesse verso i compagni", "Si relaziona con il gruppo", "Rispetta i turni di parola"
  Es. SBAGLIATI: "Dimostrare…", "Essere in grado di…", "Mostrare…" (non usare l'infinito)
- Descrivono comportamenti osservabili con indicazioni qualitative (es. "con supervisione", "in autonomia", "nella maggior parte delle situazioni")
- NON usare mai percentuali (niente "nel 70% dei casi", "3 volte su 5", ecc.)
- Coerenti e proporzionati all'obiettivo corrispondente` : isS5AttivStrat && label === "Attività" ? `
Genera da 3 a 5 ATTIVITÀ finalizzate al raggiungimento degli obiettivi già definiti.
${contestoObiettivi ? "Obiettivi di riferimento:\n" + contestoObiettivi : ""}

Formato:
— [Nome attività telegrafico, max 6 parole]

Regole:
- Scrivi SOLO il nome dell'attività, senza descrizioni né spiegazioni aggiuntive
  Es. corretti: "Ascolto di canzoni", "Giochi di coppia", "Visione di filmati", "Attività motoria in palestra"
  Es. SBAGLIATI: "Ascolto di canzoni per sviluppare la concentrazione" (troppo lungo)
- Concrete, realizzabili in classe o in piccolo gruppo
- Adeguate al livello: ${livelloCtx}
- **NON scrivere "Dimensione A:" o simili, né il titolo della dimensione, nel testo generato**` : isS5Verifica ? `
Scrivi la revisione conclusiva degli esiti della Sezione 5 del PEI (tutte le dimensioni).
Il testo deve:
- verificare il grado di raggiungimento degli obiettivi per ciascuna dimensione compilata (raggiunto, parzialmente raggiunto, non raggiunto)
- valutare l'efficacia complessiva delle strategie e degli strumenti utilizzati nel corso dell'anno
- indicare gli obiettivi da riproporre o aggiornare per l'anno successivo

Regole:
- Testo continuo in paragrafi, senza elenchi puntati
- Tono valutativo, concreto e professionale, al passato
- Tra 150 e 250 parole
- NON usare grassetto né titoli interni` : `
Genera il contenuto per il campo "${label}" nel seguente formato OBBLIGATORIO:

STRATEGIE (da 2 a 5):
— [Solo il nome della tecnica, max 6 parole]
— [Solo il nome della tecnica, max 6 parole]

STRUMENTI (da 2 a 5):
— [Solo il nome dello strumento/ausilio, max 6 parole]
— [Solo il nome dello strumento/ausilio, max 6 parole]

Regole TASSATIVE:
- Scrivi SOLO il nome della tecnica o dello strumento, NESSUNA descrizione né spiegazione aggiuntiva
  Es. STRATEGIE corretti: "Peer tutoring", "Modeling", "Prompting e fading", "Rinforzi positivi", "Circle time"
  Es. STRUMENTI corretti: "Lim e tablet", "Libri in CAA", "Materiale Montessori", "Giochi interattivi"
  Es. SBAGLIATI: "Peer tutoring per favorire la collaborazione" (non aggiungere spiegazioni)
- STRATEGIE: nomi di tecniche didattiche
- STRUMENTI: nomi di materiali, ausili tecnologici, dispositivi
- Collegati agli obiettivi e alle attività già definiti
- Tieni conto delle barriere e dei facilitatori presenti nel contesto (Sezione 6)
- Adeguati al livello: ${livelloCtx}
- **NON scrivere "Dimensione A:" o simili, né il titolo della dimensione, nel testo generato**`;

        prompt = isS5
          ? `${basePrompt()}${dimContesto}${correlazione}

ORDINE SCOLASTICO: ${ordineCtx}
${formatoObiettivi}

Regole generali TASSATIVE:
- Coerenti con il profilo dello studente e le sezioni precedenti del PEI
- Solo per la Dimensione ${dimId || "indicata"} — NON mescolare dimensioni diverse
- NON scrivere testo introduttivo o conclusivo
- NON usare grassetto o altri formati diversi da quelli indicati
- **NON includere nel testo la dimensione (es. "Dimensione A:") né il titolo della dimensione, perché è già chiaro dal contesto**
- **Non scrivere titoli o sottotitoli (es. "Obiettivi:", "Attività:") all'interno del campo.**
${storico.length > 0 ? `\nNOTA: proponi contenuti DIVERSI da quelli già suggeriti:\n${storico.slice(-2).map((t,i)=>`Sugg.${i+1}: ${t.slice(0,100)}…`).join("\n")}` : ""}`
          : `${basePrompt()}

Scrivi SOLO il testo da inserire nel campo, in italiano, senza intestazioni, senza spiegazioni, senza virgolette.
Massimo 5-6 righe. Usa terminologia ICF. Descrivi capacità e punti di forza.
**NON includere nel testo la sezione o il titolo del campo (es. "Barriere:") – scrivi direttamente il contenuto.**
**Non usare grassetto.**
${includeICFCodes && isSezione2 ? ((contestoGlobale && /[bdse]\d{3}/.test(contestoGlobale)) ? `
GUIDA CODICI ICF — SEZIONE PROFILO DI FUNZIONAMENTO (Profilo presente nel contesto):
Nel contesto è presente un Profilo di Funzionamento con codici ICF: ESTRAI da lì i codici e i loro qualificatori e riportali fedelmente. Integra 5-8 codici INLINE, tra parentesi tonde, SUBITO DOPO la frase a cui si riferiscono (NON un elenco separato).
Codici d (attività/partecipazione): formato "codice.XY" — X=Performance (ambiente reale), Y=Capacity (condizione standardizzata). Scala 0=nessuna, 1=lieve, 2=moderata, 3=grave, 4=completa.
FEDELTÀ ALLA FONTE: riporta i qualificatori ESATTAMENTE come compaiono nella fonte. Se un codice è indicato con un solo qualificatore, lascialo con uno solo: NON aggiungere né dedurre il secondo (la performance va osservata, non inventata).
REGOLA GAP: applicala SOLO ai codici che hanno entrambi i qualificatori; se X > Y (es. d415.32) l'ambiente reale presenta una BARRIERA; segnalalo con "→ barriera ambientale" dentro la stessa parentesi.
Esempio inline: "Mantiene con difficoltà la postura al banco nelle attività prolungate (d415.32 → barriera ambientale), mentre la comprensione di messaggi verbali semplici è adeguata (d310.11)."` : `
GUIDA CODICI ICF — SEZIONE PROFILO DI FUNZIONAMENTO (nessun Profilo nel contesto):
NON è presente un Profilo di Funzionamento con codici ICF. Puoi comunque inserire alcuni codici ICF dei domini rilevanti, INLINE tra parentesi tonde dopo la frase a cui si riferiscono, ma SENZA inventare i qualificatori clinici di gravità:
- codici d (attività/partecipazione): indica AL MASSIMO la CAPACITÀ stimata, etichettandola (es. "(d310 — capacità stimata: lieve)"). NON assegnare il qualificatore di Performance (richiede l'osservazione diretta del contesto reale, qui non disponibile) e di conseguenza NON usare la regola del gap né la dicitura "→ barriera ambientale".
- codici b (funzioni corporee): al massimo una gravità orientativa coerente con la diagnosi, oppure solo il dominio senza numero (es. "(b117)").
- codici e (fattori ambientali): NIENTE qualificatori né segni "+". Descrivi barriere e facilitatori in linguaggio chiaro all'interno del testo, senza codifica numerica.
- Chiudi il profilo con questa frase: "I codici ICF qui riportati sono orientativi; i qualificatori di performance e i codici definitivi andranno desunti dal Profilo di Funzionamento e dalle osservazioni sistematiche."` ) : ""}
${includeICFCodes && isSezione4 ? `
GUIDA CODICI ICF — SEZIONE OSSERVAZIONI SISTEMATICHE:
In questa sezione documenta la performance OSSERVATA in classe. Usa UN SOLO qualificatore (performance) nel formato "codice.X" tra parentesi tonde dopo la frase descrittiva.
Scala: 0=nessuna difficoltà, 1=lieve, 2=moderata, 3=grave, 4=completa.
Riferisciti ai codici ICF già definiti nella Sezione 2 (Profilo di Funzionamento) se disponibili nel contesto.
Esempio: "Lo studente fatica a mantenere l'attenzione per più di 10 minuti nelle attività strutturate (b140.2)."` : ""}
${storico.length > 0 ? `\nNOTA: hai già suggerito questi testi, proponi qualcosa di DIVERSO e complementare:\n${storico.slice(-2).map((t,i)=>`Suggerimento ${i+1}: ${t.slice(0,100)}…`).join("\n")}` : ""}`;
      }
      // Auto-retry su overload Anthropic (max 2 tentativi, backoff 3s)
      let testo = "";
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          testo = await callClaude(prompt, maxTokens, 'ai_field', { campoId, sezione: sezioneLabel });
          break; // successo, esci dal loop
        } catch (retryErr) {
          if (/overload/i.test(retryErr?.message) && attempt < 2) {
            await new Promise(r => setTimeout(r, 3000 * (attempt + 1)));
            continue;
          }
          throw retryErr; // rilancia errori non-overload o esauriti tentativi
        }
      }
      // Rimuovi titoli markdown (# o ##) ovunque nel testo e righe vuote di testa;
      // normalizza doppi a capo in singoli.
      const righeRaw = testo.split("\n");
      let i = 0;
      while (i < righeRaw.length && (righeRaw[i].trim() === "" || righeRaw[i].trim().startsWith("#"))) i++;
      const righeFiltrate = righeRaw.slice(i).filter(r => !r.trim().startsWith("#"));
      // Rimuovi righe che sono note/commenti del modello (es. "**Nota compilativa**: ...")
      const righeSenzaNote = righeFiltrate.filter(r => !/^\s*\*{1,2}nota\b/i.test(r));
      let pulito = righeSenzaNote.join("\n").replace(/\n{2,}/g, "\n").trim();
      // Per il campo Barriere: rimuovi codici ICF in qualsiasi formato
      // (es. "(e115.1)", "[b140.2]", "e115.1", "d160.32", con o senza qualificatori)
      if (campoId === "s6_barriere") {
        pulito = pulito
          .replace(/[\(\[]\s*[a-z]\d{2,}(?:\.\d+)?\s*[\)\]]/gi, "")   // (e115.1) o [e115.1]
          .replace(/\b[a-z]\d{3,}(?:\.\d{1,2})?\b/g, "")               // standalone: e1151 b140.2
          .replace(/\s{2,}/g, " ")                                       // normalizza spazi doppi residui
          .replace(/\(\s*\)/g, "")                                       // rimuovi parentesi vuote rimaste
          .trim();
      }
      // Per i campi obiettivi (sez.5 e sez.8): rimuovi soggetto esplicito da ogni riga.
      // Es. "— Lo studente S. legge…" → "— Legge…"
      if (/(_ob$|s5_[ABCD]_ob)/.test(campoId || "")) {
        pulito = pulito
          .replace(
            /^(—|-)\s+(?:Lo studente\s+[A-Z]\.\s+|Lo studente\s+|L[''`']alunno\s+[A-Z]\.\s+|L[''`']alunno\s+)/gim,
            "$1 "
          )
          .replace(
            /^(—|-) ([a-z])/gm,
            (_, dash, lettera) => dash + " " + lettera.toUpperCase()
          );
      }
      const nuovoStorico = [...storico, pulito];
      setStorico(nuovoStorico);
      setIdxStorico(nuovoStorico.length - 1);
      onChange({ target: { value: pulito } });
    } catch (e) {
      if (e?.code === 'insufficient_credits') {
        setErrCrediti(true);
      } else {
        setErrGenerico(e?.message || "Errore durante la generazione. Riprova.");
        console.error("FieldAI suggerisci error:", e);
      }
    }
    setLoading(false);
  };

  const vaiIndietro = () => {
    if (idxStorico <= 0) return;
    const nuovoIdx = idxStorico - 1;
    setIdxStorico(nuovoIdx);
    onChange({ target: { value: storico[nuovoIdx] } });
  };

  const vaiAvanti = () => {
    if (idxStorico >= storico.length - 1) return;
    const nuovoIdx = idxStorico + 1;
    setIdxStorico(nuovoIdx);
    onChange({ target: { value: storico[nuovoIdx] } });
  };

  // Prompt di correzione standard (usato sia da correggi che da correggiComunque)
  const buildPromptCorreggi = () => {
    const isS8corrOb = /(_ob$)/.test(campoId || "") && sezioneLabel === "Curricolo";
    const isS5corr = sezioneLabel === "Obiettivi e Strategie";
    const dimIdCorr = campoId?.match(/s5_([ABCD])_/)?.[1] || "";
    const dimLabelCorr = {A:"Relazione/Interazione/Socializzazione",B:"Comunicazione/Linguaggio",C:"Autonomia/Orientamento",D:"Cognitiva/Neuropsicologica/Apprendimento"}[dimIdCorr] || "";
    const correlazioneCorr = contestoObiettivi
      ? `\n\nOBIETTIVI DI RIFERIMENTO per questa dimensione:\n${contestoObiettivi}\nAssicurati che il testo corretto sia coerente con questi obiettivi.`
      : "";
    const isS5corrOb    = isS5corr && label === "Obiettivi ed esiti attesi";
    const isS5corrAttiv = isS5corr && label === "Attività";
    const livelloCorr = {
      infanzia:"Infanzia (3-6 anni)",primaria:"Primaria (6-11 anni)",
      secondaria1:"Secondaria I grado (11-14 anni)",secondaria2:"Secondaria II grado (14-19 anni)"
    }[studente?.ordine] || "";
    if (isS8corrOb) {
      return `${basePrompt()}

L'insegnante ha scritto questi obiettivi di apprendimento per la disciplina nel PEI (Sez. 8):
"${value}"

Correggi e migliora mantenendo il formato OBBLIGATORIO:
— [VERBO al presente indicativo + oggetto, concreto e osservabile, max 10 parole]

Regole TASSATIVE:
- Inizia ogni riga con — seguito direttamente dal verbo (NO "Lo studente", NO "L'alunno", NO soggetto)
- Mantieni tutti gli obiettivi presenti nel testo originale, senza aggiungerne né togliere
- Correggi grammatica, stile e terminologia pedagogica
- Verbi al PRESENTE INDICATIVO (es. "Legge", "Scrive", "Calcola") — NON all'infinito
- Tono tecnico, professionale, coerente con il profilo dello studente
- NON aggiungere testo introduttivo o conclusivo

Restituisci SOLO gli obiettivi nel formato indicato, uno per riga.`;
    }
    if (isS5corr) {
      return `${basePrompt()}${correlazioneCorr}
${dimIdCorr ? `\nDIMENSIONE ICF: ${dimIdCorr} — ${dimLabelCorr}.` : ""}
Livello scolastico: ${livelloCorr}

L'insegnante ha scritto questo testo per il campo "${label}" del PEI:
"${value}"

${isS5corrOb ? `Riscrivi nel formato OBBLIGATORIO (obiettivi con verbi all'infinito, NO arco temporale):

OBIETTIVO 1: [verbo infinito + oggetto, max 15 parole]
→ Esito a: [comportamento osservabile, max 12 parole]
→ Esito b: [secondo esito, max 12 parole] (se applicabile)

OBIETTIVO 2: [verbo infinito + oggetto, max 15 parole]
→ Esito a: [comportamento osservabile, max 12 parole]

Regole: verbi all'infinito, NO arco temporale, NO percentuali negli esiti, max 2 esiti per obiettivo, coerente con Dim.${dimIdCorr}.`
: isS5corrAttiv ? `Riscrivi come attività finalizzate agli obiettivi (3-5 voci):
• [Attività concreta, max 15 parole] (indica a quale obiettivo si riferisce)
Regole: collegate agli obiettivi, concrete, adeguate al livello ${livelloCorr}.`
: (isS5corr && label === "Revisione conclusiva degli esiti") ? `Riscrivi la revisione conclusiva degli esiti in testo continuo (paragrafi, no elenchi):
- Verifica il raggiungimento degli obiettivi citandoli esplicitamente
- Valuta l'efficacia di strategie e strumenti
- Indica cosa confermare o modificare per l'a.s. successivo
Regole: tono valutativo al passato, 80-150 parole, NO grassetto, NO percentuali.`
: `Riscrivi nel formato OBBLIGATORIO:
STRATEGIE (2-5):
• [Strategia didattica, max 12 parole]
STRUMENTI (2-5):
• [Strumento/ausilio, max 12 parole]
Regole: strategie=modalità didattiche, strumenti=ausili/materiali, adeguati al livello ${livelloCorr}.`}
Restituisci SOLO il testo nel formato indicato.`;
    }
    return `${basePrompt()}

L'insegnante ha scritto questo testo per il campo "${label}" del PEI:
"${value}"

Correggi e migliora il testo mantenendo il significato originale. Obiettivi:
1. Correggi eventuali errori grammaticali e ortografici.
2. Usa terminologia ICF appropriata.
3. Rendi il testo più professionale e preciso, ma NON stravolgere il contenuto né aggiungere informazioni non presenti nel testo originale.
4. Mantieni lo stile descrittivo delle capacità (non dei deficit).
5. Massimo 6 righe.
6. Non usare grassetto né titoli superflui.
${includeICFCodes && isSezione2 ? ((contestoGlobale && /[bdse]\d{3}/.test(contestoGlobale)) ? `
7. Nel contesto è presente un Profilo di Funzionamento con codici ICF: riportali con i qualificatori COSÌ COME COMPAIONO nella fonte (documento caricato o testo dell'insegnante), inseriti INLINE tra parentesi tonde subito dopo la frase a cui si riferiscono — MAI come elenco separato in coda al testo. Mantieni i codici già presenti senza inventarne di nuovi né alterarne i qualificatori. Se un codice ha un solo qualificatore (es. "d160.2"), lascialo con uno solo: NON aggiungere né dedurre il secondo (la performance va osservata, non inventata); riserva la regola del gap "→ barriera ambientale" ai soli codici che hanno entrambi i qualificatori.` : `
7. NON è presente un Profilo di Funzionamento con codici ICF nel contesto: NON inventare qualificatori clinici di gravità. Mantieni i codici eventualmente già scritti dall'insegnante senza alterarli; se aggiungi codici, per i codici d indica AL MASSIMO la capacità stimata etichettata (es. "(d310 — capacità stimata: lieve)"), MAI il qualificatore di performance né la dicitura "→ barriera ambientale"; per i codici e NESSUN qualificatore né segno "+", descrivendo barriere e facilitatori a parole. Inseriscili INLINE tra parentesi tonde, MAI come elenco separato.`) : ""}
${includeICFCodes && isSezione4 ? `
7. Includi codici ICF con UN SOLO qualificatore tra parentesi (es. "b140.2").` : ""}

Restituisci SOLO il testo corretto, senza spiegazioni.`;
  };

  // Applica il testo corretto allo storico e al campo
  // (stesso post-processing di suggerisci: rimuove soggetto dagli obiettivi)
  const applicaCorretto = (testo) => {
    let pulito = testo;
    if (/(_ob$|s5_[ABCD]_ob)/.test(campoId || "")) {
      pulito = pulito
        .replace(
          /^(—|-)\s+(?:Lo studente\s+[A-Z]\.\s+|Lo studente\s+|L[''`']alunno\s+[A-Z]\.\s+|L[''`']alunno\s+)/gim,
          "$1 "
        )
        .replace(
          /^(—|-) ([a-z])/gm,
          (_, dash, lettera) => dash + " " + lettera.toUpperCase()
        );
    }
    const nuovoStorico = [...storico, pulito];
    setStorico(nuovoStorico);
    setIdxStorico(nuovoStorico.length - 1);
    onChange({ target: { value: pulito } });
  };

  const correggi = async () => {
    if (!value?.trim() || !studente?.diagnosi) return;
    setErrCrediti(false);
    setAvviso(false);
    setLoadingCorr(true);
    try {
      // Per i campi semplici verifica prima la pertinenza (stesso credito)
      if (CAMPI_SEMPLICI.has(campoId)) {
        const promptPertinenza = `${basePrompt()}

L'insegnante ha scritto questo testo per il campo "${label}" del PEI:
"${value}"

Valuta se il testo è pertinente al campo "${label}".
Rispondi ESCLUSIVAMENTE in uno di questi due formati:
- Se pertinente: PERTINENTE|[testo corretto grammaticalmente e professionalmente, max 6 righe]
- Se non pertinente: NON_PERTINENTE|[una frase breve che spiega perché non è pertinente]

Non aggiungere altro testo oltre al formato richiesto.`;
        const risposta = await callClaude(promptPertinenza, maxTokens, 'ai_field', { campoId, sezione: sezioneLabel, action: 'correggi' });
        const [tag, corpo] = risposta.split("|");
        if (tag?.trim() === "NON_PERTINENTE") {
          setAvvisoMsg(corpo?.trim() || "Il testo non sembra pertinente a questo campo.");
          setAvviso(true);
          setLoadingCorr(false);
          return;
        }
        // Pertinente: usa il testo corretto già restituito
        applicaCorretto((corpo || risposta).trim());
      } else {
        // Campi tecnici: correzione diretta senza check pertinenza
        const corretto = await callClaude(buildPromptCorreggi(), maxTokens, 'ai_field', { campoId, sezione: sezioneLabel, action: 'correggi' });
        applicaCorretto(corretto.trim());
      }
    } catch (e) {
      if (e?.code === 'insufficient_credits') setErrCrediti(true);
    }
    setLoadingCorr(false);
  };

  // Corregge comunque ignorando l'avviso di pertinenza
  const correggiComunque = async () => {
    setAvviso(false);
    setErrCrediti(false);
    setLoadingCorr(true);
    try {
      const corretto = await callClaude(buildPromptCorreggi(), maxTokens, 'ai_field', { campoId, sezione: sezioneLabel, action: 'correggi' });
      applicaCorretto(corretto.trim());
    } catch (e) {
      if (e?.code === 'insufficient_credits') setErrCrediti(true);
    }
    setLoadingCorr(false);
  };

  // Adatta il contenuto al campo (riscrive partendo dal testo esistente)
  const adattaAlCampo = async () => {
    setAvviso(false);
    setErrCrediti(false);
    setLoading(true);
    try {
      const prompt = `${basePrompt()}

L'insegnante ha scritto questo testo (non pertinente al campo):
"${value}"

Riscrivi un testo appropriato per il campo "${label}" del PEI, usando come spunto il contesto o le informazioni utili presenti nel testo sopra (se presenti), e il profilo dello studente. Il testo deve essere pertinente, professionale e adeguato al campo.
Massimo 6 righe. Restituisci SOLO il testo, senza spiegazioni.`;
      const adattato = await callClaude(prompt, maxTokens, 'ai_field', { campoId, sezione: sezioneLabel, action: 'adatta' });
      applicaCorretto(adattato.trim());
    } catch (e) {
      if (e?.code === 'insufficient_credits') setErrCrediti(true);
    }
    setLoading(false);
  };

  const hasStorico = storico.length > 0;
  const canBack   = idxStorico > 0;
  const canFwd    = idxStorico < storico.length - 1;

  if (!studente?.diagnosi) {
    return (
      <div style={{ marginBottom: 14 }}>
        {label && <Label sub={sub} required={required}>{label}</Label>}
        <Textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows} />
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 5 }}>
        <Label sub={sub} required={required}>{label}</Label>
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
          {hasStorico && (
            <div style={{ display: "flex", alignItems: "center", gap: 2,
              padding: "2px 6px", background: C.bg,
              border: `1px solid ${C.border}`, borderRadius: 6 }}>
              <button onClick={vaiIndietro} disabled={!canBack} title="Suggerimento precedente"
                style={{ background: "none", border: "none", cursor: canBack ? "pointer" : "not-allowed",
                  color: canBack ? C.accent : C.mutedLight, fontSize: 12, padding: "0 3px",
                  lineHeight: 1, fontWeight: 700 }}>‹</button>
              <span style={{ fontSize: 10, color: C.muted, minWidth: 28, textAlign: "center" }}>
                {idxStorico + 1}/{storico.length}
              </span>
              <button onClick={vaiAvanti} disabled={!canFwd} title="Suggerimento successivo"
                style={{ background: "none", border: "none", cursor: canFwd ? "pointer" : "not-allowed",
                  color: canFwd ? C.accent : C.mutedLight, fontSize: 12, padding: "0 3px",
                  lineHeight: 1, fontWeight: 700 }}>›</button>
            </div>
          )}
          {value?.trim() && (
            <button onClick={correggi} disabled={loadingCorr || aiDisabilitata}
              title={motivoBlocco || "Correggi e migliora (1 credito)"}
              style={{ display: "inline-flex", alignItems: "center", gap: 3,
                padding: "2px 7px", borderRadius: 6,
                background: aiDisabilitata ? hex(C.muted, 0.10) : C.greenDim,
                border: `1px solid ${aiDisabilitata ? hex(C.muted, 0.20) : hex(C.green, 0.3)}`,
                color: aiDisabilitata ? C.muted : C.green,
                fontSize: 11, fontWeight: 600,
                fontFamily: "'Inter',sans-serif",
                cursor: aiDisabilitata ? "not-allowed" : (loadingCorr ? "wait" : "pointer"),
                opacity: aiDisabilitata ? 0.6 : 1 }}>
              {loadingCorr ? <Spinner size={10} color={C.green} /> : (aiBloccata ? "🔒" : "✓")} Correggi
            </button>
          )}
          <button onClick={suggerisci} disabled={loading || aiDisabilitata}
            title={motivoBlocco || "Genera testo con AI (1 credito)"}
            style={{ display: "inline-flex", alignItems: "center", gap: 3,
              padding: "2px 7px", borderRadius: 6,
              background: aiDisabilitata ? hex(C.muted, 0.10) : C.accentDim,
              border: `1px solid ${aiDisabilitata ? hex(C.muted, 0.20) : hex(C.accent, 0.3)}`,
              color: aiDisabilitata ? C.muted : C.accent,
              fontSize: 11, fontWeight: 600,
              fontFamily: "'Inter',sans-serif",
              cursor: aiDisabilitata ? "not-allowed" : (loading ? "wait" : "pointer"),
              opacity: aiDisabilitata ? 0.6 : 1 }}>
            {loading ? <Spinner size={10} /> : (aiBloccata ? "🔒" : "✦")} {hasStorico ? "Altro" : "Suggerisci"}
          </button>
        </div>
      </div>
      {errCrediti && (
        <div className="fadeIn" style={{
          marginBottom: 6, padding: "7px 12px", borderRadius: 8,
          background: hex(C.red, 0.08), border: `1px solid ${hex(C.red, 0.25)}`,
          fontSize: 12, color: C.red, fontWeight: 500
        }}>
          ⚠️ Crediti esauriti. Vai su <strong>Account</strong> per acquistare un pacchetto.
        </div>
      )}
      {errGenerico && (
        <div className="fadeIn" style={{
          marginBottom: 6, padding: "7px 12px", borderRadius: 8,
          background: hex(C.red, 0.08), border: `1px solid ${hex(C.red, 0.25)}`,
          fontSize: 12, color: C.red, fontWeight: 500
        }}>
          {/overload/i.test(errGenerico)
            ? "⚠️ Server AI momentaneamente sovraccarico — riprova tra qualche secondo."
            : `⚠️ ${errGenerico}`}
        </div>
      )}
      {avviso && (
        <div className="fadeIn" style={{
          marginBottom: 8, padding: "10px 14px", borderRadius: 10,
          background: hex(C.orange, 0.08), border: `1px solid ${hex(C.orange, 0.30)}`,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.orange, marginBottom: 4 }}>
            ⚠️ Testo non pertinente al campo
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>
            {avvisoMsg}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={adattaAlCampo} disabled={loading}
              style={{ fontSize: 11, fontWeight: 600, padding: "4px 12px", borderRadius: 6,
                background: hex(C.accent, 0.10), border: `1px solid ${hex(C.accent, 0.30)}`,
                color: C.accent, cursor: "pointer", fontFamily: "'Inter',sans-serif" }}>
              {loading ? <Spinner size={9} /> : "✦"} Adatta al campo
            </button>
            <button onClick={correggiComunque} disabled={loadingCorr}
              style={{ fontSize: 11, fontWeight: 600, padding: "4px 12px", borderRadius: 6,
                background: "none", border: `1px solid ${hex(C.muted, 0.30)}`,
                color: C.muted, cursor: "pointer", fontFamily: "'Inter',sans-serif" }}>
              {loadingCorr ? <Spinner size={9} /> : "✓"} Correggi comunque
            </button>
            <button onClick={() => setAvviso(false)}
              style={{ fontSize: 11, padding: "4px 8px", borderRadius: 6,
                background: "none", border: "none", color: C.mutedLight,
                cursor: "pointer", fontFamily: "'Inter',sans-serif" }}>
              ✕
            </button>
          </div>
        </div>
      )}
      <Textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows} />
      {hasStorico && (
        <div style={{ fontSize: 10, color: C.mutedLight, marginTop: 3, textAlign: "right" }}>
          Versione AI {idxStorico + 1} · puoi modificare liberamente
        </div>
      )}
    </div>
  );
}

// ─── CHAT ASSISTENTE ──────────────────────────────────────────────────────────
function ChatAssistente({ studente, sezioneLabel, open, onClose }) {
  const [msgs,    setMsgs]    = useState([]);
  const [input,   setInput]   = useState("");
  const [loading, setLoading] = useState(false);
  const chatRef = useRef(null);

  const diagCtx = studente
    ? `Studente: ${studente.iniziali||"N.N."}, ${studente.eta||""} anni, ${ORDINI[studente.ordine]?.label||""}, diagnosi: ${studente.diagnosi||"n.s."}.${studente.certInAttesa ? " Certificazione in iter / diagnosi provvisoria — adotta cautela: formule osservative, evita affermazioni cliniche definitive." : ""}${studente.comorbidita ? ` Comorbidità: ${studente.comorbidita} — considera misure compensative e dispensative coerenti (per i DSA, ad es.: sintesi vocale, calcolatrice, mappe e formulari; dispensa dalla lettura ad alta voce, tempi aggiuntivi).` : ""}${direttiveValutazioneOrdine(studente)}`
    : "";

  useEffect(() => {
    if (open && msgs.length === 0 && studente?.diagnosi) {
      setMsgs([{ role:"assistant", text:`Ciao! Sono qui per aiutarti con la sezione **${sezioneLabel}**.\n\nPer la diagnosi **${studente.diagnosi}**, posso suggerirti esempi concreti, strategie didattiche e aiutarti a formulare testi professionali. Cosa ti serve?` }]);
    }
  }, [open]);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [msgs]);

  const invia = async () => {
    if (!input.trim() || loading) return;
    const dom = input.trim(); setInput("");
    const nuovi = [...msgs, { role:"user", text:dom }];
    setMsgs(nuovi); setLoading(true);
    try {
      const storico = nuovi.slice(-6).map(m => `${m.role==="user"?"Insegnante":"Assistente"}: ${m.text}`).join("\n\n");
      const r = await callClaude(
        `${diagCtx}\nSezione: ${sezioneLabel}\n\n${storico}\n\nRispondi in modo pratico e specifico. Usa **grassetto** per i titoli.`, 700);
      setMsgs(p => [...p, { role:"assistant", text:r }]);
    } catch (e) {
      setMsgs(p => [...p, { role:"assistant", text:`⚠️ ${e.message}` }]);
    }
    setLoading(false);
  };

  const renderMd = t => t
    .replace(/\*\*(.*?)\*\*/g, `<strong style="color:${C.text};font-weight:600">$1</strong>`)
    .replace(/^- (.*?)$/gm, `<div style="display:flex;gap:5px;margin:2px 0"><span style="color:${C.accent}">•</span><span>$1</span></div>`)
    .replace(/\n/g, "<br/>");

  if (!open) return null;
  return (
    <div className="slideIn" style={{
      position: "fixed", right: 0, top: 0, bottom: 0, width: 340,
      background: C.surface, borderLeft: `1px solid ${C.border}`,
      boxShadow: "-4px 0 20px rgba(0,0,0,0.08)",
      display: "flex", flexDirection: "column", zIndex: 1000 }}>
      <div style={{ padding:"14px 16px", borderBottom:`1px solid ${C.border}`,
        background: grad(hex(C.accent,0.05), hex(C.accent2,0.03)),
        display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <div style={{ width:30,height:30,borderRadius:"50%",
            background:grad(C.accent,C.accent2), display:"flex",
            alignItems:"center", justifyContent:"center", fontSize:14, color:"#fff" }}>✦</div>
          <div>
            <div style={{ fontSize:13, fontWeight:700, color:C.text }}>Assistente AI</div>
            <div style={{ fontSize:11, color:C.muted }}>{sezioneLabel}</div>
            <div style={{ fontSize:10, color:C.mutedLight, lineHeight:1.4, marginTop:2 }}>
              Risposte generate da un sistema di intelligenza artificiale — verificale sempre
            </div>
          </div>
        </div>
        <button onClick={onClose} style={{ background:"none",border:"none",
          color:C.muted,fontSize:18,cursor:"pointer",padding:"2px 6px" }}>✕</button>
      </div>
      <div ref={chatRef} style={{ flex:1, overflowY:"auto", padding:14, display:"flex", flexDirection:"column", gap:10 }}>
        {msgs.map((m,i) => (
          <div key={i} className="fadeIn" style={{
            padding:"9px 13px",
            borderRadius: m.role==="user" ? "12px 12px 3px 12px" : "12px 12px 12px 3px",
            background: m.role==="user" ? C.accentDim : C.bg,
            border: `1px solid ${m.role==="user" ? hex(C.accent,0.2) : C.border}`,
            fontSize:13, lineHeight:1.7, color:C.text,
            alignSelf: m.role==="user" ? "flex-end" : "flex-start",
            maxWidth:"92%" }}>
            {m.role==="assistant"
              ? <div dangerouslySetInnerHTML={{ __html:renderMd(m.text) }} />
              : m.text}
          </div>
        ))}
        {loading && (
          <div style={{ display:"flex", gap:6, alignItems:"center", color:C.muted, fontSize:12 }}>
            <Spinner size={12} /> Elaboro…
          </div>
        )}
      </div>
      <div style={{ padding:"10px 12px", borderTop:`1px solid ${C.border}` }}>
        <div style={{ display:"flex", gap:8 }}>
          <input value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key==="Enter" && !e.shiftKey && invia()}
            placeholder="Chiedi all'assistente… (Invio)"
            style={{ flex:1, background:C.bg, border:`1px solid ${C.border}`,
              borderRadius:8, padding:"8px 12px", color:C.text,
              fontSize:13, fontFamily:"'Inter',sans-serif" }} />
          <button onClick={invia} disabled={loading||!input.trim()}
            style={{ width:34,height:34,borderRadius:8,
              background:grad(C.accent,C.accent2),border:"none",
              color:"#fff",fontSize:16,cursor:"pointer",
              display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>↑</button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
//  SEZIONI DEL PEI (12 sezioni obbligatorie)
// =============================================================================

function SezGLO({ dati, setD, studente, onChat }) {
  const f = (k) => dati[k] || "";
  const s = (k, v) => setD(k, v);
  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
        <SezTitle icon="🏫">Dati Generali</SezTitle>
        <Btn ghost small onClick={onChat} icon="💬">Assistente</Btn>
      </div>
      <div style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 14px", marginBottom:16 }}>
        <div style={{ fontSize:11, color:C.muted, fontWeight:600, textTransform:"uppercase", letterSpacing:.5, marginBottom:8 }}>
          Dati dal profilo studente
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, fontSize:13, color:C.textSec }}>
          <div><span style={{ color:C.muted }}>Anno Scolastico: </span>{studente?.annoScolastico || "–"}</div>
          <div><span style={{ color:C.muted }}>Iniziale: </span><strong>{studente?.iniziali || "–"}</strong></div>
          <div><span style={{ color:C.muted }}>Età: </span>{studente?.eta || "–"} anni</div>
          <div><span style={{ color:C.muted }}>Classe: </span>{studente?.classe || "–"}</div>
          <div><span style={{ color:C.muted }}>Diagnosi: </span>{studente?.diagnosi || "–"}</div>
        </div>
        {studente?.icd10?.length > 0 && (
          <div style={{ marginTop:8, display:"flex", gap:4, flexWrap:"wrap" }}>
            {studente.icd10.map(c => <Badge key={c.code} color={C.teal} small>{c.code}</Badge>)}
          </div>
        )}
      </div>
      <Divider />
      <div style={{ background:hex(C.accent,0.04), border:`1px solid ${hex(C.accent,0.15)}`,
        borderRadius:10, padding:"12px 14px" }}>
        <div style={{ fontSize:12, fontWeight:600, color:C.accent, marginBottom:10 }}>
          Progetto Individuale (art. 14 L. 328/2000)
        </div>
        <div style={{ display:"flex", gap:20, marginBottom:10 }}>
          <label style={{ display:"flex", alignItems:"center", gap:7, fontSize:13, color:C.text, cursor:"pointer" }}>
            <input type="radio" name="glo_pi_stato" value="redatto"
              checked={f("glo_pi_stato") === "redatto"}
              onChange={() => s("glo_pi_stato", "redatto")} />
            Redatto
          </label>
          <label style={{ display:"flex", alignItems:"center", gap:7, fontSize:13, color:C.text, cursor:"pointer" }}>
            <input type="radio" name="glo_pi_stato" value="non_redatto"
              checked={f("glo_pi_stato") === "non_redatto"}
              onChange={() => s("glo_pi_stato", "non_redatto")} />
            Non redatto
          </label>
        </div>
      </div>
    </div>
  );
}

// ─── CONTESTO VERIFICA COMPLETO ───────────────────────────────────────────────
// Aggrega tutto il contenuto programmatorio del PEI (Sez. 2, 4, 5, 6, 7, 8)
// per fornire alle verifiche intermedie e finali un quadro completo dello studente.
// Parametri:
//   dati  — oggetto con tutti i dati del PEI
//   voci  — array nomi discipline (opzionale, usato da Sez.8 e Sez.11)
function buildContestoVerificaCompleto(dati, voci = []) {
  const parti = [];

  // Sez.2 — Profilo di funzionamento
  if (dati.pf_descrizione)
    parti.push(`PROFILO DI FUNZIONAMENTO (Sez.2):\n${dati.pf_descrizione.slice(0, 500)}`);

  // Sez.4 — Osservazioni sistematiche per dimensione
  const obs = DIMENSIONI.filter(d => dati[`s4_${d.id}_obs`])
    .map(d => `Dim.${d.id}: ${dati[`s4_${d.id}_obs`].slice(0, 150)}`);
  if (obs.length > 0)
    parti.push(`OSSERVAZIONI SISTEMATICHE (Sez.4):\n${obs.join('\n')}`);

  // Sez.5 — Obiettivi, strategie, strumenti e piano di verifica per dimensione ICF
  const obDim = DIMENSIONI.filter(d => dati[`s5_${d.id}_ob`] || dati[`s5_${d.id}_verifica_dim`]);
  if (obDim.length > 0) {
    const righe = obDim.map(d => {
      let r = `Dim.${d.id} — ${d.label}:`;
      if (dati[`s5_${d.id}_ob`])           r += `\n  Obiettivi: ${dati[`s5_${d.id}_ob`].slice(0, 300)}`;
      if (dati[`s5_${d.id}_att`])          r += `\n  Attività: ${dati[`s5_${d.id}_att`].slice(0, 150)}`;
      if (dati[`s5_${d.id}_strat`])        r += `\n  Strategie/Strumenti: ${dati[`s5_${d.id}_strat`].slice(0, 150)}`;
      if (dati[`s5_${d.id}_verifica_dim`]) r += `\n  Piano di verifica (metodi/criteri/strumenti): ${dati[`s5_${d.id}_verifica_dim`].slice(0, 250)}`;
      return r;
    });
    parti.push(`OBIETTIVI, STRATEGIE E PIANO DI VERIFICA (Sez.5):\n${righe.join('\n')}`);
  }

  // Sez.4 / 5 / 6 / 7 / 8 / 9 — Revisioni a seguito di Verifica intermedia (GLO intermedio)
  const revisioniIntermedie = [
    dati.s4_revisione_intermedia ? `Sez.4 (Osservazioni): ${dati.s4_revisione_intermedia.slice(0, 250)}` : null,
    dati.s5_revisione_intermedia ? `Sez.5 (Obiettivi): ${dati.s5_revisione_intermedia.slice(0, 250)}` : null,
    dati.s6_revisione_intermedia ? `Sez.6 (Barriere/Facilitatori): ${dati.s6_revisione_intermedia.slice(0, 200)}` : null,
    dati.s7_revisione_intermedia ? `Sez.7 (Ambiente): ${dati.s7_revisione_intermedia.slice(0, 200)}` : null,
    dati.s8_revisione_intermedia ? `Sez.8 (Curricolo): ${dati.s8_revisione_intermedia.slice(0, 200)}` : null,
    dati.s9_revisione_intermedia ? `Sez.9 (Organizzazione): ${dati.s9_revisione_intermedia.slice(0, 200)}` : null,
  ].filter(Boolean);
  if (revisioniIntermedie.length > 0)
    parti.push(`REVISIONI A SEGUITO DI VERIFICA INTERMEDIA (GLO intermedio):\n${revisioniIntermedie.join('\n')}`);

  // Sez.6 — Barriere e facilitatori
  if (dati.s6_barriere)      parti.push(`BARRIERE (Sez.6):\n${dati.s6_barriere.slice(0, 300)}`);
  if (dati.s6_facilitatori)  parti.push(`FACILITATORI (Sez.6):\n${dati.s6_facilitatori.slice(0, 300)}`);

  // Sez.7 — Interventi sull'ambiente inclusivo
  const s7 = [
    dati.s7_macro      ? `MACRO: ${dati.s7_macro.slice(0, 200)}`      : null,
    dati.s7_meso       ? `MESO: ${dati.s7_meso.slice(0, 200)}`        : null,
    dati.s7_micro      ? `MICRO: ${dati.s7_micro.slice(0, 200)}`      : null,
    dati.s7_interventi ? `Campo unico: ${dati.s7_interventi.slice(0, 250)}` : null,
  ].filter(Boolean);
  if (s7.length > 0)
    parti.push(`INTERVENTI AMBIENTE INCLUSIVO (Sez.7):\n${s7.join('\n')}`);

  // Sez.8.1 — Modalità di sostegno
  if (dati.s8_1)
    parti.push(`MODALITÀ DI SOSTEGNO EDUCATIVO (Sez.8.1):\n${dati.s8_1.slice(0, 250)}`);

  // Discipline aggiunte a mano dall'insegnante (prefisso s8_cx_${j}_*)
  const customVoci = Array.isArray(dati.s8_custom_voci) ? dati.s8_custom_voci : [];

  // Sez.8.2 — Obiettivi disciplinari percorso B (discipline standard + custom)
  const discB = [];
  for (let i = 0; i < 25; i++) {
    if (dati[`s8_${i}_scelta`] === 'B') {
      const nome = voci[i] ? voci[i] : `Disciplina ${i + 1}`;
      const ob   = dati[`s8_${i}_ob`]    ? `Obiettivi: ${dati[`s8_${i}_ob`].slice(0, 200)}`    : '';
      const str  = dati[`s8_${i}_strat`] ? `Strategie: ${dati[`s8_${i}_strat`].slice(0, 100)}` : '';
      if (ob) discB.push(`${nome} — ${ob}${str ? ' | ' + str : ''}`);
    }
  }
  for (let j = 0; j < customVoci.length; j++) {
    if (dati[`s8_cx_${j}_scelta`] === 'B') {
      const nome = customVoci[j] ? customVoci[j] : `Disciplina aggiunta ${j + 1}`;
      const ob   = dati[`s8_cx_${j}_ob`]    ? `Obiettivi: ${dati[`s8_cx_${j}_ob`].slice(0, 200)}`    : '';
      const str  = dati[`s8_cx_${j}_strat`] ? `Strategie: ${dati[`s8_cx_${j}_strat`].slice(0, 100)}` : '';
      if (ob) discB.push(`${nome} — ${ob}${str ? ' | ' + str : ''}`);
    }
  }
  if (discB.length > 0)
    parti.push(`PROGRAMMAZIONE PERSONALIZZATA B (Sez.8.2):\n${discB.join('\n')}`);

  // Sez.8.2 — Discipline con percorso differenziato C (obiettivi nettamente ridotti, prove non equipollenti) — standard + custom
  const discC = [];
  for (let i = 0; i < 25; i++) {
    if (dati[`s8_${i}_scelta`] === 'C') {
      const nome = voci[i] ? voci[i] : `Disciplina ${i + 1}`;
      const ob = dati[`s8_${i}_c_obiettivi`] ? `Obiettivi differenziati: ${dati[`s8_${i}_c_obiettivi`].slice(0, 200)}` : '';
      if (ob) discC.push(`${nome} — ${ob}`);
    }
  }
  for (let j = 0; j < customVoci.length; j++) {
    if (dati[`s8_cx_${j}_scelta`] === 'C') {
      const nome = customVoci[j] ? customVoci[j] : `Disciplina aggiunta ${j + 1}`;
      const ob = dati[`s8_cx_${j}_c_obiettivi`] ? `Obiettivi differenziati: ${dati[`s8_cx_${j}_c_obiettivi`].slice(0, 200)}` : '';
      if (ob) discC.push(`${nome} — ${ob}`);
    }
  }
  if (discC.length > 0)
    parti.push(`PERCORSO DIFFERENZIATO C — prove non equipollenti (Sez.8.2):\n${discC.join('\n')}`);

  // Sez.8.3 — Comportamento (se percorso B)
  if ((dati.s8_comportamento_scelta || 'A') === 'B' && dati.s8_comportamento)
    parti.push(`OBIETTIVI COMPORTAMENTALI (Sez.8.3):\n${dati.s8_comportamento.slice(0, 200)}`);

  return parti.length > 0 ? parti.join('\n\n') : null;
}

// ─── ARRICCHIMENTO CONTESTO PER SEZIONE (additivo, content-gated) ─────────────
// Riassunti compatti delle sezioni-fonte da AGGIUNGERE al core di contestoGlobale
// nelle sole sezioni oggi affamate, secondo una mappa di rilevanza ancorata alle
// Linee Guida (Allegato B). Funzioni pure: stringa vuota se la fonte è vuota, così
// l'iniezione è gated dal contenuto e la navigazione libera resta gestita (entra
// solo ciò che esiste, in qualunque ordine sia stato compilato). NON tocca Sez.7
// né le verifiche, che costruiscono già il proprio contesto ricco.
function _ctxS3Raccordo(dati) {
  const p = [];
  if (dati.pi_sintesi)  p.push(`Sintesi del Progetto Individuale (art.14 L.328/2000): ${dati.pi_sintesi.slice(0, 400)}`);
  if (dati.pi_raccordo) p.push(`Raccordo PEI–interventi territoriali: ${dati.pi_raccordo.slice(0, 300)}`);
  return p.length ? `RACCORDO CON PROGETTO INDIVIDUALE (Sez.3):\n${p.join('\n')}` : '';
}
function _ctxS5Obiettivi(dati) {
  const r = DIMENSIONI.filter(d => dati[`s5_${d.id}_ob`])
    .map(d => `Dim.${d.id}: ${dati[`s5_${d.id}_ob`].slice(0, 250)}`);
  return r.length ? `OBIETTIVI DEL PEI (Sez.5):\n${r.join('\n')}` : '';
}
function _ctxS7Ambiente(dati) {
  const p = [
    dati.s7_macro      ? `MACRO: ${dati.s7_macro.slice(0, 150)}`           : null,
    dati.s7_meso       ? `MESO: ${dati.s7_meso.slice(0, 150)}`             : null,
    dati.s7_micro      ? `MICRO: ${dati.s7_micro.slice(0, 150)}`           : null,
    dati.s7_interventi ? `Interventi: ${dati.s7_interventi.slice(0, 200)}` : null,
  ].filter(Boolean);
  return p.length ? `INTERVENTI SULL'AMBIENTE (Sez.7):\n${p.join('\n')}` : '';
}
function _ctxS8Curricolo(dati) {
  const p = [];
  if (dati.s8_1) p.push(`Modalità di sostegno (Sez.8.1): ${dati.s8_1.slice(0, 200)}`);
  const disc = [];
  for (let i = 0; i < 25; i++) {
    const sc = dati[`s8_${i}_scelta`];
    if ((sc === 'B' || sc === 'C') && dati[`s8_${i}_ob`])
      disc.push(`Disciplina ${i + 1} (perc.${sc}): ${dati[`s8_${i}_ob`].slice(0, 150)}`);
  }
  if (disc.length) p.push(`Obiettivi disciplinari (Sez.8):\n${disc.join('\n')}`);
  return p.length ? p.join('\n') : '';
}

// Mappa di arricchimento: sezione attiva → riassunti correlati da aggiungere al core.
// (Sez.10 ← Sez.5+Sez.8 perché buildCertPrompt li richiama esplicitamente; Sez.3 →
// obiettivi/curricolo sec.II/risorse/progetto di vita, conforme art.6 DI + LG All.B.)
function contestoArricchimento(dati, sezId, ordine) {
  const sec2 = ordine === 'secondaria2';
  let fonti;
  switch (sezId) {
    case 's5':    fonti = [_ctxS3Raccordo(dati)]; break;
    case 's8':    fonti = sec2 ? [_ctxS5Obiettivi(dati), _ctxS3Raccordo(dati)] : [_ctxS5Obiettivi(dati)]; break;
    case 's9':    fonti = [_ctxS5Obiettivi(dati), _ctxS7Ambiente(dati), _ctxS3Raccordo(dati)]; break;
    case 's10':   fonti = [_ctxS5Obiettivi(dati), _ctxS8Curricolo(dati)]; break;
    case 's_pdv': fonti = [_ctxS3Raccordo(dati), _ctxS5Obiettivi(dati)]; break;
    default:      fonti = [];
  }
  return fonti.filter(Boolean).join('\n\n');
}

// Mappa documenti: sezione attiva → tipi di documento pertinenti (riduce token e
// rumore iniettando solo i documenti utili a quella sezione).
const DOC_SCOPE_SEZIONE = {
  glo: [],
  s1: ['pf', 'diagnosi'],
  s2: ['pf', 'diagnosi'],
  s3: ['pei_prec', 'glho'],
  s4: ['pf', 'diagnosi', 'pei_prec', 'altro'],
  s5: ['pei_prec', 'pf', 'diagnosi', 'altro'],
  s6: ['pf', 'diagnosi'],
  s7: ['pf', 'pei_prec'],
  s8: ['pei_prec', 'diagnosi'],
  s9: ['glho', 'pei_prec'],
  s10: ['pei_prec'],
  s11: ['pei_prec', 'pf', 'diagnosi'],
  s12: ['pf', 'diagnosi'],
  s_pdv: ['pei_prec', 'pf'],
};
const DOC_SCOPE_DEFAULT = ['pf', 'diagnosi'];

// ─── PROMPT "REVISIONE A SEGUITO DI VERIFICA INTERMEDIA" ──────────────────────
// Campo previsto dai modelli A1/A2/A3/A4 del PEI in Sez. 4, 5, 6, 7, 8 e 9.
// DI 182/2020 art. 9 c.2: il GLO è obbligato a tenere "incontri intermedi di verifica
// (almeno uno) per accertare il raggiungimento degli obiettivi e apportare eventuali
// modifiche ed integrazioni". Questo campo è correttivo/aggiornativo: descrive cosa
// cambia, non come è andata (per il bilancio c'è la Verifica conclusiva).
function buildRevisioneIntermediaPrompt({ sezN, sezLabel, natura, blocchi, contestoSpecifico = "" }) {
  const naturaDesc = natura === "osservativo"
    ? "Campo OSSERVATIVO-AGGIORNATIVO. Si compila al GLO intermedio (tipicamente gen-feb) e descrive cosa è cambiato nell'osservazione sistematica dopo i primi mesi dell'anno scolastico: nuovi comportamenti emersi, progressi, regressioni, situazioni nuove. NON è un bilancio retrospettivo dei risultati conseguiti."
    : "Campo CORRETTIVO-PROPOSITIVO. Si compila al GLO intermedio (tipicamente gen-feb) e descrive cosa cambiare nel piano dopo i primi mesi dell'anno scolastico, sulla base di quanto emerso dalle verifiche in itinere. NON è un bilancio retrospettivo dei risultati conseguiti — quello è raccolto nella 'Verifica conclusiva degli esiti' a fine anno.";
  const blocchiTesto = blocchi.map(b => `\n${b.titolo}\n${b.descrizione}`).join("\n");
  return `Sei un docente di sostegno esperto che compila il campo "Revisione a seguito di Verifica intermedia" della Sezione ${sezN} (${sezLabel}) del PEI.

NORMATIVA — D.I. 182/2020 (corretto da D.I. 153/2023) art. 9 c.2: il GLO è obbligato a tenere "incontri intermedi di verifica (almeno uno) per accertare il raggiungimento degli obiettivi e apportare eventuali modifiche ed integrazioni". I modelli ufficiali A1/A2/A3/A4 prevedono questo campo in tutte le sezioni del PEI con label testuale identica.

${naturaDesc}
${contestoSpecifico ? `\n${contestoSpecifico}\n` : ""}
Output STRUTTURATO nei seguenti blocchi (ometti i blocchi non pertinenti se non c'è materiale specifico nel contesto del PEI):
${blocchiTesto}

Regole TASSATIVE:
- Tono ${natura === "osservativo" ? "OSSERVATIVO-EVOLUTIVO" : "CORRETTIVO-PROPOSITIVO"}, al presente indicativo
- Cita elementi specifici del PEI (obiettivi, strategie, strumenti, contesti, ore, figure) — niente formule vuote tipo "alcune cose vanno migliorate"
- NON è un bilancio: non scrivere "gli obiettivi sono stati raggiunti" o simili; scrivi cosa cambia o cosa emerge di nuovo
- NO grassetto, NO virgolette, NO percentuali, NO date specifiche tipo "entro maggio"
- 100-200 parole totali`;
}

// ─── QUESTIONARIO PRE-GENERAZIONE VERIFICHE ───────────────────────────────────
const FATTORI_POSITIVI = [
  "Buona collaborazione nel team docenti",
  "Partecipazione attiva della famiglia",
  "Utilizzo efficace delle tecnologie assistive",
  "Clima di classe inclusivo",
  "Progressi nella motivazione e autostima dello studente",
];
const FATTORI_CRITICI = [
  "Assenze prolungate",
  "Difficoltà di collaborazione nel team docenti",
  "Scarsa partecipazione della famiglia",
  "Inadeguatezza degli strumenti/ausili disponibili",
  "Cambiamenti organizzativi (cambio docente, classe, ecc.)",
  "Peggioramento del quadro clinico",
];
const TRAIETTORIA_LABELS = {
  progressivo:       "Miglioramento progressivo nel corso dell'anno",
  stabile:           "Andamento stabile senza variazioni significative",
  buon_poi_difficile:"Buon avvio nel primo periodo, difficoltà nel secondo",
  difficile_poi_bene:"Difficoltà iniziali, poi miglioramento nel secondo periodo",
  peggioramento:     "Peggioramento progressivo",
};
const RACCOMANDAZIONE_LABELS = {
  confermare:    "Confermare gli obiettivi attuali — il percorso è efficace e va proseguito",
  ampliare:      "Ampliare gli obiettivi — i progressi consentono traguardi più ambiziosi",
  ridurre:       "Ridurre e semplificare gli obiettivi — calibrare meglio sul profilo reale",
  riprogrammare: "Riprogrammare in modo significativo — molti obiettivi non sono stati raggiunti",
  monitorare:    "Prevedere una verifica intermedia anticipata — per monitorare la transizione",
};

function buildQuestionarioContext(dati, prefix, inheritPrefix = null) {
  const get = k => dati[`${prefix}${k}`] || (inheritPrefix ? dati[`${inheritPrefix}${k}`] || "" : "");
  const giudizio = get("giudizio");
  if (!giudizio) return null;

  const parti = [];
  const giudizioLabel = { positivo: "POSITIVO", misto: "MISTO (risultati parziali)", critico: "CRITICO" }[giudizio] || giudizio.toUpperCase();
  let riga1 = `Valutazione globale: ${giudizioLabel}`;
  const criticita = get("criticita");
  if ((giudizio === "misto" || giudizio === "critico") && criticita) {
    const cLabel = {
      regressione:  "Regressione (competenze precedentemente acquisite si sono ridotte)",
      stagnazione:  "Stagnazione (nessun progresso significativo durante l'anno)",
      sovrastimati: "Obiettivi sovrastimati (troppo ambiziosi per il profilo attuale)",
      esterni:      "Fattori esterni hanno ostacolato il percorso",
    }[criticita] || criticita;
    riga1 += `\nTipo di criticità: ${cLabel}`;
  }
  parti.push(riga1);

  const dimRisultati = DIMENSIONI.map(d => {
    const v = get(`dim_${d.id}`);
    if (!v) return null;
    const lbl = { raggiunto: "Raggiunto ✓", parzialmente: "Parzialmente raggiunto", non_raggiunto: "Non raggiunto ✗" }[v] || v;
    return `  Dim.${d.id} (${d.label.split(" / ")[0]}): ${lbl}`;
  }).filter(Boolean);
  if (dimRisultati.length > 0) parti.push(`Raggiungimento obiettivi ICF:\n${dimRisultati.join("\n")}`);

  const traiettoria = get("traiettoria");
  if (traiettoria) parti.push(`Traiettoria dell'anno: ${TRAIETTORIA_LABELS[traiettoria] || traiettoria}`);

  const fattoriStr = get("fattori");
  if (fattoriStr) {
    const fattori = fattoriStr.split(";").filter(Boolean);
    if (fattori.length > 0) parti.push(`Fattori rilevanti segnalati:\n${fattori.map(f => `  - ${f}`).join("\n")}`);
  }

  const racc = get("raccomandazione");
  if (racc) parti.push(`Raccomandazione per l'anno successivo: ${RACCOMANDAZIONE_LABELS[racc] || racc}`);

  return `INFORMAZIONI SULL'ANDAMENTO (fornite dall'insegnante — guida principale per tono e contenuto del testo generato):\n${parti.join("\n\n")}`;
}

function SezQuadroInfo({ dati, setD, studente, onChat, contestoGlobale }) {
  const sl = "Quadro Informativo";
  const f = k => dati[k] || "";
  const s = (k,v) => setD(k,v);
  const [domande, setDomande] = useState([]);

  const domandeStandard = useMemo(() => {
    const ordine = studente?.ordine || "primaria";
    const eta = studente?.eta || "";
    if (ordine === "infanzia") {
      return [
        "Come si relaziona il/la bambino/a con i compagni e con gli adulti?",
        "Quali sono i suoi giochi e attività preferiti?",
        "Ci sono abitudini particolari a casa (sonno, alimentazione, autonomie)?",
        "Come reagisce a cambiamenti o frustrazioni?"
      ];
    } else if (ordine === "primaria") {
      return [
        "Come affronta i compiti a casa? È autonomo/a o necessita di supporto?",
        "Quali sono le sue materie preferite e quelle in cui incontra maggiori difficoltà?",
        "Come si relaziona con i compagni di classe e con gli insegnanti?",
        "Ci sono attività extrascolastiche che frequenta con piacere?"
      ];
    } else {
      return [
        "Qual è il suo metodo di studio? Preferisce studiare da solo/a o in gruppo?",
        "Come gestisce lo stress legato alle verifiche e alle interrogazioni?",
        "Ha amicizie solide nel contesto scolastico?",
        "Quali sono i suoi interessi e passioni al di fuori della scuola?",
        "Come vive il rapporto con i docenti e con le regole scolastiche?"
      ];
    }
  }, [studente?.ordine, studente?.eta]);

  const mostraDomande = () => setDomande(domandeStandard);

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
        <SezTitle icon="👨‍👩‍👧">1. Quadro Informativo</SezTitle>
        <Btn ghost small onClick={onChat} icon="💬">Assistente</Btn>
      </div>
      <InfoBox color={C.teal} icon="💡">
        Descrivi il contesto familiare e le informazioni raccolte dal colloquio. Puoi visualizzare delle domande guida.
      </InfoBox>
      <div style={{ marginBottom:12 }}>
        <button onClick={mostraDomande} style={{
          display:"flex", alignItems:"center", justifyContent:"center", gap:10,
          width:"100%", padding:"12px 18px",
          background:hex(C.accent,0.08),
          border:`2px solid ${hex(C.accent,0.35)}`,
          borderRadius:10, cursor:"pointer",
          fontSize:14, fontWeight:700, color:C.accent,
          boxShadow:`0 2px 8px ${hex(C.accent,0.12)}`,
          transition:"all .18s"
        }}
          onMouseEnter={e=>{e.currentTarget.style.background=hex(C.accent,0.15);e.currentTarget.style.boxShadow=`0 4px 14px ${hex(C.accent,0.22)}`;}}
          onMouseLeave={e=>{e.currentTarget.style.background=hex(C.accent,0.08);e.currentTarget.style.boxShadow=`0 2px 8px ${hex(C.accent,0.12)}`;}}
        >
          <span style={{fontSize:18}}>📋</span>
          Mostra domande guida
          <span style={{fontSize:12, opacity:.65, marginLeft:2}}>→</span>
        </button>
        {domande.length > 0 && (
          <div style={{ marginTop:8, padding:"10px 14px", background:hex(C.accent,0.05), borderRadius:8 }}>
            <div style={{ fontSize:12, fontWeight:600, marginBottom:6 }}>Domande suggerite per {ORDINI[studente?.ordine]?.label}:</div>
            <ul style={{ marginLeft:20, fontSize:13, color:C.textSec }}>
              {domande.map((q,i) => <li key={i}>{q}</li>)}
            </ul>
            <Btn ghost small onClick={() => setDomande([])} style={{ marginTop:8 }}>Nascondi</Btn>
          </div>
        )}
      </div>
      <FieldAI label="Situazione familiare e colloquio con la famiglia"
        sub="Nucleo familiare, contesto socio-ambientale, aspettative e osservazioni dei genitori"
        campoId="s1_famiglia" value={f("s1_famiglia")} onChange={e => s("s1_famiglia", e.target.value)}
        placeholder="Descrivi la situazione familiare, il contesto di vita, le informazioni raccolte durante il colloquio con la famiglia..."
        rows={5} studente={studente} sezioneLabel={sl} contestoGlobale={contestoGlobale} />
      <FieldAI label="Attività extrascolastiche attive"
        campoId="s1_extra" value={f("s1_extra")} onChange={e => s("s1_extra", e.target.value)}
        placeholder="Terapie, sport, attività riabilitative…" rows={2} studente={studente} sezioneLabel={sl} contestoGlobale={contestoGlobale} />
    </div>
  );
}

function SezPF({ dati, setD, studente, onChat, contestoGlobale }) {
  const sl = "Profilo di Funzionamento";
  const f = k => dati[k] || "";
  const s = (k, v) => setD(k, v);

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
        <div style={{ display:"flex", alignItems:"center", gap:0 }}>
          <SezTitle icon="📋">2. Profilo di Funzionamento</SezTitle>
          <GlossarioBtn voce="Profilo di Funzionamento" />
        </div>
        <Btn ghost small onClick={onChat} icon="💬">Assistente</Btn>
      </div>
      <InfoBox color={C.accent} icon="📌">Basato sul modello ICF–OMS. Descrivi capacità, attività e partecipazione. <strong>Parti sempre dai punti di forza.</strong></InfoBox>
      {UPLOAD_DOCUMENTI_ATTIVO && (
        <InfoBox color={C.teal} icon="📁">
          Hai documenti clinici o il Profilo di Funzionamento ufficiale? Caricali nella sezione <strong>Documenti</strong> dalla barra laterale — l'AI li userà automaticamente per arricchire i suggerimenti.
        </InfoBox>
      )}

      <FieldAI label="Profilo di Funzionamento complessivo (ICF–OMS)"
        sub="Descrivi in modo unitario: funzioni, attività, partecipazione e fattori contestuali"
        campoId="pf_descrizione" value={f("pf_descrizione")}
        onChange={e => s("pf_descrizione", e.target.value)}
        placeholder="Descrivi il profilo complessivo dello studente secondo il modello ICF..."
        rows={8} studente={studente} sezioneLabel={sl} contestoGlobale={contestoGlobale} />
    </div>
  );
}

function SezPI({ dati, setD, studente, onChat, contestoGlobale }) {
  const sl = "Progetto Individuale";
  const f = k => dati[k] || "";
  const s = (k,v) => setD(k,v);
  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
        <div style={{ display:"flex", alignItems:"center", gap:0 }}>
          <SezTitle icon="🔗">3. Raccordo con Progetto Individuale</SezTitle>
          <GlossarioBtn voce="Raccordo con Progetto Individuale" />
        </div>
        <Btn ghost small onClick={onChat} icon="💬">Assistente</Btn>
      </div>
      <InfoBox color={C.muted} icon="📄">Progetto di Vita Individuale (D.Lgs 62/2024). Compilare se disponibile.</InfoBox>
      <Field label="Ente titolare del Progetto Individuale">
        <Input value={f("pi_ente")} onChange={e => s("pi_ente", e.target.value)} placeholder="Nome ente responsabile…" />
      </Field>
      <FieldAI label="Sintesi del Progetto Individuale" campoId="pi_sintesi" value={f("pi_sintesi")}
        onChange={e => s("pi_sintesi", e.target.value)} placeholder="Obiettivi di vita, aree di intervento…"
        rows={4} studente={studente} sezioneLabel={sl} contestoGlobale={contestoGlobale} />
      <FieldAI label="Raccordo con interventi extrascolastici" campoId="pi_raccordo" value={f("pi_raccordo")}
        onChange={e => s("pi_raccordo", e.target.value)} placeholder="Come si integrano PEI e interventi territoriali…"
        rows={3} studente={studente} sezioneLabel={sl} contestoGlobale={contestoGlobale} />
    </div>
  );
}

function SezOsservazioni({ dati, setD, studente, onChat, contestoGlobale }) {
  const sl = "Osservazioni sull'alunno/a";
  const f = k => dati[k] || "";
  const s = (k,v) => setD(k,v);
  const { plan } = useCreditsCtx();
  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
        <div style={{ display:"flex", alignItems:"center", gap:0 }}>
          <SezTitle icon="👁">4. Osservazioni sull'alunno/a</SezTitle>
          <GlossarioBtn voce="Osservazioni sull'alunno/a" />
        </div>
        <Btn ghost small onClick={onChat} icon="💬">Assistente</Btn>
      </div>
      <InfoBox color={C.green} icon="✅">
        Descrivi le <strong>capacità e la partecipazione</strong> dello studente in ciascuna dimensione ICF.
        Usa: "sa fare…", "è in grado di…", "partecipa quando…", "mostra interesse per…"
      </InfoBox>
      {DIMENSIONI.map(d => {
        const dimBloccata = isDimensioneBloccataTrial(d.id, plan);
        return (
          <div key={d.id} style={{ marginBottom:16, padding:14,
            background: hex(d.color, 0.04), border:`1px solid ${hex(d.color,0.15)}`,
            borderRadius:10, borderLeft:`3px solid ${d.color}`,
            opacity: dimBloccata ? 0.65 : 1 }}>
            <div style={{ fontSize:13, fontWeight:700, color:d.color, marginBottom:10,
              display:"flex", alignItems:"center", gap:7 }}>
              <div style={{ width:26,height:26,borderRadius:"50%",
                background:hex(d.color,0.15), display:"flex",
                alignItems:"center", justifyContent:"center",
                fontSize:11, fontWeight:800 }}>{d.id}</div>
              <span style={{ flex: 1 }}>{d.label}</span>
              {dimBloccata && (
                <span title="Disponibile nel piano completo"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    padding: "2px 8px", borderRadius: R.pill,
                    background: hex(C.muted, 0.10),
                    border: `1px solid ${hex(C.muted, 0.20)}`,
                    fontSize: 10.5, fontWeight: 700, color: C.muted,
                    textTransform: "uppercase", letterSpacing: 0.4
                  }}>🔒 Premium</span>
              )}
            </div>
            <TrialGateContext.Provider value={{ aiBloccata: dimBloccata }}>
              <fieldset disabled={dimBloccata}
                style={{ border: "none", padding: 0, margin: 0, minInlineSize: "auto" }}>
                <FieldAI label={`Osservazioni — Dimensione ${d.id}`}
                  campoId={`s4_${d.id}_obs`}
                  value={f(`s4_${d.id}_obs`)}
                  onChange={e => s(`s4_${d.id}_obs`, e.target.value)}
                  placeholder={`Descrivi le capacità e la partecipazione dello studente nella dimensione ${d.id}: cosa sa fare, come interagisce, come si esprime, cosa lo motiva…`}
                  rows={4} studente={studente} sezioneLabel={sl} contestoGlobale={contestoGlobale} />
              </fieldset>
            </TrialGateContext.Provider>
          </div>
        );
      })}
      <Divider />
      <FieldAI
        label="Revisione a seguito di Verifica intermedia"
        sub="Modelli ufficiali A1/A2/A3/A4 D.M. 182/2020. Da compilare al GLO intermedio (gen-feb): aggiornamento delle osservazioni sistematiche dopo i primi mesi dell'anno (cosa è emerso di nuovo, progressi o regressioni osservate, nuovi contesti)."
        campoId="s4_revisione_intermedia"
        value={f("s4_revisione_intermedia")}
        onChange={e => s("s4_revisione_intermedia", e.target.value)}
        placeholder="Nel corso dei primi mesi dell'a.s. si osserva che…"
        rows={5}
        maxTokens={700}
        studente={studente}
        sezioneLabel={sl}
        contestoGlobale={contestoGlobale}
        customPrompt={buildRevisioneIntermediaPrompt({
          sezN: 4,
          sezLabel: "Osservazioni sull'alunno/a",
          natura: "osservativo",
          contestoSpecifico: "Le osservazioni di partenza per dimensione ICF (Dim. A/B/C/D) sono già nel contesto del PEI. Aggiorna riportando SOLO ciò che è emerso di nuovo dopo i primi mesi dell'anno: nuovi comportamenti, progressi, regressioni, situazioni nuove (cambi di routine, cambi di gruppo classe, eventi sopravvenuti).",
          blocchi: [
            { titolo: "Nuove osservazioni emerse", descrizione: "(2-4 voci) comportamenti, abilità o difficoltà non presenti nelle osservazioni iniziali, distinte per dimensione ICF quando rilevante" },
            { titolo: "Conferme rispetto al quadro iniziale", descrizione: "(1-3 voci) elementi che si confermano stabili o si rafforzano" },
            { titolo: "Aspetti da rivalutare", descrizione: "(1-3 voci) elementi delle osservazioni iniziali che oggi vanno riletti o aggiornati" },
          ],
        })}
      />
    </div>
  );
}

// ─── HELPERS RIASSUNTO QUESTIONARIO VERIFICA ──────────────────────────────────
// Costruisce una stringa compatta che riassume le risposte del docente, da
// mostrare nella chip-trigger del drawer (es. "🟡 Misto · Stagnazione · 3 fattori").
function buildVerificaQuestionnaireSummary(dati, prefix, inheritPrefix = null) {
  const get = k => dati[`${prefix}${k}`] || (inheritPrefix ? dati[`${inheritPrefix}${k}`] || "" : "");
  const g = get("giudizio");
  if (!g) return null;
  const lblGiudizio = { positivo: "🟢 Positivo", misto: "🟡 Misto", critico: "🔴 Critico" }[g];
  const lblCriticita = {
    regressione: "Regressione", stagnazione: "Stagnazione",
    sovrastimati: "Obiettivi sovrastimati", esterni: "Fattori esterni",
  }[get("criticita")];
  const lblTraiettoria = {
    progressivo: "Crescita costante", stabile: "Stabile",
    buon_poi_difficile: "Buon avvio → diff.", difficile_poi_bene: "Diff. → migl.",
    peggioramento: "Peggioramento",
  }[get("traiettoria")];
  const lblRacc = {
    confermare: "Confermare", ampliare: "Ampliare", ridurre: "Ridurre",
    riprogrammare: "Riprogrammare", monitorare: "Verifica anticipata",
  }[get("raccomandazione")];
  const nFattori = get("fattori") ? get("fattori").split(";").filter(Boolean).length : 0;
  const parts = [lblGiudizio];
  if (lblCriticita) parts.push(lblCriticita);
  if (lblTraiettoria) parts.push(lblTraiettoria);
  if (nFattori > 0) parts.push(`${nFattori} fattor${nFattori === 1 ? "e" : "i"}`);
  if (lblRacc) parts.push(lblRacc);
  return parts.join(" · ");
}

// Restituisce la palette per la chip in base al giudizio globale del docente.
function getVerificaQuestionnaireColor(dati, prefix, inheritPrefix = null) {
  const giudizio = dati[`${prefix}giudizio`] || (inheritPrefix ? dati[`${inheritPrefix}giudizio`] || "" : "");
  if (giudizio === "positivo") return { bg: C.greenDim, border: C.green, text: C.green };
  if (giudizio === "critico")  return { bg: C.redDim,   border: C.red,   text: C.red   };
  if (giudizio === "misto")    return { bg: C.orangeDim,border: C.orange,text: C.orange };
  return null;
}

// ─── TRIGGER + DRAWER PER IL QUESTIONARIO VERIFICA ───────────────────────────
// Sostituisce la card inline che si "fondeva" col campo verifica sotto.
// Renderizza una chip riassuntiva colorata (verde/ambra/rosa secondo giudizio)
// che apre un drawer laterale contenente il VerificaQuestionnaire esistente.
function VerificaQuestionnaireTrigger({ dati, setD, prefix, tipo = "conclusiva", dimensioniAttive = [], inheritPrefix = null, sezioneLabel = "Revisione conclusiva" }) {
  const [open, setOpen] = useState(false);
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.innerWidth < 600);
  const giudizio = dati[`${prefix}giudizio`] || (inheritPrefix ? dati[`${inheritPrefix}giudizio`] || "" : "");
  const compiled = !!giudizio;
  const summary = buildVerificaQuestionnaireSummary(dati, prefix, inheritPrefix);
  const palette = getVerificaQuestionnaireColor(dati, prefix, inheritPrefix);
  const chipBg     = palette ? hex(palette.bg, 1)        : hex(C.accent, 0.04);
  const chipBorder = palette ? hex(palette.border, 0.35) : hex(C.accent, 0.15);
  const chipText   = palette ? palette.text              : C.textSec;

  // Annullamento vero: i campi del questionario si salvano in tempo reale,
  // quindi all'apertura memorizziamo uno snapshot dei valori e lo ripristiniamo
  // se l'utente preme "Annulla". Ogni altra chiusura mantiene le modifiche.
  const snapshotRef = useRef(null);
  const VQ_KEYS = ["giudizio", "criticita", "traiettoria", "fattori", "raccomandazione", "dim_A", "dim_B", "dim_C", "dim_D"];
  const openDrawer = () => {
    const snap = {};
    VQ_KEYS.forEach(k => { snap[k] = dati[`${prefix}${k}`]; });
    snapshotRef.current = snap;
    setOpen(true);
  };
  const cancelDrawer = () => {
    const snap = snapshotRef.current;
    if (snap) {
      VQ_KEYS.forEach(k => { setD(`${prefix}${k}`, snap[k] !== undefined ? snap[k] : ""); });
    }
    setOpen(false);
  };

  // Esc per chiudere (mantiene le modifiche) + listener resize per padding responsive
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onResize = () => setNarrow(window.innerWidth < 600);
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    onResize();
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  const pad       = narrow ? "14px 16px" : "18px 22px";
  const padFooter = narrow ? "12px 16px" : "14px 22px";

  return (
    <>
      {/* Chip-trigger */}
      <div style={{
        display:"flex", alignItems:"center", gap:10,
        padding:"8px 12px",
        background: chipBg,
        border: `1px solid ${chipBorder}`,
        borderRadius: 10, marginBottom: 10,
      }}>
        <span style={{ fontSize:15, lineHeight:1 }} aria-hidden="true">🧭</span>
        <div style={{ flex:1, fontSize:12.5, lineHeight:1.4, color: chipText, minWidth:0 }}>
          <span style={{ fontWeight:600 }}>Guida IA</span>
          {compiled ? (
            <> · <span>{summary}</span></>
          ) : (
            <span style={{ color:C.muted, fontWeight:400 }}>
              {" · non compilato — le risposte aiutano l'IA a personalizzare il testo"}
            </span>
          )}
        </div>
        <button onClick={openDrawer} style={{
          fontSize:11.5, padding:"4px 12px",
          background: C.surface,
          border: `1px solid ${compiled ? hex(palette.border, 0.5) : C.borderMed}`,
          borderRadius: 6,
          color: compiled ? chipText : C.text,
          cursor:"pointer", fontWeight:500, whiteSpace:"nowrap",
          transition:"all 0.12s",
        }}>
          {compiled ? "Modifica" : "Compila"}
        </button>
      </div>

      {/* Backdrop + Drawer — montati in portal su document.body così il
          position:fixed si ancora al viewport e non a un contenitore .fadeUp
          (che, avendo una transform, farebbe diventare il fixed relativo a sé) */}
      {open && createPortal(
        <>
          <div onClick={() => setOpen(false)} style={{
            position:"fixed", inset:0,
            background:"rgba(15,12,30,0.42)",
            backdropFilter:"blur(4px)",
            WebkitBackdropFilter:"blur(4px)",
            zIndex:1100,
          }} />
          <div className="slideIn" role="dialog" aria-modal="true" aria-label={`Guida l'IA — ${sezioneLabel}`} style={{
            position:"fixed", right:0, top:0, bottom:0,
            width:"clamp(400px, 40vw, 520px)", maxWidth:"95vw",
            background: C.surface,
            borderRadius:"16px 0 0 16px",
            boxShadow:"-12px 0 36px rgba(28,25,23,0.10), -2px 0 8px rgba(28,25,23,0.04)",
            display:"flex", flexDirection:"column", zIndex:1101,
          }}>
            {/* Header drawer */}
            <div style={{
              padding: pad, borderBottom:`1px solid ${hex(C.border, 0.8)}`,
              background: grad(hex(C.accent,0.08), hex(C.accent2,0.04)),
              borderRadius:"16px 0 0 0",
              display:"flex", alignItems:"center", justifyContent:"space-between",
              flexShrink: 0,
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:12, minWidth:0 }}>
                <div style={{
                  width:38, height:38, borderRadius:12,
                  background: grad(C.accent, C.accent2),
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:18, color:"#fff",
                  boxShadow:"0 4px 12px rgba(99,102,241,0.25)",
                  flexShrink:0,
                }}>🧭</div>
                <div style={{ minWidth:0 }}>
                  <div style={{ fontSize:15, fontWeight:700, color:C.text, letterSpacing:"-0.01em" }}>Guida l'IA</div>
                  <div style={{ fontSize:12, color:C.muted, marginTop:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{sezioneLabel}</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} style={{
                width:30, height:30, borderRadius:8,
                background:"none", border:"none", color:C.muted,
                fontSize:16, cursor:"pointer", display:"flex",
                alignItems:"center", justifyContent:"center",
                flexShrink:0,
              }} aria-label="Chiudi">✕</button>
            </div>
            {/* Body */}
            <div style={{ flex:1, minHeight:0, overflowY:"auto", padding: pad }}>
              <div style={{
                display:"flex", gap:10, marginBottom:18,
                padding:"10px 12px",
                background: hex(C.accent, 0.04),
                borderLeft:`3px solid ${C.accent}`,
                borderRadius:"0 8px 8px 0",
              }}>
                <span style={{ fontSize:13, color:C.accent, lineHeight:1.4 }} aria-hidden="true">ℹ</span>
                <div style={{ fontSize:12, color:C.textSec, lineHeight:1.5 }}>
                  Le risposte qui sotto guidano <strong>tono e contenuti</strong> del testo che l'IA genererà nel campo di verifica. Nessun campo è obbligatorio.
                </div>
              </div>
              <VerificaQuestionnaire
                dati={dati}
                setD={setD}
                prefix={prefix}
                tipo={tipo}
                dimensioniAttive={dimensioniAttive}
                inheritPrefix={inheritPrefix}
                embedded
              />
            </div>
            {/* Footer */}
            <div style={{
              padding: padFooter, borderTop:`1px solid ${hex(C.border, 0.8)}`,
              background: C.cardHov,
              borderRadius:"0 0 0 16px",
              display:"flex", alignItems:"center", justifyContent:"space-between", gap:8,
              flexShrink: 0,
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:11.5, color:C.muted }}>
                <span style={{ color:C.green, fontSize:13, lineHeight:1 }} aria-hidden="true">✓</span>
                Salvato automaticamente
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={cancelDrawer} style={{
                  padding:"9px 16px",
                  background:"transparent",
                  border:`1px solid ${C.borderMed}`,
                  borderRadius:10,
                  fontSize:12.5, color:C.textSec, cursor:"pointer", fontWeight:500,
                }}>Annulla</button>
                <button onClick={() => setOpen(false)} style={{
                  padding:"9px 18px",
                  background: grad(C.accent, C.accent2),
                  border:"none", borderRadius:10,
                  fontSize:12.5, color:"#fff", cursor:"pointer", fontWeight:600,
                  boxShadow:"0 4px 12px rgba(99,102,241,0.25)",
                  display:"flex", alignItems:"center", gap:6,
                }}>
                  <span style={{ fontSize:13, lineHeight:1 }} aria-hidden="true">✓</span>
                  Salva e chiudi
                </button>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}

function VerificaQuestionnaire({ dati, setD, prefix, tipo = "conclusiva", dimensioniAttive = [], inheritPrefix = null, embedded = false }) {
  const get = k => dati[`${prefix}${k}`] || (inheritPrefix ? dati[`${inheritPrefix}${k}`] || "" : "");
  const set = (k, v) => setD(`${prefix}${k}`, v);
  const giudizio = get("giudizio");
  const withDim = dimensioniAttive.length > 0;

  // Expanded = true se giudizio già compilato (utente che torna), oppure dopo la prima selezione
  const [expanded, setExpanded] = useState(() => !!giudizio);

  const fattoriSet = new Set(get("fattori") ? get("fattori").split(";").filter(Boolean) : []);
  const toggleFattore = (fat) => {
    const ns = new Set(fattoriSet);
    if (ns.has(fat)) ns.delete(fat); else ns.add(fat);
    set("fattori", Array.from(ns).join(";"));
  };

  const RadioPill = ({ name, options, value, onChange }) => (
    <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
      {options.map(opt => (
        <label key={opt.v} style={{
          display:"flex", alignItems:"center",
          padding:"4px 12px", borderRadius:20,
          border:`1.5px solid ${value === opt.v ? C.accent : C.borderMed}`,
          background: value === opt.v ? hex(C.accent, 0.09) : C.surface,
          cursor:"pointer", fontSize:12.5,
          fontWeight: value === opt.v ? 600 : 400,
          color: value === opt.v ? C.accent : C.text,
          userSelect:"none", transition:"all 0.12s",
        }}>
          <input type="radio" name={name} value={opt.v} checked={value === opt.v}
            onChange={() => onChange(opt.v)} style={{ display:"none" }} />
          {opt.label}
        </label>
      ))}
    </div>
  );

  const CheckPill = ({ fat, active, color }) => (
    <label style={{
      display:"flex", alignItems:"center",
      padding:"3px 10px", borderRadius:16,
      border:`1.5px solid ${active ? color : C.borderMed}`,
      background: active ? hex(color, 0.09) : C.surface,
      cursor:"pointer", fontSize:12,
      color: active ? color : C.text,
      userSelect:"none", transition:"all 0.12s",
    }}>
      <input type="checkbox" checked={active} onChange={() => toggleFattore(fat)} style={{ display:"none" }} />
      {fat}
    </label>
  );

  const Q = ({ label, children }) => (
    <div style={{ marginBottom: embedded ? 18 : 12 }}>
      <div style={{
        fontSize:11, fontWeight:600, color:C.muted, marginBottom:8,
        letterSpacing:"0.04em", textTransform:"uppercase",
      }}>
        {label}
      </div>
      {children}
    </div>
  );

  return (
    <div style={embedded ? {
      // dentro al drawer: niente box wrapper, il drawer fa già da contenitore
      marginBottom: 0,
    } : {
      background: hex(C.accent, 0.03),
      border: `1px solid ${C.border}`,
      borderRadius: 10, padding: "12px 16px", marginBottom: 10,
    }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom: embedded ? 14 : 10 }}>
        <span style={{ fontSize:14 }} aria-hidden="true">📋</span>
        <span style={{ fontSize:12.5, fontWeight:600, color:C.text }}>
          Com'è andato l'anno? L'IA usa queste risposte per personalizzare la verifica.
        </span>
        {giudizio && (
          <span style={{ fontSize:11, color:C.green, marginLeft:"auto", fontWeight:600 }}>✓ Compilato</span>
        )}
      </div>

      {/* Q1 — Valutazione globale — sempre visibile */}
      <Q label="Valutazione globale">
        <RadioPill
          name={`${prefix}giudizio`}
          value={giudizio}
          onChange={v => { set("giudizio", v); setExpanded(true); }}
          options={[
            { v:"positivo", label:"🟢 Positivo" },
            { v:"misto",    label:"🟡 Misto" },
            { v:"critico",  label:"🔴 Critico" },
          ]}
        />
      </Q>

      {/* Resto delle domande — appare dopo la prima selezione */}
      {expanded && (
        <div>
          {/* Q1b — Tipo di criticità (condizionale) */}
          {(giudizio === "misto" || giudizio === "critico") && (
            <div style={{ marginBottom:12, marginLeft:14, paddingLeft:12,
              borderLeft:`3px solid ${giudizio === "critico" ? C.red : C.orange}` }}>
              <Q label="Tipo di criticità principale">
                <RadioPill name={`${prefix}criticita`} value={get("criticita")} onChange={v => set("criticita", v)}
                  options={[
                    { v:"regressione",  label:"Regressione" },
                    { v:"stagnazione",  label:"Stagnazione" },
                    { v:"sovrastimati", label:"Obiettivi sovrastimati" },
                    { v:"esterni",      label:"Fattori esterni" },
                  ]} />
              </Q>
            </div>
          )}

          {/* Q2 — Per dimensione ICF */}
          {withDim && (
            <Q label="Raggiungimento obiettivi per dimensione">
              {dimensioniAttive.map(dimId => {
                const dim = DIMENSIONI.find(d => d.id === dimId);
                const val = get(`dim_${dimId}`);
                return (
                  <div key={dimId} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6, flexWrap:"wrap" }}>
                    <div style={{
                      fontSize:12, fontWeight:700, color:dim.color,
                      minWidth:100, padding:"2px 8px",
                      background:hex(dim.color,0.09), borderRadius:6, whiteSpace:"nowrap",
                    }}>Dim.{dimId}</div>
                    <RadioPill name={`${prefix}dim_${dimId}`} value={val} onChange={v => set(`dim_${dimId}`, v)}
                      options={[
                        { v:"raggiunto",     label:"Raggiunto" },
                        { v:"parzialmente",  label:"Parzialmente" },
                        { v:"non_raggiunto", label:"Non raggiunto" },
                      ]} />
                  </div>
                );
              })}
            </Q>
          )}

          {/* Q3 — Traiettoria */}
          <Q label="Traiettoria dell'anno">
            <RadioPill name={`${prefix}traiettoria`} value={get("traiettoria")} onChange={v => set("traiettoria", v)}
              options={[
                { v:"progressivo",        label:"Crescita costante" },
                { v:"stabile",            label:"Stabile" },
                { v:"buon_poi_difficile", label:"Buon avvio → difficoltà" },
                { v:"difficile_poi_bene", label:"Difficoltà → miglioramento" },
                { v:"peggioramento",      label:"Peggioramento" },
              ]} />
          </Q>

          {/* Q4 — Fattori rilevanti */}
          <Q label="Fattori rilevanti (seleziona tutti quelli applicabili)">
            <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontStyle:"italic" }}>Positivi</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:5, marginBottom:8 }}>
              {FATTORI_POSITIVI.map(fat => (
                <CheckPill key={fat} fat={fat} active={fattoriSet.has(fat)} color={C.teal} />
              ))}
            </div>
            <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontStyle:"italic" }}>Critici</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
              {FATTORI_CRITICI.map(fat => (
                <CheckPill key={fat} fat={fat} active={fattoriSet.has(fat)} color={C.orange} />
              ))}
            </div>
          </Q>

          {/* Q5 — Raccomandazione */}
          {tipo === "conclusiva" && (
            <Q label="Raccomandazione per l'anno successivo">
              <RadioPill name={`${prefix}raccomandazione`} value={get("raccomandazione")} onChange={v => set("raccomandazione", v)}
                options={[
                  { v:"confermare",    label:"Confermare gli obiettivi" },
                  { v:"ampliare",      label:"Ampliare" },
                  { v:"ridurre",       label:"Ridurre / semplificare" },
                  { v:"riprogrammare", label:"Riprogrammare significativamente" },
                  { v:"monitorare",    label:"Verifica anticipata" },
                ]} />
            </Q>
          )}

          <div style={{ fontSize:11, color:C.mutedLight, fontStyle:"italic", marginTop:4 }}>
            Le risposte guidano l'IA nel generare un testo coerente con l'andamento reale dello studente.
          </div>
        </div>
      )}
    </div>
  );
}

function SezObiettivi({ dati, setD, studente, onChat, contestoGlobale }) {
  const sl = "Obiettivi e Strategie";
  const f = k => dati[k] || "";
  const s = (k,v) => setD(k,v);
  const contestoPrec = contestoGlobale;

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
        <div style={{ display:"flex", alignItems:"center", gap:0 }}>
          <SezTitle icon="🎯">5. Obiettivi e Strategie</SezTitle>
          <GlossarioBtn voce="Obiettivi e Strategie" />
        </div>
        <Btn ghost small onClick={onChat} icon="💬">Assistente</Btn>
      </div>
      <InfoBox color={C.accent} icon="✦">
        L'AI genera obiettivi e strategie <strong>basandosi su tutte le informazioni inserite nelle sezioni precedenti</strong>. Più dati hai inserito, più precisi saranno i suggerimenti.
      </InfoBox>
      {contestoPrec && (
        <div style={{ marginBottom:14, padding:"8px 12px", background:C.greenDim,
          border:`1px solid ${hex(C.green,0.3)}`, borderRadius:8, fontSize:11, color:C.green }}>
          ✓ L'AI ha accesso alle informazioni delle sezioni 1–4 e al contesto scolastico
        </div>
      )}
      {DIMENSIONI.map(d => (
        <div key={d.id} style={{ marginBottom:16, border:`1px solid ${hex(d.color,0.2)}`, borderRadius:10, overflow:"hidden" }}>
          <div style={{ background:hex(d.color,0.08), padding:"9px 14px",
            display:"flex", alignItems:"center", gap:7,
            borderBottom:`1px solid ${hex(d.color,0.15)}` }}>
            <div style={{ width:22,height:22,borderRadius:"50%",background:hex(d.color,0.2),
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:10,fontWeight:800,color:d.color }}>{d.id}</div>
            <div style={{ fontSize:13,fontWeight:700,color:d.color }}>Dimensione {d.id} — {d.label}</div>
          </div>
          <div style={{ padding:14 }}>
            <FieldAI label="Obiettivi ed esiti attesi"
              sub={studente?.ordine === "infanzia"
                ? "Per ogni obiettivo educativo indica il corrispondente esito atteso (3-5 coppie). Alla scuola dell'infanzia gli obiettivi sono prevalentemente educativi (Linee Guida D.M. 182/2020) e declinano i traguardi per lo sviluppo della competenza delle Indicazioni Nazionali (D.M. 254/2012; dall'a.s. 2026/27 nuove Indicazioni Nazionali, D.M. 221/2025) sul singolo bambino/sulla singola bambina, in percorsi che attraversano i cinque campi di esperienza nei contesti di gioco, routine e laboratorio."
                : "Per ogni obiettivo SMART indica il corrispondente esito atteso (3-5 coppie)"}
              campoId={`s5_${d.id}_ob`} value={f(`s5_${d.id}_ob`)}
              onChange={e => s(`s5_${d.id}_ob`, e.target.value)}
              placeholder="OBIETTIVO 1: Sviluppare…&#10;→ Esito a: …&#10;→ Esito b: …&#10;&#10;OBIETTIVO 2: Migliorare…&#10;→ Esito a: …"
              rows={5} studente={studente} sezioneLabel={sl} contestoGlobale={contestoGlobale} />
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:4 }}>
              <FieldAI label="Attività" sub="3-5 attività finalizzate agli obiettivi (indica a quale)"
                campoId={`s5_${d.id}_attiv`} value={f(`s5_${d.id}_attiv`)}
                onChange={e => s(`s5_${d.id}_attiv`, e.target.value)}
                placeholder="• Attività 1 (finalizzata all'Obiettivo 1)&#10;• Attività 2…"
                rows={5} studente={studente} sezioneLabel={sl}
                contestoGlobale={contestoPrec}
                contestoObiettivi={f(`s5_${d.id}_ob`) || null} />
              <FieldAI label="Strategie e Strumenti" sub="2-5 strategie didattiche + 2-5 strumenti/ausili"
                campoId={`s5_${d.id}_strat`} value={f(`s5_${d.id}_strat`)}
                onChange={e => s(`s5_${d.id}_strat`, e.target.value)}
                placeholder="STRATEGIE:&#10;• Strategia 1&#10;• Strategia 2&#10;STRUMENTI:&#10;• Strumento 1&#10;• Strumento 2"
                rows={5} studente={studente} sezioneLabel={sl}
                contestoGlobale={contestoPrec}
                contestoObiettivi={f(`s5_${d.id}_ob`) || null} />
            </div>
            {(studente?.ordine === "secondaria1" || studente?.ordine === "secondaria2") && (
              <div style={{ marginTop:8 }}>
                <FieldAI
                  label="VERIFICA — Metodi, criteri e strumenti"
                  sub="Modelli ufficiali Allegati A3/A4 D.M. 182/2020: «metodi, criteri e strumenti utilizzati per verificare se gli obiettivi sono stati raggiunti». Campo PROSPETTICO — descrive come si verificheranno gli obiettivi (non un bilancio retrospettivo: per quello c'è la Revisione conclusiva)."
                  campoId={`s5_${d.id}_verifica_dim`}
                  value={f(`s5_${d.id}_verifica_dim`)}
                  onChange={e => s(`s5_${d.id}_verifica_dim`, e.target.value)}
                  placeholder="METODI:&#10;— …&#10;CRITERI:&#10;— …&#10;STRUMENTI:&#10;— …"
                  rows={5}
                  studente={studente}
                  sezioneLabel={sl}
                  contestoGlobale={contestoPrec}
                  contestoObiettivi={f(`s5_${d.id}_ob`) || null}
                  maxTokens={600}
                  customPrompt={`Sei un docente di sostegno che redige il campo VERIFICA della Dimensione ${d.id} (${d.label}) della Sezione 5 del PEI.

NORMATIVA: Allegati A3 e A4 D.M. 182/2020 (corretto da D.I. 153/2023). Nella tabella di ogni dimensione ICF, la colonna VERIFICA è descritta testualmente come "metodi, criteri e strumenti utilizzati per verificare se gli obiettivi sono stati raggiunti".

NATURA DEL CAMPO: PROSPETTICO. Si compila al PEI iniziale e descrive COME si verificheranno gli obiettivi nel corso dell'anno — NON è un bilancio retrospettivo (la valutazione retrospettiva è raccolta nella "Revisione conclusiva degli esiti" più sotto).

${f(`s5_${d.id}_ob`) ? `OBIETTIVI DI QUESTA DIMENSIONE (devono guidare metodi/criteri/strumenti):\n${f(`s5_${d.id}_ob`)}\n` : "(Obiettivi non ancora definiti per questa dimensione: proponi un piano di verifica coerente con la Dimensione ICF e con l'ordine scolastico, che andrà poi raffinato quando gli obiettivi saranno compilati.)"}

Produci il piano di verifica per la Dimensione ${d.id} nel formato OBBLIGATORIO a tre blocchi:

METODI:
— [metodo di verifica specifico, max 8 parole]
— [metodo di verifica specifico, max 8 parole]
(2-4 voci. Es. per Dim.${d.id === "A" ? "A: osservazioni sistematiche in classe, role play strutturati, circle time osservativi, prove di interazione di gruppo" : d.id === "B" ? "B: prove orali con scaffolding, prove scritte adattate, produzioni multimediali, conversazioni guidate, valutazione della CAA" : d.id === "C" ? "C: prove pratiche di autonomia, simulazioni di routine, compiti di realtà, osservazione in contesti diversi" : "D: prove scritte adattate, prove orali strutturate, compiti di realtà, project work, prove pratiche, verifiche graduate"})

CRITERI:
— [criterio agganciato a un obiettivo, max 8 parole]
— [criterio agganciato a un obiettivo, max 8 parole]
(2-4 voci. Es: correttezza dell'esecuzione, livello di autonomia, costanza nella partecipazione, transfer ad altri contesti, qualità delle interazioni, progressione rispetto al livello di partenza)

STRUMENTI:
— [strumento di rilevazione, max 6 parole]
— [strumento di rilevazione, max 6 parole]
(2-4 voci. Es: rubriche valutative ICF-coerenti, griglie di osservazione, checklist comportamentali, prove strutturate, registro osservazioni quotidiane, diario di bordo)

Regole TASSATIVE:
- Specifici per la Dimensione ${d.id} — NON mescolare dimensioni diverse
- Coerenti con gli obiettivi della dimensione, se presenti nel contesto
- Telegrafici: solo il nome del metodo/criterio/strumento, NESSUNA descrizione né spiegazione
- NON usare percentuali né "entro maggio" o date specifiche
- Tono operativo, neutro, prospettico
- NON usare grassetto, virgolette, intestazioni diverse da METODI/CRITERI/STRUMENTI
- NON scrivere "Dimensione ${d.id}:" o simili nel testo: è già chiaro dal contesto`}
                />
              </div>
            )}
          </div>
        </div>
      ))}

      <Divider />
      <FieldAI
        label="Revisione a seguito di Verifica intermedia"
        sub="Modelli ufficiali A1/A2/A3/A4 D.M. 182/2020. Da compilare al GLO intermedio (gen-feb): modifiche al piano di Sez. 5 a seguito della verifica intermedia, riferite a tutte le dimensioni ICF (D.I. 182/2020 art. 9)."
        campoId="s5_revisione_intermedia"
        value={f("s5_revisione_intermedia")}
        onChange={e => s("s5_revisione_intermedia", e.target.value)}
        placeholder="A seguito della verifica intermedia, gli obiettivi della Dim. … vengono confermati / riformulati; le strategie … sono sostituite da …"
        rows={5}
        maxTokens={700}
        studente={studente}
        sezioneLabel={sl}
        contestoGlobale={contestoPrec}
        customPrompt={buildRevisioneIntermediaPrompt({
          sezN: 5,
          sezLabel: "Obiettivi, strategie e strumenti",
          natura: "correttivo",
          contestoSpecifico: "Gli obiettivi, le attività, le strategie/strumenti e il piano di verifica per dimensione sono già nel contesto del PEI. Indica per quali Dimensioni ICF si confermano e per quali vanno modificati, con quali criteri.",
          blocchi: [
            { titolo: "Conferme al piano", descrizione: "(1-3 voci) obiettivi/strategie che si confermano efficaci e mantenuti per le dimensioni interessate" },
            { titolo: "Criticità emerse", descrizione: "(1-3 voci) obiettivi sovrastimati o sottostimati, strategie che non hanno funzionato, situazioni inattese" },
            { titolo: "Modifiche agli obiettivi", descrizione: "(1-3 voci) riformulazioni, ridimensionamenti o nuovi obiettivi proposti, distinti per Dimensione ICF quando rilevante" },
            { titolo: "Modifiche a strategie/strumenti", descrizione: "(1-3 voci) sostituzioni o integrazioni di metodologie, ausili, attività" },
          ],
        })}
      />
      <Divider />
      <div style={{ border:`1.5px solid ${hex(C.accent2,0.35)}`, background:hex(C.accent2,0.05),
        borderRadius:12, padding:16, marginTop:4 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12, flexWrap:"wrap" }}>
          <span style={{ fontSize:16, lineHeight:1 }} aria-hidden="true">🏁</span>
          <span style={{ fontSize:13.5, fontWeight:700, color:C.accent2 }}>Bilancio di fine anno — assistito dalla Guida IA</span>
          <span style={{ fontSize:11, color:C.muted, flexBasis:"100%" }}>Compila prima la Guida IA: le risposte personalizzano il testo generato.</span>
        </div>
        <VerificaQuestionnaireTrigger
          dati={dati}
          setD={setD}
          prefix="s5_vq_"
          tipo="conclusiva"
          dimensioniAttive={DIMENSIONI.filter(d => f(`s5_${d.id}_ob`)).map(d => d.id)}
          sezioneLabel="Revisione conclusiva — Sez. 5 Obiettivi"
        />
        <FieldAI
        label="Revisione conclusiva degli esiti"
        sub="Da compilare a fine anno. Verifica i risultati conseguiti per tutte le dimensioni e valuta l'efficacia di interventi, strategie e strumenti."
        campoId="s5_verifica_conclusiva"
        value={f("s5_verifica_conclusiva")}
        onChange={e => s("s5_verifica_conclusiva", e.target.value)}
        placeholder="Nel corso dell'anno scolastico, gli obiettivi definiti nelle quattro dimensioni sono stati raggiunti/parzialmente raggiunti/non raggiunti. In particolare…"
        rows={6}
        maxTokens={1600}
        studente={studente}
        sezioneLabel={sl}
        contestoGlobale={contestoPrec}
        customPrompt={(() => {
  const cvc = buildContestoVerificaCompleto(dati);
  const qCtx = buildQuestionarioContext(dati, "s5_vq_");
  return `Sei un docente di sostegno esperto che redige la revisione conclusiva degli esiti della Sezione 5 del PEI (D.M. 182/2020).
${cvc ? `\nCONTENUTO PROGRAMMATORIO DEL PEI (tutto ciò che è stato pianificato per questo studente):\n${cvc}\n` : ""}
${qCtx ? `\n${qCtx}\n` : ""}
ISTRUZIONE CRITICA — FORMATO OUTPUT:
La tua risposta deve essere ESCLUSIVAMENTE testo valutativo continuo. La prima parola che scrivi deve essere la prima parola del contenuto (es. "Nel corso…", "Lo studente…"). Non scrivere nulla prima di essa: niente titoli, niente intestazioni, niente righe vuote iniziali, niente simboli come # o *.
Separa i paragrafi con un singolo ritorno a capo (non due): niente righe vuote tra un paragrafo e l'altro.

PRIORITÀ DEL TESTO — Allegato B Linee Guida D.I. 182/2020 (testuale): "la valutazione è riferita PRIORITARIAMENTE all'efficacia degli interventi, NON SOLO al raggiungimento degli obiettivi". Struttura quindi il testo in tre parti narrative integrate:
1. EFFICACIA DEGLI INTERVENTI (priorità prima): valuta criticamente quali strategie didattiche, strumenti e interventi educativi del PEI si sono rivelati efficaci per ciascuna dimensione ICF compilata, e quali no — citando elementi specifici (nomi delle strategie, materiali, ausili) attinti dalle Sez. 5 e 7
2. RISULTATI CONSEGUITI: descrivi quali obiettivi e relativi esiti attesi sono stati raggiunti pienamente, parzialmente o non raggiunti, motivando il giudizio anche in relazione alle barriere e ai facilitatori (Sez. 6)
3. INDICAZIONI PER L'ANNO SUCCESSIVO: cosa confermare, modificare o aggiornare (coerente con la raccomandazione fornita dall'insegnante nel questionario)

Regole TASSATIVE:
- Il tono complessivo (positivo/misto/critico) deve rispecchiare OBBLIGATORIAMENTE la valutazione globale fornita dall'insegnante nel questionario
- L'efficacia degli interventi deve emergere come asse portante del testo, non come accenno marginale
- NON citare le dimensioni come etichette (vietato "Dimensione A", "Dimensione B" o simili) — fondi i contenuti in modo naturale
- Niente elenchi puntati, niente grassetto, niente titoli interni
- Tono valutativo, concreto e professionale, al passato
- Tra 150 e 350 parole (adatta la lunghezza al numero di obiettivi compilati)
- Cita obiettivi, strategie e strumenti specifici del PEI — NON scrivere valutazioni generiche`;
})()} />
      </div>
    </div>
  );
}

function SezBarriere({ dati, setD, studente, onChat, contestoGlobale }) {
  const sl = "Barriere e Facilitatori";
  const f = k => dati[k] || "";
  const s = (k, v) => setD(k, v);
  const [showAnalisi, setShowAnalisi] = useState(true);
  const [activeTab, setActiveTab] = useState('macro');

  const contestoStrutturato = useMemo(() => {
    const saved = dati.contesto_strutturato;
    if (saved) return saved;
    return {
      macro: {
        barriereArchitettoniche: [],
        spaziRisorse: [],
        organizzazione: [],
        note: ''
      },
      meso: {
        settingAula: [],
        clima: [],
        metodologie: [],
        note: ''
      },
      micro: {
        puntiForza: [],
        difficolta: [],
        facilitatoriPresenti: [],
        note: ''
      }
    };
  }, [dati.contesto_strutturato]);

  const updateContesto = (livello, campo, valore) => {
    const nuovo = { ...contestoStrutturato };
    if (Array.isArray(nuovo[livello][campo])) {
      if (nuovo[livello][campo].includes(valore)) {
        nuovo[livello][campo] = nuovo[livello][campo].filter(v => v !== valore);
      } else {
        nuovo[livello][campo] = [...nuovo[livello][campo], valore];
      }
    } else {
      nuovo[livello][campo] = valore;
    }
    s("contesto_strutturato", nuovo);
  };

  const generaPromptDaContesto = () => {
    const { macro, meso, micro } = contestoStrutturato;

    // Includi solo le voci effettivamente compilate dall'utente
    const righe = [];

    const macroVoci = [
      macro.barriereArchitettoniche.length && `- Barriere architettoniche: ${macro.barriereArchitettoniche.join(', ')}`,
      macro.spaziRisorse.length            && `- Spazi e risorse: ${macro.spaziRisorse.join(', ')}`,
      macro.organizzazione.length          && `- Organizzazione: ${macro.organizzazione.join(', ')}`,
      macro.note                           && `- Note: ${macro.note}`,
    ].filter(Boolean);
    if (macroVoci.length) righe.push(`MACRO (Istituto):\n${macroVoci.join('\n')}`);

    const mesoVoci = [
      meso.settingAula.length   && `- Setting aula: ${meso.settingAula.join(', ')}`,
      meso.clima.length         && `- Clima e dinamiche: ${meso.clima.join(', ')}`,
      meso.metodologie.length   && `- Metodologie utilizzate: ${meso.metodologie.join(', ')}`,
      meso.note                 && `- Note: ${meso.note}`,
    ].filter(Boolean);
    if (mesoVoci.length) righe.push(`MESO (Classe):\n${mesoVoci.join('\n')}`);

    const microVoci = [
      micro.puntiForza.length          && `- Punti di forza: ${micro.puntiForza.join(', ')}`,
      micro.difficolta.length          && `- Difficoltà individuali: ${micro.difficolta.join(', ')}`,
      micro.facilitatoriPresenti.length && `- Facilitatori già presenti: ${micro.facilitatoriPresenti.join(', ')}`,
      micro.note                        && `- Note: ${micro.note}`,
    ].filter(Boolean);
    if (microVoci.length) righe.push(`MICRO (Studente):\n${microVoci.join('\n')}`);

    if (!righe.length) return '';
    return `\nCONTESTO SCOLASTICO STRUTTURATO:\n\n${righe.join('\n\n')}
`;
  };

  const customPromptBarriere = `Genera SOLO barriere (ostacoli, difficoltà, impedimenti) presenti nel contesto scolastico. Non includere facilitatori o aspetti positivi.
Considera attentamente le informazioni sul contesto strutturato (Macro / Meso / Micro) fornite di seguito: esse devono guidare la scelta delle barriere da segnalare.

Organizza le barriere in tre livelli con questo formato OBBLIGATORIO:

MACRO — Istituto
(Barriere istituzionali: risorse umane e finanziarie della scuola, spazi fisici strutturali dell'edificio, politiche e organizzazione dell'istituto nel suo complesso — NON dinamiche di aula)
Risorse e organizzazione dell'istituto
— [voce]
Spazi e ambienti strutturali
— [voce]
Politiche e pratiche scolastiche
— [voce]

MESO — Classe
(Barriere a livello di aula e team docenti: setting fisico dell'aula, organizzazione delle attività, metodologie e dinamiche relazionali del gruppo classe)
Contesto fisico
— [voce]
Contesto organizzativo
— [voce]
Contesto didattico e relazionale
— [voce]

MICRO — Alunno/a
(Barriere individuali: caratteristiche dello studente che ostacolano il funzionamento e la partecipazione)
Barriere individuali
— [voce]

Ometti le sottocategorie prive di contenuto pertinente. Includi solo le voci realmente rilevanti per il profilo dello studente.

Regole TASSATIVE per ogni voce:
- Frase nominale breve senza verbo, 3–8 parole (es. "Mancanza di spazi laboratoriali", "Predilezione per la lezione frontale", "Tempi di attenzione ridotti")
- NON includere codici ICF né sigle tecniche
- NON aggiungere descrizioni, spiegazioni o commenti — solo la voce
- NON usare lo stesso testo per barriere e facilitatori
- VIETATO usare grassetto o asterischi (**testo**) — né per i titoli di livello, né per le sottocategorie, né per le voci
- VIETATO inserire separatori o linee orizzontali (---, ***, ===) tra i livelli`;
  const customPromptFacilitatori = `Genera SOLO facilitatori (risorse, condizioni favorevoli, punti di forza del contesto) presenti nel contesto scolastico. Non includere barriere.
Considera attentamente le informazioni sul contesto strutturato (Macro / Meso / Micro) fornite di seguito: esse devono guidare la scelta dei facilitatori da valorizzare.

Organizza i facilitatori in tre livelli con questo formato OBBLIGATORIO:

MACRO — Istituto
(Facilitatori istituzionali: risorse umane e finanziarie della scuola, spazi fisici strutturali dell'edificio, politiche e organizzazione dell'istituto nel suo complesso — NON dinamiche di aula)
Risorse e organizzazione dell'istituto
— [voce]
Spazi e ambienti strutturali
— [voce]
Politiche e pratiche scolastiche
— [voce]

MESO — Classe
(Facilitatori a livello di aula e team docenti: setting fisico dell'aula, organizzazione delle attività, metodologie e dinamiche relazionali del gruppo classe)
Contesto fisico
— [voce]
Contesto organizzativo
— [voce]
Contesto didattico e relazionale
— [voce]

MICRO — Alunno/a
(Facilitatori individuali: caratteristiche dello studente che supportano il funzionamento e la partecipazione)
Facilitatori individuali
— [voce]

Ometti le sottocategorie prive di contenuto pertinente. Includi solo le voci realmente rilevanti per il profilo dello studente.

Regole TASSATIVE per ogni voce:
- Frase nominale breve senza verbo, 3–8 parole (es. "Aule ampie e luminose", "Clima sereno tra colleghi", "Carattere socievole e curioso")
- NON includere codici ICF né sigle tecniche
- NON aggiungere descrizioni, spiegazioni o commenti — solo la voce
- I facilitatori devono essere speculari alle barriere dove possibile
- VIETATO usare grassetto o asterischi (**testo**) — né per i titoli di livello, né per le sottocategorie, né per le voci
- VIETATO inserire separatori o linee orizzontali (---, ***, ===) tra i livelli`;

  const contestoPrompt = generaPromptDaContesto();
  const promptBarriereCompleto = customPromptBarriere + (contestoPrompt ? '\n\n' + contestoPrompt : '');
  const promptFacilitatoriCompleto = customPromptFacilitatori + (contestoPrompt ? '\n\n' + contestoPrompt : '');

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
        <div style={{ display:"flex", alignItems:"center", gap:0 }}>
          <SezTitle icon="⚖️">6. Barriere e Facilitatori</SezTitle>
          <GlossarioBtn voce="Barriere e Facilitatori" />
        </div>
        <Btn ghost small onClick={onChat} icon="💬">Assistente</Btn>
      </div>

      <div style={{ marginBottom:16 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
          <Btn ghost small onClick={() => setShowAnalisi(!showAnalisi)} icon="🔍">
            {showAnalisi ? "Nascondi analisi guidata del contesto" : "Analisi guidata del contesto (Macro / Meso / Micro)"}
          </Btn>
          <Badge color={C.accent2} small>✦ consigliata</Badge>
        </div>
        {showAnalisi && (
          <Card style={{ marginTop:12, padding:16 }}>
            <div style={{ display:"flex", gap:2, marginBottom:16, borderBottom:`1px solid ${C.border}` }}>
              {['macro', 'meso', 'micro'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding:"8px 16px",
                    background: activeTab === tab ? C.accentDim : 'transparent',
                    border: 'none',
                    borderBottom: activeTab === tab ? `2px solid ${C.accent}` : '2px solid transparent',
                    color: activeTab === tab ? C.accent : C.muted,
                    fontWeight:600,
                    fontSize:13,
                    cursor:'pointer',
                    textTransform:'capitalize'
                  }}
                >{tab}</button>
              ))}
            </div>

            {activeTab === 'macro' && (
              <div>
                <h4 style={{ fontSize:14, fontWeight:600, marginBottom:12, color:C.text }}>Istituto / Plesso</h4>
                <div style={{ marginBottom:16 }}>
                  <div style={{ fontSize:12, fontWeight:600, marginBottom:6, color:C.muted }}>Barriere architettoniche</div>
                  {['Scale senza ascensore', 'Porte strette', 'Bagni non accessibili', 'Percorsi esterni dissestati', 'Assenza di rampe'].map(item => (
                    <label key={item} style={{ display:'block', marginBottom:4, fontSize:13, color:C.text }}>
                      <input type="checkbox" checked={contestoStrutturato.macro.barriereArchitettoniche.includes(item)} onChange={() => updateContesto('macro', 'barriereArchitettoniche', item)} /> {item}
                    </label>
                  ))}
                </div>
                <div style={{ marginBottom:16 }}>
                  <div style={{ fontSize:12, fontWeight:600, marginBottom:6, color:C.muted }}>Spazi e risorse</div>
                  {['Aule ampie', 'Laboratori informatici', 'Palestra', 'Cortile accessibile', 'Connessione Wi-Fi', 'LIM/PC in aula'].map(item => (
                    <label key={item} style={{ display:'block', marginBottom:4, fontSize:13, color:C.text }}>
                      <input type="checkbox" checked={contestoStrutturato.macro.spaziRisorse.includes(item)} onChange={() => updateContesto('macro', 'spaziRisorse', item)} /> {item}
                    </label>
                  ))}
                </div>
                <div style={{ marginBottom:16 }}>
                  <div style={{ fontSize:12, fontWeight:600, marginBottom:6, color:C.muted }}>Organizzazione</div>
                  {['Docenti di sostegno specializzati', 'Progetti inclusivi attivi', 'Collaborazione con ASL/enti', 'Formazione TIC per docenti'].map(item => (
                    <label key={item} style={{ display:'block', marginBottom:4, fontSize:13, color:C.text }}>
                      <input type="checkbox" checked={contestoStrutturato.macro.organizzazione.includes(item)} onChange={() => updateContesto('macro', 'organizzazione', item)} /> {item}
                    </label>
                  ))}
                </div>
                <Field label="Note aggiuntive">
                  <Textarea value={contestoStrutturato.macro.note} onChange={e => updateContesto('macro', 'note', e.target.value)} placeholder="Ulteriori dettagli..." rows={2} />
                </Field>
              </div>
            )}

            {activeTab === 'meso' && (
              <div>
                <h4 style={{ fontSize:14, fontWeight:600, marginBottom:12, color:C.text }}>Classe</h4>
                <div style={{ marginBottom:16 }}>
                  <div style={{ fontSize:12, fontWeight:600, marginBottom:6, color:C.muted }}>Setting d'aula</div>
                  {['Banchi in file fisse', 'Banchi modulabili', 'Postazione fissa per lo studente', 'Spazio insufficiente per muoversi'].map(item => (
                    <label key={item} style={{ display:'block', marginBottom:4, fontSize:13, color:C.text }}>
                      <input type="checkbox" checked={contestoStrutturato.meso.settingAula.includes(item)} onChange={() => updateContesto('meso', 'settingAula', item)} /> {item}
                    </label>
                  ))}
                </div>
                <div style={{ marginBottom:16 }}>
                  <div style={{ fontSize:12, fontWeight:600, marginBottom:6, color:C.muted }}>Clima e dinamiche</div>
                  {['Classe collaborativa', 'Presenza di conflitti tra pari', 'Livello di rumore elevato', 'Compagni inclusivi'].map(item => (
                    <label key={item} style={{ display:'block', marginBottom:4, fontSize:13, color:C.text }}>
                      <input type="checkbox" checked={contestoStrutturato.meso.clima.includes(item)} onChange={() => updateContesto('meso', 'clima', item)} /> {item}
                    </label>
                  ))}
                </div>
                <div style={{ marginBottom:16 }}>
                  <div style={{ fontSize:12, fontWeight:600, marginBottom:6, color:C.muted }}>Metodologie utilizzate</div>
                  {['Lezione frontale prevalente', 'Didattica laboratoriale', 'Apprendimento cooperativo', 'Peer tutoring'].map(item => (
                    <label key={item} style={{ display:'block', marginBottom:4, fontSize:13, color:C.text }}>
                      <input type="checkbox" checked={contestoStrutturato.meso.metodologie.includes(item)} onChange={() => updateContesto('meso', 'metodologie', item)} /> {item}
                    </label>
                  ))}
                </div>
                <Field label="Note aggiuntive">
                  <Textarea value={contestoStrutturato.meso.note} onChange={e => updateContesto('meso', 'note', e.target.value)} placeholder="Ulteriori dettagli..." rows={2} />
                </Field>
              </div>
            )}

            {activeTab === 'micro' && (
              <div>
                <h4 style={{ fontSize:14, fontWeight:600, marginBottom:12, color:C.text }}>Studente</h4>
                <div style={{ marginBottom:16 }}>
                  <div style={{ fontSize:12, fontWeight:600, marginBottom:6, color:C.muted }}>Punti di forza personali</div>
                  {['Motivazione', 'Curiosità', 'Buone capacità verbali', 'Autonomia di base', 'Competenze digitali'].map(item => (
                    <label key={item} style={{ display:'block', marginBottom:4, fontSize:13, color:C.text }}>
                      <input type="checkbox" checked={contestoStrutturato.micro.puntiForza.includes(item)} onChange={() => updateContesto('micro', 'puntiForza', item)} /> {item}
                    </label>
                  ))}
                </div>
                <div style={{ marginBottom:16 }}>
                  <div style={{ fontSize:12, fontWeight:600, marginBottom:6, color:C.muted }}>Difficoltà individuali</div>
                  {['Affaticabilità', 'Ansia da prestazione', 'Difficoltà attentive', 'Difficoltà motorie', 'Difficoltà di memoria'].map(item => (
                    <label key={item} style={{ display:'block', marginBottom:4, fontSize:13, color:C.text }}>
                      <input type="checkbox" checked={contestoStrutturato.micro.difficolta.includes(item)} onChange={() => updateContesto('micro', 'difficolta', item)} /> {item}
                    </label>
                  ))}
                </div>
                <div style={{ marginBottom:16 }}>
                  <div style={{ fontSize:12, fontWeight:600, marginBottom:6, color:C.muted }}>Facilitatori già presenti</div>
                  {['Supporto compagni', 'Uso di PC/tablet', 'Tempi aggiuntivi', 'Materiale semplificato', 'Rinforzo positivo'].map(item => (
                    <label key={item} style={{ display:'block', marginBottom:4, fontSize:13, color:C.text }}>
                      <input type="checkbox" checked={contestoStrutturato.micro.facilitatoriPresenti.includes(item)} onChange={() => updateContesto('micro', 'facilitatoriPresenti', item)} /> {item}
                    </label>
                  ))}
                </div>
                <Field label="Note aggiuntive">
                  <Textarea value={contestoStrutturato.micro.note} onChange={e => updateContesto('micro', 'note', e.target.value)} placeholder="Ulteriori dettagli..." rows={2} />
                </Field>
              </div>
            )}

            <div style={{ marginTop:20, display:'flex', justifyContent:'flex-end' }}>
              <Btn primary small onClick={() => setShowAnalisi(false)}>Salva risposte come contesto</Btn>
            </div>
          </Card>
        )}
      </div>

      <FieldAI
        label="Barriere"
        sub="Ostacoli e difficoltà presenti nel contesto scolastico (architettoniche, didattiche, relazionali…)"
        campoId="s6_barriere"
        value={f("s6_barriere")}
        onChange={e => s("s6_barriere", e.target.value)}
        placeholder="Elenca le barriere riscontrate nell'ambiente, nelle attività e nelle relazioni…"
        rows={4}
        maxTokens={1200}
        studente={studente}
        sezioneLabel={sl}
        contestoGlobale={contestoGlobale}
        customPrompt={promptBarriereCompleto}
      />
      <FieldAI
        label="Facilitatori"
        sub="Risorse, strategie e condizioni che favoriscono l'inclusione"
        campoId="s6_facilitatori"
        value={f("s6_facilitatori")}
        onChange={e => s("s6_facilitatori", e.target.value)}
        placeholder="Descrivi i facilitatori presenti (strumenti, supporti, relazioni positive, metodologie efficaci…)"
        rows={4}
        maxTokens={1200}
        studente={studente}
        sezioneLabel={sl}
        contestoGlobale={contestoGlobale}
        customPrompt={promptFacilitatoriCompleto}
      />
      <Divider />
      <FieldAI
        label="Revisione a seguito di Verifica intermedia"
        sub="Modelli ufficiali A1/A2/A3/A4 D.M. 182/2020. Da compilare al GLO intermedio (gen-feb): modifiche al quadro di barriere e facilitatori a seguito di nuove osservazioni sul contesto."
        campoId="s6_revisione_intermedia"
        value={f("s6_revisione_intermedia")}
        onChange={e => s("s6_revisione_intermedia", e.target.value)}
        placeholder="A seguito della verifica intermedia, emergono nuove barriere/facilitatori; quelli già rilevati si confermano o vanno rivalutati come segue…"
        rows={5}
        maxTokens={700}
        studente={studente}
        sezioneLabel={sl}
        contestoGlobale={contestoGlobale}
        customPrompt={buildRevisioneIntermediaPrompt({
          sezN: 6,
          sezLabel: "Osservazioni sul contesto: barriere e facilitatori",
          natura: "correttivo",
          contestoSpecifico: "Le barriere e i facilitatori già rilevati sono nel contesto del PEI. Indica cosa è cambiato dopo i primi mesi dell'anno scolastico (nuove barriere emerse, facilitatori che si sono dimostrati efficaci o inefficaci, contesti mutati).",
          blocchi: [
            { titolo: "Conferme", descrizione: "(1-3 voci) barriere o facilitatori già rilevati che si confermano nel loro impatto" },
            { titolo: "Nuove barriere emerse", descrizione: "(0-3 voci) barriere ambientali, didattiche, relazionali rilevate nei primi mesi e non presenti nel quadro iniziale" },
            { titolo: "Nuovi facilitatori o facilitatori da rinforzare", descrizione: "(0-3 voci) risorse, strategie o condizioni efficaci da consolidare" },
            { titolo: "Modifiche al piano di intervento", descrizione: "(1-3 voci) azioni concrete su barriere/facilitatori da inserire o modificare nel PEI" },
          ],
        })}
      />
    </div>
  );
}

function SezAmbiente({ dati, setD, studente, onChat, contestoGlobale }) {
  // Controlla se le sezioni rilevanti sono compilate
  const hasS2 = !!(dati.pf_descrizione);
  const hasS5 = !!(dati.s5_A_ob || dati.s5_B_ob || dati.s5_C_ob || dati.s5_D_ob);
  const hasS6 = !!(dati.s6_barriere || dati.s6_facilitatori);
  const sezioniMancanti = [
    !hasS2 && "Sezione 2 (Profilo di Funzionamento)",
    !hasS5 && "Sezione 5 (Obiettivi e Strategie)",
    !hasS6 && "Sezione 6 (Barriere e Facilitatori)",
  ].filter(Boolean);
  const sl = "Ambiente Inclusivo";
  const f = k => dati[k] || "";
  const s = (k,v) => setD(k,v);

  // Modalità di compilazione: "rapida" (campo unico) o "guidata" (tre sotto-campi MACRO/MESO/MICRO)
  const [modalitaS7, setModalitaS7] = useState(dati.s7_modalita || "guidata");
  const cambiaModalita = (m) => {
    setModalitaS7(m);
    setD("s7_modalita", m);
  };

  // contestoSez: usato dai prompt degli interventi (MACRO/MESO/MICRO) — contesto sintetico
  const contestoS2 = dati.pf_descrizione ? `\nProfilo di Funzionamento (Sez. 2): ${dati.pf_descrizione.slice(0, 400)}` : "";
  const contestoS5 = DIMENSIONI.filter(d => dati[`s5_${d.id}_ob`]).map(d => `Dim.${d.id}: ${dati[`s5_${d.id}_ob`].slice(0, 150)}`).join(" | ");
  const contestoS6b = dati.s6_barriere ? `\nBarriere (Sez. 6): ${dati.s6_barriere.slice(0, 200)}` : "";
  const contestoS6f = dati.s6_facilitatori ? `\nFacilitatori (Sez. 6): ${dati.s6_facilitatori.slice(0, 200)}` : "";
  const contestoSez = `${contestoS2}${contestoS5 ? `\nObiettivi (Sez. 5): ${contestoS5}` : ""}${contestoS6b}${contestoS6f}`;
  // contestoVerificaS7: usato SOLO dal campo verifica conclusiva — contesto completo
  const contestoVerificaS7 = buildContestoVerificaCompleto(dati);

  // Adattamento lessicale per scuola dell'infanzia (appeso ai prompt MACRO/MESO/MICRO + rapido)
  const adattInfanziaS7 = studente?.ordine === "infanzia"
    ? `\n\nADATTAMENTO INFANZIA (regola tassativa): ovunque nel prompt leggi "classe" / "aula" / "team docenti in classe" / "studente" / "alunno", utilizza ESCLUSIVAMENTE "sezione" / "spazi della sezione" / "docenti della sezione" / "bambino" o "bambina". Gli interventi devono essere coerenti con la pedagogia infantile: gioco simbolico, routine quotidiane, attività di laboratorio, esperienze nei cinque campi di esperienza, organizzazione degli angoli di sezione.`
    : "";

  // Istruzioni comuni per il formato interventi (usate in entrambe le modalità)
  const regolaInterventi = `
Genera un piano d'azione strutturato con interventi operativi e verificabili.
Ogni voce è una frase breve (6–15 parole), concreta e attribuibile a un referente.
Usa i verbi all'infinito OPPURE al presente indicativo — scegli un solo registro e mantienilo coerente per tutta la risposta.
NON mescolare infinito e presente nella stessa risposta.
NON usare grassetto né titoli aggiuntivi. NON aggiungere testo introduttivo o conclusivo.
Includi solo gli interventi realmente pertinenti al profilo dello studente.
Tieni conto delle barriere (da ridurre) e dei facilitatori (da potenziare) della Sez. 6.
BANDISCI formule generiche e vuote: "favorire l'inclusione", "supportare l'alunno", "attivare percorsi personalizzati", "garantire il benessere", "promuovere la partecipazione" — ogni voce deve descrivere un'azione concreta, non un obiettivo astratto.`;

  // Prompt modalità RAPIDA: campo unico con struttura MACRO/MESO/MICRO
  const promptInterventiRapido = `Sei un docente di sostegno esperto che compila la Sezione 7 del PEI secondo il D.M. 182/2020.
Scrivi il piano d'azione per il campo "Interventi sul contesto per realizzare un ambiente di apprendimento inclusivo".
${contestoSez ? `\nContesto del PEI:\n${contestoSez}\n` : ""}
DISTINZIONE OBBLIGATORIA TRA I LIVELLI:
- MACRO = proposte al Dirigente Scolastico (acquisti, organizzazione orario, formazione personale, risorse aggiuntive). NON include interventi d'aula.
- MESO = azioni del team docenti in classe (disposizione banchi, metodologie, peer tutoring, gestione tempi). NON include richieste al Dirigente né adattamenti solo per lo studente.
- MICRO = adattamenti individuali per questo studente (ausili personali, routine compensative, accordi individuali). NON include azioni di gruppo o richieste istituzionali.

Organizza gli interventi in tre livelli con questo formato OBBLIGATORIO:

MACRO — Proposte al Dirigente Scolastico
— [proposta istituzionale, 6–15 parole]
— [proposta istituzionale, 6–15 parole]

MESO — Interventi del team docenti in classe
— [azione del team docenti, 6–15 parole]
— [azione del team docenti, 6–15 parole]

MICRO — Adattamenti individuali per lo studente
— [adattamento individuale, 6–15 parole]
— [adattamento individuale, 6–15 parole]

Genera 3–6 voci per livello, omettendo i livelli privi di interventi pertinenti.
${regolaInterventi}${adattInfanziaS7}`;

  // Prompt per i tre sotto-campi in modalità GUIDATA
  const promptMacro = `Sei un docente di sostegno esperto che compila la Sezione 7 del PEI secondo il D.M. 182/2020.
Scrivi SOLO gli interventi per il livello MACRO — Proposte al Dirigente Scolastico.

DEFINIZIONE MACRO: sono le richieste formali che il team docenti rivolge al Dirigente Scolastico o all'istituzione scolastica nel suo complesso. Il Dirigente può attivarle o autorizzarle, ma non le esegue direttamente in aula. Riguardano: accessibilità e sicurezza degli spazi comuni, acquisto di attrezzature o dotazioni d'istituto, organizzazione dell'orario scolastico, formazione del personale docente e non docente, attivazione di risorse professionali aggiuntive (AEC, assistente alla comunicazione, educatore), progetti d'istituto per l'inclusione.

Esempi CORRETTI per MACRO:
— Richiedere l'installazione di pannelli fonoassorbenti nell'aula assegnata alla classe
— Garantire la continuità del docente di sostegno per l'intero anno scolastico
— Attivare un monte ore AEC di almeno 10 ore settimanali
— Organizzare un corso di aggiornamento per il team docenti sulle strategie per l'ADHD
— Dotare l'aula di una postazione informatica dedicata con software compensativi

Esempi SBAGLIATI per MACRO (appartengono a MESO o MICRO — NON scriverli qui):
— Strutturare il peer tutoring tra lo studente e i compagni (→ MESO)
— Posizionare il banco vicino alla cattedra (→ MESO)
— Fornire allo studente una scaletta visiva delle attività giornaliere (→ MICRO)

${contestoSez ? `\nContesto del PEI:\n${contestoSez}\n` : ""}
Formato OBBLIGATORIO:
— [proposta concreta al Dirigente, 6–15 parole]
— [proposta concreta al Dirigente, 6–15 parole]

Genera 3–5 voci. Ogni voce deve essere una proposta rivolta al Dirigente Scolastico o all'istituzione, NON un intervento d'aula.
${regolaInterventi}${adattInfanziaS7}`;

  const promptMeso = `Sei un docente di sostegno esperto che compila la Sezione 7 del PEI secondo il D.M. 182/2020.
Scrivi SOLO gli interventi per il livello MESO — Interventi del team docenti in classe.

DEFINIZIONE MESO: sono le azioni che il consiglio di classe o il team docenti mette in atto direttamente nella gestione quotidiana dell'aula e del gruppo classe. Non richiedono autorizzazione del Dirigente. Riguardano: organizzazione degli spazi e della disposizione dei banchi, gestione dei tempi e dei ritmi della lezione, clima relazionale e dinamiche tra pari, metodologie didattiche inclusive (cooperative learning, peer tutoring, flipped classroom), uso di tecnologie e ausili in classe, accordi di valutazione condivisi tra i docenti.

Esempi CORRETTI per MESO:
— Strutturare sessioni di peer tutoring con coppie stabili e ruoli definiti
— Posizionare il banco dello studente vicino alla cattedra, lontano da fonti di distrazione
— Alternare attività frontali e attività motorie ogni 20 minuti per gestire l'attenzione
— Concordare tra i docenti una griglia di valutazione adattata per le verifiche
— Utilizzare la LIM per presentare le consegne in forma visiva prima di ogni attività

Esempi SBAGLIATI per MESO (appartengono ad altri livelli — NON scriverli qui):
— Richiedere al Dirigente l'acquisto di software compensativi (→ MACRO)
— Fornire allo studente una checklist personale per il materiale (→ MICRO)

${contestoSez ? `\nContesto del PEI:\n${contestoSez}\n` : ""}
Formato OBBLIGATORIO:
— [intervento concreto del team docenti, 6–15 parole]
— [intervento concreto del team docenti, 6–15 parole]

Genera 4–6 voci. Ogni voce deve essere un'azione attuabile direttamente dai docenti in classe, senza bisogno di autorizzazioni superiori.
${regolaInterventi}${adattInfanziaS7}`;

  const promptMicro = `Sei un docente di sostegno esperto che compila la Sezione 7 del PEI secondo il D.M. 182/2020.
Scrivi SOLO gli interventi per il livello MICRO — Adattamenti individuali per lo studente.

DEFINIZIONE MICRO: sono gli adattamenti personalizzati pensati specificamente per questo studente, calibrati sulla sua diagnosi e sul suo profilo di funzionamento. Non riguardano il gruppo classe ma solo l'individuo. Riguardano: strumenti e ausili personali (schede, tabelle, calcolatrice, sintesi vocale), routine e strategie compensative individuali, supporti visivi o cognitivi personalizzati, accordi individuali su tempi e modalità di risposta, raccordo con le terapie extrascolastiche.

Esempi CORRETTI per MICRO:
— Fornire una scaletta visiva delle attività giornaliere da tenere sul banco
— Concordare con lo studente un segnale discreto per richiedere una pausa
— Utilizzare una checklist personale per il controllo del materiale a fine lezione
— Adottare la sintesi vocale per la lettura autonoma dei testi disciplinari
— Prevedere tempi aggiuntivi del 30% per le verifiche scritte

Esempi SBAGLIATI per MICRO (appartengono ad altri livelli — NON scriverli qui):
— Formare il personale sulle strategie per la disabilità (→ MACRO)
— Organizzare il peer tutoring in classe (→ MESO)

${contestoSez ? `\nContesto del PEI:\n${contestoSez}\n` : ""}
Formato OBBLIGATORIO:
— [adattamento individuale per lo studente, 6–15 parole]
— [adattamento individuale per lo studente, 6–15 parole]

Genera 3–5 voci. Ogni voce deve riguardare esclusivamente questo studente, coerente con il profilo ICF e la diagnosi.
${regolaInterventi}${adattInfanziaS7}`;

  const qCtxS7 = buildQuestionarioContext(dati, "s7_vq_");
  const promptVerificaFinale = `Sei un docente di sostegno esperto che compila la verifica conclusiva della Sezione 7 del PEI (D.M. 182/2020).
Scrivi il testo per il campo "Verifica conclusiva degli esiti" della Sezione 7.
${contestoVerificaS7 ? `\nCONTENUTO PROGRAMMATORIO DEL PEI (tutto ciò che è stato pianificato per questo studente):\n${contestoVerificaS7}\n` : ""}
${qCtxS7 ? `\n${qCtxS7}\n` : ""}
PRIORITÀ DEL TESTO — Allegato B Linee Guida D.I. 182/2020 (testuale): "la valutazione è riferita PRIORITARIAMENTE all'efficacia degli interventi, NON SOLO al raggiungimento degli obiettivi". Quindi: valuta in primo luogo l'EFFICACIA degli interventi sull'ambiente (MACRO, MESO, MICRO), delle misure di sostegno e delle risorse attivate — cosa ha funzionato, cosa è stato modificato in itinere e cosa resta da potenziare —, poi inquadra il livello di inclusione raggiunto. Fai riferimento agli obiettivi ICF della Sez.5 e alle barriere/facilitatori della Sez.6 per motivare il giudizio.

Regole:
- Il tono complessivo (positivo/misto/critico) deve rispecchiare OBBLIGATORIAMENTE la valutazione globale fornita dall'insegnante nel questionario
- Testo continuo, senza elenchi o titoli
- Tono valutativo, al passato; le ultime 1-2 righe possono indicare cosa potenziare nell'anno successivo
- Tra 120 e 200 parole
- NON usare grassetto
- Cita interventi specifici del PEI (es. nomi delle strategie, livelli MACRO/MESO/MICRO) — NON scrivere valutazioni generiche`;

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
        <SezTitle icon="🌿">7. Ambiente Inclusivo</SezTitle>
        <Btn ghost small onClick={onChat} icon="💬">Assistente</Btn>
      </div>

      <InfoBox color={C.accent} icon="📌">
        Questa sezione corrisponde al campo SIDI <strong>"Interventi sul contesto per realizzare un ambiente di apprendimento inclusivo"</strong>.
      </InfoBox>

      {sezioniMancanti.length > 0 && (
        <div style={{ marginBottom:16, padding:"10px 14px",
          background:"#fff7ed", border:"1px solid #fed7aa",
          borderRadius:8, fontSize:12, color:"#92400e", lineHeight:1.6 }}>
          ⚠️ <strong>Suggerimenti AI meno precisi:</strong> per ottenere testi personalizzati sul profilo dello studente, compila prima: {sezioniMancanti.join(", ")}.
        </div>
      )}

      {/* Toggle modalità compilazione */}
      <div style={{ display:"flex", gap:8, marginBottom:16, alignItems:"center" }}>
        <span style={{ fontSize:12, color:C.muted, marginRight:4 }}>Modalità compilazione:</span>
        {[
          { key:"guidata", label:"Compilazione guidata", desc:"✦ consigliata" },
          { key:"rapida",  label:"Compilazione rapida",  desc:"campo unico" },
        ].map(({ key, label, desc }) => (
          <button key={key} onClick={() => cambiaModalita(key)}
            style={{
              padding:"5px 12px", borderRadius:6, fontSize:12, cursor:"pointer",
              border: modalitaS7 === key ? `2px solid ${C.accent}` : "1px solid #ddd",
              background: modalitaS7 === key ? hex(C.accent, 0.08) : "#fff",
              color: modalitaS7 === key ? C.accent : C.muted,
              fontWeight: modalitaS7 === key ? 700 : 400,
            }}>
            {label} <span style={{ fontSize:10, opacity:0.7 }}>({desc})</span>
          </button>
        ))}
      </div>

      {modalitaS7 === "guidata" ? (
        <>
          {/* MACRO */}
          <div style={{ marginBottom:4, padding:"6px 12px", background:hex(C.accent,0.06),
            border:`1px solid ${hex(C.accent,0.2)}`, borderRadius:"8px 8px 0 0",
            fontSize:12, fontWeight:700, color:C.accent }}>
            MACRO — Proposte al Dirigente Scolastico
            <span style={{ fontWeight:400, marginLeft:8, opacity:0.7 }}>accessibilità fisica, dotazioni d'istituto, formazione del personale, risorse aggiuntive</span>
          </div>
          <FieldAI
            label=""
            campoId="s7_macro"
            value={f("s7_macro")}
            onChange={e => s("s7_macro", e.target.value)}
            placeholder="— Acquisire arredi ergonomici adeguati al profilo posturale dello studente&#10;— Garantire la continuità dell'insegnante di sostegno per l'intero anno…"
            rows={4}
            maxTokens={600}
            studente={studente}
            sezioneLabel={sl}
            contestoGlobale={contestoGlobale}
            customPrompt={promptMacro}
            style={{ borderRadius:"0 0 8px 8px", marginBottom:12 }}
          />

          {/* MESO */}
          <div style={{ marginBottom:4, padding:"6px 12px", background:hex(C.green,0.06),
            border:`1px solid ${hex(C.green,0.2)}`, borderRadius:"8px 8px 0 0",
            fontSize:12, fontWeight:700, color:C.green }}>
            MESO — Interventi del team docenti in classe
            <span style={{ fontWeight:400, marginLeft:8, opacity:0.7 }}>spazi, tempi, clima relazionale, metodologie, tecnologie in classe</span>
          </div>
          <FieldAI
            label=""
            campoId="s7_meso"
            value={f("s7_meso")}
            onChange={e => s("s7_meso", e.target.value)}
            placeholder="— Posizionare il banco vicino alla LIM per facilitare l'accesso agli stimoli visivi&#10;— Alternare attività in piccolo gruppo e lavoro individuale ogni 20 minuti…"
            rows={5}
            maxTokens={700}
            studente={studente}
            sezioneLabel={sl}
            contestoGlobale={contestoGlobale}
            customPrompt={promptMeso}
            style={{ borderRadius:"0 0 8px 8px", marginBottom:12 }}
          />

          {/* MICRO */}
          <div style={{ marginBottom:4, padding:"6px 12px", background:"#f5f0ff",
            border:"1px solid #d8c8f8", borderRadius:"8px 8px 0 0",
            fontSize:12, fontWeight:700, color:"#6b21a8" }}>
            MICRO — Adattamenti individuali per lo studente
            <span style={{ fontWeight:400, marginLeft:8, opacity:0.7 }}>ausili personalizzati, supporti individuali, strategie compensative specifiche</span>
          </div>
          <FieldAI
            label=""
            campoId="s7_micro"
            value={f("s7_micro")}
            onChange={e => s("s7_micro", e.target.value)}
            placeholder="— Fornire istruzioni in formato semplificato con supporto iconografico&#10;— Prevedere pause strutturate ogni 20 minuti nelle attività cognitive…"
            rows={4}
            maxTokens={600}
            studente={studente}
            sezioneLabel={sl}
            contestoGlobale={contestoGlobale}
            customPrompt={promptMicro}
            style={{ borderRadius:"0 0 8px 8px", marginBottom:0 }}
          />
        </>
      ) : (
        <FieldAI
          label="Interventi sul contesto per realizzare un ambiente di apprendimento inclusivo"
          sub="Piano d'azione strutturato per livello (MACRO/MESO/MICRO) — campo principale SIDI"
          campoId="s7_interventi"
          value={f("s7_interventi")}
          onChange={e => s("s7_interventi", e.target.value)}
          placeholder="MACRO — Proposte al Dirigente Scolastico&#10;— …&#10;&#10;MESO — Interventi del team docenti in classe&#10;— …&#10;&#10;MICRO — Adattamenti individuali per lo studente&#10;— …"
          rows={10}
          maxTokens={1400}
          required
          studente={studente}
          sezioneLabel={sl}
          contestoGlobale={contestoGlobale}
          customPrompt={promptInterventiRapido}
        />
      )}

      <Divider />
      <FieldAI
        label="Revisione a seguito di Verifica intermedia"
        sub="Modelli ufficiali A1/A2/A3/A4 D.M. 182/2020. Da compilare al GLO intermedio (gen-feb): modifiche agli interventi sul contesto/ambiente di apprendimento."
        campoId="s7_revisione_intermedia"
        value={f("s7_revisione_intermedia")}
        onChange={e => s("s7_revisione_intermedia", e.target.value)}
        placeholder="A seguito della verifica intermedia, gli interventi MACRO/MESO/MICRO sull'ambiente vengono confermati / modificati come segue…"
        rows={5}
        maxTokens={700}
        studente={studente}
        sezioneLabel={sl}
        contestoGlobale={contestoGlobale}
        customPrompt={buildRevisioneIntermediaPrompt({
          sezN: 7,
          sezLabel: "Interventi sul contesto per un ambiente di apprendimento inclusivo",
          natura: "correttivo",
          contestoSpecifico: "Gli interventi MACRO/MESO/MICRO sull'ambiente di apprendimento sono già nel contesto del PEI. Indica cosa funziona, cosa va modificato e quali nuovi interventi vanno introdotti sull'ambiente.",
          blocchi: [
            { titolo: "Conferme MACRO/MESO/MICRO", descrizione: "(1-3 voci) interventi sull'ambiente che si confermano efficaci ai diversi livelli" },
            { titolo: "Criticità emerse", descrizione: "(1-3 voci) interventi che non hanno prodotto l'effetto atteso o ostacoli organizzativi rilevati" },
            { titolo: "Modifiche agli interventi", descrizione: "(1-3 voci) nuovi interventi sull'ambiente o modifiche a quelli previsti, specificando il livello MACRO/MESO/MICRO" },
          ],
        })}
      />
      <Divider />
      <div style={{ border:`1.5px solid ${hex(C.accent2,0.35)}`, background:hex(C.accent2,0.05),
        borderRadius:12, padding:16, marginTop:4 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12, flexWrap:"wrap" }}>
          <span style={{ fontSize:16, lineHeight:1 }} aria-hidden="true">🏁</span>
          <span style={{ fontSize:13.5, fontWeight:700, color:C.accent2 }}>Bilancio di fine anno — assistito dalla Guida IA</span>
          <span style={{ fontSize:11, color:C.muted, flexBasis:"100%" }}>Compila prima la Guida IA: le risposte personalizzano il testo generato.</span>
        </div>
        <VerificaQuestionnaireTrigger
          dati={dati}
          setD={setD}
          prefix="s7_vq_"
          tipo="conclusiva"
          dimensioniAttive={[]}
          sezioneLabel="Verifica conclusiva — Sez. 7 Ambiente"
        />
        <FieldAI
          label="Verifica conclusiva degli esiti"
          sub="Da compilare a fine anno. Valuta l'efficacia complessiva degli interventi ambientali realizzati."
          campoId="s7_verifica_conclusiva"
          value={f("s7_verifica_conclusiva")}
          onChange={e => s("s7_verifica_conclusiva", e.target.value)}
          placeholder="Gli interventi sul contesto realizzati nel corso dell'anno scolastico hanno prodotto i seguenti esiti…"
          rows={4}
          studente={studente}
          sezioneLabel={sl}
          contestoGlobale={contestoGlobale}
          customPrompt={promptVerificaFinale}
        />
      </div>
    </div>
  );
}

function SezCurricolo({ dati, setD, studente, ord, onChat, contestoGlobale }) {
  const sl = "Curricolo";
  const f = k => dati[k] || "";
  const s = (k,v) => setD(k,v);

  const ordKey = Object.keys(ORDINI).find(k => ORDINI[k] === ord) || "primaria";
  const isSec2 = ordKey === "secondaria2";
  const classeStr = String(studente?.classe || "").trim();
  const aree = {
    infanzia:    ["Il sé e l'altro","Il corpo e il movimento","Immagini, suoni, colori","I discorsi e le parole","La conoscenza del mondo"],
    primaria:    ["Italiano","Matematica","Area Storico-Geografica","Lingua Inglese","Scienze","Arte e Immagine","Musica","Ed. Fisica","Religione / Attività Alternative"],
    secondaria1: ["Italiano","Storia","Geografia","Matematica","Scienze","Tecnologia","Inglese","Seconda Lingua","Arte e Immagine","Musica","Ed. Fisica","Religione"],
    secondaria2: ["Italiano","Storia","Filosofia","Matematica","Fisica","Scienze","Inglese","Altre Lingue","Discipline di indirizzo","Ed. Fisica","Religione"],
  };
  const voci = aree[ordKey] || aree.primaria;

  // ─── Programmazione / grado di riferimento (Fase 1) ───────────────────────
  // Sotto-attributo di B (Progettazione disciplinare, modelli A2/A3/A4): per un
  // alunno di classe X si può indicare che, in una disciplina, segue gli obiettivi
  // di apprendimento di una classe inferiore. Default SICURO = classe attuale; la
  // scelta è del GLO e non si abbassa mai in automatico.
  const classeNum = parseInt(classeStr, 10);
  const classeValida = Number.isInteger(classeNum) && classeNum >= 1;
  const ORDINALI = ["", "prima", "seconda", "terza", "quarta", "quinta"];
  const ordinale = n => ORDINALI[n] || `${n}ª`;
  // ── Riferimento cross-order ────────────────────────────────────────────────
  // Il riferimento è un token "ordine:classe". Permette di scendere fino a TUTTO
  // l'ordine immediatamente precedente (prima media→primaria, prima superiore→sec I,
  // prima primaria→infanzia), non solo a una classe inferiore dello stesso ordine.
  const NUM_CLASSI = { infanzia: 0, primaria: 5, secondaria1: 3, secondaria2: 5 };
  const ORDINE_PREC = { primaria: "infanzia", secondaria1: "primaria", secondaria2: "secondaria1" };
  const ORDINE_BREVE = { infanzia: "infanzia", primaria: "primaria", secondaria1: "secondaria di primo grado", secondaria2: "secondaria di secondo grado" };
  const ORDINE_LABEL = { infanzia: "scuola dell'infanzia", primaria: "scuola primaria", secondaria1: "scuola secondaria di primo grado", secondaria2: "scuola secondaria di secondo grado" };
  // Parser retrocompatibile: un valore "5" (numero nudo, formato storico) = classe 5
  // dell'ordine ATTUALE; un token "primaria:5" / "infanzia:0" è già strutturato.
  const parseRif = raw => {
    const v = String(raw || "").trim();
    if (!v) return null;
    if (/^\d+$/.test(v)) return { ordine: ordKey, classe: Number(v) };
    const m = v.match(/^([a-z0-9_]+):(\d+)$/i);
    return m ? { ordine: m[1], classe: Number(m[2]) } : null;
  };
  const rifToken = (ordine, classe) => `${ordine}:${classe}`;
  const rifFallback = classeValida ? { ordine: ordKey, classe: classeNum } : null;
  const rifVal = key => parseRif(f(key)) || parseRif(f("s8_classeRif_default")) || rifFallback;
  const isRifAttuale = r => r && r.ordine === ordKey && r.classe === classeNum;
  const descrRif = r => !r ? "classe di appartenenza"
    : r.ordine === "infanzia" ? "scuola dell'infanzia (campi di esperienza)"
    : `classe ${ordinale(r.classe)} ${ORDINE_BREVE[r.ordine]}`.trim();
  // Opzioni discendenti: ordine attuale (classe attuale→1) + ordine precedente.
  const buildOpzioniRif = () => {
    if (!classeValida) return [];
    const out = [];
    for (let n = classeNum; n >= 1; n--) out.push({ ordine: ordKey, classe: n, attuale: n === classeNum });
    const prec = ORDINE_PREC[ordKey];
    if (prec === "infanzia") out.push({ ordine: "infanzia", classe: 0 });
    else if (prec) for (let n = NUM_CLASSI[prec]; n >= 1; n--) out.push({ ordine: prec, classe: n });
    return out;
  };
  const opzioniRif = buildOpzioniRif();
  // Render delle <option> con separatore (optgroup) fra ordine attuale e precedente.
  const renderOpzioniRif = () => {
    const cur = opzioniRif.filter(o => o.ordine === ordKey);
    const prev = opzioniRif.filter(o => o.ordine !== ordKey);
    const precKey = ORDINE_PREC[ordKey];
    return (
      <>
        <optgroup label={`Scuola ${ORDINE_BREVE[ordKey]} (ordine attuale)`}>
          {cur.map(o => (
            <option key={rifToken(o.ordine, o.classe)} value={rifToken(o.ordine, o.classe)}>
              {o.attuale ? `Classe attuale (${ordinale(o.classe)})` : `Classe ${ordinale(o.classe)}`}
            </option>
          ))}
        </optgroup>
        {prev.length > 0 && (
          <optgroup label={precKey === "infanzia" ? "Ordine precedente" : `Scuola ${ORDINE_BREVE[precKey]} (ordine precedente)`}>
            {prev.map(o => (
              <option key={rifToken(o.ordine, o.classe)} value={rifToken(o.ordine, o.classe)}>
                {o.ordine === "infanzia" ? "Campi di esperienza (infanzia)" : `Classe ${ordinale(o.classe)}`}
              </option>
            ))}
          </optgroup>
        )}
      </>
    );
  };
  // Frase iniettata nel prompt AI (riferimento del grado; la calibrazione la fa il prompt)
  const classeRifPhrase = key => {
    const r = rifVal(key);
    if (!r) return "classe di appartenenza";
    if (isRifAttuale(r)) return `${descrRif(r)} (classe di appartenenza)`;
    if (r.ordine === ordKey)
      return `${descrRif(r)} (classe precedente nello stesso ordine rispetto alla ${ordinale(classeNum)} frequentata)`;
    if (r.ordine === "infanzia")
      return `traguardi e competenze della scuola dell'infanzia (campi di esperienza), ripresi dal ciclo precedente rispetto alla classe ${ordinale(classeNum)} ${ORDINE_BREVE[ordKey]} frequentata`;
    return `${descrRif(r)} (programmazione ripresa dall'ordine precedente, la ${ORDINE_LABEL[r.ordine]}, rispetto alla classe ${ordinale(classeNum)} frequentata)`;
  };
  // Selettore riutilizzabile (funzione, NON componente, per non rimontare in fase di typing)
  const renderClasseRif = key => {
    if (!classeValida || opzioniRif.length < 2) return null;
    const r = rifVal(key);
    const inferiore = !isRifAttuale(r);
    return (
      <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap",
        padding:"8px 10px", borderRadius:8, background:C.bg, border:`1px solid ${C.border}` }}>
        <span style={{ fontSize:12, fontWeight:600, color:C.muted }}>Programmazione di riferimento:</span>
        <select value={rifToken(r.ordine, r.classe)} onChange={e => s(key, e.target.value)}
          style={{ fontSize:12, fontFamily:"'Inter',sans-serif", padding:"4px 8px",
            borderRadius:6, border:`1px solid ${C.border}`, background:C.surface, color:C.text, cursor:"pointer" }}>
          {renderOpzioniRif()}
        </select>
        {inferiore && (
          <span style={{ fontSize:11, color:C.accent }}>
            {r.ordine === "infanzia"
              ? "Riferimento ai campi di esperienza della scuola dell'infanzia"
              : `Obiettivi di apprendimento riferiti alla ${descrRif(r)}`}
          </span>
        )}
      </div>
    );
  };

  // ─── Retrieval KB Indicazioni Nazionali (Fase c) ──────────────────────────
  // Recupera il testo VERBATIM di obiettivi (fascia) + competenze/traguardi per
  // ancorare la generazione AI. Copre Primaria + Secondaria I grado + Infanzia
  // (come SORGENTE di riferimento cross-order). La versione (DM 254/2012 ↔
  // DM 221/2025) è scelta in base all'ORDINE/CLASSE DI RIFERIMENTO (non a quella
  // frequentata): così una quinta primaria nel 2026/27 prende correttamente il 254
  // anche se l'alunno frequenta la prima media (in rollout 221).
  // Una label di disciplina può mappare su 1+ chiavi KB (es. "Area Storico-Geografica"
  // della primaria = storia + geografia). Le chiavi sono allineate fra le due versioni.
  // Seconda lingua e Latino esistono solo nel 221 (lookup → "" sul 254).
  const normDisc = label => {
    const t = String(label || "").toLowerCase().trim();
    if (t.startsWith("italiano") || t.startsWith("lingua e letteratura")) return ["italiano"];
    if (t.startsWith("matematica")) return ["matematica"];
    if (t.startsWith("area storico")) return ["storia", "geografia"];
    if (t.startsWith("storia")) return ["storia"];
    if (t.startsWith("geografia")) return ["geografia"];
    if (t.startsWith("scienze")) return ["scienze"];
    if (t.includes("inglese")) return ["inglese"];
    if (t.startsWith("seconda lingua") || t.startsWith("francese") || t.startsWith("spagnolo")
        || t.startsWith("tedesco") || t.includes("lingua comunitaria")) return ["seconda_lingua"];
    if (t.startsWith("latino")) return ["latino"];
    if (t.startsWith("arte")) return ["arte_immagine"];
    if (t.startsWith("ed. fisica") || t.startsWith("educazione fisica")
        || t.startsWith("educazione motoria")) return ["educazione_fisica"];
    if (t.startsWith("strumento")) return ["strumento_musicale"];
    if (t.startsWith("musica")) return ["musica"];
    if (t.startsWith("tecnologia")) return ["tecnologia"];
    return [];
  };
  // Primaria: gli obiettivi esistono a fine cl3 e fine cl5 → mappa grado→fascia.
  const bandPerGrado = n => (Number(n) >= 4 ? "fine_classe_5" : "fine_classe_3");
  const hasObj = b => b && Object.keys(b).length > 0;
  // Mappa la disciplina (label dell'ordine attuale) ai campi di esperienza
  // dell'infanzia, usata SOLO quando la programmazione di riferimento è l'infanzia.
  const discToCampi = label => {
    const t = String(label || "").toLowerCase().trim();
    if (t.startsWith("italiano") || t.startsWith("lingua e letteratura")) return ["discorsi_parole"];
    if (t.includes("inglese") || t.startsWith("lingua ")) return ["discorsi_parole"];
    if (t.startsWith("matematica")) return ["conoscenza_mondo"];
    if (t.startsWith("scienze")) return ["conoscenza_mondo"];
    if (t.startsWith("area storico") || t.startsWith("storia")) return ["se_e_altro", "conoscenza_mondo"];
    if (t.startsWith("geografia")) return ["conoscenza_mondo"];
    if (t.startsWith("arte")) return ["immagini_suoni_colori"];
    if (t.startsWith("musica")) return ["immagini_suoni_colori"];
    if (t.startsWith("ed. fisica") || t.startsWith("educazione fisica")
        || t.startsWith("educazione motoria")) return ["corpo_movimento"];
    if (t.startsWith("tecnologia")) return ["conoscenza_mondo"];
    return [];  // Religione/Alternativa → nessun campo → fallback testuale (prompt)
  };
  // Blocco KB quando il riferimento è la scuola dell'infanzia (campi di esperienza).
  const kbBlockInfanzia = (disciplina, ver) => {
    const campiKeys = discToCampi(disciplina);
    if (!campiKeys.length) return "";
    const is221v = String(ver?.versione || "").includes("221");
    const decreto = is221v ? "D.M. 221/2025" : "D.M. 254/2012";
    const compHdr = is221v ? "Competenze attese" : "Traguardi per lo sviluppo della competenza";
    const blocks = campiKeys.map(ck => {
      const rec = ver?.ordini?.infanzia?.[ck];
      if (!rec) return "";
      const comp = (rec.competenze || []).map(x => `- ${x}`).join("\n");
      const ob = (rec.obiettivi || []).map(x => `  - ${x}`).join("\n");
      let body = `RIFERIMENTO UFFICIALE — Indicazioni Nazionali (${decreto}), scuola dell'infanzia, campo di esperienza "${rec.nome}" (programmazione ripresa dal ciclo precedente: declinala sul profilo di funzionamento dell'alunno, non inventare traguardi estranei a questo elenco):\n`;
      if (ob) body += `Obiettivi specifici (usa QUESTI come base):\n${ob}\n\n`;
      body += `${compHdr} (cornice di competenza, non obiettivi operativi):\n${comp}\n`;
      return body;
    }).filter(Boolean);
    return blocks.join("\n");
  };
  const kbBlock = (disciplina, classeRifKey) => {
    const r = rifVal(classeRifKey);
    if (!r) return "";
    const refOrd = r.ordine;
    // Versione decisa sull'ordine/classe DI RIFERIMENTO (rollout della classe citata).
    const ver = versioneKB(refOrd, r.classe, annoAvvioStudente(studente));
    if (refOrd === "infanzia") return kbBlockInfanzia(disciplina, ver);
    if (refOrd !== "primaria" && refOrd !== "secondaria1") return "";  // KB strutturata: primo ciclo
    const keys = normDisc(disciplina);
    if (!keys.length) return "";
    const n = r.classe || classeNum;
    const is221v = String(ver?.versione || "").includes("221");
    const isPrim = refOrd === "primaria";
    const trKey = isPrim ? "traguardi_fine_primaria" : "traguardi_fine_secondaria";
    const trLabel = isPrim ? "al termine della scuola primaria"
                           : "al termine della scuola secondaria di primo grado";
    // Etichette version-aware (terminologia ministeriale corretta per ciascun decreto)
    const decreto  = is221v ? "D.M. 221/2025" : "D.M. 254/2012";
    const obHdr    = is221v ? "Obiettivi specifici di apprendimento" : "Obiettivi di apprendimento";
    const compHdr  = is221v ? "Competenze attese" : "Traguardi per lo sviluppo delle competenze";
    const compNote = is221v ? "(competenze attese a fine percorso, cornice di riferimento, non obiettivi operativi)"
                           : "(cornice di competenza, non obiettivi operativi)";
    const blocks = keys.map(key => {
      const rec = ver?.ordini?.[refOrd]?.[key];
      if (!rec) return "";
      let band, obLabel;
      if (isPrim) {
        band = bandPerGrado(n);
        // alcune discipline primaria hanno solo fine_classe_5 → fallback
        if (!hasObj(rec.obiettivi?.[band]) && hasObj(rec.obiettivi?.fine_classe_5)) band = "fine_classe_5";
        obLabel = `al termine della ${band === "fine_classe_5" ? "classe quinta" : "classe terza"} di scuola primaria`;
      } else {
        band = "fine_classe_3";        // secondaria I: obiettivi solo a fine ciclo
        obLabel = "al termine della scuola secondaria di primo grado (classe terza)";
      }
      const ob = rec.obiettivi?.[band] || {};
      const obTxt = Object.entries(ob)
        .map(([nucleo, items]) => (nucleo ? `${nucleo}:\n` : "") + items.map(x => `  - ${x}`).join("\n"))
        .join("\n");
      if (!obTxt) return "";
      const tr = (rec[trKey] || []).map(x => `- ${x}`).join("\n");
      const discLabel = keys.length > 1 ? key.replace(/_/g, " ") : disciplina;
      return `RIFERIMENTO UFFICIALE — Indicazioni Nazionali (${decreto}), ${discLabel}.
${obHdr} ${obLabel} (usa QUESTI come base e declinali sul profilo di funzionamento dell'alunno; non inventare obiettivi estranei a questo elenco):
${obTxt}

${compHdr} ${trLabel} ${compNote}:
${tr}
`;
    }).filter(Boolean);
    return blocks.join("\n");
  };

  // ─── Discipline personalizzate ────────────────────────────────────────────
  // Salvate come array di stringhe in dati.s8_custom_voci
  // I loro campi usano il prefisso s8_cx_${j}_* per non collidere con gli indici fissi
  const customVoci = Array.isArray(dati.s8_custom_voci) ? dati.s8_custom_voci : [];
  const [nuovaDisciplina, setNuovaDisciplina] = useState("");

  const aggiungiDisciplina = () => {
    const nome = nuovaDisciplina.trim();
    if (!nome) return;
    s("s8_custom_voci", [...customVoci, nome]);
    setNuovaDisciplina("");
  };

  const rimuoviDisciplina = (j) => {
    const nuove = customVoci.filter((_, idx) => idx !== j);
    s("s8_custom_voci", nuove);
    // Re-indicizza i campi: sposta i dati di ogni disciplina dopo j un indice in su,
    // poi cancella l'ultimo indice (che ora è duplicato).
    const campi = ["scelta","ob","strat","strum","verifica","classeRif"];
    // 1. Shifta in avanti (j+1 → j, j+2 → j+1, ...)
    for (let k = j; k < customVoci.length - 1; k++) {
      campi.forEach(campo => {
        s(`s8_cx_${k}_${campo}`, dati[`s8_cx_${k + 1}_${campo}`]);
      });
    }
    // 2. Cancella l'ultimo slot (ora duplicato)
    campi.forEach(campo => {
      s(`s8_cx_${customVoci.length - 1}_${campo}`, undefined);
    });
  };

  const contestoPrec = contestoGlobale;

  const disciplinaContext = (disciplina) => {
    if (!contestoPrec) return "";
    return `Contesto dello studente (punti di forza, barriere, facilitatori, profilo di funzionamento):
${contestoPrec.slice(0, 500)}`;
  };

  const customPromptB = `
Sei un esperto di inclusione scolastica, pedagogia speciale e didattica inclusiva con profonda conoscenza del D.M. 182/2020 e delle Indicazioni Nazionali.

DATI DELLO STUDENTE:
- Ordine scolastico: ${ORDINI[studente?.ordine]?.label || ord.label}
- Disciplina/area: {{DISCIPLINA}}
- Programmazione di riferimento: {{CLASSE_RIF}}
- Età: ${studente?.eta || "non specificata"} anni
- Diagnosi: ${buildDiagnosiContesto(studente)}${buildRiferimentoICD10(studente)}
${contestoPrec ? `\nPROFILO DI CONTESTO (profilo di funzionamento, barriere, facilitatori):\n${contestoPrec.slice(0, 800)}` : ""}

{{TRAGUARDI_KB}}
REGOLE RIGIDE DI STILE (obbligatorie per tutti i campi):
- Usa ESCLUSIVAMENTE il PRESENTE INDICATIVO (es. per testi descrittivi: "Lo studente legge…", "I docenti utilizzano…", "Si adotta…"). ECCEZIONE: negli OBIETTIVI DI APPRENDIMENTO ometti il soggetto (es. "Legge testi brevi con supporto visivo", NON "Lo studente legge…")
- NON usare MAI l'infinito ("Leggere…", "Adottare…") né il futuro ("sarà…", "verrà…")
- BANDISCI formule generiche: "didattica personalizzata", "facilitazioni", "sarà seguito", "supporto adeguato", "attività mirate"
- Ogni azione o obiettivo deve essere concreto, visibile e verificabile
- Tono tecnico, pedagogico-clinico, professionale
- NON usare grassetto né titoli aggiuntivi rispetto a quelli richiesti

STRUTTURA DA GENERARE:

1. OBIETTIVI DI APPRENDIMENTO
Definisci 3-6 obiettivi realistici partendo dagli Obiettivi di apprendimento delle Indicazioni Nazionali riferiti alla {{CLASSE_RIF}}, declinati in step minimi e osservabili. Per discipline centrali (Italiano, Matematica) tendi al limite superiore. Se sopra è presente un "RIFERIMENTO UFFICIALE", usa QUEL testo come base degli obiettivi (è il testo vigente verbatim); altrimenti usa la tua conoscenza delle Indicazioni Nazionali.
Se la programmazione di riferimento è di una classe precedente rispetto a quella frequentata, àncora gli obiettivi ai traguardi di QUELLA classe (non di quella frequentata) e calibrali ulteriormente sul profilo di funzionamento dell'alunno. Parti sempre dal funzionamento osservato, non abbassare meccanicamente il livello.
⚠️ REGOLA ASSOLUTA — INIZIA OGNI OBIETTIVO DIRETTAMENTE CON IL VERBO. MAI scrivere "Lo studente", "L'alunno", "S." o qualsiasi soggetto prima del verbo.
Formato obbligatorio (una voce per riga con trattino):
— [VERBO al presente indicativo + oggetto, concreto e osservabile, max 10 parole]
Es. CORRETTI: "— Legge semplici testi narrativi con supporto visivo", "— Scrive parole di uso frequente in modo autonomo"
Es. VIETATI: "— Lo studente legge…", "— Lo studente S. legge…", "— L'alunno legge…" → inizia SEMPRE con il verbo
Es. VIETATI: "Leggere brevi testi…" (infinito), "Sviluppare la capacità di…" (generico)

2. STRATEGIE E METODOLOGIE
Elenca 3-5 strategie in forma sintetica. Scrivi SOLO il nome della tecnica, senza descrizioni né spiegazioni aggiuntive.
Formato obbligatorio — inizia con la riga "STRATEGIE E METODOLOGIE" (in maiuscolo):
STRATEGIE E METODOLOGIE
- [solo il nome della tecnica, max 5 parole]
Es. corretti: "Peer tutoring", "Task analysis", "Chunking", "Prompting e fading", "Rinforzo immediato"
Es. SBAGLIATI: "Task analysis: il docente scompone ogni attività in micro-passi" (non aggiungere spiegazioni)

3. STRUMENTI E MATERIALI
Elenca 3-5 strumenti compensativi e/o dispensativi realmente utilizzati.
Formato obbligatorio — inizia con la riga "STRUMENTI E MATERIALI" (in maiuscolo):
STRUMENTI E MATERIALI
- [nome specifico dello strumento]
Es. corretti: "Testo semplificato con immagini", "Calcolatrice scientifica", "Sintesi vocale", "Tabella delle tabelline"

4. MODALITÀ DI VERIFICA E VALUTAZIONE
Descrivi tipologia di verifiche e criteri valutativi. La valutazione scorpore l'errore formale causato dalla disabilità e misura il processo e l'impegno.
Testo continuo al presente indicativo, massimo 3 frasi. Sii specifico sulla tipologia di prova e sul criterio di misurazione.

Restituisci SOLO il testo della parte richiesta, senza spiegazioni introduttive o conclusive.`;

  // ─── Opzione C (solo secondaria II grado — Allegato A4 D.I. 182/2020) ──────
  // L'opzione C si seleziona quando gli obiettivi disciplinari sono nettamente
  // ridotti rispetto a quelli della classe e non è possibile una valutazione con
  // prove equipollenti: il percorso diventa "differenziato" (attestato di crediti
  // formativi, non diploma). Anche una sola disciplina in C rende l'intero
  // percorso differenziato (Linee Guida All. B + Allegato A4, sez. 8.3).
  const opzioniDisc = isSec2 ? ["A","B","C"] : ["A","B"];
  const labelOpz = { A: C.green, B: C.accent, C: C.orange };

  const customPromptC = `
Sei un esperto di inclusione scolastica, pedagogia speciale e didattica inclusiva con profonda conoscenza del D.I. 182/2020 (Allegato A4 — secondaria di II grado) e delle relative Linee Guida (Allegato B).

DATI DELLO STUDENTE:
- Ordine scolastico: Scuola secondaria di II grado
- Disciplina/insegnamento: {{DISCIPLINA}}
- Età: ${studente?.eta || "non specificata"} anni
- Diagnosi: ${buildDiagnosiContesto(studente)}${buildRiferimentoICD10(studente)}
${contestoPrec ? `\nPROFILO DI CONTESTO (profilo di funzionamento, barriere, facilitatori):\n${contestoPrec.slice(0, 800)}` : ""}

QUADRO NORMATIVO DI RIFERIMENTO (vincolante per i contenuti):
- Opzione "C" (Allegato A4, sez. 8.3): si seleziona quando gli obiettivi disciplinari progettati sono NETTAMENTE RIDOTTI rispetto a quelli della classe, per cui NON è possibile una valutazione con prove equipollenti. Il percorso diventa "differenziato": lo studente non consegue il diploma ma un attestato di credito formativo.
- Nel campo occorre definire quali sono gli obiettivi previsti, specificando i RISULTATI ATTESI e i relativi CRITERI DI VALUTAZIONE — riferiti alla progettazione personalizzata definita nel PEI, non a quella della classe.
- Se gli obiettivi disciplinari sono strettamente legati a quelli educativi (Sez. 5, dimensioni ICF), puoi richiamarli esplicitamente.

REGOLE RIGIDE DI STILE (obbligatorie per tutti i campi):
- Usa ESCLUSIVAMENTE il PRESENTE INDICATIVO. ECCEZIONE: negli OBIETTIVI ometti il soggetto (es. "Riconosce e nomina…", NON "Lo studente riconosce…")
- NON usare MAI l'infinito né il futuro
- BANDISCI formule generiche: "didattica personalizzata", "facilitazioni", "sarà seguito", "supporto adeguato", "attività mirate"
- Ogni obiettivo o criterio deve essere concreto, osservabile e misurabile
- Tono tecnico, pedagogico-clinico, professionale
- NON usare grassetto né titoli aggiuntivi rispetto a quelli richiesti

STRUTTURA DA GENERARE:

1. OBIETTIVI DISCIPLINARI DIFFERENZIATI
Definisci 3-5 obiettivi NETTAMENTE RIDOTTI rispetto ai traguardi della classe — non si tratta di una semplificazione degli stessi contenuti, ma di una progettazione autonoma e specifica per lo studente.
⚠️ REGOLA ASSOLUTA — INIZIA OGNI OBIETTIVO DIRETTAMENTE CON IL VERBO. MAI scrivere "Lo studente", "L'alunno" o altro soggetto prima del verbo.
Formato obbligatorio (una voce per riga con trattino):
— [VERBO al presente indicativo + oggetto, concreto e osservabile, max 12 parole]

2. RISULTATI ATTESI
Descrivi in 2-3 frasi cosa lo studente dimostra di saper fare al termine del percorso su questa disciplina, in coerenza con gli obiettivi sopra indicati e con il profilo di funzionamento.
Formato obbligatorio — inizia con la riga "RISULTATI ATTESI" (in maiuscolo).

3. CRITERI DI VALUTAZIONE
Descrivi come viene valutato il raggiungimento degli obiettivi (NON equipollente a quella della classe): tipologia di verifiche, criteri di misurazione, riferimenti alla progettazione personalizzata del PEI (richiama la Sez. 5 se pertinente).
Formato obbligatorio — inizia con la riga "CRITERI DI VALUTAZIONE" (in maiuscolo). Testo continuo, massimo 3 frasi.

Restituisci SOLO il testo della parte richiesta, senza spiegazioni introduttive o conclusive.`;

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
        <div style={{ display:"flex", alignItems:"center", gap:0 }}>
          <SezTitle icon="📖">{ordKey === "infanzia" ? "8. Interventi sul percorso curricolare" : "8. Curricolo"}</SezTitle>
          <GlossarioBtn voce="Curricolo" />
        </div>
        <Btn ghost small onClick={onChat} icon="💬">Assistente</Btn>
      </div>

      {/* Banner tipo percorso — secondaria2 */}
      {isSec2 && (() => {
        const hasDisciplinaC =
          voci.some((_, i) => f(`s8_${i}_scelta`) === "C") ||
          customVoci.some((_, j) => f(`s8_cx_${j}_scelta`) === "C");
        if (hasDisciplinaC && studente?.tipoPercorso !== "differenziato") {
          return (
            <div style={{ marginBottom:14, padding:"12px 16px", borderRadius:10,
              background: hex(C.orange, 0.08),
              border: `1.5px solid ${hex(C.orange, 0.35)}`,
              display:"flex", alignItems:"flex-start", gap:10 }}>
              <span style={{ fontSize:18, flexShrink:0 }}>⚠️</span>
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:"#7a4f00", marginBottom:3 }}>
                  Hai selezionato l'opzione C su almeno una disciplina
                </div>
                <div style={{ fontSize:11, color:"#7a4f00" }}>
                  Secondo l'Allegato A4, anche una sola disciplina con obiettivi differenziati e prove non equipollenti rende <strong>obbligatoriamente differenziato l'intero percorso</strong> (l'alunno conseguirà l'attestato di credito formativo, non il diploma). Aggiorna il campo "Tipo di percorso" nel profilo dello studente (Sez. 1) per allinearlo a questa scelta.
                </div>
              </div>
            </div>
          );
        }
        return null;
      })()}

      {studente?.ordine === "secondaria2" && (
        <div style={{ marginBottom:14, padding:"12px 16px", borderRadius:10,
          background: studente?.tipoPercorso === "differenziato" ? "#fef3e2" : hex(C.accent, 0.06),
          border: `1.5px solid ${studente?.tipoPercorso === "differenziato" ? "#f0a500" : hex(C.accent, 0.25)}`,
          display:"flex", alignItems:"flex-start", gap:10 }}>
          <span style={{ fontSize:18, flexShrink:0 }}>{studente?.tipoPercorso === "differenziato" ? "📋" : "🎓"}</span>
          <div>
            <div style={{ fontSize:12, fontWeight:700, color: studente?.tipoPercorso === "differenziato" ? "#7a4f00" : C.accent, marginBottom:3 }}>
              {studente?.tipoPercorso === "differenziato"
                ? "Percorso differenziato — obiettivi non riconducibili ai programmi ministeriali"
                : "Percorso riconducibile — obiettivi minimi ai programmi ministeriali"}
            </div>
            <div style={{ fontSize:11, color: studente?.tipoPercorso === "differenziato" ? "#7a4f00" : C.muted }}>
              {studente?.tipoPercorso === "differenziato"
                ? "Gli obiettivi per ogni disciplina devono essere chiaramente differenziati rispetto alla programmazione di classe. Lo studente conseguirà un attestato di crediti, non il diploma."
                : "Gli obiettivi possono essere ridotti o semplificati rispetto alla classe, ma restano riconducibili ai programmi ministeriali. Lo studente può conseguire il diploma."}
            </div>
          </div>
        </div>
      )}

      {ordKey === "infanzia" ? (
        <InfoBox color={C.accent} icon="🧸">
          Compila <strong>"8.1 Interventi educativi, strategie, strumenti nei diversi campi di esperienza"</strong> in modo integrato (Allegato A1 D.I. 182/2020).
          Alla scuola dell'infanzia non esistono discipline né programmazioni A/B: gli interventi sono individualizzati nella cornice della pedagogia infantile.
        </InfoBox>
      ) : isSec2 ? (
        <InfoBox color={C.accent} icon="📌">
          Per ogni disciplina scegli <strong>A</strong> (segue la programmazione di classe), <strong>B</strong> (programmazione personalizzata con obiettivi minimi e prove equipollenti — porta al diploma) oppure <strong>C</strong> (percorso differenziato: obiettivi nettamente ridotti, prove non equipollenti — porta all'attestato di crediti formativi).
          Se scegli B, compila obiettivi, strategie/metodologie e strumenti coerenti con il profilo (Sez.2) e le barriere/facilitatori (Sez.6).
          Se scegli C, ricorda che <strong>anche una sola disciplina differenziata rende differenziato l'intero percorso</strong> (Allegato A4): aggiorna il "Tipo di percorso" dello studente in Sez.1. Nota: dal D.I. 153/2023 l'esonero totale da una disciplina non è più previsto — vanno comunque definiti obiettivi, anche se nettamente ridotti.
        </InfoBox>
      ) : (
        <InfoBox color={C.accent} icon="📌">
          Per ogni disciplina scegli <strong>A</strong> (segue la programmazione di classe) o <strong>B</strong> (programmazione personalizzata).
          Se scegli B, compila obiettivi, strategie/metodologie e strumenti coerenti con il profilo (Sez.2) e le barriere/facilitatori (Sez.6).
        </InfoBox>
      )}

      {contestoPrec && (
        <div style={{ marginBottom:14, padding:"8px 12px",
          background:C.greenDim, border:`1px solid ${hex(C.green,0.3)}`,
          borderRadius:8, fontSize:11, color:C.green }}>
          ✓ L'AI ha accesso al profilo di funzionamento e alle barriere/facilitatori delle sezioni precedenti
        </div>
      )}

      <Divider />
      <FieldAI
        label={ordKey === "infanzia"
          ? "8.1 Interventi educativi, strategie, strumenti nei diversi campi di esperienza"
          : "8.1 Interventi educativo-didattici, strategie, strumenti nelle diverse discipline"}
        sub={ordKey === "infanzia"
          ? "Modalità di sostegno educativo e ulteriori interventi di inclusione (Allegato A1 D.I. 182/2020). Descrivi in modo integrato come si declinano interventi, strategie e strumenti attraverso i cinque campi di esperienza: gioco simbolico, routine quotidiane, autonomie di base, interazione tra pari, esperienze di laboratorio."
          : "Descrivi le modalità di sostegno educativo didattico e gli interventi di inclusione"}
        campoId="s8_1"
        value={f("s8_1")}
        onChange={e => s("s8_1", e.target.value)}
        placeholder={ordKey === "infanzia"
          ? "Descrivi gli interventi educativi, le strategie e gli strumenti utilizzati nei diversi campi di esperienza, attraversando gioco, routine, laboratori…"
          : "Descrivi gli interventi educativo-didattici, le strategie e gli strumenti utilizzati nelle diverse discipline…"}
        rows={5}
        studente={studente}
        sezioneLabel={sl}
        contestoGlobale={contestoPrec}
        customPrompt={`Sei un esperto di inclusione scolastica e didattica inclusiva che compila la Sezione 8.1 "Modalità di sostegno educativo didattico e ulteriori interventi di inclusione" del PEI (D.M. 182/2020).
${contestoPrec ? `\nContesto dello studente:\n${contestoPrec.slice(0, 1000)}\n` : ""}
Descrivi la cornice organizzativa e metodologica dell'intervento educativo. Il testo deve coprire:
- Gestione della compresenza tra docente curricolare e docente di sostegno (modalità, discipline, frequenza)
- Metodologie inclusive trasversali (es. Peer Tutoring, Cooperative Learning, apprendimento per stazioni)
- Organizzazione dell'ambiente di apprendimento: disposizione degli spazi, materiali strutturati, supporti visivi
- Gestione dei tempi, delle routine e delle pause (soprattutto se presente ADHD o difficoltà attentive)
${ordKey === "infanzia" ? "\nATTENZIONE — Scuola dell'Infanzia: non esistono discipline. Focalizza su routine quotidiane, gioco simbolico, autonomie di base e interazione tra pari. Non menzionare voti o verifiche scritte." : ""}
${ordKey === "secondaria2" ? `\nATTENZIONE — Secondaria II Grado: percorso ${studente?.tipoPercorso === "differenziato" ? "differenziato (Attestato di credito formativo)" : "con prove equipollenti (porta al Diploma)"}. Includi il raccordo con i PCTO e i facilitatori nel contesto aziendale.` : ""}

Regole:
- Usa ESCLUSIVAMENTE il PRESENTE INDICATIVO ("I docenti utilizzano…", "Si strutturano…")
- NON usare l'infinito né il futuro
- BANDISCI formule generiche: "didattica personalizzata", "facilitazioni", "verrà seguito", "supporto adeguato"
- Ogni azione deve essere concreta, visibile e attribuibile a un referente preciso
- Testo continuo in 2-3 paragrafi, tra 120 e 200 parole
- Tono tecnico, pedagogico-clinico, professionale
- NON usare grassetto né titoli interni
- Per ogni metodologia o tecnica specialistica, usa prima il linguaggio descrittivo e poi il termine tecnico tra parentesi (es. "pause cognitive brevi (brain break)", "apprendimento tra pari (peer tutoring)", "lavoro in piccoli gruppi cooperativi (cooperative learning)")`}
      />
      <Divider />

      {ordKey !== "infanzia" && (
      <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
        {classeValida && opzioniRif.length >= 2 && (
          <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap",
            padding:"10px 12px", borderRadius:10,
            background:hex(C.accent,0.05), border:`1px solid ${hex(C.accent,0.25)}` }}>
            <span style={{ fontSize:12, fontWeight:700, color:C.text }}>
              Programmazione di riferimento (predefinita per tutte le discipline):
            </span>
            <select value={(rif => rifToken(rif.ordine, rif.classe))(rifVal("s8_classeRif_default"))}
              onChange={e => s("s8_classeRif_default", e.target.value)}
              style={{ fontSize:12, fontFamily:"'Inter',sans-serif", padding:"4px 8px",
                borderRadius:6, border:`1px solid ${C.border}`, background:C.surface, color:C.text, cursor:"pointer" }}>
              {renderOpzioniRif()}
            </select>
            <span style={{ fontSize:11, color:C.muted }}>Puoi modificarla per singola disciplina (solo percorso B).</span>
          </div>
        )}
        {classeValida && opzioniRif.length >= 2 && (
          <InfoBox color={C.accent} icon="ℹ️">
            In assenza di indicazioni si applica la <strong>programmazione della classe attuale</strong>. Riferirsi a una classe inferiore — anche dell'<strong>ordine precedente</strong> (es. una prima media che riprende la primaria, una prima primaria che riprende i campi di esperienza dell'infanzia) — è una personalizzazione (percorso B): è una scelta del <strong>GLO</strong>, da deliberare sulla base del Profilo di Funzionamento (Sez. 5), non un abbassamento automatico.
          </InfoBox>
        )}
        {voci.map((v, i) => {
          const scelta = f(`s8_${i}_scelta`) || "A";
          const colS = labelOpz[scelta] || C.green;
          const promptB = (suffix, withKB = false) => customPromptB
            .replace(/\{\{DISCIPLINA\}\}/g, v)
            .replace(/\{\{CLASSE_RIF\}\}/g, classeRifPhrase(`s8_${i}_classeRif`))
            .replace(/\{\{TRAGUARDI_KB\}\}/g, withKB ? kbBlock(v, `s8_${i}_classeRif`) : "") + suffix;
          return (
            <div key={i} style={{
              border:`1px solid ${scelta === "A" ? C.border : hex(colS,0.3)}`,
              borderRadius:10, overflow:"hidden",
              background: scelta === "A" ? C.surface : hex(colS,0.02) }}>

              <div style={{ padding:"10px 14px",
                background: scelta === "A" ? C.bg : hex(colS,0.06),
                borderBottom:`1px solid ${scelta === "A" ? C.border : hex(colS,0.2)}`,
                display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{v}</div>
                <div style={{ display:"flex", gap:6 }}>
                  {opzioniDisc.map(opt => (
                    <button key={opt} onClick={() => s(`s8_${i}_scelta`, opt)}
                      style={{ padding:"4px 16px", borderRadius:6, fontWeight:700,
                        fontSize:12, fontFamily:"'Inter',sans-serif", cursor:"pointer",
                        border:`2px solid ${scelta === opt ? labelOpz[opt] : C.border}`,
                        background: scelta === opt ? hex(labelOpz[opt],0.12) : "transparent",
                        color: scelta === opt ? labelOpz[opt] : C.muted,
                        transition:"all .15s" }}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ padding:"6px 14px", fontSize:11,
                color: colS,
                background: hex(colS,0.04),
                borderBottom:`1px solid ${hex(colS,0.15)}` }}>
                {scelta === "A"
                  ? "✓ A — Segue la progettazione didattica della classe · stessi criteri di valutazione"
                  : scelta === "B"
                  ? "✎ B — Programmazione personalizzata con prove equipollenti · compila obiettivi, strategie e strumenti · valida per il diploma"
                  : "⚠ C — Percorso differenziato, obiettivi nettamente ridotti · prove NON equipollenti · porta all'attestato di crediti formativi (rende differenziato l'intero percorso)"}
              </div>

              {scelta === "B" && (
                <div style={{ padding:14, display:"flex", flexDirection:"column", gap:12 }}>
                  {renderClasseRif(`s8_${i}_classeRif`)}
                  <FieldAI
                    label={`Obiettivi di apprendimento — ${v}`}
                    sub="Obiettivi a breve/medio termine, semplificati o ridotti rispetto alla classe"
                    campoId={`s8_${i}_ob`} value={f(`s8_${i}_ob`)}
                    onChange={e => s(`s8_${i}_ob`, e.target.value)}
                    placeholder={`Es. Leggere e comprendere testi semplici con supporto visivo…`}
                    rows={3} studente={studente} sezioneLabel={`${sl} — ${v} — Obiettivi`}
                    contestoGlobale={contestoPrec}
                    customPrompt={promptB("\n\nRestituisci SOLO la parte relativa agli OBIETTIVI, senza titoli introduttivi.", true)}
                  />
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                    <FieldAI
                      label="Strategie e Metodologie"
                      sub="Modalità didattiche: apprendimento cooperativo, tutoring, semplificazione, tempi aggiuntivi…"
                      campoId={`s8_${i}_strat`} value={f(`s8_${i}_strat`)}
                      onChange={e => s(`s8_${i}_strat`, e.target.value)}
                      placeholder="Es. Uso di mappe concettuali, apprendimento cooperativo, tempi più lunghi…"
                      rows={3} studente={studente} sezioneLabel={`${sl} — ${v} — Strategie`}
                      contestoGlobale={contestoPrec}
                      customPrompt={promptB("\n\nRestituisci SOLO la parte relativa a STRATEGIE E METODOLOGIE (iniziando con la riga 'STRATEGIE E METODOLOGIE').")}
                    />
                    <FieldAI
                      label="Strumenti e Materiali"
                      sub="Strumenti compensativi, ausili tecnologici, materiali semplificati"
                      campoId={`s8_${i}_strum`} value={f(`s8_${i}_strum`)}
                      onChange={e => s(`s8_${i}_strum`, e.target.value)}
                      placeholder="Es. PC con sintesi vocale, calcolatrice, schede semplificate…"
                      rows={3} studente={studente} sezioneLabel={`${sl} — ${v} — Strumenti`}
                      contestoGlobale={contestoPrec}
                      customPrompt={promptB("\n\nRestituisci SOLO la parte relativa a STRUMENTI E MATERIALI (iniziando con la riga 'STRUMENTI E MATERIALI').")}
                    />
                  </div>
                  <FieldAI
                    label="Modalità di verifica e valutazione"
                    sub="Come viene verificato il raggiungimento degli obiettivi personalizzati"
                    campoId={`s8_${i}_verifica`} value={f(`s8_${i}_verifica`)}
                    onChange={e => s(`s8_${i}_verifica`, e.target.value)}
                    placeholder="Es. Prove orali, verifiche semplificate con supporto visivo, portfolio…"
                    rows={2} studente={studente} sezioneLabel={`${sl} — ${v} — Verifica`}
                    contestoGlobale={contestoPrec}
                    customPrompt={promptB("\n\nRestituisci SOLO la parte relativa a MODALITÀ DI VERIFICA E VALUTAZIONE, senza titoli introduttivi.")}
                  />
                </div>
              )}

              {scelta === "C" && (
                <div style={{ padding:14, display:"flex", flexDirection:"column", gap:12 }}>
                  <InfoBox color={C.orange} icon="⚠️">
                    Dal D.I. 153/2023 (correttivo al D.I. 182/2020) l'<strong>esonero totale</strong> da una disciplina non è più previsto: per ogni disciplina vanno sempre progettati obiettivi disciplinari, anche se nettamente ridotti rispetto alla classe.
                  </InfoBox>
                  <FieldAI
                    label={`Obiettivi, risultati attesi e criteri di valutazione (percorso differenziato) — ${v}`}
                    sub="Obiettivi nettamente ridotti rispetto alla classe: specifica anche i risultati attesi e i criteri di valutazione (puoi richiamare gli obiettivi educativi della Sez. 5, se collegati)"
                    campoId={`s8_${i}_c_obiettivi`} value={f(`s8_${i}_c_obiettivi`)}
                    onChange={e => s(`s8_${i}_c_obiettivi`, e.target.value)}
                    placeholder="Es. Riconosce e nomina i numeri naturali entro il 100… RISULTATI ATTESI: … CRITERI DI VALUTAZIONE: …"
                    rows={5} studente={studente} sezioneLabel={`${sl} — ${v} — Obiettivi differenziati`}
                    contestoGlobale={contestoPrec}
                    customPrompt={customPromptC.replace(/\{\{DISCIPLINA\}\}/g, v)}
                  />
                </div>
              )}
            </div>
          );
        })}

        {/* ─── Discipline personalizzate ──────────────────────────────────────── */}
        {customVoci.map((v, j) => {
          const scelta = f(`s8_cx_${j}_scelta`) || "B"; // default B: è una disciplina aggiunta apposta
          const colSx = labelOpz[scelta] || C.accent;
          const promptB = (suffix, withKB = false) => customPromptB
            .replace(/\{\{DISCIPLINA\}\}/g, v)
            .replace(/\{\{CLASSE_RIF\}\}/g, classeRifPhrase(`s8_cx_${j}_classeRif`))
            .replace(/\{\{TRAGUARDI_KB\}\}/g, withKB ? kbBlock(v, `s8_cx_${j}_classeRif`) : "") + suffix;
          return (
            <div key={`cx_${j}`} style={{
              border:`2px dashed ${hex(colSx,0.4)}`,
              borderRadius:10, overflow:"hidden",
              background: hex(colSx,0.02) }}>

              <div style={{ padding:"10px 14px",
                background: hex(colSx,0.06),
                borderBottom:`1px solid ${hex(colSx,0.2)}`,
                display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, flex:1 }}>
                  <span style={{ fontSize:10, fontWeight:700, color:C.muted,
                    background:C.bg, border:`1px solid ${C.border}`,
                    borderRadius:4, padding:"1px 6px", whiteSpace:"nowrap" }}>
                    PERSONALIZZATA
                  </span>
                  <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{v}</div>
                </div>
                <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                  {opzioniDisc.map(opt => (
                    <button key={opt} onClick={() => s(`s8_cx_${j}_scelta`, opt)}
                      style={{ padding:"4px 16px", borderRadius:6, fontWeight:700,
                        fontSize:12, fontFamily:"'Inter',sans-serif", cursor:"pointer",
                        border:`2px solid ${scelta === opt ? labelOpz[opt] : C.border}`,
                        background: scelta === opt ? hex(labelOpz[opt],0.12) : "transparent",
                        color: scelta === opt ? labelOpz[opt] : C.muted,
                        transition:"all .15s" }}>
                      {opt}
                    </button>
                  ))}
                  <button onClick={() => rimuoviDisciplina(j)}
                    title="Rimuovi disciplina"
                    style={{ marginLeft:4, width:26, height:26, borderRadius:"50%",
                      border:`1px solid ${C.border}`, background:"transparent",
                      cursor:"pointer", color:C.muted, fontSize:14, lineHeight:1,
                      display:"flex", alignItems:"center", justifyContent:"center" }}>
                    ×
                  </button>
                </div>
              </div>

              <div style={{ padding:"6px 14px", fontSize:11,
                color: colSx,
                background: hex(colSx,0.04),
                borderBottom:`1px solid ${hex(colSx,0.15)}` }}>
                {scelta === "A"
                  ? "✓ A — Segue la progettazione didattica della classe · stessi criteri di valutazione"
                  : scelta === "B"
                  ? "✎ B — Programmazione personalizzata con prove equipollenti · compila obiettivi, strategie e strumenti · valida per il diploma"
                  : "⚠ C — Percorso differenziato, obiettivi nettamente ridotti · prove NON equipollenti · porta all'attestato di crediti formativi (rende differenziato l'intero percorso)"}
              </div>

              {scelta === "B" && (
                <div style={{ padding:14, display:"flex", flexDirection:"column", gap:12 }}>
                  {renderClasseRif(`s8_cx_${j}_classeRif`)}
                  <FieldAI
                    label={`Obiettivi di apprendimento — ${v}`}
                    sub="Obiettivi a breve/medio termine, semplificati o ridotti rispetto alla classe"
                    campoId={`s8_cx_${j}_ob`} value={f(`s8_cx_${j}_ob`)}
                    onChange={e => s(`s8_cx_${j}_ob`, e.target.value)}
                    placeholder="Es. Acquisire le competenze base della disciplina con supporto…"
                    rows={3} studente={studente} sezioneLabel={`${sl} — ${v} — Obiettivi`}
                    contestoGlobale={contestoPrec}
                    customPrompt={promptB("\n\nRestituisci SOLO la parte relativa agli OBIETTIVI, senza titoli introduttivi.", true)}
                  />
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                    <FieldAI
                      label="Strategie e Metodologie"
                      sub="Modalità didattiche: apprendimento cooperativo, tutoring, semplificazione…"
                      campoId={`s8_cx_${j}_strat`} value={f(`s8_cx_${j}_strat`)}
                      onChange={e => s(`s8_cx_${j}_strat`, e.target.value)}
                      placeholder="Es. Uso di schemi, lavoro in coppia, tempi aggiuntivi…"
                      rows={3} studente={studente} sezioneLabel={`${sl} — ${v} — Strategie`}
                      contestoGlobale={contestoPrec}
                      customPrompt={promptB("\n\nRestituisci SOLO la parte relativa a STRATEGIE E METODOLOGIE (iniziando con la riga 'STRATEGIE E METODOLOGIE').")}
                    />
                    <FieldAI
                      label="Strumenti e Materiali"
                      sub="Strumenti compensativi, ausili tecnologici, materiali semplificati"
                      campoId={`s8_cx_${j}_strum`} value={f(`s8_cx_${j}_strum`)}
                      onChange={e => s(`s8_cx_${j}_strum`, e.target.value)}
                      placeholder="Es. PC con sintesi vocale, schede semplificate, calcolatrice…"
                      rows={3} studente={studente} sezioneLabel={`${sl} — ${v} — Strumenti`}
                      contestoGlobale={contestoPrec}
                      customPrompt={promptB("\n\nRestituisci SOLO la parte relativa a STRUMENTI E MATERIALI (iniziando con la riga 'STRUMENTI E MATERIALI').")}
                    />
                  </div>
                  <FieldAI
                    label="Modalità di verifica e valutazione"
                    sub="Come viene verificato il raggiungimento degli obiettivi personalizzati"
                    campoId={`s8_cx_${j}_verifica`} value={f(`s8_cx_${j}_verifica`)}
                    onChange={e => s(`s8_cx_${j}_verifica`, e.target.value)}
                    placeholder="Es. Prove orali, verifiche semplificate, osservazione…"
                    rows={2} studente={studente} sezioneLabel={`${sl} — ${v} — Verifica`}
                    contestoGlobale={contestoPrec}
                    customPrompt={promptB("\n\nRestituisci SOLO la parte relativa a MODALITÀ DI VERIFICA E VALUTAZIONE, senza titoli introduttivi.")}
                  />
                </div>
              )}

              {scelta === "C" && (
                <div style={{ padding:14, display:"flex", flexDirection:"column", gap:12 }}>
                  <InfoBox color={C.orange} icon="⚠️">
                    Dal D.I. 153/2023 (correttivo al D.I. 182/2020) l'<strong>esonero totale</strong> da una disciplina non è più previsto: per ogni disciplina vanno sempre progettati obiettivi disciplinari, anche se nettamente ridotti rispetto alla classe.
                  </InfoBox>
                  <FieldAI
                    label={`Obiettivi, risultati attesi e criteri di valutazione (percorso differenziato) — ${v}`}
                    sub="Obiettivi nettamente ridotti rispetto alla classe: specifica anche i risultati attesi e i criteri di valutazione (puoi richiamare gli obiettivi educativi della Sez. 5, se collegati)"
                    campoId={`s8_cx_${j}_c_obiettivi`} value={f(`s8_cx_${j}_c_obiettivi`)}
                    onChange={e => s(`s8_cx_${j}_c_obiettivi`, e.target.value)}
                    placeholder="Es. Riconosce e nomina i numeri naturali entro il 100… RISULTATI ATTESI: … CRITERI DI VALUTAZIONE: …"
                    rows={5} studente={studente} sezioneLabel={`${sl} — ${v} — Obiettivi differenziati`}
                    contestoGlobale={contestoPrec}
                    customPrompt={customPromptC.replace(/\{\{DISCIPLINA\}\}/g, v)}
                  />
                </div>
              )}
            </div>
          );
        })}

        {/* ─── Aggiungi disciplina personalizzata ─────────────────────────────── */}
        <div style={{
          border:`1px dashed ${C.border}`, borderRadius:10,
          padding:"12px 14px", background:C.bg,
          display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:18, color:C.muted }}>➕</span>
          <input
            value={nuovaDisciplina}
            onChange={e => setNuovaDisciplina(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") aggiungiDisciplina(); }}
            placeholder="Aggiungi disciplina di indirizzo o altra materia… (es. Diritto, Spagnolo, Economia aziendale)"
            style={{
              flex:1, border:"none", background:"transparent", outline:"none",
              fontSize:13, color:C.text, fontFamily:"'Inter',sans-serif",
            }}
          />
          <button onClick={aggiungiDisciplina}
            disabled={!nuovaDisciplina.trim()}
            style={{
              padding:"6px 16px", borderRadius:8, fontSize:13, fontWeight:700,
              fontFamily:"'Inter',sans-serif", cursor: nuovaDisciplina.trim() ? "pointer" : "default",
              border:`2px solid ${nuovaDisciplina.trim() ? C.accent : C.border}`,
              background: nuovaDisciplina.trim() ? C.accentDim : "transparent",
              color: nuovaDisciplina.trim() ? C.accent : C.muted,
              transition:"all .15s",
            }}>
            Aggiungi
          </button>
        </div>

      </div>
      )}

      <Divider />

      {/* ── 8.3 PCTO — Percorsi per le Competenze Trasversali e l'Orientamento ── */}
      {ordKey === "secondaria2" && (
        <div>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
            <span style={{ fontSize:13, fontWeight:700, color:C.text }}>
              8.3 Percorsi per le Competenze Trasversali e l'Orientamento (PCTO)
            </span>
            <span style={{ fontSize:10, color:C.muted, fontWeight:600,
              background:C.bg, border:`1px solid ${C.border}`,
              borderRadius:4, padding:"1px 6px", whiteSpace:"nowrap" }}>
              classi III · IV · V
            </span>
          </div>

          <InfoBox color={C.accent} icon="🏢">
            Obbligatorio per le classi III, IV e V (L. 145/2018, art. 1 cc. 784-787; D.Lgs. 66/2017 art. 7 comma 2).{" "}
            Nella <strong>prima redazione</strong> (entro ottobre) alcune informazioni possono non essere ancora disponibili: il campo si completa nelle revisioni successive.{" "}
            La <strong>tipologia C</strong> va prevista solo in casi eccezionali (es. istruzione domiciliare).
          </InfoBox>

          {classeStr && /^[12]/.test(classeStr) && (
            <div style={{ marginBottom:12, padding:"10px 14px", borderRadius:8,
              background:"#fffbea", border:"1.5px solid #f0c040",
              fontSize:12, color:"#7a5c00", display:"flex", alignItems:"flex-start", gap:8 }}>
              <span>⚠️</span>
              <span>Il PCTO è previsto a partire dalla classe III — questa sezione sarà compilata nei prossimi anni scolastici.</span>
            </div>
          )}

          {/* Tipologia percorso */}
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:6 }}>
              Tipologia di percorso
            </div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {[
                { id:"A", label:"A — Aziendale" },
                { id:"B", label:"B — Scolastico" },
                { id:"C", label:"C — Altra tipologia" },
              ].map(({ id, label }) => (
                <button key={id} onClick={() => s("s8_pcto_tipologia", id)}
                  style={{
                    padding:"6px 16px", borderRadius:8, fontSize:12, fontWeight:700,
                    fontFamily:"'Inter',sans-serif", cursor:"pointer", transition:"all .15s",
                    border:`2px solid ${f("s8_pcto_tipologia") === id ? C.accent : C.border}`,
                    background: f("s8_pcto_tipologia") === id ? C.accentDim : "transparent",
                    color: f("s8_pcto_tipologia") === id ? C.accent : C.muted,
                  }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Dettagli organizzativi — condizionali per tipologia */}
          {f("s8_pcto_tipologia") && (
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:12, fontWeight:600, color:C.muted, marginBottom:4 }}>
                {f("s8_pcto_tipologia") === "A"
                  ? "Convenzione, tutor e organizzazione temporale"
                  : f("s8_pcto_tipologia") === "B"
                  ? "Figure coinvolte e organizzazione temporale"
                  : "Descrizione del percorso ed ente locale coinvolto"}
              </div>
              <textarea
                value={f("s8_pcto_organizzazione")}
                onChange={e => s("s8_pcto_organizzazione", e.target.value)}
                rows={f("s8_pcto_tipologia") === "A" ? 6 : 4}
                placeholder={
                  f("s8_pcto_tipologia") === "A"
                    ? "Nome e tipologia azienda: \nTutor aziendale esterno: \nTutor scolastico interno (se diverso dal docente di sostegno): \nDurata e organizzazione temporale del percorso: \nModalità di raggiungimento della struttura e mezzi di trasporto: "
                    : f("s8_pcto_tipologia") === "B"
                    ? "Figure coinvolte e loro compiti: \nDurata e organizzazione temporale del percorso: "
                    : "Descrizione dell'altra tipologia di percorso: \nEnte locale coinvolto (per raccordo con Progetto Individuale, se previsto): "
                }
                style={{
                  width:"100%", boxSizing:"border-box",
                  border:`1px solid ${C.border}`, borderRadius:8,
                  padding:"10px 12px", fontSize:12,
                  fontFamily:"'Inter',sans-serif", color:C.text,
                  resize:"vertical", outline:"none", background:C.surface,
                  lineHeight:1.5,
                }}
              />
            </div>
          )}

          <FieldAI
            label="Progettazione del percorso — Obiettivi di competenza e contesto"
            sub="Obiettivi di competenza del progetto formativo; tipologia del contesto con barriere e facilitatori nello specifico ambiente del percorso (D.Lgs. 66/2017; Linee Guida All. B D.I. 182/2020)"
            campoId="s8_pcto_progettazione"
            value={f("s8_pcto_progettazione")}
            onChange={e => s("s8_pcto_progettazione", e.target.value)}
            placeholder={"OBIETTIVI DI COMPETENZA\n— …\n— …\n\nCONTESTO — BARRIERE E FACILITATORI\n…"}
            rows={6}
            studente={studente}
            sezioneLabel={sl}
            contestoGlobale={contestoPrec}
            customPrompt={`Sei un docente coordinatore esperto di inclusione e transizione scuola-lavoro che compila la "Progettazione del percorso PCTO (Sez. 8.3)" del PEI per la scuola secondaria di II grado (D.Lgs. 66/2017, L. 145/2018, Linee Guida Allegato B D.I. 182/2020).

Tipologia percorso: ${f("s8_pcto_tipologia") === "A" ? "A — Aziendale" : f("s8_pcto_tipologia") === "B" ? "B — Scolastico" : f("s8_pcto_tipologia") === "C" ? "C — Altra tipologia" : "non ancora definita"}
${contestoPrec ? `\nProfilo dello studente/essa (funzionamento, barriere, facilitatori):\n${contestoPrec.slice(0,600)}` : ""}
${f("s8_pcto_organizzazione") ? `\nDati organizzativi del percorso:\n${f("s8_pcto_organizzazione").slice(0,400)}` : ""}

Genera il testo in DUE blocchi distinti, separati da una riga vuota:

OBIETTIVI DI COMPETENZA DEL PROGETTO FORMATIVO
Definisci 3-5 obiettivi di competenza coerenti con il profilo dello studente/essa. Cornice pedagogica di riferimento (Linee Guida Allegato B D.I. 182/2020): accrescere le chances di inserimento lavorativo adeguato; collegare interessi, motivazioni e competenze dello studente/essa con i requisiti del contesto; accrescere autonomia, autopercezione e sicurezza; creare una situazione vincente per lo studente/essa e i colleghi di lavoro/classe.
Formato obbligatorio — un obiettivo per riga con trattino, al presente indicativo omettendo il soggetto (inizia col verbo), concreto e osservabile, max 12 parole:
— [VERBO + oggetto]

CONTESTO — BARRIERE E FACILITATORI
Descrivi le caratteristiche dello specifico contesto (${f("s8_pcto_tipologia") === "A" ? "aziendale" : f("s8_pcto_tipologia") === "B" ? "scolastico simulato" : "del percorso"}) con le principali barriere e i facilitatori per lo studente/essa in quell'ambiente. Testo continuo, 3-5 frasi. Cita elementi concreti del profilo (diagnosi, punti di forza, limitazioni) riferiti al contesto specifico.

Regole: presente indicativo, NON usare grassetto né titoli interni oltre ai due richiesti, tono tecnico e professionale.`}
          />

          <FieldAI
            label="Tipologie di attività, monitoraggio e rete dei servizi territoriali"
            sub="Attività previste con modalità/fasi; indicatori di monitoraggio e valutazione; raccordo con servizi per l'inserimento lavorativo o la prosecuzione degli studi"
            campoId="s8_pcto_attivita"
            value={f("s8_pcto_attivita")}
            onChange={e => s("s8_pcto_attivita", e.target.value)}
            placeholder={"TIPOLOGIE DI ATTIVITÀ E MODALITÀ/FASI\n— …\n\nMONITORAGGIO E VALUTAZIONE\n…\n\nRETE DI COLLABORAZIONE DEI SERVIZI TERRITORIALI\n…"}
            rows={7}
            studente={studente}
            sezioneLabel={sl}
            contestoGlobale={contestoPrec}
            customPrompt={`Sei un docente esperto di inclusione e PCTO che compila la sezione "Attività, monitoraggio e rete (Sez. 8.3)" del PEI per la scuola secondaria di II grado (D.Lgs. 66/2017, Linee Guida Allegato B D.I. 182/2020).

${contestoPrec ? `Profilo dello studente/essa:\n${contestoPrec.slice(0,500)}\n` : ""}
${f("s8_pcto_organizzazione") ? `Dati organizzativi:\n${f("s8_pcto_organizzazione").slice(0,300)}\n` : ""}
${f("s8_pcto_progettazione") ? `Obiettivi e contesto già definiti:\n${f("s8_pcto_progettazione").slice(0,400)}\n` : ""}

Genera il testo in TRE blocchi distinti, separati da una riga vuota:

TIPOLOGIE DI ATTIVITÀ E MODALITÀ/FASI
Descrivi 3-5 tipologie di attività previste (es: incontro con esperti, visite aziendali, impresa formativa simulata, project work in e con l'impresa, tirocini, progetti di imprenditorialità, ecc.) con le loro modalità e fasi di svolgimento.
Formato: "— [attività]: [modalità/fase concreta]"

MONITORAGGIO E VALUTAZIONE
Descrivi gli indicatori per il monitoraggio in itinere e per la verifica finale, con gli esiti previsti rispetto agli obiettivi di competenza. Testo continuo, 3-4 frasi.

RETE DI COLLABORAZIONE DEI SERVIZI TERRITORIALI
Descrivi gli interventi coordinati con servizi per l'inserimento lavorativo (es: CPI, cooperative sociali, enti di tutela) per la prosecuzione degli studi o l'inserimento nel mondo del lavoro. Se è stato definito un Progetto Individuale, raccorda con quanto indicato in Sez. 3 del PEI. Testo continuo, 2-3 frasi.

Regole: presente indicativo, no grassetto, no titoli interni aggiuntivi, tono tecnico e professionale.`}
          />

          {/* Osservazioni studente/essa — nessuna AI */}
          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:2 }}>
              Osservazioni dello Studente/essa
            </div>
            <div style={{ fontSize:11, color:C.muted, marginBottom:6 }}>
              Voce diretta dello studente/essa sul percorso PCTO — compilare con le parole dello studente/essa, non generare con AI
            </div>
            <textarea
              value={f("s8_pcto_osservazioni_studente")}
              onChange={e => s("s8_pcto_osservazioni_studente", e.target.value)}
              rows={3}
              placeholder="Interessi, aspettative, preferenze e osservazioni dello studente/essa sul percorso PCTO…"
              style={{
                width:"100%", boxSizing:"border-box",
                border:`1px solid ${C.border}`, borderRadius:8,
                padding:"10px 12px", fontSize:12,
                fontFamily:"'Inter',sans-serif", color:C.text,
                resize:"vertical", outline:"none", background:C.surface,
                lineHeight:1.5,
              }}
            />
          </div>

          <FieldAI
            label="Revisione a seguito di Verifica intermedia — PCTO"
            sub="Da compilare al GLO intermedio (gen-feb): modifiche alla progettazione e alle attività del percorso PCTO"
            campoId="s8_pcto_revisione_intermedia"
            value={f("s8_pcto_revisione_intermedia")}
            onChange={e => s("s8_pcto_revisione_intermedia", e.target.value)}
            placeholder="A seguito della verifica intermedia, la progettazione PCTO viene confermata / modificata nei seguenti aspetti…"
            rows={4}
            studente={studente}
            sezioneLabel={sl}
            contestoGlobale={contestoPrec}
            customPrompt={buildRevisioneIntermediaPrompt({
              sezN: "8.3",
              sezLabel: "PCTO — Percorsi per le Competenze Trasversali e l'Orientamento",
              natura: "correttivo",
              contestoSpecifico: `Il percorso PCTO (tipologia: ${f("s8_pcto_tipologia") === "A" ? "aziendale" : f("s8_pcto_tipologia") === "B" ? "scolastico" : f("s8_pcto_tipologia") === "C" ? "altra tipologia" : "non definita"}) è in corso. Specifica i punti oggetto di eventuale revisione della progettazione del percorso (L. 145/2018, D.Lgs. 66/2017 art. 7).`,
              blocchi: [
                { titolo: "Aspetti confermati del percorso", descrizione: "(1-2 voci) tipologia, struttura, attività o modalità che si confermano adeguate al profilo" },
                { titolo: "Criticità emerse nel contesto", descrizione: "(1-2 voci) barriere inattese, difficoltà organizzative o vincoli di contesto emersi durante il percorso" },
                { titolo: "Modifiche alla progettazione PCTO", descrizione: "(0-3 voci) adattamenti agli obiettivi di competenza, alle attività, alle modalità di monitoraggio o alla rete di supporto" },
              ],
            })}
          />

          <FieldAI
            label="Verifica finale PCTO"
            sub="Da compilare a fine anno. Valuta il conseguimento degli obiettivi di competenza, la riduzione dei vincoli di contesto e la replicabilità del percorso (Linee Guida Allegato B D.I. 182/2020)"
            campoId="s8_pcto_verifica_finale"
            value={f("s8_pcto_verifica_finale")}
            onChange={e => s("s8_pcto_verifica_finale", e.target.value)}
            placeholder="Nel corso del percorso PCTO, gli obiettivi di competenza sono stati raggiunti/parzialmente raggiunti… I vincoli di contesto inizialmente rilevati si sono ridotti/permangono in… Il percorso risulta replicabile/da adattare nei seguenti aspetti…"
            rows={5}
            studente={studente}
            sezioneLabel={sl}
            contestoGlobale={contestoPrec}
            customPrompt={`Sei un docente di sostegno esperto che compila la "Verifica finale PCTO (Sez. 8.3)" del PEI per la scuola secondaria di II grado (D.Lgs. 66/2017, Linee Guida Allegato B D.I. 182/2020).

${contestoPrec ? `Profilo dello studente/essa:\n${contestoPrec.slice(0,500)}\n` : ""}
${f("s8_pcto_progettazione") ? `Progettazione PCTO (obiettivi e contesto):\n${f("s8_pcto_progettazione").slice(0,400)}\n` : ""}
${f("s8_pcto_attivita") ? `Attività, monitoraggio e rete:\n${f("s8_pcto_attivita").slice(0,400)}\n` : ""}

Formula la Verifica finale del PCTO articolando OBBLIGATORIAMENTE due aspetti distinti (Linee Guida Allegato B D.I. 182/2020, testuale):

ASPETTO 1 — CONSEGUIMENTO OBIETTIVI E RIDUZIONE VINCOLI DI CONTESTO
Valuta il conseguimento degli obiettivi di competenza rispetto agli esiti previsti e alle modalità di valutazione indicate. Analizza il livello di riduzione dei vincoli di contesto e la loro eventuale permanenza.

ASPETTO 2 — REPLICABILITÀ E MISURE DI MIGLIORAMENTO
Valuta la replicabilità dell'attività e indica le misure di miglioramento da assumere per l'anno successivo, sulla base del riscontro dei tutor (aziendale e scolastico). Queste indicazioni serviranno da traccia iniziale per la progettazione del PCTO dell'anno scolastico successivo.

Testo continuo in paragrafi per ciascun aspetto, al passato, tra 120 e 200 parole totali.
Regole: no grassetto, no titoli interni, tono valutativo e professionale.`}
          />
        </div>
      )}
      {ordKey === "secondaria2" && <Divider />}

      {/* ── 8.3/8.4 Criteri di valutazione del comportamento ── */}
      {ordKey !== "infanzia" && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                {ordKey === "secondaria2" ? "8.4" : "8.3"} Criteri di valutazione del comportamento ed eventuali obiettivi specifici
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                Dichiara se lo studente segue i criteri della classe o criteri personalizzati
              </div>
            </div>
            <div style={{ display:"flex", gap: 6 }}>
              {["A","B"].map(opt => (
                <button key={opt} onClick={() => s("s8_comportamento_scelta", opt)}
                  style={{
                    padding:"4px 14px", borderRadius: 6, fontSize: 12, cursor:"pointer",
                    border: (f("s8_comportamento_scelta") || "A") === opt ? `2px solid ${C.accent}` : "1px solid #ddd",
                    background: (f("s8_comportamento_scelta") || "A") === opt ? hex(C.accent, 0.08) : "#fff",
                    color: (f("s8_comportamento_scelta") || "A") === opt ? C.accent : C.muted,
                    fontWeight: (f("s8_comportamento_scelta") || "A") === opt ? 700 : 400,
                  }}>
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {(f("s8_comportamento_scelta") || "A") === "A" ? (
            <div style={{ padding:"10px 14px", background: C.bg, border:`1px solid ${C.border}`,
              borderRadius: 8, fontSize: 12, color: C.muted }}>
              Segue i criteri di valutazione del comportamento della classe.
            </div>
          ) : (
            <FieldAI
              label=""
              sub="Definisci obiettivi comportamentali precisi e le strategie di rinforzo positivo adottate"
              campoId="s8_comportamento"
              value={f("s8_comportamento")}
              onChange={e => s("s8_comportamento", e.target.value)}
              placeholder="OBIETTIVI COMPORTAMENTALI&#10;— Rispetta i turni di parola durante le discussioni di classe&#10;— Gestisce il materiale sul banco in modo ordinato&#10;&#10;STRATEGIE DI RINFORZO&#10;— Rinforzo verbale positivo immediato&#10;— Token economy con tabella visiva dei punti"
              rows={6}
              studente={studente}
              sezioneLabel={sl}
              contestoGlobale={contestoPrec}
              customPrompt={`Sei un esperto di inclusione scolastica che compila la Sezione 8.3 "Criteri di valutazione del comportamento ed eventuali obiettivi specifici" del PEI (D.M. 182/2020).
${contestoPrec ? `\nContesto dello studente:\n${contestoPrec.slice(0,800)}\n` : ""}
Lo studente segue criteri di valutazione del comportamento PERSONALIZZATI (Opzione B).
Definisci 3-5 obiettivi comportamentali precisi e osservabili, e indica le strategie di rinforzo positivo adottate.

Formato OBBLIGATORIO:
OBIETTIVI COMPORTAMENTALI
— [comportamento target al presente indicativo, concreto e osservabile, max 12 parole]
— [comportamento target al presente indicativo, max 12 parole]

STRATEGIE DI RINFORZO
— [nome e descrizione sintetica del rinforzo positivo, max 8 parole]
— [nome e descrizione sintetica del rinforzo positivo, max 8 parole]

Regole:
- Usa ESCLUSIVAMENTE il PRESENTE INDICATIVO
- Obiettivi esempi: "Rispetta i turni di parola durante le discussioni di classe", "Gestisce il materiale sul banco in modo ordinato", "Regola autonomamente i momenti di frustrazione con le strategie concordate"
- BANDISCI formule generiche: "migliorare il comportamento", "rispettare le regole"
- NON usare infiniti né futuro
- Tono tecnico e professionale`}
            />
          )}
        </div>
      )}

      <Divider />
      <FieldAI
        label="Revisione a seguito di Verifica intermedia"
        sub="Modelli ufficiali A2/A3/A4 D.M. 182/2020 (A1 per infanzia). Da compilare al GLO intermedio (gen-feb): modifiche alla progettazione disciplinare e/o ai criteri di valutazione del comportamento."
        campoId="s8_revisione_intermedia"
        value={f("s8_revisione_intermedia")}
        onChange={e => s("s8_revisione_intermedia", e.target.value)}
        placeholder="A seguito della verifica intermedia, la progettazione disciplinare di … viene confermata / modificata; i criteri di valutazione del comportamento …"
        rows={5}
        maxTokens={700}
        studente={studente}
        sezioneLabel={sl}
        contestoGlobale={contestoPrec}
        customPrompt={buildRevisioneIntermediaPrompt({
          sezN: 8,
          sezLabel: ordKey === "infanzia"
            ? "Interventi sul percorso curricolare (campi di esperienza)"
            : "Curricolo: progettazione disciplinare e valutazione del comportamento",
          natura: "correttivo",
          contestoSpecifico: ordKey === "infanzia"
            ? "Gli interventi educativo-didattici nei cinque campi di esperienza sono già nel contesto del PEI. Indica cosa va confermato e cosa va modificato nel percorso educativo."
            : "La progettazione disciplinare (8.1, 8.2) e i criteri di valutazione del comportamento (8.3) sono nel contesto del PEI. Il modello ministeriale (A4 testuale) descrive questo campo come 'Specificare i punti eventualmente oggetto di revisione (progettazione disciplinare e/o comportamento)'.",
          blocchi: ordKey === "infanzia" ? [
            { titolo: "Conferme negli interventi educativi", descrizione: "(1-3 voci) attività, strategie o strumenti che si confermano efficaci nei campi di esperienza" },
            { titolo: "Criticità rilevate", descrizione: "(1-3 voci) interventi che non hanno funzionato o nuove esigenze emerse" },
            { titolo: "Modifiche al percorso educativo", descrizione: "(1-3 voci) interventi da modificare o introdurre nei campi di esperienza" },
          ] : [
            { titolo: "Conferme nella progettazione disciplinare", descrizione: "(1-3 voci) discipline o ambiti dove la progettazione (A/B/C) si conferma adeguata" },
            { titolo: "Criticità rilevate", descrizione: "(1-3 voci) discipline con difficoltà inattese, criteri di valutazione del comportamento da rivedere, ostacoli emersi" },
            { titolo: "Modifiche alla progettazione disciplinare", descrizione: "(0-3 voci) modifiche a obiettivi, strategie o strumenti per discipline specifiche; passaggi A→B, B→A o verso/da il percorso differenziato C (con relative implicazioni su tipo di percorso e attestazione finale) se motivati" },
            { titolo: "Modifiche ai criteri di valutazione del comportamento", descrizione: "(0-2 voci) revisione di obiettivi comportamentali o criteri personalizzati" },
          ],
        })}
      />
      <Divider />
      <VerificaQuestionnaireTrigger
        dati={dati}
        setD={setD}
        prefix="s8_vq_"
        tipo="conclusiva"
        dimensioniAttive={[]}
        sezioneLabel="Verifica conclusiva 8.4 — Curricolo"
      />
      <FieldAI
        label={ordKey === "infanzia" ? "Verifica conclusiva degli esiti" : "8.4 Verifica conclusiva degli esiti"}
        sub={ordKey === "infanzia"
          ? "Da compilare a fine anno. Verifica dei risultati EDUCATIVI conseguiti e valutazione sull'efficacia di interventi, strategie e strumenti riferiti anche all'ambiente di apprendimento (Allegato A1 D.I. 182/2020). NB: la valutazione finale degli apprendimenti è di competenza di tutti i docenti della sezione."
          : "Da compilare a fine anno. Verifica i risultati didattici conseguiti e valuta l'efficacia di interventi, strategie e strumenti, anche in riferimento all'ambiente di apprendimento."}
        campoId="s8_verifica_conclusiva"
        value={f("s8_verifica_conclusiva")}
        onChange={e => s("s8_verifica_conclusiva", e.target.value)}
        placeholder={ordKey === "infanzia"
          ? "Nel corso dell'anno scolastico si osserva che il bambino/la bambina ha mostrato progressi nei diversi campi di esperienza, in particolare…"
          : "Nel corso dell'anno scolastico, gli obiettivi didattici delle discipline con programmazione personalizzata sono stati raggiunti/parzialmente raggiunti/non raggiunti…"}
        rows={5}
        maxTokens={1600}
        studente={studente}
        sezioneLabel={sl}
        contestoGlobale={contestoPrec}
        customPrompt={(() => {
  const cvc = buildContestoVerificaCompleto(dati, voci);
  const qCtx = buildQuestionarioContext(dati, "s8_vq_");
  if (ordKey === "infanzia") {
    // Per l'infanzia non esistono "discipline B": bilancio osservativo per campi di esperienza
    return `Sei un docente di sostegno esperto della scuola dell'infanzia che compila il campo "Verifica conclusiva degli esiti" della Sezione 8 del PEI (Allegato A1 D.I. 182/2020).
${cvc ? `\nCONTENUTO PROGRAMMATORIO DEL PEI:\n${cvc}` : ""}
${qCtx ? `\n${qCtx}\n` : ""}
Formula un bilancio narrativo-osservativo dei risultati EDUCATIVI conseguiti dal bambino/dalla bambina nel corso dell'anno scolastico, attraverso i cinque campi di esperienza (Il sé e l'altro; Il corpo e il movimento; Immagini, suoni, colori; I discorsi e le parole; La conoscenza del mondo). Il bilancio deve essere ancorato agli obiettivi educativi del PEI per le quattro dimensioni ICF (Sez. 5), agli interventi di sostegno e all'ambiente di apprendimento. Valuta l'efficacia di interventi, strategie e strumenti rispetto ai progressi osservati nelle routine, nel gioco simbolico, nelle attività di laboratorio e nell'interazione con il gruppo sezione.

Regole TASSATIVE:
- Il tono complessivo (positivo/misto/critico) deve rispecchiare OBBLIGATORIAMENTE la valutazione globale fornita dall'insegnante nel questionario
- Tono OSSERVATIVO ED EVOLUTIVO al passato ("si è osservato che…", "il bambino/la bambina ha mostrato…", "sono emersi progressi nell'area…"); evita formule cliniche definitive
- Testo continuo in paragrafi, senza elenchi puntati, senza grassetto, senza titoli interni
- Tra 150 e 280 parole
- NON usare termini come "disciplina", "programmazione personalizzata B", "voti", "livelli", "percentuali" — non si applicano alla scuola dell'infanzia
- Usa la formula del modello A1: "verifica dei risultati EDUCATIVI conseguiti" — mai "risultati didattici"
- Cita esplicitamente strategie e strumenti indicati nel PEI — niente formule generiche`;
  }
  const discB = voci.map((v, i) => f(`s8_${i}_scelta`) === "B" ? { nome: v, ob: f(`s8_${i}_ob`), strat: f(`s8_${i}_strat`), strum: f(`s8_${i}_strum`) } : null).filter(Boolean);
  const discBTesto = discB.length > 0
    ? `\nDISCIPLINE CON PROGRAMMAZIONE PERSONALIZZATA (B):\n${discB.map(d => `- ${d.nome}${d.ob ? `: ${d.ob.slice(0,200)}` : ""}${d.strat ? ` | Strategie: ${d.strat.slice(0,100)}` : ""}${d.strum ? ` | Strumenti: ${d.strum.slice(0,80)}` : ""}`).join("\n")}`
    : "\n(Nessuna disciplina con programmazione B rilevata — genera in modo generico)";
  return `Sei un docente di sostegno esperto che compila il campo 8.4 "Verifica conclusiva degli esiti" del PEI (D.M. 182/2020).
${cvc ? `\nCONTENUTO PROGRAMMATORIO DEL PEI:\n${cvc}` : ""}
${discBTesto}
${qCtx ? `\n${qCtx}\n` : ""}
PRIORITÀ DEL TESTO — Allegato B Linee Guida D.I. 182/2020 (testuale): "la valutazione è riferita PRIORITARIAMENTE all'efficacia degli interventi, NON SOLO al raggiungimento degli obiettivi".

Sulla base di tutto il contenuto programmatorio del PEI (profilo ICF, obiettivi Sez.5, barriere/facilitatori Sez.6, interventi Sez.7, obiettivi disciplinari Sez.8), formula un giudizio valutativo per ciascuna disciplina con programmazione personalizzata. Il giudizio deve articolare DUE elementi distinti per ogni disciplina:
1. EFFICACIA degli interventi educativi/didattici, delle strategie e degli strumenti adottati (citando elementi specifici del PEI: nomi delle metodologie, ausili, adattamenti effettuati)
2. GRADO DI RAGGIUNGIMENTO degli obiettivi programmati (pieno / parziale / non raggiunto), motivato in relazione ai fattori contestuali (barriere, facilitatori) e all'efficacia degli interventi al punto 1.

Il testo proposto costituisce una base che il consiglio di classe è libero di confermare, integrare o modificare.

Regole:
- Il tono complessivo (positivo/misto/critico) deve rispecchiare OBBLIGATORIAMENTE la valutazione globale fornita dall'insegnante nel questionario
- L'efficacia degli interventi deve emergere come asse portante del testo, non come accenno marginale
- Testo continuo in paragrafi, senza elenchi puntati
- Tono valutativo, motivato e professionale, al passato
- Tra 150 e 280 parole
- NON usare grassetto né titoli interni
- Cita obiettivi e strategie specifici del PEI — NON scrivere valutazioni generiche`;
})()} />
    </div>
  );
}

function SezRisorse({ dati, setD, studente, onChat, contestoGlobale }) {
  const sl = "Risorse professionali per il sostegno";
  const f = k => dati[k] || "";
  const s = (k,v) => setD(k,v);
  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
        <SezTitle icon="📦">9. Risorse professionali per il sostegno</SezTitle>
        <Btn ghost small onClick={onChat} icon="💬">Assistente</Btn>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
        <Field label="Ore sostegno settimanali">
          <Input value={f("s9_ore_sost")} onChange={e => s("s9_ore_sost", e.target.value)} placeholder="Es. 18 ore" />
        </Field>
        <Field label="Ore assistente autonomia e comunicazione">
          <Input value={f("s9_ore_aec")} onChange={e => s("s9_ore_aec", e.target.value)} placeholder="Es. 6 ore" />
        </Field>
      </div>
      <FieldAI label="Modalità di sostegno" campoId="s9_modalita" value={f("s9_modalita")}
        onChange={e => s("s9_modalita", e.target.value)}
        placeholder={studente?.ordine === "infanzia"
          ? "In sezione, nei contesti di gioco, routine, piccolo gruppo, attività di laboratorio…"
          : "In classe, piccolo gruppo, individuale…"}
        rows={3}
        studente={studente} sezioneLabel={sl} contestoGlobale={contestoGlobale}
        customPrompt={studente?.ordine === "infanzia"
          ? `Sei un docente di sostegno esperto della scuola dell'infanzia che compila la Sezione 9 del PEI (D.M. 182/2020), campo "Modalità di sostegno".
Descrivi in modo concreto e completo l'organizzazione del sostegno alla scuola dell'infanzia: setting prevalente (compresenza con i docenti della sezione nei contesti di gioco simbolico, routine quotidiane, attività di laboratorio, piccolo gruppo nella sezione), la distribuzione oraria per ciascun contesto, i campi di esperienza o le attività di sezione coinvolte in ogni setting, la frequenza delle attività di piccolo gruppo (es. quante volte a settimana, con quanti compagni della sezione), e la presenza dell'assistente all'autonomia e comunicazione se prevista.
Regole:
- Testo continuo in 3-5 frasi, senza elenchi puntati
- Usa il PRESENTE INDICATIVO
- Sii specifico su setting, ore, contesti di sezione, frequenza
- NON usare "classe" né "discipline" — alla scuola dell'infanzia si parla di "sezione" e "campi di esperienza"
- NON usare formule generiche come "viene supportato" o "riceve aiuto"`
          : `Sei un docente di sostegno esperto che compila la Sezione 9 del PEI (D.M. 182/2020), campo "Modalità di sostegno".
Descrivi in modo concreto e completo l'organizzazione del sostegno: setting prevalente (compresenza in classe, piccolo gruppo, lavoro individuale 1:1), la distribuzione oraria per ciascun contesto, le discipline o attività coinvolte in ogni setting, la frequenza delle sessioni di piccolo gruppo o laboratorio (es. quante volte a settimana, con quanti compagni), e la presenza dell'assistente all'autonomia e comunicazione se prevista.
Regole:
- Testo continuo in 3-5 frasi, senza elenchi puntati
- Usa il PRESENTE INDICATIVO
- Sii specifico su setting, ore, discipline e frequenza
- NON usare formule generiche come "viene supportato" o "riceve aiuto"`} />
      <FieldAI label="Tecnologie assistive e ausili" campoId="s9_tech" value={f("s9_tech")}
        onChange={e => s("s9_tech", e.target.value)} placeholder="Tablet, sintesi vocale, software specifici…" rows={2}
        studente={studente} sezioneLabel={sl} contestoGlobale={contestoGlobale}
        customPrompt={`Sei un esperto di tecnologie assistive che compila la Sezione 9 del PEI (D.M. 182/2020), campo "Tecnologie assistive e ausili".
Elenca gli strumenti compensativi e le tecnologie assistive realmente usati da questo studente, coerenti con la diagnosi e il profilo.
Formato OBBLIGATORIO — una voce per riga, terminata da punto fermo:
[Nome specifico dello strumento o del software e suo utilizzo principale, max 8 parole].
Regole:
- Nomi tecnici precisi (es. "Timer visivo Time Timer.", "Tablet con app GeoGebra Primaria.", "Calcolatrice scientifica per le verifiche.")
- NON scrivere descrizioni generiche (es. "ausili tecnologici appropriati")
- Tra 3 e 6 voci`} />
      <Divider />
      <FieldAI
        label="Revisione a seguito di Verifica intermedia"
        sub="Modelli ufficiali A1/A2/A3/A4 D.M. 182/2020. Da compilare al GLO intermedio (gen-feb): modifiche all'organizzazione del progetto di inclusione e all'utilizzo delle risorse professionali."
        campoId="s9_revisione_intermedia"
        value={f("s9_revisione_intermedia")}
        onChange={e => s("s9_revisione_intermedia", e.target.value)}
        placeholder="A seguito della verifica intermedia, l'organizzazione del sostegno (ore, setting, distribuzione) viene confermata / modificata come segue…"
        rows={5}
        maxTokens={700}
        studente={studente}
        sezioneLabel={sl}
        contestoGlobale={contestoGlobale}
        customPrompt={buildRevisioneIntermediaPrompt({
          sezN: 9,
          sezLabel: "Organizzazione generale del progetto di inclusione e utilizzo delle risorse",
          natura: "correttivo",
          contestoSpecifico: "Le modalità di sostegno, le tecnologie assistive e le risorse professionali assegnate sono nel contesto del PEI. Indica cosa va modificato nell'organizzazione (ore, setting, distribuzione, ausili) dopo i primi mesi.",
          blocchi: [
            { titolo: "Conferme nell'organizzazione", descrizione: "(1-3 voci) modalità di sostegno o utilizzo delle risorse che si confermano efficaci" },
            { titolo: "Criticità organizzative", descrizione: "(1-3 voci) ostacoli nell'utilizzo delle risorse, ausili che non funzionano, distribuzione oraria non efficace" },
            { titolo: "Modifiche organizzative", descrizione: "(1-3 voci) ridistribuzione delle ore, cambio di setting, nuovi ausili da introdurre, integrazioni alle risorse" },
          ],
        })}
      />
    </div>
  );
}

// ─── Riferimenti DM 14/2024 (Raccomandazione UE 22 maggio 2018) ───────────
// Gli 8 nomi delle competenze chiave sono IDENTICI nei tre modelli (Allegati A, B, C).
// Le descrizioni di ciascuna competenza variano per ordine. Mantenute fedeli al testo
// ministeriale per garantire conformità sostanziale: l'app NON deve modificare gli
// enunciati. Il modello nazionale è unico e non modificabile (art. 3 c.1 DM 14/2024).
const COMPETENZE_CHIAVE_DM14 = [
  "Competenza alfabetica funzionale",
  "Competenza multilinguistica",
  "Competenza matematica e competenza in scienze, tecnologie e ingegneria",
  "Competenza digitale",
  "Competenza personale, sociale e capacità di imparare a imparare",
  "Competenza in materia di cittadinanza",
  "Competenza imprenditoriale",
  "Competenza in materia di consapevolezza ed espressione culturali",
];

// Descrizioni ufficiali — Allegato A (termine scuola primaria)
const COMPETENZE_DESCR_PRIMARIA = [
  "Padroneggiare la lingua di scolarizzazione in modo da comprendere enunciati, raccontare le proprie esperienze e adottare un registro linguistico appropriato alle diverse situazioni.",
  "Utilizzare la lingua inglese a livello elementare in forma orale e scritta (comprensione orale e scritta, produzione scritta e produzione/interazione orale) in semplici situazioni di vita quotidiana in aree che riguardano bisogni immediati o argomenti molto familiari (Livello A1 del QCER). Avere consapevolezza che esistono lingue e culture diverse.",
  "Utilizzare le conoscenze matematiche e scientifico-tecnologiche per trovare e giustificare soluzioni a problemi reali. Osservare comportamenti e atteggiamenti rispettosi verso l'ambiente, i beni comuni, la sostenibilità.",
  "Utilizzare con responsabilità le tecnologie digitali in contesti comunicativi concreti per ricercare informazioni e per interagire con altre persone, come supporto alla creatività e alla soluzione di problemi semplici.",
  "Avere cura e rispetto di sé, degli altri e dell'ambiente; affrontare positivamente i conflitti, collaborare con altri, empatizzare. Esprimere le proprie personali opinioni e sensibilità nel rispetto di sé e degli altri. Utilizzare conoscenze e nozioni di base per ricercare nuove informazioni. Accedere a nuovi apprendimenti anche in modo autonomo. Portare a compimento il lavoro iniziato, da solo o insieme agli altri.",
  "Rispettare le regole condivise e collaborare con gli altri per la costruzione del bene comune. Partecipare alle diverse forme di vita comunitaria, divenendo consapevole dei valori costituzionali. Riconoscere le diverse identità, le tradizioni culturali e religiose in un'ottica di dialogo e di rispetto reciproco.",
  "Dimostrare originalità e spirito di iniziativa. Realizzare semplici progetti. Assumersi le proprie responsabilità, chiedere aiuto e fornirlo quando necessario. Riflettere sulle proprie scelte.",
  "Orientarsi nello spazio e nel tempo, osservando e descrivendo ambienti, fatti, fenomeni e produzioni artistiche, esprimendo curiosità e ricerca di senso. In relazione alle proprie potenzialità e al proprio talento, esprimersi negli ambiti più congeniali: motori, artistici e musicali.",
];

// Descrizioni ufficiali — Allegato B (termine primo ciclo / 3ª sec. I grado)
const COMPETENZE_DESCR_SEC1 = [
  "Padroneggiare la lingua di scolarizzazione in modo da comprendere enunciati di una certa complessità, esprimere le proprie idee, adottare un registro linguistico appropriato alle diverse situazioni.",
  "Utilizzare la lingua inglese a livello elementare in forma orale e scritta in semplici situazioni di vita quotidiana relative ad ambiti di immediata rilevanza e su argomenti familiari e abituali, compresi contenuti di studio di altre discipline (Livello A2 del QCER). Riconoscere le più evidenti somiglianze e differenze tra le lingue e le culture oggetto di studio. Utilizzare una seconda lingua comunitaria a livello elementare (Livello A1 del QCER).",
  "Utilizzare le conoscenze matematiche e scientifico-tecnologiche per analizzare dati e fatti della realtà e per verificare l'attendibilità di analisi quantitative proposte da altri. Utilizzare il pensiero logico-scientifico per affrontare problemi e situazioni sulla base di elementi certi. Avere consapevolezza dei limiti delle affermazioni che riguardano situazioni complesse.",
  "Utilizzare con consapevolezza e responsabilità le tecnologie digitali per ricercare, produrre ed elaborare dati e informazioni, per interagire con le altre persone, come supporto alla creatività e alla soluzione di problemi.",
  "Avere cura e rispetto di sé, degli altri e dell'ambiente come presupposto di uno stile di vita sano e corretto. Utilizzare conoscenze e nozioni di base in modo organico per ricercare e organizzare nuove informazioni. Accedere a nuovi apprendimenti in modo autonomo. Portare a compimento il lavoro iniziato, da solo o insieme ad altri.",
  "Comprendere la necessità di una convivenza civile, pacifica e solidale per la costruzione del bene comune e agire in modo coerente. Esprimere le proprie personali opinioni e sensibilità nel rispetto di sé e degli altri. Partecipare alle diverse funzioni pubbliche nelle forme possibili, in attuazione dei principi costituzionali. Riconoscere ed apprezzare le diverse identità, le tradizioni culturali e religiose in un'ottica di dialogo e di rispetto reciproco. Osservare comportamenti e atteggiamenti rispettosi dell'ambiente, dei beni comuni, della sostenibilità (Agenda 2030).",
  "Dimostrare spirito di iniziativa, produrre idee e progetti creativi. Assumersi le proprie responsabilità, chiedere aiuto e fornirlo quando necessario. Riflettere su se stesso e misurarsi con le novità e gli imprevisti. Orientare le proprie scelte in modo consapevole.",
  "Orientarsi nello spazio e nel tempo e interpretare i sistemi simbolici e culturali della società, esprimendo curiosità e ricerca di senso. In relazione alle proprie potenzialità e al proprio talento, esprimersi negli ambiti più congeniali: motori, artistici e musicali.",
];

// Descrizioni ufficiali — Allegato C (assolvimento obbligo / 2ª sec. II grado)
const COMPETENZE_DESCR_SEC2 = [
  "Padroneggiare la lingua di scolarizzazione in forma sia orale sia scritta in tutti i suoi aspetti (comprensione, interpretazione, produzione) utilizzando materiali di vario genere all'interno delle diverse discipline, dei diversi contesti e scopi comunicativi. Comunicare e relazionarsi con gli altri in modo efficace e opportuno.",
  "Utilizzare le diverse lingue in forma orale e scritta in modo appropriato ed efficace per diversi scopi comunicativi in diversi contesti sociali e culturali in base ai propri bisogni o desideri. Comunicare in maniera appropriata, efficace e rispettosa con interlocutori che hanno riferimenti culturali diversi dai propri. (Specificare livello QCER per ciascuna lingua del curricolo).",
  "Utilizzare le tecniche e le procedure del calcolo aritmetico ed algebrico. Confrontare ed analizzare figure geometriche, individuando invarianti e relazioni. Individuare le strategie appropriate per la soluzione di problemi. Analizzare dati e interpretarli sviluppando deduzioni e ragionamenti. Osservare, descrivere ed analizzare fenomeni della realtà naturale e artificiale utilizzando le metodologie proprie dell'indagine scientifica. Analizzare fenomeni legati alle trasformazioni di energia. Individuare potenzialità e limiti delle tecnologie nel contesto culturale e sociale, anche a tutela della sostenibilità.",
  "Utilizzare le reti e gli strumenti informatici nelle attività di studio, ricerca e approfondimento, valutando criticamente la credibilità delle fonti. Osservare le norme comportamentali nell'utilizzo delle tecnologie digitali. Proteggere la propria reputazione, gestire e tutelare i dati personali. Osservare le regole a tutela della riservatezza. Evitare rischi per la salute e minacce al proprio benessere.",
  "Osservare comportamenti rispettosi verso il proprio benessere personale, sociale e fisico e quello degli altri, della comunità e del pianeta. Collaborare e partecipare, valorizzando le proprie e le altrui risorse, gestendo la conflittualità, nel rispetto dei diritti degli altri e delle diversità. Imparare ad imparare, organizzando il proprio apprendimento, individuando varie fonti e modalità. Sviluppare fiducia nei propri mezzi, valutare i propri punti critici. Individuare collegamenti e relazioni tra fenomeni, eventi e concetti. Ricercare ed interpretare criticamente l'informazione distinguendo fatti e opinioni.",
  "Agire in modo autonomo e responsabile inserendosi nella vita sociale, facendo valere i propri diritti e bisogni e riconoscendo al contempo quelli altrui. Collocare l'esperienza personale in un sistema di regole fondato sul reciproco riconoscimento dei diritti garantiti dalla Costituzione. Osservare comportamenti rispettosi dell'ambiente, dei beni comuni, della sostenibilità (Agenda 2030).",
  "Elaborare e realizzare progetti riguardanti le proprie attività di studio e di lavoro, utilizzando le conoscenze per stabilire obiettivi significativi e realistici. Individuare, affrontare e risolvere problemi costruendo e verificando ipotesi. Pianificare e progettare, scegliere tra opzioni diverse agendo con integrità e nel rispetto del bene comune. Collaborare con altri valorizzandone le risorse. Riconoscere le caratteristiche essenziali del sistema socio-economico per orientarsi nel tessuto produttivo del territorio.",
  "Comunicare e rappresentare creativamente eventi, fenomeni, principi, concetti, norme, procedure, atteggiamenti, stati d'animo, emozioni utilizzando linguaggi diversi (verbale, matematico, scientifico, simbolico, corporeo, artistico). Utilizzare gli strumenti fondamentali per una fruizione consapevole del patrimonio culturale, artistico, letterario, paesaggistico. Comprendere il cambiamento e la diversità dei tempi storici, in dimensione diacronica e sincronica.",
];

// Indicatori esplicativi dei livelli — IDENTICI nei 3 modelli DM 14/2024.
// Per sec. II grado l'enunciato usa "Lo/a studente/ssa" anziché "L'alunno/a".
const LIVELLI_ENUNCIATI_DM14 = {
  alunno: {
    A: "L'alunno/a svolge compiti e risolve problemi complessi, mostrando padronanza nell'uso delle conoscenze e delle abilità; propone e sostiene le proprie opinioni e assume in modo responsabile decisioni consapevoli.",
    B: "L'alunno/a svolge compiti e risolve problemi in situazioni nuove, compie scelte consapevoli, mostrando di saper utilizzare le conoscenze e le abilità acquisite.",
    C: "L'alunno/a svolge compiti semplici anche in situazioni nuove, mostrando di possedere conoscenze e abilità fondamentali e di saper applicare basilari regole e procedure apprese.",
    D: "L'alunno/a, se opportunamente guidato/a, svolge compiti semplici in situazioni note.",
  },
  studente: {
    A: "Lo/a studente/ssa svolge compiti e risolve problemi complessi, mostrando padronanza nell'uso delle conoscenze e delle abilità; propone e sostiene le proprie opinioni e assume in modo responsabile decisioni consapevoli.",
    B: "Lo/a studente/ssa svolge compiti e risolve problemi in situazioni nuove, compie scelte consapevoli, mostrando di saper utilizzare le conoscenze e le abilità acquisite.",
    C: "Lo/a studente/ssa svolge compiti semplici anche in situazioni nuove, mostrando di possedere conoscenze e abilità fondamentali e di saper applicare basilari regole e procedure apprese.",
    D: "Lo/a studente/ssa, se opportunamente guidato/a, svolge compiti semplici in situazioni note.",
  },
};

function SezCertificazione({ dati, setD, studente, ord, onChat, contestoGlobale }) {
  const sl = "Certificazione delle competenze";
  const f = k => dati[k] || "";
  const s = (k,v) => setD(k,v);

  // Sceglie i descrittori ufficiali del modello per ordine (Allegati A/B/C DM 14/2024)
  const compDescr = studente?.ordine === "secondaria2" ? COMPETENZE_DESCR_SEC2
                  : studente?.ordine === "secondaria1" ? COMPETENZE_DESCR_SEC1
                  : COMPETENZE_DESCR_PRIMARIA;
  // Enunciati dei livelli: "Lo/a studente/ssa" per sec. II, "L'alunno/a" altrove
  const livEnunc = studente?.ordine === "secondaria2"
    ? LIVELLI_ENUNCIATI_DM14.studente
    : LIVELLI_ENUNCIATI_DM14.alunno;

  // ─── Rilevamento classe terminale (DM 14/2024 art. 2; Linee Guida All. B D.I. 182/2020 art. 14) ───
  // primaria → 5ª, sec. I grado → 3ª, sec. II grado → 2ª (assolvimento obbligo)
  const classeStr = String(studente?.classe || "").trim();
  const isPrimaria5  = studente?.ordine === "primaria"    && /^5/.test(classeStr);
  const isSec1_3     = studente?.ordine === "secondaria1" && /^3/.test(classeStr);
  const isSec2_2     = studente?.ordine === "secondaria2" && /^2/.test(classeStr);
  const isClasseTerminale = isPrimaria5 || isSec1_3 || isSec2_2;
  const labelTerminale = isPrimaria5 ? "5ª primaria"
                       : isSec1_3    ? "3ª secondaria di I grado"
                       : isSec2_2    ? "2ª secondaria di II grado (assolvimento obbligo)"
                       : null;

  // Etichetta normativa del modello — DM 14/2024 unifica i modelli per i tre ambiti
  const modelloNorm = studente?.ordine === "secondaria2"
    ? "DM 14/2024 — Allegato C (assolvimento obbligo)"
    : studente?.ordine === "secondaria1"
      ? "DM 14/2024 — Allegato B (primo ciclo)"
      : "DM 14/2024 — Allegato A (primaria)";

  const livelloLabel = { A: "Avanzato", B: "Intermedio", C: "Base", D: "Iniziale" };

  // Prompt strutturato per ogni singola competenza (nota esplicativa / descrittore personalizzato).
  // Vincoli normativi: il GLO redige le note esplicative; il modello nazionale NON è modificabile;
  // sono consentite solo: (1) scelta competenze certificabili, (2) personalizzazione descrittori,
  // (3) personalizzazione giudizi descrittivi dei livelli (DM 14/2024 art. 3 c.2 e analoghi).
  const buildCertPrompt = (idx, competenza) => {
    const livello = f(`cert_${idx}_l`);
    const etichettaLiv = livello
      ? `${livello} — ${livelloLabel[livello]}. Enunciato ufficiale: "${livEnunc[livello]}"`
      : "non ancora selezionato (proponi un livello coerente con il PEI)";
    const ambitoModello = studente?.ordine === "secondaria2"
      ? "termine della classe seconda della Scuola Secondaria di II grado (assolvimento dell'obbligo di istruzione)"
      : studente?.ordine === "secondaria1"
        ? "termine della classe terza della Scuola Secondaria di I grado (primo ciclo)"
        : "termine della classe quinta della Scuola Primaria";
    return `Sei un docente di sostegno esperto che compila la SEZIONE 10 del PEI — Certificazione delle competenze (Allegato B Linee Guida D.I. 182/2020 corretto dal D.I. 153/2023, art. 14).

NORMATIVA DI RIFERIMENTO:
- Decreto vigente: DM 14 del 30 gennaio 2024 — modelli nazionali di certificazione delle competenze (raccordati alla Raccomandazione UE 22 maggio 2018), che ha sostituito DM 742/2017 (primo ciclo) e DM 9/2010 (assolvimento obbligo).
- Modello ministeriale di riferimento per questo alunno: ${modelloNorm}.
- Ambito di certificazione: ${ambitoModello}.
- DLgs 62/2017 art. 9 (primo ciclo) e DM 139/2007 (assolvimento obbligo) restano cornice normativa.
- L'enunciato della competenza e la descrizione del modello sono FISSI e NON RIFORMULABILI: scrivi SOLO la nota esplicativa/descrittore personalizzato.
- Sono consentite SOLO tre personalizzazioni (DM 14/2024 art. 3 c.2): (1) scelta delle competenze effettivamente certificabili, (2) personalizzazione dei descrittori per ciascuna competenza, (3) personalizzazione dei giudizi descrittivi dei livelli.
- Certificare le competenze spetta al team docenti / consiglio di classe in sede di scrutinio finale; il GLO definisce qui le NOTE ESPLICATIVE che rapportano l'enunciato di competenza agli obiettivi specifici del PEI.
- La certificazione va compilata anche per PEI differenziato: le considerazioni sulla validità del titolo non si applicano.

COMPETENZA CHIAVE (enunciato fisso, NON riscriverlo): "${competenza}"
DESCRIZIONE UFFICIALE NEL MODELLO ${modelloNorm} (testo ministeriale, NON riformulare): "${compDescr[idx]}"
LIVELLO INDICATO: ${etichettaLiv}

Compito: genera la NOTA ESPLICATIVA / DESCRITTORE PERSONALIZZATO per questa singola competenza, raccordando l'enunciato e la descrizione ministeriale agli obiettivi specifici già definiti nel PEI (Sez. 4 Osservazioni, Sez. 5 Obiettivi, Sez. 8 Curricolo) e al profilo dello studente.

Il testo deve:
- descrivere in concreto COSA L'ALUNNO/A SA FARE in relazione a questa competenza, in modo osservabile
- collegarsi a obiettivi, attività o strategie del PEI realmente presenti nel contesto fornito
- essere coerente con il livello selezionato (se presente) e con il suo enunciato ufficiale, giustificandolo implicitamente
- usare il presente indicativo, terza persona${studente?.ordine === "secondaria2" ? ", forma 'lo/a studente/ssa'" : ", forma 'l\\'alunno/a'"}
- restare tra 40 e 80 parole (3-5 righe)

Regole TASSATIVE:
- NON riformulare né riscrivere l'enunciato della competenza né la descrizione ministeriale
- NON usare grassetto, titoli, virgolette, elenchi puntati
- NON usare percentuali né quantificazioni numeriche (es. "nel 70% dei casi")
- NON usare formule generiche/vuote ("ha raggiunto un buon livello", "mostra una buona competenza"): cita comportamenti, attività o situazioni didattiche concrete del PEI
- NON aggiungere intestazioni come "Nota esplicativa:" o "Descrittore:" — scrivi direttamente il testo`;
  };

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
        <SezTitle icon="🏅">10. Certificazione Competenze</SezTitle>
        <Btn ghost small onClick={onChat} icon="💬">Assistente</Btn>
      </div>

      {isClasseTerminale ? (
        <InfoBox color={C.teal} icon="📜">
          Classe terminale rilevata ({labelTerminale}). Modello di riferimento: <strong>{modelloNorm}</strong>.
          Il GLO redige qui le <strong>note esplicative</strong>; la firma del modello ufficiale spetta al team docenti / consiglio di classe in sede di scrutinio finale.
        </InfoBox>
      ) : (
        <InfoBox color={C.orange} icon="⚠️">
          Sezione prevista solo per le classi terminali (5ª primaria, 3ª sec. I grado, 2ª sec. II grado).
          Puoi comunque predisporre delle bozze: la certificazione va compilata anche per PEI differenziato.
        </InfoBox>
      )}

      <div style={{
        fontSize:12, color:C.muted, background:hex(C.muted,0.06),
        border:`1px solid ${hex(C.muted,0.18)}`, borderRadius:8,
        padding:"8px 12px", marginBottom:14, lineHeight:1.5
      }}>
        Personalizzazioni consentite (DM 14/2024 art. 3 c.2): selezione delle competenze certificabili, personalizzazione dei descrittori e dei giudizi descrittivi dei livelli.
        Le 8 competenze chiave e i 4 livelli sono fissati dal modello ministeriale (Raccomandazione UE 2018) e <strong>non sono modificabili</strong>. Se il modello ufficiale risulta del tutto incompatibile con il PEI può essere lasciato in bianco motivando la scelta nelle annotazioni.
      </div>

      {COMPETENZE_CHIAVE_DM14.map((c,i) => (
        <div key={i} style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:12, marginBottom:12 }}>
          <FieldAI
            label={`${i+1}. ${c}`}
            sub={`Modello DM 14/2024: ${compDescr[i]}`}
            campoId={`cert_${i}_d`}
            value={f(`cert_${i}_d`)}
            onChange={e => s(`cert_${i}_d`, e.target.value)}
            placeholder="Nota esplicativa: descrizione del livello raggiunto, raccordata agli obiettivi del PEI…"
            rows={3}
            studente={studente}
            sezioneLabel={sl}
            contestoGlobale={contestoGlobale}
            customPrompt={buildCertPrompt(i, c)}
            maxTokens={600}
          />
          <Field label="Livello" sub={f(`cert_${i}_l`) ? livEnunc[f(`cert_${i}_l`)] : "Indicatori esplicativi DM 14/2024"}>
            <Select value={f(`cert_${i}_l`) || ""} onChange={e => s(`cert_${i}_l`, e.target.value)}>
              <option value="">—</option>
              <option value="A" title={livEnunc.A}>A – Avanzato</option>
              <option value="B" title={livEnunc.B}>B – Intermedio</option>
              <option value="C" title={livEnunc.C}>C – Base</option>
              <option value="D" title={livEnunc.D}>D – Iniziale</option>
            </Select>
          </Field>
        </div>
      ))}
    </div>
  );
}

function SezVerifica({ dati, setD, studente, onChat, contestoGlobale }) {
  const sl = "Verifica Finale";
  const f = k => dati[k] || "";
  const s = (k,v) => setD(k,v);

  // Contesto completo per i campi di verifica finale:
  // aggrega tutto il contenuto programmatorio + le verifiche intermedie già compilate
  const cvcS11 = buildContestoVerificaCompleto(dati);
  const verificheIntermedie = [
    f("s5_verifica_conclusiva") ? `Revisione conclusiva Sez.5:\n${f("s5_verifica_conclusiva").slice(0, 400)}` : null,
    f("s7_verifica_conclusiva") ? `Verifica conclusiva Sez.7:\n${f("s7_verifica_conclusiva").slice(0, 300)}` : null,
    f("s8_verifica_conclusiva") ? `Verifica conclusiva Sez.8:\n${f("s8_verifica_conclusiva").slice(0, 300)}` : null,
  ].filter(Boolean).join("\n\n");
  const contestoVerifica = [
    cvcS11,
    verificheIntermedie || null,
    contestoGlobale,
  ].filter(Boolean).join("\n\n");
  // Questionario Sez.11: eredita i valori di Sez.5 come default se Sez.11 non è ancora compilata
  const qCtxS11 = buildQuestionarioContext(dati, "s11_vq_", "s5_vq_");
  const dimAttiveS11 = DIMENSIONI.filter(d => dati[`s5_${d.id}_ob`]).map(d => d.id);

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
        <div style={{ display:"flex", alignItems:"center", gap:0 }}>
          <SezTitle icon="✅">11. Verifica Finale</SezTitle>
          <GlossarioBtn voce="Verifica Finale" />
        </div>
        <Btn ghost small onClick={onChat} icon="💬">Assistente</Btn>
      </div>
      <VerificaQuestionnaireTrigger
        dati={dati}
        setD={setD}
        prefix="s11_vq_"
        tipo="conclusiva"
        dimensioniAttive={dimAttiveS11}
        inheritPrefix="s5_vq_"
        sezioneLabel="Verifica finale — Sez. 11"
      />
      <FieldAI
        label="Verifica finale del PEI — Valutazione globale dei risultati raggiunti"
        sub="Con riferimento agli elementi di verifica delle varie sezioni del PEI (Sez. 5, 7, 8). Da approvare in sede di GLO di fine anno."
        campoId="s11_valutazione"
        value={f("s11_valutazione")}
        onChange={e => s("s11_valutazione", e.target.value)}
        placeholder="Il GLO, a conclusione dell'anno scolastico, esprime una valutazione globale del percorso di inclusione e dei risultati raggiunti rispetto agli obiettivi del PEI…"
        rows={5}
        studente={studente}
        sezioneLabel={sl}
        contestoGlobale={contestoGlobale}
        customPrompt={`Sei un docente di sostegno esperto che compila la "Verifica finale del PEI" nella Sezione 11 (D.M. 182/2020).
${contestoVerifica ? `\nCONTENUTO PROGRAMMATORIO E VERIFICHE INTERMEDIE DEL PEI:\n${contestoVerifica}\n` : ""}
${qCtxS11 ? `\n${qCtxS11}\n` : ""}
PRIORITÀ DEL TESTO — Allegato B Linee Guida D.I. 182/2020 (testuale): "la valutazione è riferita PRIORITARIAMENTE all'efficacia degli interventi, NON SOLO al raggiungimento degli obiettivi".

Struttura la valutazione per dimensione ICF, citando per ciascuna gli obiettivi specifici della Sez.5, il livello di raggiungimento E l'efficacia degli interventi/strategie/strumenti che hanno determinato l'esito. Dove disponibili, integra le valutazioni delle verifiche intermedie (Sez.5, Sez.7, Sez.8) già presenti nel contesto. Il tono complessivo (positivo/misto/critico) deve rispecchiare OBBLIGATORIAMENTE la valutazione globale fornita dall'insegnante nel questionario. Usa OBBLIGATORIAMENTE questo formato:

DIMENSIONE A — Relazione e interazione
Obiettivi: [cita testualmente gli obiettivi Sez.5 dim.A]
Esito: [valutazione motivata al passato, 1-2 righe: livello di raggiungimento + efficacia delle strategie/strumenti specifici adottati per quella dimensione]

DIMENSIONE B — Comunicazione e linguaggio
Obiettivi: [cita testualmente gli obiettivi Sez.5 dim.B]
Esito: [valutazione motivata al passato, 1-2 righe: livello di raggiungimento + efficacia delle strategie/strumenti specifici]

DIMENSIONE C — Autonomia e orientamento
Obiettivi: [cita testualmente gli obiettivi Sez.5 dim.C]
Esito: [valutazione motivata al passato, 1-2 righe: livello di raggiungimento + efficacia delle strategie/strumenti specifici]

DIMENSIONE D — Cognitiva e apprendimento
Obiettivi: [cita testualmente gli obiettivi Sez.5 dim.D]
Esito: [valutazione motivata al passato, 1-2 righe: livello di raggiungimento + efficacia delle strategie/strumenti specifici]

Conclusione: [sintesi globale 2-3 righe sull'EFFICACIA COMPLESSIVA del dispositivo educativo-didattico — interventi, strategie e strumenti adottati nel corso dell'anno — con riferimento ai facilitatori e alle barriere (Sez.6) e agli interventi sul contesto (Sez.7) che hanno influito]

Regole:
- Tono valutativo, al passato
- Scritto in terza persona (es. "${studente?.ordine === "infanzia" ? "Il bambino/la bambina ha mostrato…" : "L'alunno/a ha dimostrato…"}")
- NON usare grassetto
- Ometti le dimensioni per cui non ci sono obiettivi nel contesto
- Cita obiettivi, strategie e interventi specifici del PEI — NON scrivere valutazioni generiche`}
      />
      {DIMENSIONI.map(d => (
        <div key={d.id} style={{ marginBottom:12, padding:"10px 14px",
          background:hex(d.color,0.04), border:`1px solid ${hex(d.color,0.15)}`, borderRadius:10 }}>
          <div style={{ fontSize:12,fontWeight:700,color:d.color,marginBottom:8 }}>Dimensione {d.id} — {d.label}</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            <Field label="Obiettivi raggiunti">
              <Textarea value={f(`s11_${d.id}_r`)} onChange={e => s(`s11_${d.id}_r`, e.target.value)}
                placeholder="Raggiunti o parzialmente…" rows={2} />
            </Field>
            <Field label="Da riportare a.s. successivo">
              <Textarea value={f(`s11_${d.id}_p`)} onChange={e => s(`s11_${d.id}_p`, e.target.value)}
                placeholder="Da continuare…" rows={2} />
            </Field>
          </div>
        </div>
      ))}
      <Divider />
      <FieldAI
        label="Aggiornamento delle condizioni di contesto e progettazione per l'a.s. successivo"
        sub="Con riferimento a Sez. 5 (obiettivi), Sez. 6 (barriere/facilitatori) e Sez. 7 (ambiente inclusivo)."
        campoId="s11_aggiornamento_contesto"
        value={f("s11_aggiornamento_contesto")}
        onChange={e => s("s11_aggiornamento_contesto", e.target.value)}
        placeholder="Per il prossimo anno scolastico si raccomanda di aggiornare le condizioni di contesto come segue, in riferimento agli obiettivi, alle barriere/facilitatori e all'ambiente inclusivo…"
        rows={5}
        studente={studente}
        sezioneLabel={sl}
        contestoGlobale={contestoGlobale}
        customPrompt={`Sei un docente di sostegno esperto che compila il campo "Aggiornamento delle condizioni di contesto e progettazione per l'a.s. successivo" nella Sezione 11 del PEI (D.M. 182/2020).
${contestoVerifica ? `\nCONTENUTO PROGRAMMATORIO E VERIFICHE INTERMEDIE DEL PEI:\n${contestoVerifica}\n` : ""}
${qCtxS11 ? `\n${qCtxS11}\n` : ""}
Sulla base dell'intero contenuto del PEI (obiettivi programmati, barriere/facilitatori, interventi attivati, esiti delle verifiche) e delle indicazioni fornite dall'insegnante nel questionario (in particolare la raccomandazione per l'a.s. successivo), organizza le proposte concrete per il prossimo anno scolastico. Usa OBBLIGATORIAMENTE questo formato a categorie:

Obiettivi da riproporre
— [obiettivo all'infinito, max 8 parole]
— [obiettivo all'infinito, max 8 parole]

Proposte di azione nel contesto
— [intervento concreto su barriere/facilitatori, max 10 parole]
— [intervento concreto su barriere/facilitatori, max 10 parole]

Strategie
— [nome strategia o approccio metodologico, max 6 parole]
— [nome strategia o approccio metodologico, max 6 parole]

Criticità da monitorare
— [elemento critico da tenere sotto osservazione, max 8 parole]
— [elemento critico da tenere sotto osservazione, max 8 parole]

Regole:
- Genera 2-4 voci per categoria; ometti le categorie non pertinenti
- Tono propositivo e orientato al futuro
- NON usare grassetto
- NON ripetere la valutazione già espressa nel campo precedente`}
      />
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginTop:14 }}>
        <Field label="Proposta ore sostegno">
          <Input value={f("s11_ore")} onChange={e => s("s11_ore", e.target.value)} placeholder="Es. 18 ore settimanali" />
        </Field>
        <Field label="Tipo frequenza">
          <Input value={f("s11_freq")} onChange={e => s("s11_freq", e.target.value)} placeholder="Es. tempo pieno…" />
        </Field>
      </div>
    </div>
  );
}

function SezProvvisorio({ dati, setD, studente, onChat, contestoGlobale }) {
  const sl = "PEI Provvisorio";
  const f = k => dati[k] || "";
  const s = (k,v) => setD(k,v);
  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
        <SezTitle icon="🔮">12. PEI Provvisorio</SezTitle>
        <Btn ghost small onClick={onChat} icon="💬">Assistente</Btn>
      </div>
      <InfoBox color={C.muted} icon="📅">Da compilare entro il 30 giugno — nuove certificazioni o passaggi d'ordine.</InfoBox>
      <Field label="Ordine scolastico a.s. successivo">
        <Input value={f("s12_ord")} onChange={e => s("s12_ord", e.target.value)}
          placeholder={studente?.ordine === "infanzia" ? "Es. Scuola Primaria…" : "Es. Scuola Secondaria di I Grado…"} />
      </Field>
      <Field label={studente?.ordine === "infanzia" ? "Classe / Sezione prevista" : "Classe prevista"}>
        <Input value={f("s12_classe")} onChange={e => s("s12_classe", e.target.value)}
          placeholder={studente?.ordine === "infanzia" ? "Es. Classe 1A primaria, oppure Sezione Gialli…" : "Es. 3A…"} />
      </Field>
      <FieldAI label="Obiettivi educativi provvisori" campoId="s12_ob" value={f("s12_ob")}
        onChange={e => s("s12_ob", e.target.value)} placeholder="Obiettivi indicativi per il prossimo anno…" rows={3}
        studente={studente} sezioneLabel={sl} contestoGlobale={contestoGlobale} />
      <FieldAI label="Risorse richieste" campoId="s12_ris" value={f("s12_ris")}
        onChange={e => s("s12_ris", e.target.value)} placeholder="Ore sostegno, AEC, ausili…" rows={2}
        studente={studente} sezioneLabel={sl} contestoGlobale={contestoGlobale} />
      <FieldAI label="Note per il team accogliente" campoId="s12_note" value={f("s12_note")}
        onChange={e => s("s12_note", e.target.value)} placeholder="Informazioni per il nuovo team…" rows={2}
        studente={studente} sezioneLabel={sl} contestoGlobale={contestoGlobale} />
    </div>
  );
}

// =============================================================================
//  SEZIONE PROGETTO DI VITA (INTEGRATIVA — non parte del modello PEI ministeriale)
//  Raccoglie elementi pedagogici utili al docente per contribuire alla
//  costruzione del Progetto di Vita Individualizzato (L. 227/2021, D.Lgs 62/2024),
//  che è documento autonomo redatto dall'ente territoriale su richiesta della
//  persona/famiglia. NON è prevista come sezione del PEI dal D.I. 182/2020.
// =============================================================================
function SezProgettoDiVita({ dati, setD, studente, onChat, contestoGlobale }) {
  const sl = "Progetto di Vita (integrativo)";
  const f = k => dati[k] || "";
  const s = (k,v) => setD(k,v);
  const ordine = studente?.ordine;
  const isInfanzia = ordine === "infanzia";
  const isSuperiori = ordine === "secondaria2";

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
        <div style={{ display:"flex", alignItems:"center", gap:0 }}>
          <SezTitle icon="🌱">Progetto di Vita <span style={{ fontSize:13, fontWeight:500, color:C.muted, marginLeft:6 }}>(sezione integrativa, facoltativa)</span></SezTitle>
          <GlossarioBtn voce="Progetto di Vita" />
        </div>
        <Btn ghost small onClick={onChat} icon="💬">Assistente</Btn>
      </div>

      <InfoBox color={C.orange} icon="ℹ️">
        <strong>Questa sezione non è parte del modello PEI ministeriale</strong> (D.I. 182/2020). Raccoglie elementi pedagogici utili al docente di sostegno per contribuire — su richiesta della famiglia — alla costruzione del <strong>Progetto di Vita Individualizzato</strong> previsto dalla <strong>L. 22 dicembre 2021 n. 227</strong> e dal <strong>D.Lgs. 3 maggio 2024 n. 62</strong>. Il Progetto di Vita è un documento autonomo, distinto dal PEI, di cui è titolare la persona con disabilità (o la famiglia per i minori) e che viene redatto dall'ente territoriale competente. La compilazione di questa sezione è <strong>facoltativa</strong>.
      </InfoBox>

      <InfoBox color={C.teal} icon="📌">
        {isInfanzia
          ? <>Per la scuola dell'infanzia, qui puoi annotare gli interessi osservati nel bambino/nella bambina, ciò che lo motiva nel gioco e nelle routine, le indicazioni emerse dai colloqui con la famiglia. Sono elementi che — quando attivato — andranno a confluire nel Progetto di Vita.</>
          : <>Descrive chi vuole diventare la persona, quali competenze di vita sta costruendo e come la scuola concorre al suo sviluppo, al di là del solo curricolo.{isSuperiori && <span> Per la scuola secondaria di II grado include anche l'orientamento post-diploma.</span>}</>
        }
      </InfoBox>

      <Divider />

      <FieldAI
        label={isInfanzia ? "Interessi osservati e voce della famiglia" : "Interessi, passioni e aspirazioni dello studente"}
        sub={isInfanzia
          ? "Cosa attira l'attenzione del bambino/della bambina, cosa lo motiva nel gioco e nelle routine, indicazioni emerse dai colloqui con la famiglia"
          : "Cosa ama fare, cosa lo motiva, cosa immagina per il suo futuro — anche attraverso la voce della famiglia"}
        campoId="pdv_aspirazioni"
        value={f("pdv_aspirazioni")}
        onChange={e => s("pdv_aspirazioni", e.target.value)}
        placeholder={isInfanzia
          ? "Descrivi gli interessi osservati nel bambino/nella bambina (giochi preferiti, esperienze che lo motivano, ambiti in cui si esprime con piacere) e gli elementi emersi dai colloqui con la famiglia…"
          : "Descrivi gli interessi, le passioni e le aspirazioni dello studente, emerse dai colloqui con la famiglia e dall'osservazione diretta…"}
        rows={4}
        required
        studente={studente} sezioneLabel={sl} contestoGlobale={contestoGlobale} />

      <FieldAI
        label={isInfanzia ? "Autonomie di base in costruzione" : "Competenze di vita autonoma da sviluppare"}
        sub={isInfanzia
          ? "Autonomie personali, routine quotidiane, prime competenze relazionali, abilità senso-motorie in via di sviluppo"
          : "Autonomia personale, gestione del tempo, orientamento, abilità sociali e pratiche di vita quotidiana"}
        campoId="pdv_competenze_vita"
        value={f("pdv_competenze_vita")}
        onChange={e => s("pdv_competenze_vita", e.target.value)}
        placeholder={isInfanzia
          ? "Identifica le autonomie di base prioritarie per il bambino/la bambina (cura di sé, alimentazione, igiene, vestirsi, prime regole di gruppo, autoregolazione emotiva)…"
          : "Identifica le competenze di vita autonoma prioritarie per questo studente (cura di sé, gestione del tempo, spostamenti, attività pratiche, relazioni sociali)…"}
        rows={4}
        studente={studente} sezioneLabel={sl} contestoGlobale={contestoGlobale} />

      <FieldAI
        label="Reti di supporto extrascolastico"
        sub="Famiglia, servizi sociali, centri diurni, associazioni, terapisti, comunità di riferimento"
        campoId="pdv_reti_supporto"
        value={f("pdv_reti_supporto")}
        onChange={e => s("pdv_reti_supporto", e.target.value)}
        placeholder="Descrivi le reti di supporto presenti al di fuori della scuola (famiglia, servizi sociali, centri riabilitativi, associazioni sportive o ricreative, gruppi di pari)…"
        rows={3}
        studente={studente} sezioneLabel={sl} contestoGlobale={contestoGlobale} />

      <FieldAI
        label="Partecipazione sociale e inclusione nella comunità"
        sub="Come la scuola favorisce l'inclusione nella comunità più ampia"
        campoId="pdv_partecipazione"
        value={f("pdv_partecipazione")}
        onChange={e => s("pdv_partecipazione", e.target.value)}
        placeholder="Descrivi le azioni che la scuola mette in campo per favorire la partecipazione sociale dello studente (uscite, attività extracurricolari, rapporti con il territorio)…"
        rows={3}
        studente={studente} sezioneLabel={sl} contestoGlobale={contestoGlobale} />

      {/* Orientamento post-diploma — solo superiori */}
      {isSuperiori && (
        <>
          <Divider />
          <div style={{ fontSize:12, fontWeight:700, color:C.accent, marginBottom:10, textTransform:"uppercase", letterSpacing:.5 }}>
            Orientamento post-diploma
          </div>
          <FieldAI
            label="Percorso post-diploma ipotizzato"
            sub="Università, ITS Academy, lavoro, centri diurni, percorsi di formazione professionale — da costruire con lo studente e la famiglia"
            campoId="pdv_post_diploma"
            value={f("pdv_post_diploma")}
            onChange={e => s("pdv_post_diploma", e.target.value)}
            placeholder="Descrivi le possibilità post-diploma esplorate con lo studente e la famiglia: percorsi universitari o di formazione, inserimento lavorativo, servizi territoriali di supporto all'autonomia…"
            rows={4}
            studente={studente} sezioneLabel={sl} contestoGlobale={contestoGlobale} />

          <FieldAI
            label="Competenze spendibili e punti di forza per il futuro"
            sub="Abilità trasversali, soft skills, competenze specifiche su cui investire per l'inserimento nel mondo adulto"
            campoId="pdv_competenze_spendibili"
            value={f("pdv_competenze_spendibili")}
            onChange={e => s("pdv_competenze_spendibili", e.target.value)}
            placeholder="Elenca le competenze già acquisite o in sviluppo che rappresentano risorse concrete per il futuro dello studente (autonomia lavorativa, competenze digitali, attitudini specifiche, capacità relazionali)…"
            rows={3}
            studente={studente} sezioneLabel={sl} contestoGlobale={contestoGlobale} />
        </>
      )}
    </div>
  );
}

// =============================================================================
//  VERIFICA COERENZA
// =============================================================================
function VerificaCoerenza({ dati, studente, onClose, risultatiIniziali, onRicalcola, onSalvaCorrezione }) {
  const { balance } = useCreditsCtx();
  const [loading,           setLoading]           = useState(false);
  const [risultati,         setRisultati]         = useState(risultatiIniziali || null);
  const [errore,            setErrore]            = useState("");
  const [minimized,         setMinimized]         = useState(false);
  const [correzionePending, setCorrezionePending] = useState(null);
  // correzionePending = { problema, campoKey, valoreOriginale, valoreProposto, valoreModificato, loadingCorr, erroreCorr }
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const senzaCrediti = balance !== null && balance < COSTI_CREDITI.VERIFICA_COERENZA;

  // Tono incoraggiante basato su punteggio e numero problemi
  const etichettaPunteggio = (p, nProb) => {
    if (p >= 75) return nProb === 0 ? "PEI eccellente! ✨" : `Ottimo lavoro! ${nProb} piccol${nProb===1?"o":"i"} ritocch${nProb===1?"o":"i"}`;
    if (p >= 50) return `Buona base! ${nProb} aspett${nProb===1?"o":"i"} da migliorare`;
    return `Ci siamo quasi — ${nProb} punt${nProb===1?"o":"i"} important${nProb===1?"e":"i"} da rivedere`;
  };

  const verifica = async () => {
    setErrore("");
    setLoading(true); setRisultati(null);
    try {
      const sezioni = [];
      if (dati.s1_famiglia)    sezioni.push(`SEZ.1 Quadro familiare: ${dati.s1_famiglia.slice(0,200)}`);
      if (dati.pf_descrizione) sezioni.push(`SEZ.2 Profilo funzionamento: ${dati.pf_descrizione.slice(0,1000)}`);
      DIMENSIONI.forEach(d => {
        if (dati[`s4_${d.id}_obs`]) sezioni.push(`SEZ.4 Dim.${d.id} Osservazioni: ${dati[`s4_${d.id}_obs`].slice(0,450)}`);
      });
      DIMENSIONI.forEach(d => {
        if (dati[`s5_${d.id}_ob`])    sezioni.push(`SEZ.5 Dim.${d.id} Obiettivi: ${dati[`s5_${d.id}_ob`].slice(0,250)}`);
        if (dati[`s5_${d.id}_attiv`]) sezioni.push(`SEZ.5 Dim.${d.id} Attività: ${dati[`s5_${d.id}_attiv`].slice(0,300)}`);
        if (dati[`s5_${d.id}_strat`]) sezioni.push(`SEZ.5 Dim.${d.id} Strategie: ${dati[`s5_${d.id}_strat`].slice(0,450)}`);
      });
      if (dati.s6_barriere)     sezioni.push(`SEZ.6 Barriere: ${dati.s6_barriere.slice(0,500)}`);
      if (dati.s6_facilitatori) sezioni.push(`SEZ.6 Facilitatori: ${dati.s6_facilitatori.slice(0,500)}`);
      if (dati.s7_interventi)   sezioni.push(`SEZ.7 Ambiente inclusivo: ${dati.s7_interventi.slice(0,300)}`);
      if (dati.s7_tech)         sezioni.push(`SEZ.7 Tecnologie: ${dati.s7_tech.slice(0,150)}`);
      if (dati.s9_ore_sost)     sezioni.push(`SEZ.9 Ore sostegno: ${dati.s9_ore_sost}`);
      if (dati.s9_ore_aec)      sezioni.push(`SEZ.9 Ore assistente autonomia: ${dati.s9_ore_aec}`);
      if (dati.s9_modalita)     sezioni.push(`SEZ.9 Modalità sostegno: ${dati.s9_modalita.slice(0,200)}`);
      if (dati.s9_tech)         sezioni.push(`SEZ.9 Tecnologie/Ausili: ${dati.s9_tech.slice(0,150)}`);
      if (dati.s11_valutazione) sezioni.push(`SEZ.11 Verifica finale: ${dati.s11_valutazione.slice(0,200)}`);

      const s8Riepilogo = [];
      for (let i = 0; i < 12; i++) {
        const scelta = dati[`s8_${i}_scelta`] || "A";
        const ob     = dati[`s8_${i}_ob`] || "";
        const obC    = dati[`s8_${i}_c_obiettivi`] || "";
        if (scelta === "B" && ob) s8Riepilogo.push(`  Disciplina ${i+1} (B): ${ob.slice(0,80)}`);
        if (scelta === "C" && obC) s8Riepilogo.push(`  Disciplina ${i+1} (C — percorso differenziato): ${obC.slice(0,80)}`);
      }
      if (s8Riepilogo.length > 0) sezioni.push(`SEZ.8 Curricolo personalizzato (B/C):\n${s8Riepilogo.join("\n")}`);

      const peiRiepilogo = sezioni.join("\n\n");
      const sezioniCompilate = sezioni.length;

      if (sezioniCompilate < 3) {
        setRisultati({ punteggio: 0, problemi: [], avvisi: ["Il PEI ha troppo poche sezioni compilate per una verifica significativa. Compila almeno le sezioni 2, 4 e 5 prima di verificare la coerenza."], positivi: [] });
        setLoading(false);
        return;
      }

      const prompt = `Analizza il seguente PEI e verifica la coerenza interna tra le sezioni.

DATI STUDENTE:
- Iniziali: ${studente?.iniziali || "N.N."}
- Età: ${studente?.eta || "n.d."} anni
- Ordine: ${ORDINI[studente?.ordine]?.label || "n.d."}
- Diagnosi: ${buildDiagnosiContesto(studente, "n.d.")}${buildRiferimentoICD10(studente)}

CONTENUTO PEI COMPILATO:
${peiRiepilogo}

MAPPA CAMPI (per il campo campoChiave):
SEZ.1→s1_famiglia | SEZ.2→pf_descrizione
SEZ.4 Dim.X Osservazioni→s4_X_obs (es. s4_A_obs, s4_B_obs, s4_C_obs, s4_D_obs, s4_E_obs)
SEZ.5 Dim.X Obiettivi→s5_X_ob | SEZ.5 Dim.X Attività→s5_X_attiv | SEZ.5 Dim.X Strategie→s5_X_strat
SEZ.6 Barriere→s6_barriere | SEZ.6 Facilitatori→s6_facilitatori
SEZ.7 Ambiente→s7_interventi | SEZ.7 Tech→s7_tech | SEZ.9 Ore→s9_ore_sost | SEZ.9 Ore assistente→s9_ore_aec | SEZ.9 Modalità→s9_modalita | SEZ.9 Tech→s9_tech | SEZ.11→s11_valutazione

Analizza la coerenza interna e rispondi SOLO con JSON valido (senza markdown):
{
  "punteggio": <numero 0-100>,
  "problemi": [
    {
      "gravita": "alta|media|bassa",
      "sezioni": "etichetta sezione in linguaggio semplice (es. 'Osservazioni → Obiettivi')",
      "descrizione": "descrizione chiara del problema (max 20 parole)",
      "suggerimento": "come correggere concretamente (max 40 parole, indica la sezione e cosa scrivere)",
      "campoChiave": "chiave del campo da correggere secondo la mappa sopra (es. s5_A_ob)"
    }
  ],
  "avvisi": ["avviso 1"],
  "positivi": ["punto di forza 1"]
}

Controlla: coerenza sez.4→sez.5, strategie vs barriere sez.6, ausili vs obiettivi, curricolo vs profilo, obiettivi SMART, copertura 4 dimensioni ICF, ore sostegno adeguate.
CODICI ICF — NON SONO MATERIA DI QUESTA VERIFICA: i codici ICF eventualmente presenti in SEZ.2 provengono dal Profilo di Funzionamento redatto dall'unità di valutazione multidisciplinare dell'ASL e vanno trascritti fedelmente. NON segnalarli mai come problema, non discutere la scelta del codice o la sua classificazione (attività "d" vs funzioni "b"), non proporre di sostituirli, aggiungerli o modificarne i qualificatori. Nessun suggerimento deve invitare a cambiare un codice.
IMPORTANTE: max 3 problemi, max 2 avvisi, max 3 positivi. Usa campoChiave solo se sei certo del campo corretto.`;

      const risposta = await callClaude(prompt, 2000, 'verifica_coerenza', { studenteId: studente?.id });
      let jsonStr = risposta.match(/\{[\s\S]*\}/)?.[0];
      if (!jsonStr) throw new Error("Risposta non valida dall'AI");

      const tryParse = (s) => { try { return JSON.parse(s); } catch { return null; } };
      let parsed = tryParse(jsonStr);
      if (!parsed) {
        let repaired = jsonStr;
        const opens = (repaired.match(/\[/g)||[]).length;
        const closes = (repaired.match(/\]/g)||[]).length;
        for (let i = 0; i < opens - closes; i++) repaired += "]";
        if (!repaired.endsWith("}")) repaired += "}";
        parsed = tryParse(repaired);
      }
      if (!parsed) throw new Error("JSON Parse error: " + jsonStr.slice(-50));
      if (mounted.current) setRisultati(parsed);
      if (onRicalcola) onRicalcola(parsed);
    } catch(e) {
      if (mounted.current) {
        if (e?.code === 'insufficient_credits') {
          setErrore(`Servono ${COSTI_CREDITI.VERIFICA_COERENZA} crediti per la verifica. Vai su Account per ricaricare.`);
        } else {
          setErrore(e.message);
        }
      }
    }
    if (mounted.current) setLoading(false);
  };

  const avviaCorrezione = async (problema) => {
    const campoKey = problema.campoChiave;
    const valoreOriginale = campoKey ? (dati[campoKey] || "") : "";
    setCorrezionePending({
      problema, campoKey, valoreOriginale,
      valoreProposto: "", valoreModificato: "",
      loadingCorr: true, erroreCorr: ""
    });

    const promptCorr = `Sei un assistente per la redazione di PEI (Piano Educativo Individualizzato) italiani.

PROBLEMA RILEVATO:
${problema.descrizione}

TESTO ATTUALE del campo da migliorare:
"${valoreOriginale || "(campo vuoto)"}"

SUGGERIMENTO:
${problema.suggerimento}

Riscrivi il testo in modo da risolvere il problema, mantenendo uno stile professionale adatto a un PEI. Rispondi SOLO con il testo corretto, senza virgolette, senza spiegazioni, senza markdown.`;

    try {
      const risposta = await callClaude(promptCorr, 600);
      if (mounted.current) {
        const testo = risposta.trim();
        setCorrezionePending(p => ({ ...p, valoreProposto: testo, valoreModificato: testo, loadingCorr: false }));
      }
    } catch(e) {
      if (mounted.current) {
        setCorrezionePending(p => ({ ...p, loadingCorr: false, erroreCorr: e.message }));
      }
    }
  };

  const coloreGravita = { alta: C.red,     media: C.orange,    bassa: C.teal };
  const bgGravita     = { alta: C.redDim,  media: C.orangeDim, bassa: C.tealDim };
  const iconGravita   = { alta: "🔴",      media: "🟡",        bassa: "🔵" };
  const colPunteggio  = risultati
    ? risultati.punteggio >= 75 ? C.green
    : risultati.punteggio >= 50 ? C.orange : C.red
    : C.muted;

  return (
    <>

    {/* ── Modal correzione Prima/Dopo ─────────────────────────────────── */}
    {correzionePending && (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.65)",
        zIndex:3000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
        <div style={{ background:C.surface, borderRadius:16, width:"100%", maxWidth:560,
          boxShadow:"0 24px 64px rgba(0,0,0,0.35)", display:"flex", flexDirection:"column",
          maxHeight:"80vh" }}>
          {/* Header */}
          <div style={{ padding:"16px 20px", borderBottom:`1px solid ${C.border}`,
            display:"flex", alignItems:"center", justifyContent:"space-between",
            background:grad(hex(C.accent,0.07), hex(C.accent2,0.04)),
            borderRadius:"16px 16px 0 0" }}>
            <div>
              <div style={{ fontSize:14, fontWeight:700, color:C.text }}>✏️ Correzione AI</div>
              <div style={{ fontSize:11, color:C.muted, marginTop:1 }}>Rivedi e accetta la proposta</div>
            </div>
            <button onClick={() => setCorrezionePending(null)}
              style={{ background:"none", border:"none", color:C.muted, fontSize:20,
                cursor:"pointer", lineHeight:1 }}>✕</button>
          </div>
          {/* Body */}
          <div style={{ padding:"18px 20px", overflowY:"auto", flex:1 }}>
            <div style={{ fontSize:12, color:C.muted, marginBottom:16, lineHeight:1.6,
              padding:"8px 12px", background:C.bg, borderRadius:7, border:`1px solid ${C.border}` }}>
              🔎 <strong>Problema:</strong> {correzionePending.problema.descrizione}
            </div>

            {correzionePending.loadingCorr ? (
              <div style={{ textAlign:"center", padding:"32px 0" }}>
                <Spinner size={28} />
                <div style={{ fontSize:13, color:C.muted, marginTop:14 }}>
                  L'AI sta preparando la correzione…
                </div>
              </div>
            ) : correzionePending.erroreCorr ? (
              <InfoBox color={C.red} icon="⚠️">{correzionePending.erroreCorr}</InfoBox>
            ) : (
              <>
                {/* Prima */}
                <div style={{ marginBottom:12 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:C.muted,
                    textTransform:"uppercase", letterSpacing:.5, marginBottom:6 }}>
                    Prima
                  </div>
                  <div style={{ padding:"10px 13px", background:hex(C.red,0.06),
                    border:`1px solid ${hex(C.red,0.2)}`, borderRadius:8,
                    fontSize:12, color:C.textSec, lineHeight:1.65,
                    maxHeight:90, overflowY:"auto", whiteSpace:"pre-wrap" }}>
                    {correzionePending.valoreOriginale
                      ? correzionePending.valoreOriginale
                      : <em style={{color:C.muted}}>(campo vuoto)</em>}
                  </div>
                </div>
                {/* Dopo */}
                <div style={{ marginBottom:20 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:C.green,
                    textTransform:"uppercase", letterSpacing:.5, marginBottom:6 }}>
                    Dopo · modifica se vuoi
                  </div>
                  <textarea
                    value={correzionePending.valoreModificato}
                    onChange={e => setCorrezionePending(p => ({ ...p, valoreModificato: e.target.value }))}
                    rows={5}
                    style={{ width:"100%", padding:"10px 13px",
                      background:hex(C.green,0.05), border:`1.5px solid ${hex(C.green,0.3)}`,
                      borderRadius:8, fontSize:12, color:C.text, lineHeight:1.65,
                      resize:"vertical", fontFamily:"inherit", outline:"none",
                      boxSizing:"border-box", transition:"border .15s" }}
                    onFocus={e => e.target.style.borderColor = C.green}
                    onBlur={e => e.target.style.borderColor = hex(C.green,0.3)}
                  />
                </div>
                {/* Azioni */}
                <div style={{ display:"flex", gap:10 }}>
                  <Btn primary
                    onClick={() => {
                      if (correzionePending.campoKey && onSalvaCorrezione) {
                        onSalvaCorrezione(correzionePending.campoKey, correzionePending.valoreModificato);
                      }
                      setCorrezionePending(null);
                    }}
                    style={{ flex:1, justifyContent:"center" }}>
                    ✅ Accetta e salva
                  </Btn>
                  <Btn ghost onClick={() => setCorrezionePending(null)}>Annulla</Btn>
                </div>
                {!correzionePending.campoKey && (
                  <div style={{ marginTop:10, fontSize:11, color:C.orange, textAlign:"center" }}>
                    ⚠ Campo non identificato automaticamente — vai alla sezione manualmente.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    )}

    {/* ── Pill minimizzata ────────────────────────────────────────────── */}
    {minimized && (
      <div onClick={() => setMinimized(false)}
        title="Clicca per riaprire la verifica"
        style={{ position:"fixed", bottom:24, right:24, zIndex:700,
          display:"flex", alignItems:"center", gap:10,
          background:risultati ? colPunteggio : C.accent,
          color:"#fff", borderRadius:99, padding:"10px 20px",
          boxShadow:"0 4px 20px rgba(0,0,0,0.25)", cursor:"pointer",
          fontSize:13, fontWeight:700, userSelect:"none" }}
        onMouseEnter={e=>e.currentTarget.style.opacity="0.9"}
        onMouseLeave={e=>e.currentTarget.style.opacity="1"}
      >
        <span>🔍</span>
        {risultati
          ? `Coerenza ${risultati.punteggio}/100${risultati.problemi?.length ? ` · ${risultati.problemi.length} problemi` : " · OK"}`
          : "Verifica coerenza"}
        <span style={{fontSize:11, opacity:.8, marginLeft:4}}>↑ espandi</span>
      </div>
    )}

    {/* ── Pannello principale ─────────────────────────────────────────── */}
    {!minimized && (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)",
      display:"flex", alignItems:"center", justifyContent:"center",
      zIndex:2000, padding:20 }}>
      <div className="fadeUp" style={{ background:C.surface, borderRadius:16,
        width:"100%", maxWidth:680, maxHeight:"85vh",
        display:"flex", flexDirection:"column",
        boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>

        {/* Header */}
        <div style={{ padding:"18px 22px", borderBottom:`1px solid ${C.border}`,
          display:"flex", alignItems:"center", justifyContent:"space-between",
          background:grad(hex(C.accent2,0.06), hex(C.accent,0.04)),
          borderRadius:"16px 16px 0 0" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:38, height:38, borderRadius:10,
              background:grad(C.accent2, C.accent),
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:18 }}>🔍</div>
            <div>
              <div style={{ fontSize:15, fontWeight:700, color:C.text }}>Verifica Coerenza PEI</div>
              <div style={{ fontSize:11, color:C.muted }}>Analisi AI delle incongruenze tra sezioni</div>
            </div>
          </div>
          <div style={{ display:"flex", gap:6 }}>
            <button onClick={() => setMinimized(true)}
              title="Riduci ad icona per correggere"
              style={{ background:hex(C.accent,0.08), border:`1px solid ${hex(C.accent,0.2)}`,
                borderRadius:6, color:C.accent, fontSize:16, fontWeight:700,
                cursor:"pointer", padding:"2px 10px", lineHeight:1 }}>—</button>
            <button onClick={onClose} style={{ background:"none", border:"none",
              color:C.muted, fontSize:20, cursor:"pointer", padding:"2px 6px" }}>✕</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex:1, overflowY:"auto", padding:22 }}>

          {/* Stato iniziale */}
          {!risultati && !loading && (
            <div style={{ textAlign:"center", padding:"30px 0" }}>
              <div style={{ fontSize:48, marginBottom:14 }}>🔍</div>
              <div style={{ fontSize:14, color:C.text, fontWeight:600, marginBottom:8 }}>
                Pronto per la verifica
              </div>
              <div style={{ fontSize:13, color:C.muted, marginBottom:24, lineHeight:1.7 }}>
                L'AI analizzerà tutte le sezioni compilate e segnalerà<br/>
                incongruenze, avvisi e punti di forza del PEI.
              </div>
              <Btn primary onClick={verifica} icon="🔍" disabled={senzaCrediti}
                title={senzaCrediti ? `Servono ${COSTI_CREDITI.VERIFICA_COERENZA} crediti` : "Avvia (3 crediti)"}
                style={{ fontSize:14, padding:"11px 28px" }}>
                Avvia verifica coerenza · {COSTI_CREDITI.VERIFICA_COERENZA} crediti
              </Btn>
              {senzaCrediti && (
                <div style={{ marginTop:14, fontSize:12, color:C.red, fontWeight:500 }}>
                  ⚠ Crediti insufficienti ({balance}/{COSTI_CREDITI.VERIFICA_COERENZA}). Vai su Account per acquistare.
                </div>
              )}
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div style={{ textAlign:"center", padding:"40px 0" }}>
              <Spinner size={36} />
              <div style={{ fontSize:13, color:C.muted, marginTop:16 }}>
                Analisi in corso — sto leggendo tutte le sezioni…
              </div>
            </div>
          )}

          {errore && <InfoBox color={C.red} icon="⚠️">{errore}</InfoBox>}

          {/* Risultati */}
          {risultati && (
            <div className="fadeIn">

              {/* Score card con progress bar */}
              <div style={{ marginBottom:22, padding:"18px 20px",
                background:hex(colPunteggio, 0.06),
                border:`1px solid ${hex(colPunteggio, 0.2)}`, borderRadius:14 }}>
                <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:14 }}>
                  <div style={{ width:64, height:64, borderRadius:"50%",
                    background:grad(colPunteggio, hex(colPunteggio, 0.7)),
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontSize:20, fontWeight:800, color:"#fff", flexShrink:0 }}>
                    {risultati.punteggio}
                  </div>
                  <div>
                    <div style={{ fontSize:16, fontWeight:700, color:colPunteggio }}>
                      {etichettaPunteggio(risultati.punteggio, risultati.problemi?.length || 0)}
                    </div>
                    <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>
                      Punteggio di coerenza interna
                    </div>
                  </div>
                </div>
                {/* Progress bar */}
                <div style={{ height:8, borderRadius:99, background:hex(colPunteggio, 0.15),
                  overflow:"hidden" }}>
                  <div style={{ height:"100%", borderRadius:99,
                    background:grad(hex(colPunteggio,0.6), colPunteggio),
                    width:`${risultati.punteggio}%`,
                    transition:"width .8s cubic-bezier(.4,0,.2,1)" }} />
                </div>
                <div style={{ display:"flex", justifyContent:"space-between",
                  fontSize:10, color:C.muted, marginTop:4 }}>
                  <span>0</span><span>50</span><span>100</span>
                </div>
              </div>

              {/* Problemi */}
              {risultati.problemi?.length > 0 && (
                <div style={{ marginBottom:18 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:10 }}>
                    ⚠️ Punti da migliorare ({risultati.problemi.length})
                  </div>
                  {risultati.problemi.map((p, i) => (
                    <div key={i} style={{ marginBottom:12, padding:"13px 15px",
                      background:bgGravita[p.gravita] || C.bg,
                      border:`1px solid ${hex(coloreGravita[p.gravita] || C.muted, 0.3)}`,
                      borderLeft:`4px solid ${coloreGravita[p.gravita] || C.muted}`,
                      borderRadius:10 }}>
                      {/* Intestazione problema */}
                      <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:6,
                        flexWrap:"wrap" }}>
                        <span>{iconGravita[p.gravita] || "⚪"}</span>
                        <span style={{ fontSize:11, fontWeight:700,
                          color:coloreGravita[p.gravita] || C.muted,
                          textTransform:"uppercase" }}>{p.gravita}</span>
                        {p.sezioni && (
                          <span style={{ fontSize:11, color:C.muted,
                            background:C.surface, padding:"2px 8px",
                            borderRadius:4, border:`1px solid ${C.border}` }}>
                            {p.sezioni}
                          </span>
                        )}
                      </div>
                      {/* Descrizione */}
                      <div style={{ fontSize:13, color:C.text, marginBottom:6 }}>
                        {p.descrizione}
                      </div>
                      {/* Suggerimento */}
                      {p.suggerimento && (
                        <div style={{ padding:"8px 11px", marginBottom:10,
                          background:hex(C.accent,0.06),
                          border:`1px solid ${hex(C.accent,0.2)}`,
                          borderRadius:7, fontSize:12, color:C.accent, lineHeight:1.6 }}>
                          <span style={{ fontWeight:700 }}>💡 Come correggere: </span>
                          {p.suggerimento}
                        </div>
                      )}
                      {/* Bottone correggi */}
                      <button
                        onClick={() => avviaCorrezione(p)}
                        style={{ display:"inline-flex", alignItems:"center", gap:7,
                          background:C.surface, border:`1.5px solid ${hex(coloreGravita[p.gravita]||C.accent, 0.35)}`,
                          borderRadius:7, padding:"6px 14px", cursor:"pointer",
                          fontSize:12, fontWeight:600, color:coloreGravita[p.gravita]||C.accent,
                          transition:"all .15s" }}
                        onMouseEnter={e => { e.currentTarget.style.background = hex(coloreGravita[p.gravita]||C.accent, 0.08); }}
                        onMouseLeave={e => { e.currentTarget.style.background = C.surface; }}
                      >
                        ✏️ Suggerisci correzione AI
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Avvisi */}
              {risultati.avvisi?.length > 0 && (
                <div style={{ marginBottom:18 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:10 }}>
                    📋 Avvisi generali
                  </div>
                  {risultati.avvisi.map((a, i) => (
                    <div key={i} style={{ marginBottom:7, padding:"9px 13px",
                      background:C.bg, border:`1px solid ${C.border}`,
                      borderRadius:7, fontSize:13, color:C.textSec }}>
                      • {a}
                    </div>
                  ))}
                </div>
              )}

              {/* Positivi */}
              {risultati.positivi?.length > 0 && (
                <div style={{ marginBottom:14 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:C.green, marginBottom:10 }}>
                    ✅ Punti di forza del PEI
                  </div>
                  {risultati.positivi.map((p, i) => (
                    <div key={i} style={{ marginBottom:7, padding:"9px 13px",
                      background:C.greenDim, border:`1px solid ${hex(C.green, 0.25)}`,
                      borderRadius:7, fontSize:13, color:C.green }}>
                      ✓ {p}
                    </div>
                  ))}
                </div>
              )}

              <div style={{ textAlign:"center", marginTop:16 }}>
                <Btn ghost small onClick={verifica} icon="🔄">Riesegui verifica</Btn>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    )}
    </>
  );
}

// =============================================================================
//  PEI EDITOR (con modalità guidata)
// =============================================================================
function PeiEditor({ studente, peiDatiMap, setPeiDatiMap, documentiStudenti, salvaRef, setTab }) {
  const { guidedMode, setGuidedMode } = useSettings();
  const { plan } = useCreditsCtx();
  const isTrial = isPianoTrial(plan);
  const [sezAtt, setSezAtt] = useState("glo");
  const [dati, setDati] = useState({});
  const [salvato, setSalvato] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [verificaOpen, setVerificaOpen] = useState(false);
  const [ultimiRisultatiVerifica, setUltimiRisultatiVerifica] = useState(null);
  const [showGuidedTooltip, setShowGuidedTooltip] = useState(false);
  const mountedRef = useRef(true);


  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  useEffect(() => {
    setSezAtt("glo");
    setDati(peiDatiMap?.[studente?.id] || {});
    setSalvato(false);
    const key = `guided_tooltip_shown_${studente?.id}`;
    if (guidedMode && !localStorage.getItem(key)) {
      setShowGuidedTooltip(true);
      localStorage.setItem(key, 'true');
    }
  }, [studente?.id]);

  // Sezione corrente bloccata per utenti in piano free.
  // Le sezioni sbloccate sono glo/s1/s2/s3/s4 (sez 4 con gating dimensioni).
  // Calcolato qui (prima di qualsiasi early return) per rispettare le rules of hooks
  // del useEffect sotto, che chiude la ChatAssistente quando l'utente naviga in una
  // sezione bloccata mentre la chat era aperta da una sezione sbloccata: la chat è
  // renderizzata fuori dal fieldset quindi non si disabilita automaticamente.
  const sezioneBloccata = isSezioneBloccataTrial(sezAtt, plan);
  useEffect(() => {
    if (sezioneBloccata && chatOpen) setChatOpen(false);
  }, [sezioneBloccata, chatOpen]);

  const setD = (k, v) => setDati(p => ({ ...p, [k]: v }));

  const datiRef  = useRef(dati);
  const dirtyRef = useRef(false);
  useEffect(() => { datiRef.current = dati; }, [dati]);

  const contestoGlobale = useMemo(() => {
    const parti = [];
    if (dati.s1_famiglia) parti.push(`Quadro familiare: ${dati.s1_famiglia.slice(0,350)}`);
    if (dati.pf_descrizione) parti.push(`Profilo di funzionamento (ICF): ${dati.pf_descrizione.slice(0,600)}`);
    DIMENSIONI.forEach(d => {
      if (dati[`s4_${d.id}_obs`]) parti.push(`Osservazioni Dim.${d.id}: ${dati[`s4_${d.id}_obs`].slice(0,350)}`);
    });
    if (dati.s9_ore_sost) parti.push(`Ore di sostegno settimanali: ${dati.s9_ore_sost}`);
    if (dati.s9_ore_aec)  parti.push(`Ore assistente autonomia e comunicazione: ${dati.s9_ore_aec}`);
    // Arricchimento ADDITIVO per la sezione attiva: aggiunge al core i contenuti
    // delle sezioni correlate dove oggi mancano (es. Sez.8/10 non vedevano gli
    // obiettivi di Sez.5; Sez.5/9 non vedevano il Progetto Individuale di Sez.3).
    // Content-gated: entra solo ciò che è compilato → compatibile con la navigazione
    // libera. Sez.7 e le verifiche non sono in mappa: restano come oggi.
    const arricchimento = contestoArricchimento(dati, sezAtt, studente?.ordine);
    if (arricchimento) parti.push(arricchimento);
    // Documenti dall'archivio, scoped per sezione attiva: solo i tipi pertinenti a
    // questa sezione (riduce token e rumore). (`storicoPEI` è un campo legacy non
    // più popolato: il PEI precedente vive qui come documento.)
    const tipiAmmessi = DOC_SCOPE_SEZIONE[sezAtt] ?? DOC_SCOPE_DEFAULT;
    const docsArchivio = (documentiStudenti?.[studente?.id] || []).filter(d => tipiAmmessi.includes(d.tipo));
    if (docsArchivio.length > 0) {
      // Budget di iniezione per tipo: l'estrazione AI del PEI precedente è densa
      // (fino a ~900 parole) e va preservata; diagnosi e PF sono ricchi di dati
      // clinici; gli altri tipi restano contenuti. Evita di sprecare l'estrazione.
      const budgetDoc = (tipo) =>
        tipo === 'pei_prec' ? 5000 :
        (tipo === 'pf' || tipo === 'diagnosi') ? 2500 :
        1200;
      // Priorità: i dati clinici/funzionali entrano per primi, "altro" per ultimo.
      const PRIORITA_DOC = { pf: 0, diagnosi: 1, pei_prec: 2, glho: 3, altro: 4 };
      // Tetto globale sul blocco documenti: evita che molti documenti gonfino il
      // prompt e diluiscano i dati prioritari. Il troncamento parte dalla coda.
      const TETTO_DOC = 9000;
      let usato = 0;
      const docsTesto = docsArchivio
        .filter(d => d.testo && d.testo.trim().length > 0)
        .slice()
        .sort((a, b) => (PRIORITA_DOC[a.tipo] ?? 9) - (PRIORITA_DOC[b.tipo] ?? 9))
        .map(d => {
          if (usato >= TETTO_DOC) return null;
          const spazio = Math.min(budgetDoc(d.tipo), TETTO_DOC - usato);
          const testo = d.testo.slice(0, spazio);
          usato += testo.length;
          return `[${d.label} — ${d.nome}]:\n${testo}`;
        })
        .filter(Boolean)
        .join('\n\n---\n\n');
      if (docsTesto) parti.push(`DOCUMENTI DELL'ALUNNO (diagnosi, PEI precedenti, relazioni cliniche):\n${docsTesto}`);
    }
    return parti.length > 0 ? parti.join("\n") : null;
  }, [dati, documentiStudenti, studente?.id, studente?.ordine, sezAtt]);

  const salva = () => {
    setPeiDatiMap(p => ({ ...p, [studente.id]: dati }));
    dirtyRef.current = false;
    setSalvato(true);
    setTimeout(() => { if (mountedRef.current) setSalvato(false); }, 2500);
  };

  const salvaImmediato = () => {
    if (!studente?.id) return;
    setPeiDatiMap(p => ({ ...p, [studente.id]: dati }));
    dirtyRef.current = false;
  };
  useEffect(() => {
    if (salvaRef) salvaRef.current = salvaImmediato;
    return () => { if (salvaRef) salvaRef.current = null; };
  }, [studente?.id]);

  useEffect(() => {
    const id = studente?.id;
    if (!id) return;
    if (peiDatiMap?.[id] === dati) return;
    if (!peiDatiMap?.[id] && Object.keys(dati).length === 0) return;
    dirtyRef.current = true;
    const t = setTimeout(() => {
      setPeiDatiMap(p => (p[id] === dati ? p : { ...p, [id]: dati }));
      dirtyRef.current = false;
    }, 2000);
    return () => clearTimeout(t);
  }, [dati, studente?.id]);

  useEffect(() => {
    const id = studente?.id;
    return () => {
      if (!id || !dirtyRef.current) return;
      const ultimi = datiRef.current;
      setPeiDatiMap(p => (p[id] === ultimi ? p : { ...p, [id]: ultimi }));
      dirtyRef.current = false;
    };
  }, [studente?.id]);

  useEffect(() => {
    const avvisa = (e) => {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', avvisa);
    return () => window.removeEventListener('beforeunload', avvisa);
  }, []);

  const sezioniFiltrate = SEZIONI_PEI_BASE.filter(s => !s.soloOrdini || s.soloOrdini.includes(studente?.ordine));
  const sezCorrente = sezioniFiltrate.find(s => s.id === sezAtt);
  const currentIndex = sezioniFiltrate.findIndex(s => s.id === sezAtt);
  const totalSezioni = sezioniFiltrate.length;

  // Validazione della sezione corrente (per modalità guidata)
  const validazione = useMemo(() => validaSezionePEI(sezAtt, dati), [sezAtt, dati]);

  // Sezioni opzionali "saltate" dall'utente: vengono marcate come completate
  // SOLO quando l'utente clicca "Salta e procedi" / "Salva e procedi" da loro,
  // così non risultano spuntate da subito ma solo man mano che procede.
  // Persistiamo il dato dentro `dati._sezioniSaltate` (array di id).
  const sezioniSaltate = useMemo(() => new Set(dati._sezioniSaltate || []), [dati._sezioniSaltate]);

  // Stato di completamento per OGNI sezione (usato dalle icone di check nella lista)
  const statoSezioni = useMemo(() => {
    const map = {};
    sezioniFiltrate.forEach(s => {
      const v = validaSezionePEI(s.id, dati);
      // Una sezione è "completata" quando:
      //   - i suoi campi obbligatori sono compilati (validazione passata), OPPURE
      //   - è una sezione opzionale che l'utente ha esplicitamente saltato.
      // Nota: per le opzionali la validazione di base ritorna sempre valida=true,
      // quindi qui forziamo il check sui campi reali.
      let realmenteCompilata = false;
      if (v.opzionale) {
        // Per le opzionali consideriamo completate solo se l'utente le ha saltate
        // o se ha effettivamente compilato qualcosa di significativo.
        const hasContent = (
          (s.id === "s3"  && (dati.pi_sintesi || "").trim().length >= MIN_TESTO) ||
          (s.id === "s10" && Object.keys(dati).some(k => k.startsWith("cert_") && (dati[k] || "").trim().length > 0)) ||
          (s.id === "s12" && (dati.s12_ob || "").trim().length >= 10)
        );
        realmenteCompilata = hasContent || sezioniSaltate.has(s.id);
      } else {
        realmenteCompilata = v.valida;
      }
      map[s.id] = { ...v, completata: realmenteCompilata };
    });
    return map;
  }, [sezioniFiltrate, dati, sezioniSaltate]);

  const vaiASezione = (id) => { salvaImmediato(); setSezAtt(id); };
  const precedente = () => {
    if (currentIndex > 0) vaiASezione(sezioniFiltrate[currentIndex - 1].id);
  };

  // "Salva e procedi": salva immediatamente + avanza alla sezione successiva.
  // È il pulsante primario nella action bar in basso.
  // Se la sezione corrente è opzionale, la aggiunge a _sezioniSaltate così
  // viene "spuntata" nella lista solo dopo che l'utente le è passato sopra.
  const salvaEProcedi = () => {
    if (validazione.opzionale && !statoSezioni[sezAtt]?.completata) {
      // Aggiungi questa sezione opzionale a quelle "saltate/visitate"
      setDati(p => {
        const attuali = new Set(p._sezioniSaltate || []);
        attuali.add(sezAtt);
        return { ...p, _sezioniSaltate: Array.from(attuali) };
      });
    }
    salva();
    if (currentIndex < totalSezioni - 1) {
      // piccolo delay per dare modo al feedback "Salvato" di apparire
      setTimeout(() => {
        if (mountedRef.current) {
          if (guidedMode && !validazione.valida) return;
          vaiASezione(sezioniFiltrate[currentIndex + 1].id);
        }
      }, 250);
    }
  };

  if (!studente) return (
    <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:12, color:C.muted }}>
      <div style={{ fontSize:48 }}>📄</div>
      <div style={{ fontSize:15, color:C.muted }}>Seleziona uno studente per aprire il PEI</div>
    </div>
  );

  const ord = ORDINI[studente.ordine] || ORDINI.primaria;

  const renderSez = () => {
    const props = { dati, setD, studente, ord, onChat: () => setChatOpen(true), contestoGlobale };
    switch (sezAtt) {
      case "glo":  return <SezGLO  {...props} />;
      case "s1":   return <SezQuadroInfo {...props} />;
      case "s2":   return <SezPF   {...props} />;
      case "s3":   return <SezPI   {...props} />;
      case "s4":   return <SezOsservazioni {...props} />;
      case "s5":   return <SezObiettivi {...props} />;
      case "s6":   return <SezBarriere {...props} />;
      case "s7":   return <SezAmbiente {...props} />;
      case "s8":   return <SezCurricolo {...props} />;
      case "s9":   return <SezRisorse {...props} />;
      case "s10":  return <SezCertificazione {...props} />;
      case "s11":  return <SezVerifica {...props} />;
      case "s12":  return <SezProvvisorio {...props} />;
      case "s_pdv": return <SezProgettoDiVita {...props} />;
      default:     return null;
    }
  };

  return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:0, background: C.bg }}>
      {/* ─── HEADER con titolo + barra di progresso + azioni ───────────────────── */}
      <div style={{
        padding: "20px 32px 16px",
        background: C.surface,
        borderBottom: `1px solid ${C.border}`,
        flexShrink: 0
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
            <div className="popIn" style={{
              width: 46, height: 46, borderRadius: R.md,
              background: grad(studente.colore || C.accent, hex(studente.colore || C.accent, 0.7)),
              color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 700, fontSize: 17,
              boxShadow: `0 8px 20px ${hex(studente.colore || C.accent, 0.35)}`,
              flexShrink: 0
            }}>{studente.avatar}</div>
            <div style={{ minWidth: 0 }}>
              <h1 className="serif" style={{
                fontSize: 26, fontWeight: 700, color: C.text,
                letterSpacing: "-0.02em", lineHeight: 1.1
              }}>
                Piano Educativo Individualizzato
              </h1>
              <div style={{ fontSize: 13, color: C.muted, display: "flex", alignItems: "center", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
                <span>Studente <strong style={{ color: C.text }}>{studente.iniziali}</strong></span>
                <span>·</span>
                <span>{ord.icon} {ord.label}</span>
                {studente.classe && <><span>·</span><span>Classe {studente.classe}</span></>}
                {studente.diagnosi && (<><span>·</span><Badge color={C.orange} small>{studente.diagnosi}</Badge></>)}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {salvato && (
              <span className="fadeIn" style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "6px 14px", borderRadius: R.pill,
                background: hex(C.green, 0.12), color: C.green,
                fontSize: 12, fontWeight: 600,
                border: `1px solid ${hex(C.green, 0.25)}`
              }}><span className="popIn">✓</span> Salvato</span>
            )}
            {(documentiStudenti?.[studente?.id]?.length > 0) && (
              <Badge color={C.teal}>
                📁 {documentiStudenti[studente.id].length} doc{documentiStudenti[studente.id].length > 1 ? "umenti" : "umento"}
              </Badge>
            )}
            <Btn ghost onClick={() => setVerificaOpen(true)} icon={isTrial ? "🔒" : "🔍"}
              disabled={isTrial}
              title={isTrial ? "Verifica coerenza — disponibile nel piano completo" : "Verifica la coerenza del PEI (3 crediti)"}>
              Verifica coerenza
            </Btn>
          </div>
        </div>

      </div>

      {/* ─── BANNER MODALITÀ GUIDATA ─────────────────────────────────────────── */}
      {guidedMode && (
        <div className="fadeIn" style={{
          background: `linear-gradient(135deg, ${hex(C.accent2, 0.08)} 0%, ${hex(C.accent, 0.05)} 100%)`,
          borderBottom: `1px solid ${hex(C.accent2, 0.18)}`,
          padding: "10px 32px"
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
              <span style={{ fontSize: 13, color: C.accent2, fontWeight: 600 }}>
                🧭 Modalità guidata · {sezCorrente?.label} ({currentIndex+1} di {totalSezioni})
              </span>
              {validazione.opzionale ? (
                <span style={{ fontSize: 11, color: C.muted, fontWeight: 600,
                  padding: "3px 10px", borderRadius: R.pill,
                  background: hex(C.muted, 0.10), border: `1px solid ${hex(C.muted, 0.20)}` }}>
                  ⏭ Sezione opzionale — puoi saltarla
                </span>
              ) : validazione.valida ? (
                <span style={{ fontSize: 11, color: C.green, fontWeight: 600,
                  padding: "3px 10px", borderRadius: R.pill,
                  background: hex(C.green, 0.10), border: `1px solid ${hex(C.green, 0.25)}` }}>
                  ✓ Sezione completa
                </span>
              ) : (
                <span style={{ fontSize: 11, color: C.orange, fontWeight: 600,
                  padding: "3px 10px", borderRadius: R.pill,
                  background: hex(C.orange, 0.10), border: `1px solid ${hex(C.orange, 0.30)}` }}>
                  ⚠️ Manca: {validazione.mancanti.join(", ")}
                </span>
              )}
            </div>
            <Btn ghost small onClick={() => setGuidedMode(false)}>Disattiva</Btn>
          </div>
        </div>
      )}

      {showGuidedTooltip && guidedMode && (
        <div style={{ position: "relative", margin: "0 32px" }}>
          <div className="fadeIn" style={{
            position: "absolute", top: 10, right: 0,
            background: C.surface, border: `1px solid ${hex(C.accent2, 0.4)}`,
            borderRadius: R.md, padding: "14px 18px",
            boxShadow: C.shadowLg, zIndex: 10, maxWidth: 360
          }}>
            <div style={{ fontSize: 13, color: C.text, marginBottom: 10, lineHeight: 1.55 }}>
              💡 <strong>Modalità guidata attiva</strong>. La lista delle sezioni resta visibile ma non è cliccabile: navighi solo con <em>« Precedente</em> e <em>Salva e procedi »</em>. Il bottone si sblocca quando i campi essenziali sono compilati. Puoi disattivarla in qualsiasi momento.
            </div>
            <Btn primary small onClick={() => setShowGuidedTooltip(false)}>Ho capito</Btn>
          </div>
        </div>
      )}

      {/* ─── BODY: lista sezioni + contenuto ────────────────────────────────── */}
      <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
        {/* Colonna lista sezioni — card con check di completamento */}
        <div style={{
          width: 260, background: "transparent",
          borderRight: `1px solid ${C.border}`,
          overflowY: "auto", flexShrink: 0,
          padding: "16px 12px"
        }}>
          {guidedMode && (
            <div style={{
              fontSize: 10, color: C.mutedLight, fontWeight: 700,
              textTransform: "uppercase", letterSpacing: 0.6,
              padding: "0 12px 12px", lineHeight: 1.4
            }}>
              🔒 Navigazione bloccata
            </div>
          )}

          {sezioniFiltrate.map((sec, idx) => {
            const active = sezAtt === sec.id;
            const bloccata = guidedMode && !active;
            const premium = isSezioneBloccataTrial(sec.id, plan);
            const stato = statoSezioni[sec.id];
            const completa = stato?.completata;
            return (
              <button key={sec.id}
                onClick={() => { if (!bloccata) vaiASezione(sec.id); }}
                disabled={bloccata}
                title={bloccata
                  ? "Modalità guidata attiva: usa Precedente/Salva e procedi"
                  : premium
                    ? `${sec.label} — disponibile nel piano completo`
                    : sec.label}
                style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%",
                  padding: "10px 12px", borderRadius: R.md,
                  background: active
                    ? `linear-gradient(135deg, ${hex(C.accent, 0.10)}, ${hex(C.accent2, 0.06)})`
                    : "transparent",
                  color: active ? C.accent : C.textSec,
                  fontSize: 13, fontWeight: active ? 600 : 500,
                  fontFamily: "'Inter',sans-serif", textAlign: "left",
                  cursor: bloccata ? "not-allowed" : "pointer",
                  opacity: bloccata ? 0.45 : 1,
                  transition: "all .2s cubic-bezier(.16,1,.3,1)",
                  marginBottom: 4,
                  border: active ? `1px solid ${hex(C.accent, 0.25)}` : "1px solid transparent",
                  boxShadow: active ? `0 4px 12px ${hex(C.accent, 0.10)}` : "none"
                }}
                onMouseEnter={e => { if (!bloccata && !active) e.currentTarget.style.background = hex(C.text, 0.03); }}
                onMouseLeave={e => { if (!bloccata && !active) e.currentTarget.style.background = "transparent"; }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>{sec.icon}</span>
                <span style={{
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  flex: 1, fontSize: 12.5,
                  color: premium ? C.muted : undefined,
                }}>{sec.label}</span>
                {/* Lucchetto per sezioni premium (utenti in trial) */}
                {premium && (
                  <span title="Disponibile nel piano completo"
                    style={{ fontSize: 12, color: C.muted, flexShrink: 0, lineHeight: 1 }}>🔒</span>
                )}
                {/* Cerchio di stato */}
                {!premium && (
                  <span style={{
                    width: 18, height: 18, borderRadius: "50%",
                    background: completa ? C.green : "transparent",
                    border: `1.5px solid ${completa ? C.green : C.borderMed}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 10, color: "#fff", fontWeight: 700, flexShrink: 0,
                    transition: "all .25s cubic-bezier(.34,1.56,.64,1)"
                  }}>{completa && <span className="popIn">✓</span>}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Contenuto della sezione */}
        <div style={{ flex: 1, overflowY: "auto", padding: "28px 40px 32px" }}>
          <div className="fadeUp" style={{ maxWidth: 980, margin: "0 auto" }}>
            {/* "In compilazione" pill + meta */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "5px 12px", borderRadius: R.pill,
                background: validazione.valida ? hex(C.green, 0.10) : hex(C.accent, 0.08),
                border: `1px solid ${validazione.valida ? hex(C.green, 0.25) : hex(C.accent, 0.20)}`,
                color: validazione.valida ? C.green : C.accent,
                fontSize: 11, fontWeight: 600,
                textTransform: "uppercase", letterSpacing: 0.5
              }}>
                <span style={{
                  width: 6, height: 6, borderRadius: "50%",
                  background: validazione.valida ? C.green : C.accent
                }} />
                {validazione.valida ? "Sezione completa" : "In compilazione"}
              </span>
              <span style={{ fontSize: 12, color: C.mutedLight }}>
                Sezione {currentIndex + 1} di {totalSezioni} — D.I. 153/2023 / D.Lgs 62/2024
              </span>
            </div>

            {sezioneBloccata && (
              <div className="fadeIn" style={{
                marginBottom: 18, padding: "14px 18px", borderRadius: R.md,
                background: `linear-gradient(135deg, ${hex(C.accent2, 0.08)}, ${hex(C.accent, 0.05)})`,
                border: `1px solid ${hex(C.accent2, 0.25)}`,
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 14, flexWrap: "wrap"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                  <span style={{ fontSize: 22, lineHeight: 1 }}>🔒</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 2 }}>
                      Sezione disponibile nel piano completo
                    </div>
                    <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
                      Con il piano free puoi compilare solo le prime sezioni del PEI. Sblocca tutte le sezioni, l'AI completa e la Verifica coerenza.
                    </div>
                  </div>
                </div>
                <Btn primary small icon="✦"
                  onClick={() => { if (typeof setTab === "function") setTab("account"); }}>
                  Sblocca piano completo
                </Btn>
              </div>
            )}
            <TrialGateContext.Provider value={{ aiBloccata: sezioneBloccata }}>
              <fieldset disabled={sezioneBloccata}
                style={{ border: "none", padding: 0, margin: 0, minInlineSize: "auto" }}>
                {renderSez()}
              </fieldset>
            </TrialGateContext.Provider>
          </div>
        </div>
      </div>

      {/* ─── ACTION BAR (flex child in fondo alla colonna) ─────────────────── */}
      <div style={{
        background: C.surface,
        borderTop: `1px solid ${C.border}`,
        padding: "14px 40px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 12, flexShrink: 0,
        boxShadow: "0 -4px 16px rgba(28,25,23,0.04)"
      }}>
        <Btn ghost onClick={precedente} disabled={currentIndex === 0} icon="←">Precedente</Btn>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>

          <Btn primary
            onClick={salvaEProcedi}
            disabled={(guidedMode && !validazione.valida) || currentIndex === totalSezioni - 1}
            title={
              currentIndex === totalSezioni - 1 ? "Sei all'ultima sezione" :
              (guidedMode && !validazione.valida) ? `Compila prima: ${validazione.mancanti.join(", ")}` :
              "Salva e vai alla sezione successiva"
            }>
            {currentIndex === totalSezioni - 1
              ? "💾 Salva PEI"
              : validazione.opzionale && !validazione.valida
                ? "Salta e procedi →"
                : "Salva e procedi →"}
          </Btn>
        </div>
      </div>

      <ChatAssistente
        studente={studente}
        sezioneLabel={sezCorrente?.label || ""}
        open={chatOpen}
        onClose={() => setChatOpen(false)} />

      {verificaOpen && (
        <VerificaCoerenza
          dati={dati}
          studente={studente}
          onClose={() => setVerificaOpen(false)}
          risultatiIniziali={ultimiRisultatiVerifica}
          onRicalcola={(nuovi) => setUltimiRisultatiVerifica(nuovi)}
          onSalvaCorrezione={(campoKey, nuovoValore) => {
            setD(campoKey, nuovoValore);
            setPeiDatiMap(p => ({ ...p, [studente.id]: { ...(p[studente.id]||{}), [campoKey]: nuovoValore } }));
          }}
        />
      )}
    </div>
  );
}

const UPLOAD_DOCUMENTI_ATTIVO = false;

// =============================================================================
//  DOCUMENTI STUDENTE (archivio con OCR e testo estratto)
// =============================================================================
function DocumentiStudente({ studente, documenti, setDocumenti, setTab }) {
  const { balance, consume, plan } = useCreditsCtx();
  const isTrial = isPianoTrial(plan);
  const [ocr,        setOcr]        = useState(false);
  const [ocrFase,    setOcrFase]    = useState(""); // "estrazione" | "analisi"
  const [espanso,    setEspanso]    = useState(null);
  const TIPI_DOC = [
    { id: "pf",        label: "Profilo di Funzionamento",  icon: "📋" },
    { id: "pei_prec",  label: "PEI anno precedente",       icon: "📄" },
    { id: "diagnosi",  label: "Diagnosi / Relazione clinica", icon: "🏥" },
    { id: "glho",      label: "GLHO / Verbale riunione",   icon: "🗓"  },
    { id: "altro",     label: "Altro documento",           icon: "📎"  },
  ];

  const docsStudente = (documenti[studente?.id] || []);

  const aggiungiDoc = (nuovoDoc) => {
    // Anti-doppione: stesso nome file + stesso tipo già presente → chiedi conferma
    // prima di aggiungere (evita di iniettare due volte lo stesso documento nel
    // contesto AI e di sprecare un credito).
    const giaPresente = (documenti[studente.id] || []).some(
      d => d.tipo === nuovoDoc.tipo && d.nome === nuovoDoc.nome
    );
    if (giaPresente && !window.confirm(
      `"${nuovoDoc.nome}" risulta già caricato come "${nuovoDoc.label}". Aggiungerlo comunque?`
    )) return;
    setDocumenti(prev => ({
      ...prev,
      [studente.id]: [...(prev[studente.id] || []), nuovoDoc]
    }));
  };

  const rimuoviDoc = (docId) => {
    setDocumenti(prev => ({
      ...prev,
      [studente.id]: (prev[studente.id] || []).filter(d => d.id !== docId)
    }));
  };

  const handleFileUpload = async (e, tipoId) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    if (isTrial) {
      alert("Caricamento documenti riservato al piano completo. Vai su Account per fare l'upgrade.");
      return;
    }
    const tipoInfo = TIPI_DOC.find(t => t.id === tipoId) || TIPI_DOC[4];
    setOcr(true);
    try {
      // Archivio: analisi AI sempre attiva (null = modalità demo).
      const haCrediti = balance === null || balance > 0;
      const { testo, isImg, aiAnalizzato } = await caricaDocumento(file, tipoId, {
        usaAI: true,
        haCrediti,
        onFase: setOcrFase,
      });
      aggiungiDoc({
        id:       Date.now(),
        tipo:     tipoId,
        label:    tipoInfo.label,
        icon:     tipoInfo.icon,
        nome:     file.name,
        data:     new Date().toISOString().split("T")[0],
        testo,
        isImg,
        aiAnalizzato, // true se l'AI ha letto/strutturato il documento
      });
    } catch (err) {
      if (err instanceof InsufficientCreditsError) {
        alert("Per leggere immagini o PDF scansionati serve almeno 1 credito: questi documenti vanno letti otticamente dall'AI.");
      } else if (err.message === 'FORMATO_NON_SUPPORTATO') {
        alert("Formato non supportato. Usa immagini (JPG/PNG), PDF o DOCX.");
      } else {
        alert("⚠️ Errore durante l'elaborazione: " + err.message);
      }
    }
    setOcr(false);
    setOcrFase("");
  };

  if (!studente) return (
    <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:12, color:C.muted }}>
      <div style={{ fontSize:48 }}>📁</div>
      <div style={{ fontSize:15, color:C.muted }}>Seleziona uno studente per gestire i documenti</div>
    </div>
  );

  return (
    <div className="fadeUp" style={{ padding:28, maxWidth:820, margin:"0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom:22 }}>
        <div style={{ fontSize:20, fontWeight:700, color:C.text, marginBottom:4 }}>
          📁 Archivio Documenti — {studente.iniziali}
        </div>
        <div style={{ fontSize:13, color:C.muted }}>
          I documenti caricati vengono letti dall'AI per generare suggerimenti più precisi in tutte le sezioni del PEI.
        </div>
      </div>

      <InfoBox color={C.accent} icon="✦">
        <strong>Come funziona:</strong> carica foto, scansioni o file dei documenti dell'alunno (diagnosi, PEI precedenti, profilo di funzionamento, verbali GLHO). L'AI analizzerà il documento ed estrarrà automaticamente le sezioni più rilevanti (diagnosi, obiettivi, strategie, indicazioni degli specialisti) per arricchire tutti i campi del PEI. <strong>Ogni caricamento consuma 1 credito.</strong>
      </InfoBox>

      {isTrial && (
        <div className="fadeIn" style={{
          marginBottom: 20, padding: "16px 20px", borderRadius: 12,
          background: `linear-gradient(135deg, ${hex(C.accent2, 0.08)}, ${hex(C.accent, 0.05)})`,
          border: `1px solid ${hex(C.accent2, 0.25)}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 14, flexWrap: "wrap"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <span style={{ fontSize: 22, lineHeight: 1 }}>🔒</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 2 }}>
                Caricamento documenti riservato al piano completo
              </div>
              <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
                Con il piano completo l'AI legge diagnosi, PEI precedenti e relazioni cliniche e li usa per personalizzare ogni sezione del PEI.
              </div>
            </div>
          </div>
          <Btn primary small icon="✦"
            onClick={() => { if (typeof setTab === "function") setTab("account"); }}>
            Sblocca piano completo
          </Btn>
        </div>
      )}

      {/* Carica nuovo documento */}
      <Card style={{ marginBottom:20, opacity: isTrial ? 0.55 : 1, pointerEvents: isTrial ? "none" : "auto" }}>
        <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:14 }}>
          + Aggiungi documento
        </div>
        {ocr && (
          <div style={{ marginBottom:14, padding:"10px 14px", background:hex(C.accent,0.06),
            border:`1px solid ${hex(C.accent,0.2)}`, borderRadius:8,
            display:"flex", alignItems:"center", gap:10, fontSize:13, color:C.accent }}>
            <Spinner size={14} />
            {ocrFase === "analisi"
              ? "Analisi AI in corso — estrazione sezioni rilevanti (1 credito)…"
              : "Estrazione testo in corso…"}
          </div>
        )}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(200px,1fr))", gap:10 }}>
          {TIPI_DOC.map(tipo => (
            <label key={tipo.id} style={{ cursor: ocr ? "not-allowed" : "pointer" }}>
              <input
                type="file"
                accept=".pdf,.docx,image/jpeg,image/png,image/webp,image/gif"
                disabled={ocr}
                onChange={e => handleFileUpload(e, tipo.id)}
                style={{ display:"none" }}
              />
              <div style={{
                padding:"12px 14px", borderRadius:10, textAlign:"center",
                border:`2px dashed ${ocr ? C.border : hex(C.accent,0.35)}`,
                background: ocr ? C.bg : hex(C.accent,0.03),
                transition:"all .15s",
                opacity: ocr ? 0.5 : 1,
              }}
                onMouseEnter={e => { if(!ocr) e.currentTarget.style.background = hex(C.accent,0.07); }}
                onMouseLeave={e => { if(!ocr) e.currentTarget.style.background = hex(C.accent,0.03); }}>
                <div style={{ fontSize:22, marginBottom:6 }}>{tipo.icon}</div>
                <div style={{ fontSize:12, fontWeight:600, color:C.text, marginBottom:2 }}>{tipo.label}</div>
                <div style={{ fontSize:10, color:C.mutedLight }}>PDF, DOCX, foto JPG/PNG</div>
                <div style={{ fontSize:10, color:C.accent, marginTop:3, fontWeight:600 }}>✦ 1 credito</div>
              </div>
            </label>
          ))}
        </div>
      </Card>

      {/* Lista documenti caricati */}
      {docsStudente.length === 0 ? (
        <div style={{ textAlign:"center", padding:"40px 0", color:C.muted }}>
          <div style={{ fontSize:40, marginBottom:10 }}>📂</div>
          <div style={{ fontSize:14 }}>Nessun documento caricato</div>
          <div style={{ fontSize:12, marginTop:4 }}>Carica i documenti dell'alunno per potenziare l'AI</div>
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          <div style={{ fontSize:12, fontWeight:600, color:C.muted, textTransform:"uppercase", letterSpacing:.5, marginBottom:4 }}>
            {docsStudente.length} documenti nel profilo
          </div>
          {docsStudente.map(doc => (
            <Card key={doc.id} style={{ padding:"12px 16px" }}>
              <div style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
                <div style={{ fontSize:28, flexShrink:0 }}>{doc.icon}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:3 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:C.text,
                      overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {doc.nome}
                    </div>
                    {doc.isImg && (
                      <Badge color={C.teal} small>📷 OCR</Badge>
                    )}
                    {doc.aiAnalizzato && (
                      <Badge color={C.accent} small>✦ AI</Badge>
                    )}
                  </div>
                  <div style={{ fontSize:11, color:C.muted, marginBottom:6 }}>
                    {doc.label} · Caricato il {isoToIt(doc.data)}
                  </div>
                  {doc.testo && (
                    <>
                      <div style={{
                        fontSize:12, color:C.textSec, background:C.bg,
                        border:`1px solid ${C.border}`, borderRadius:6,
                        padding:"7px 10px", lineHeight:1.6,
                        maxHeight: espanso === doc.id ? "60vh" : 60,
                        overflow: espanso === doc.id ? "auto" : "hidden",
                        transition:"max-height .3s ease",
                        whiteSpace:"pre-wrap", wordBreak:"break-word"
                      }}>
                        {doc.testo}
                      </div>
                      <button onClick={() => setEspanso(espanso === doc.id ? null : doc.id)}
                        style={{ background:"none", border:"none", color:C.accent,
                          fontSize:11, fontWeight:600, cursor:"pointer", marginTop:4, padding:0 }}>
                        {espanso === doc.id ? "▲ Comprimi" : "▼ Espandi testo estratto"}
                      </button>
                    </>
                  )}
                </div>
                <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                  <Btn danger small onClick={() => {
                    if (window.confirm(`Rimuovere "${doc.nome}"?`)) rimuoviDoc(doc.id);
                  }}>✕</Btn>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
//  SCHEDA STUDENTE — WIZARD 3 PASSI
// =============================================================================
function SchedaStudente({ onSalva, onAnnulla, studentiEsistenti, setDocumentiStudenti, setPeiDatiMap, studenteIniziale = null }) {
  const inModifica = !!studenteIniziale;
  const [passo, setPasso] = useState(1);
  const [form, setForm] = useState(() => {
    const vuoto = {
      iniziale: "", eta: "", classe: "", ordine: "primaria",
      diagnosi: "", icd10: [], note: "",
      certInAttesa: false,             // true = certificazione in iter / diagnosi funzionale provvisoria — guida l'IA a tono prudente, formule osservative
      comorbidita: "",                 // es. "dislessia e discalculia" — guida l'IA su misure compensative/dispensative (104 + DSA)
      annoScolastico: asEsteso(),   // a.s. in corso, ricalcolato a ogni apertura (modificabile a mano)
      tipoPercorso: "riconducibile",   // "riconducibile" | "differenziato" — rilevante solo per secondaria2
      oreSostegno: "", oreAec: "",     // pre-compilano s9_ore_sost e s9_ore_aec nel PEI
    };
    if (!studenteIniziale) return vuoto;
    const { id, iniziali, avatar, colore, ...resto } = studenteIniziale;
    return { ...vuoto, ...resto, iniziale: resto.iniziale || iniziali || "" };
  });
  const [icdQuery,    setIcdQuery]    = useState("");
  const [icdSugg,     setIcdSugg]     = useState([]);
  const [icdResults,  setIcdResults]  = useState([]);
  const [icdAILoading,setIcdAILoading]= useState(false);
  const [icdAIMsg,    setIcdAIMsg]    = useState(null);
  const [docUploading,setDocUploading]= useState({ pei: false, pf: false });
  const [docsCaricati,setDocsCaricati]= useState([]);
  const [analizzaAI, setAnalizzaAI]   = useState(false); // opt-in: estrazione AI strutturata dei documenti del wizard (1 credito/doc)
  const { balance, plan, band } = useCreditsCtx();
  const isTrial = isPianoTrial(plan); // upload documenti riservato al piano completo (come nell'Archivio)
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Ordini di scuola selezionabili in base alla fascia del profilo (band).
  // Il vincolo è imposto anche lato DB (trigger migration 0007): qui evitiamo
  // che l'utente scelga un ordine fuori fascia e incappi in un salvataggio che
  // fallirebbe. band null → tutti gli ordini.
  const ordiniAmmessi = ordiniConsentiti(band);
  const ordiniVisibili = inModifica ? [form.ordine] : ordiniAmmessi;

  // Se l'ordine corrente non è ammesso dalla fascia (es. fascia caricata in
  // ritardo, o default 'primaria' con fascia secondaria), riallinea al primo
  // ordine consentito.
  useEffect(() => {
    if (inModifica) return;
    setForm(f => {
      const amm = ordiniConsentiti(band);
      return amm.includes(f.ordine) ? f : { ...f, ordine: amm[0] };
    });
  }, [band, inModifica]);

  // Carica (una volta) il dataset ICD-10 italiano completo all'apertura del wizard.
  useEffect(() => { caricaICD10Completo(); }, []);

  // ── ICD-10 logic ──────────────────────────────────────────────────────────
  useEffect(() => {
    const diagTrim = form.diagnosi.trim();
    setIcdAIMsg(null);
    setIcdAILoading(false);
    if (diagTrim.length < 2) { setIcdSugg([]); return; }
    const locali = suggerisciICD10(diagTrim);
    setIcdSugg(locali);
    if (diagTrim.length >= 4 && locali.length === 0) {
      const timer = setTimeout(async () => {
        const localiCheck = suggerisciICD10(diagTrim);
        if (localiCheck.length > 0) { setIcdSugg(localiCheck); return; }
        setIcdAILoading(true);
        try {
          const trovati = await cercaICD10ViaAI(diagTrim);
          if (trovati && trovati.length > 0) {
            trovati.forEach(e => aggiungiACodiciCustom(e));
            setIcdSugg(trovati);
            setIcdAIMsg({ type: "found", testo: `${trovati.length} codici trovati e salvati nel database locale.` });
          } else {
            setIcdAIMsg({ type: "notfound", testo: `Nessun codice ICD-10 trovato per "${diagTrim}". Cerca il codice corretto online e inseriscilo manualmente qui sotto.` });
          }
        } catch (e) {
          setIcdAIMsg({ type: "error", testo: "Ricerca non disponibile. Inserisci il codice ICD-10 manualmente." });
        }
        setIcdAILoading(false);
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [form.diagnosi]);

  useEffect(() => { setIcdResults(cercaICD10(icdQuery)); }, [icdQuery]);

  const [icdManualLoading, setIcdManualLoading] = useState(false);
  const [icdManualMsg,     setIcdManualMsg]     = useState(null);
  const [icdNomeLoading,   setIcdNomeLoading]   = useState(false);

  const icdQueryECodice = RE_CODICE_ICD.test(normICD(icdQuery));

  const cercaNomeConAI = async () => {
    const q = icdQuery.trim();
    if (!q) return;
    setIcdManualMsg(null);
    setIcdNomeLoading(true);
    try {
      const trovati = await cercaICD10ViaAI(q);
      if (trovati && trovati.length > 0) {
        trovati.forEach(e => aggiungiACodiciCustom(e));
        setIcdResults(trovati);
        setIcdManualMsg({ type: "found", testo: `${trovati.length} codici trovati per "${q}" — clicca quello corretto.` });
      } else {
        setIcdManualMsg({ type: "notfound", testo: `Nessun codice ICD-10 corrisponde a "${q}". Prova con le parole esatte della certificazione, oppure inserisci direttamente il codice (es. F71).` });
      }
    } catch (e) {
      setIcdManualMsg({ type: "error", testo: "Ricerca non disponibile in questo momento. Inserisci direttamente il codice ICD-10." });
    }
    setIcdNomeLoading(false);
  };

  // pulisciMsg=true → resetta il messaggio (uso "normale", click su risultato/preset)
  // pulisciMsg=false → mantiene il messaggio (uso da addIcdManuale per non cancellare il feedback)
  const addIcd = (e, pulisciMsg = true) => {
    if (!form.icd10.find(i => i.code === e.code)) set("icd10", [...form.icd10, e]);
    setIcdQuery("");
    if (pulisciMsg) setIcdManualMsg(null);
  };
  const remIcd = code => set("icd10", form.icd10.filter(i => i.code !== code));

  // Aggiunta MANUALE di un codice ICD-10:
  // 1) controlla se è già presente nel DB locale → usa quella descrizione
  // 2) altrimenti chiama l'AI per ricavare la diagnosi corrispondente
  // 3) in caso di esito positivo, salva nel DB custom così da arricchire l'archivio
  const addIcdManuale = async () => {
    const codeRaw = icdQuery.trim().toUpperCase();
    if (!codeRaw) return;
    if (form.icd10.find(i => i.code === codeRaw)) {
      setIcdQuery("");
      return;
    }
    setIcdManualMsg(null);
    // 1) presente nel DB locale?
    const inDB = ICD10_DB.find(e => e.code === codeRaw);
    if (inDB) {
      addIcd(inDB);
      return;
    }
    // 2) ricerca via AI
    setIcdManualLoading(true);
    try {
      const trovato = await cercaDiagnosiViaAI(codeRaw);
      if (trovato && trovato.description) {
        // 3) salva nel DB custom (arricchisce l'archivio locale)
        aggiungiACodiciCustom(trovato);
        addIcd(trovato, false);
        setIcdManualMsg({ type: "segnalato", code: trovato.code, description: trovato.description });
        // Segnala al developer: il codice era assente dal DB locale (trovato solo via AI)
        segnalaCodiceMancanteASupabase(trovato.code, trovato.description);
      } else {
        // Nessuna descrizione trovata: aggiungi comunque il codice come "Inserito manualmente"
        addIcd({ code: codeRaw, description: "Inserito manualmente" }, false);
        setIcdManualMsg({ type: "segnalato", code: codeRaw, description: null });
        // Segnala al developer: codice completamente sconosciuto
        segnalaCodiceMancanteASupabase(codeRaw, null);
      }
    } catch (err) {
      addIcd({ code: codeRaw, description: "Inserito manualmente" }, false);
      setIcdManualMsg({ type: "segnalato", code: codeRaw, description: null });
      // Segnala comunque (errore AI, codice potenzialmente mancante)
      segnalaCodiceMancanteASupabase(codeRaw, null);
    }
    setIcdManualLoading(false);
  };

  // ── Documenti passo 3 ─────────────────────────────────────────────────────
  const TIPI_WIZARD = [
    { id: "pei_prec", label: "PEI anno precedente",        icon: "📄",
      desc: "Il Piano Educativo Individualizzato redatto negli anni scolastici precedenti." },
    { id: "pf",       label: "Profilo di Funzionamento",   icon: "📋",
      desc: "Il documento redatto dagli specialisti secondo il modello ICF–OMS." },
  ];

  const handleDocUpload = async (e, tipoId) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    if (isTrial) {
      alert("Caricamento documenti riservato al piano completo. Potrai aggiungerli dopo l'upgrade dalla sezione Documenti.");
      return;
    }
    setDocUploading(p => ({ ...p, [tipoId]: true }));
    try {
      // Opt-in AI (checkbox) governa solo i documenti di testo (PDF/DOCX). Immagini
      // e PDF scansionati richiedono comunque l'AI per essere letti (1 credito).
      const haCrediti = balance === null || balance > 0;
      const { testo, isImg, aiAnalizzato } = await caricaDocumento(file, tipoId, {
        usaAI: analizzaAI,
        haCrediti,
      });
      const tipoInfo = TIPI_WIZARD.find(t => t.id === tipoId);
      setDocsCaricati(prev => [
        ...prev.filter(d => d.tipo !== tipoId),
        { id: Date.now(), tipo: tipoId, label: tipoInfo.label, icon: tipoInfo.icon,
          nome: file.name, data: new Date().toISOString().split("T")[0],
          testo, isImg, aiAnalizzato }
      ]);
    } catch (err) {
      if (err instanceof InsufficientCreditsError) {
        alert("Per leggere immagini o PDF scansionati serve almeno 1 credito: questi documenti vanno letti otticamente dall'AI. Carica un PDF/DOCX testuale oppure aggiungi crediti.");
      } else if (err.message === 'FORMATO_NON_SUPPORTATO') {
        alert("Formato non supportato. Usa PDF, DOCX o immagini JPG/PNG.");
      } else {
        alert("⚠️ Errore durante l'elaborazione: " + err.message);
      }
    }
    setDocUploading(p => ({ ...p, [tipoId]: false }));
  };

  const rimuoviDoc = (tipoId) => setDocsCaricati(prev => prev.filter(d => d.tipo !== tipoId));

  // ── Salvataggio finale ────────────────────────────────────────────────────
  const salva = () => {
    if (!valido) return;
    const inizialeMod = form.iniziale.trim().toUpperCase().slice(0,1);
    if (inModifica) {
      onSalva({
        ...studenteIniziale, ...form,
        iniziali: inizialeMod, avatar: inizialeMod,
        ordine: studenteIniziale.ordine,
        classe: studenteIniziale.ordine === "infanzia" ? "" : form.classe,
      });
      return;
    }
    const colore = COLORI_AVATAR[studentiEsistenti.length % COLORI_AVATAR.length];
    const iniziale = form.iniziale.trim().toUpperCase().slice(0,1);
    const id = Date.now();
    const nuovoStudente = {
      id, ...form,
      iniziali: iniziale, avatar: iniziale, colore,
      classe: form.ordine === "infanzia" ? "" : form.classe,
    };
    // Salva i documenti del wizard nell'archivio documenti
    if (docsCaricati.length > 0 && setDocumentiStudenti) {
      setDocumentiStudenti(prev => ({ ...prev, [id]: docsCaricati }));
    }
    // Pre-popola sez. 9 del PEI con le ore inserite nel wizard
    if (setPeiDatiMap && (form.oreSostegno.trim() || form.oreAec.trim())) {
      const preset = {};
      if (form.oreSostegno.trim()) preset.s9_ore_sost = form.oreSostegno.trim();
      if (form.oreAec.trim())      preset.s9_ore_aec  = form.oreAec.trim();
      setPeiDatiMap(prev => ({ ...prev, [id]: preset }));
    }
    onSalva(nuovoStudente);
  };

  const valido1 = form.iniziale.trim().length >= 1 && form.ordine;
  const valido2 = form.diagnosi.trim().length >= 2;
  const valido  = valido1 && valido2;
  const mostraClasse = form.ordine !== "infanzia";

  const PASSI = (inModifica || !UPLOAD_DOCUMENTI_ATTIVO)
    ? [{ n:1, label:"Dati studente" }, { n:2, label:"Diagnosi" }]
    : [{ n:1, label:"Dati studente" }, { n:2, label:"Diagnosi" }, { n:3, label:"Documentazione" }];

  // ── Indicatore passi ──────────────────────────────────────────────────────
  const StepBar = () => (
    <div style={{ display:"flex", alignItems:"center", gap:0, marginBottom:28 }}>
      {PASSI.map((s, i) => {
        const fatto  = passo > s.n;
        const attivo = passo === s.n;
        return (
          <div key={s.n} style={{ display:"flex", alignItems:"center", flex: i < PASSI.length - 1 ? 1 : "none" }}>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
              <div style={{
                width:32, height:32, borderRadius:"50%",
                background: fatto ? C.green : attivo ? grad(C.accent, C.accent2) : C.bg,
                border: `2px solid ${fatto ? C.green : attivo ? C.accent : C.borderMed}`,
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:13, fontWeight:700,
                color: fatto || attivo ? "#fff" : C.muted,
                transition:"all .3s"
              }}>
                {fatto ? "✓" : s.n}
              </div>
              <div style={{ fontSize:10, fontWeight:600,
                color: attivo ? C.accent : fatto ? C.green : C.mutedLight,
                whiteSpace:"nowrap" }}>{s.label}</div>
            </div>
            {i < PASSI.length - 1 && (
              <div style={{ flex:1, height:2, margin:"0 6px", marginBottom:16,
                background: fatto ? C.green : C.border,
                transition:"background .3s" }} />
            )}
          </div>
        );
      })}
    </div>
  );

  // ── PASSO 1 ───────────────────────────────────────────────────────────────
  const Passo1 = () => (
    <div className="fadeUp">
      <div style={{ marginBottom:18 }}>
        <div style={{ fontSize:17, fontWeight:700, color:C.text, marginBottom:4 }}>Dati dell'alunno/a</div>
        <div style={{ fontSize:13, color:C.muted }}>Solo l'iniziale del nome — nessun dato anagrafico completo.</div>
      </div>
      <InfoBox color={C.accent} icon="🔒">
        <strong>Tutela della privacy (GDPR)</strong> — Inserisci esclusivamente l'<strong>iniziale del nome</strong>. Non inserire nome completo, cognome o codice fiscale.
      </InfoBox>
      <InfoBox color={C.green} icon="🛡️">
        <strong>I tuoi dati sono protetti</strong> — L'alunno è identificato <strong>solo dalle iniziali</strong>: non chiediamo nome, cognome, scuola o comune. I dati del profilo e del PEI sono salvati sul tuo dispositivo e sincronizzati sul nostro cloud (server in Irlanda, Unione Europea), così li ritrovi anche cambiando computer. Quando usi le funzioni AI, il contenuto del campo insieme al profilo dell'alunno — iniziali, età, ordine di scuola, diagnosi e codici ICD-10 — viene inviato a un fornitore esterno di intelligenza artificiale, sotto contratto di trattamento dati, esclusivamente per generare il suggerimento. Il fornitore non usa questi dati per addestrare i propri modelli e non li conserva, salvo per un periodo limitato in caso di sospette violazioni delle sue policy.
      </InfoBox>
      <div style={{ display:"grid", gridTemplateColumns: mostraClasse ? "1fr 1fr 1fr" : "1fr 1fr", gap:14, marginBottom:14 }}>
        <Field label="Iniziale del nome" required sub="Es. M">
          <Input value={form.iniziale} onChange={e => set("iniziale", e.target.value)}
            placeholder="Es. M" style={{ fontWeight:700, fontSize:16, letterSpacing:1, textTransform:"uppercase" }} />
        </Field>
        <Field label="Età">
          <Input type="number" value={form.eta} onChange={e => set("eta", e.target.value)}
            placeholder={`Es. ${ETA_ESEMPIO_ORDINE[form.ordine] ?? 6}`} />
        </Field>
        {mostraClasse && (
          <Field label="Classe" sub="Solo il numero, es. 3">
            <Input value={form.classe} onChange={e => set("classe", e.target.value)} placeholder="Es. 3" />
          </Field>
        )}
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
        <Field label="Anno Scolastico" required>
          <Input value={form.annoScolastico} onChange={e => set("annoScolastico", e.target.value)} placeholder={`Es. ${asEsteso()}`} />
        </Field>
      </div>
      <Field label="Ordine Scolastico" required
        sub={inModifica
          ? "L'ordine di scuola non è modificabile: il PEI già compilato segue la struttura del modello ministeriale di questo ordine."
          : (band ? `In base al tuo profilo puoi creare PEI per: ${ordiniAmmessi.map(k => ORDINI[k].label).join(", ")}` : undefined)}>
        <div style={{ display:"grid", gridTemplateColumns:`repeat(${ordiniVisibili.length},1fr)`, gap:8 }}>
          {Object.entries(ORDINI).filter(([k]) => ordiniVisibili.includes(k)).map(([k,v]) => (
            <button key={k} onClick={() => { if (!inModifica) set("ordine",k); }} disabled={inModifica}
              style={{ padding:"10px 6px", borderRadius:10,
                border:`2px solid ${form.ordine===k ? v.color : C.border}`,
                background: form.ordine===k ? hex(v.color,0.08) : C.bg,
                opacity: inModifica ? 0.75 : 1,
                cursor: inModifica ? "default" : "pointer", textAlign:"center", transition:"all .2s" }}>
              <div style={{ fontSize:22, marginBottom:3 }}>{v.icon}</div>
              <div style={{ fontSize:11, fontWeight:600, color:form.ordine===k ? v.color : C.muted }}>{v.label}</div>
              <div style={{ fontSize:10, color:C.mutedLight }}>All. {v.allegato}</div>
            </button>
          ))}
        </div>
      </Field>
      {/* Tipo di percorso — solo scuola secondaria II grado */}
      {form.ordine === "secondaria2" && (
        <Field label="Tipo di percorso" required
          sub="Distinzione obbligatoria ai sensi del D.M. 182/2020 — ha implicazioni sul titolo di studio">
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:8 }}>
            {[
              { k:"riconducibile", label:"Percorso riconducibile", desc:"Obiettivi minimi riconducibili ai programmi ministeriali. Lo studente consegue il diploma legale.", icon:"🎓" },
              { k:"differenziato",  label:"Percorso differenziato",  desc:"Obiettivi non riconducibili ai programmi ministeriali. Lo studente consegue un attestato di crediti, non il diploma.", icon:"📋" },
            ].map(opt => (
              <button key={opt.k} onClick={() => set("tipoPercorso", opt.k)}
                style={{ padding:"12px 14px", borderRadius:10, textAlign:"left", cursor:"pointer",
                  border:`2px solid ${form.tipoPercorso === opt.k ? (opt.k === "differenziato" ? "#e67e22" : C.accent) : C.border}`,
                  background: form.tipoPercorso === opt.k
                    ? hex(opt.k === "differenziato" ? "#e67e22" : C.accent, 0.07)
                    : C.bg,
                  fontFamily:"'Inter',sans-serif", transition:"all .2s" }}>
                <div style={{ fontSize:18, marginBottom:4 }}>{opt.icon}</div>
                <div style={{ fontSize:12, fontWeight:700, color: form.tipoPercorso === opt.k ? (opt.k === "differenziato" ? "#e67e22" : C.accent) : C.text, marginBottom:4 }}>{opt.label}</div>
                <div style={{ fontSize:11, color:C.muted, lineHeight:1.4 }}>{opt.desc}</div>
              </button>
            ))}
          </div>
          {form.tipoPercorso === "differenziato" && (
            <div style={{ padding:"10px 14px", borderRadius:8, background:"#fef3e2",
              border:"1px solid #f0a500", fontSize:12, color:"#7a4f00", lineHeight:1.5 }}>
              <strong>⚠️ Attenzione — implicazioni legali importanti:</strong> il percorso differenziato non consente allo studente di conseguire il diploma di maturità, ma solo un attestato di crediti formativi. Questa scelta deve essere deliberata dal Consiglio di Classe, condivisa con la famiglia e documentata nel verbale del GLHO. Assicurati che la famiglia sia pienamente informata delle conseguenze.
            </div>
          )}
        </Field>
      )}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
        <Field label="Ore di sostegno settimanali (facoltativo)"
          sub="Pre-compila la sez. 9 del PEI">
          <Input value={form.oreSostegno} onChange={e => set("oreSostegno", e.target.value)}
            placeholder="Es. 18 ore" />
        </Field>
        <Field label="Ore assistente autonomia e comunicazione (facoltativo)"
          sub="Pre-compila la sez. 9 del PEI">
          <Input value={form.oreAec} onChange={e => set("oreAec", e.target.value)}
            placeholder="Es. 6 ore" />
        </Field>
      </div>
      <Field label="Note aggiuntive (facoltativo)">
        <Textarea value={form.note} onChange={e => set("note", e.target.value)}
          placeholder="Informazioni utili per personalizzare il percorso…" rows={2} />
      </Field>
      <div style={{ position:"sticky", bottom:0, background:C.surface,
        borderTop:`1px solid ${C.border}`, padding:"12px 0 4px", marginTop:16,
        display:"flex", justifyContent:"flex-end" }}>
        <Btn primary onClick={() => setPasso(2)} disabled={!valido1} icon="→">Avanti</Btn>
      </div>
    </div>
  );

  // ── PASSO 2 ───────────────────────────────────────────────────────────────
  const Passo2 = () => (
    <div className="fadeUp">
      <div style={{ marginBottom:18 }}>
        <div style={{ fontSize:17, fontWeight:700, color:C.text, marginBottom:4 }}>Diagnosi e codici ICD-10</div>
        <div style={{ fontSize:13, color:C.muted }}>I codici ICD-10 vengono cercati automaticamente mentre scrivi.</div>
      </div>

      <Field label="Diagnosi" required>
        <Input value={form.diagnosi} onChange={e => set("diagnosi", e.target.value)}
          placeholder="Riporta la diagnosi come nel Profilo di Funzionamento o nella certificazione clinica" />
        <div style={{ fontSize:11, color:C.mutedLight, marginTop:6, lineHeight:1.5 }}>
          Inserisci la diagnosi così come riportata nel <strong>Profilo di Funzionamento</strong> o nella
          certificazione/diagnosi clinica (UONPIA/ASL). Mentre scrivi, i codici ICD-10 vengono cercati e
          puoi aggiungerli dai suggerimenti qui sotto.
        </div>
      </Field>

      {icdSugg.length > 0 && (
        <div className="fadeIn" style={{ marginBottom:14, padding:"12px 14px",
          background:hex(C.accent,0.05), border:`1px solid ${hex(C.accent,0.2)}`, borderRadius:10 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
            <span style={{ fontSize:13 }}>🔍</span>
            <div style={{ fontSize:12, color:C.accent, fontWeight:600 }}>
              Codici ICD-10 trovati per "{form.diagnosi}"
            </div>
            {icdAIMsg?.type === "found" && <Badge color={C.teal} small>✦ salvati in archivio</Badge>}
            <div style={{ fontSize:11, color:C.muted }}>— clicca per aggiungere</div>
          </div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
            {icdSugg.map(e => {
              const già = !!form.icd10.find(i => i.code === e.code);
              return (
                <button key={e.code} onClick={() => addIcd(e)} disabled={già}
                  style={{ padding:"5px 12px", borderRadius:20,
                    border:`1px solid ${già ? hex(C.green,.4) : hex(C.accent,.4)}`,
                    background: già ? C.greenDim : C.surface,
                    color: già ? C.green : C.accent,
                    fontSize:12, fontWeight:600, cursor: già ? "default" : "pointer",
                    fontFamily:"'Inter',sans-serif", transition:"all .15s",
                    boxShadow: già ? "none" : C.shadow }}>
                  {già ? "✓ " : ""}<strong>{e.code}</strong> — {e.description || e.desc}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {icdAILoading && (
        <div className="fadeIn" style={{ marginBottom:14, padding:"10px 14px",
          background:hex(C.accent,0.04), border:`1px solid ${hex(C.accent,0.15)}`,
          borderRadius:8, display:"flex", alignItems:"center", gap:10,
          fontSize:13, color:C.accent }}>
          <Spinner size={13} /> Ricerca codice in corso…
        </div>
      )}

      {icdAIMsg && !icdAILoading && icdAIMsg.type !== "found" && (
        <div className="fadeIn" style={{ marginBottom:14, padding:"11px 14px",
          background: icdAIMsg.type === "notfound" ? C.orangeDim : C.redDim,
          border:`1px solid ${hex(icdAIMsg.type === "notfound" ? C.orange : C.red, 0.35)}`,
          borderRadius:8, fontSize:12,
          color: icdAIMsg.type === "notfound" ? C.orange : C.red }}>
          {icdAIMsg.type === "notfound" ? (
            <>
              <div style={{ fontWeight:700, marginBottom:5 }}>⚠️ Codice ICD-10 non trovato in archivio</div>
              <div style={{ marginBottom:8 }}>{icdAIMsg.testo}</div>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                <a href="https://icd.who.int/browse10/2019/en#/"
                  target="_blank" rel="noopener noreferrer"
                  style={{ padding:"4px 12px", borderRadius:6, fontSize:11, fontWeight:600,
                    background:C.orangeDim, border:`1px solid ${hex(C.orange,0.4)}`,
                    color:C.orange, textDecoration:"none" }}>
                  🌐 Cerca su icd.who.int
                </a>
                <a href={`https://www.google.com/search?q=ICD-10+codice+${encodeURIComponent(form.diagnosi)}`}
                  target="_blank" rel="noopener noreferrer"
                  style={{ padding:"4px 12px", borderRadius:6, fontSize:11, fontWeight:600,
                    background:C.orangeDim, border:`1px solid ${hex(C.orange,0.4)}`,
                    color:C.orange, textDecoration:"none" }}>
                  🔎 Cerca su Google
                </a>
              </div>
            </>
          ) : (
            <div>{icdAIMsg.testo}</div>
          )}
        </div>
      )}

      <Field label="Cerca o inserisci codice ICD-10" sub="Cerca per codice (es. F81.0) o con le parole della certificazione, anche copiando l'intera riga — se non trova nulla puoi chiedere all'AI">
        <div style={{ position:"relative" }}>
          <div style={{ display:"flex", gap:8 }}>
            <Input value={icdQuery} onChange={e => setIcdQuery(e.target.value)}
              placeholder="Es. F81.0 oppure 'dislessia'…"
              onKeyDown={e => {
                if (e.key === "Enter" && icdQuery.trim() && icdResults.length === 0
                    && !icdManualLoading && !icdNomeLoading) {
                  if (icdQueryECodice) addIcdManuale(); else cercaNomeConAI();
                }
              }} />
            {icdQuery.trim() && icdResults.length === 0 && (
              <button onClick={icdQueryECodice ? addIcdManuale : cercaNomeConAI}
                disabled={icdManualLoading || icdNomeLoading}
                style={{ padding:"9px 13px", borderRadius:8, flexShrink:0,
                  border:`1px solid ${hex(C.orange,.4)}`,
                  background:C.orangeDim, color:C.orange,
                  fontSize:12, fontWeight:600,
                  fontFamily:"'Inter',sans-serif",
                  cursor: (icdManualLoading || icdNomeLoading) ? "wait" : "pointer",
                  opacity: (icdManualLoading || icdNomeLoading) ? 0.6 : 1,
                  whiteSpace:"nowrap" }}>
                {(icdManualLoading || icdNomeLoading)
                  ? "🔎 Ricerca…"
                  : icdQueryECodice
                    ? `+ Aggiungi "${icdQuery.trim().toUpperCase()}"`
                    : "🔎 Cerca con l'AI"}
              </button>
            )}
          </div>
          {icdManualMsg && (
            icdManualMsg.type === "segnalato" ? (
              <div style={{ marginTop:8, padding:"12px 14px", borderRadius:8, fontSize:12,
                background: C.accentDim || "#e8f0fe",
                border: `1px solid ${hex(C.accent, 0.3)}`,
                color: C.accent,
                position: "relative" }}>
                <button onClick={() => setIcdManualMsg(null)}
                  style={{ position:"absolute", top:6, right:8, background:"none", border:"none",
                    cursor:"pointer", color: C.accent, fontSize:14, lineHeight:1, opacity:0.6 }}
                  title="Chiudi">✕</button>
                <div style={{ fontWeight:600, marginBottom:4 }}>
                  ✓ Codice {icdManualMsg.code} preso in carico
                </div>
                <div style={{ lineHeight:1.5, color: C.textMuted || C.muted }}>
                  {icdManualMsg.description
                    ? <>La diagnosi <em>"{icdManualMsg.description}"</em> è stata registrata e sarà disponibile nell'app entro 24 ore.</>
                    : <>Il codice <strong>{icdManualMsg.code}</strong> è stato registrato e sarà disponibile nell'app entro 24 ore.</>
                  }
                  {" "}La compilazione del PEI può proseguire normalmente: il sistema utilizzerà le informazioni attuali.
                </div>
              </div>
            ) : (
            <div style={{ marginTop:8, padding:"7px 12px", borderRadius:8, fontSize:12,
              background: icdManualMsg.type === "found" ? C.greenDim
                        : icdManualMsg.type === "notfound" ? C.orangeDim : C.redDim,
              border: `1px solid ${hex(
                icdManualMsg.type === "found" ? C.green
                : icdManualMsg.type === "notfound" ? C.orange : C.red, 0.35)}`,
              color: icdManualMsg.type === "found" ? C.green
                   : icdManualMsg.type === "notfound" ? C.orange : C.red }}>
              {icdManualMsg.type === "found" && "✦ "}
              {icdManualMsg.testo}
            </div>
            )
          )}
          {icdResults.length > 0 && icdQuery && (
            <div style={{ position:"absolute", top:"100%", left:0, right:0,
              background:C.surface, border:`1px solid ${C.border}`,
              borderRadius:8, zIndex:10, maxHeight:180, overflowY:"auto", boxShadow:C.shadowMd }}>
              {icdResults.map(e => (
                <button key={e.code} onClick={() => addIcd(e)}
                  style={{ display:"block", width:"100%", padding:"9px 13px",
                    background:"transparent", border:"none",
                    borderBottom:`1px solid ${C.border}`, color:C.text,
                    fontSize:13, textAlign:"left", cursor:"pointer",
                    fontFamily:"'Inter',sans-serif" }}>
                  <span style={{ color:C.accent, fontWeight:600 }}>{e.code}</span> — {e.description || e.desc}
                </button>
              ))}
            </div>
          )}
        </div>
      </Field>

      {form.icd10.length > 0 && (
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:11, color:C.muted, fontWeight:600, marginBottom:6,
            textTransform:"uppercase", letterSpacing:.5 }}>Codici selezionati</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
            {form.icd10.map(e => (
              <div key={e.code} style={{ display:"flex", alignItems:"center", gap:5,
                padding:"3px 10px", background:C.greenDim,
                border:`1px solid ${hex(C.green,.3)}`,
                borderRadius:20, fontSize:12, color:C.green }}>
                <span style={{ fontWeight:600 }}>{e.code}</span>
                <span style={{ color:C.muted, fontSize:11 }}>— {e.description || e.desc}</span>
                <button onClick={() => remIcd(e.code)}
                  style={{ background:"none", border:"none", color:C.muted,
                    cursor:"pointer", padding:0, fontSize:14, lineHeight:1 }}>×</button>
              </div>
            ))}
          </div>
        </div>
      )}


      <Field label="Stato della certificazione (facoltativo)">
        <label style={{ display:"flex", alignItems:"flex-start", gap:10, cursor:"pointer",
          padding:"10px 12px", borderRadius:8,
          border:`1px solid ${form.certInAttesa ? hex(C.orange,0.4) : C.border}`,
          background: form.certInAttesa ? hex(C.orange,0.05) : C.surface }}>
          <input type="checkbox" checked={form.certInAttesa}
            onChange={e => set("certInAttesa", e.target.checked)}
            style={{ marginTop:3, flexShrink:0 }} />
          <div>
            <div style={{ fontSize:13, fontWeight:600, color:C.text }}>
              Certificazione in attesa o diagnosi funzionale provvisoria
            </div>
            <div style={{ fontSize:11, color:C.muted, marginTop:3, lineHeight:1.4 }}>
              Da attivare quando la 104 è in iter o la documentazione clinica è ancora provvisoria.
              L'IA adotterà un tono più prudente e formule osservative, evitando affermazioni cliniche definitive.
            </div>
          </div>
        </label>
      </Field>

      <Field label="Comorbidità con DSA o altri disturbi specifici (facoltativo)"
        sub="Se l'alunno presenta, oltre alla disabilità certificata, disturbi specifici (es. dislessia, discalculia), indicali qui: l'IA integrerà misure compensative e dispensative coerenti nei suggerimenti">
        <Input value={form.comorbidita} onChange={e => set("comorbidita", e.target.value)}
          placeholder="Es. dislessia e discalculia associate" />
      </Field>

      <div style={{ position:"sticky", bottom:0, background:C.surface,
        borderTop:`1px solid ${C.border}`, padding:"12px 0 4px", marginTop:16,
        display:"flex", gap:10, justifyContent:"space-between" }}>
        <Btn ghost onClick={() => setPasso(1)} icon="←">Indietro</Btn>
        {inModifica
          ? <Btn primary onClick={salva} disabled={!valido} icon="✓">Salva modifiche</Btn>
          : UPLOAD_DOCUMENTI_ATTIVO
            ? <Btn primary onClick={() => setPasso(3)} disabled={!valido2} icon="→">Avanti</Btn>
            : <Btn primary onClick={salva} disabled={!valido2} icon="✓">Crea profilo</Btn>}
      </div>
    </div>
  );

  // ── PASSO 3 ───────────────────────────────────────────────────────────────
  const Passo3 = () => (
    <div className="fadeUp">
      <div style={{ marginBottom:18 }}>
        <div style={{ fontSize:17, fontWeight:700, color:C.text, marginBottom:4 }}>
          Documentazione dell'alunno/a
        </div>
        <div style={{ fontSize:13, color:C.muted, lineHeight:1.6 }}>
          Carica i documenti in tuo possesso per ricevere suggerimenti più mirati e coerenti con il profilo reale dell'alunno/a. Puoi aggiungere altri documenti in qualsiasi momento dalla sezione <strong>Documenti</strong>.
        </div>
      </div>

      <InfoBox color={C.teal} icon="💡">
        Questo passaggio è <strong>facoltativo</strong>. Se non hai i documenti a portata di mano, puoi saltarlo e procedere direttamente alla compilazione del PEI.
      </InfoBox>

      {isTrial && (
        <div style={{ marginBottom:16, padding:"14px 16px", borderRadius:10,
          background:`linear-gradient(135deg, ${hex(C.accent2,0.08)}, ${hex(C.accent,0.05)})`,
          border:`1px solid ${hex(C.accent2,0.25)}`,
          display:"flex", alignItems:"center", gap:12 }}>
          <span style={{ fontSize:22, lineHeight:1 }}>🔒</span>
          <div style={{ fontSize:12, color:C.muted, lineHeight:1.5 }}>
            <strong style={{ color:C.text }}>Caricamento documenti riservato al piano completo.</strong> Puoi creare il profilo ora e aggiungere diagnosi, PEI precedenti e relazioni dalla sezione <strong>Documenti</strong> dopo l'upgrade.
          </div>
        </div>
      )}

      {!isTrial && (
      <>
      {/* Opt-in estrazione AI strutturata (1 credito/doc) */}
      <label style={{ display:"flex", alignItems:"flex-start", gap:10, cursor:"pointer",
        padding:"12px 14px", borderRadius:10, marginBottom:16,
        border:`1px solid ${analizzaAI ? hex(C.accent,0.35) : C.border}`,
        background: analizzaAI ? hex(C.accent,0.05) : C.surface, transition:"all .15s" }}>
        <input type="checkbox" checked={analizzaAI}
          onChange={e => setAnalizzaAI(e.target.checked)}
          style={{ marginTop:2, accentColor:C.accent, width:16, height:16, cursor:"pointer" }} />
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:C.text }}>
            ✦ Analizza i documenti con l'AI <span style={{ fontSize:11, fontWeight:600, color:C.accent }}>(1 credito per documento)</span>
          </div>
          <div style={{ fontSize:12, color:C.muted, lineHeight:1.5, marginTop:2 }}>
            L'AI estrae e struttura solo le sezioni rilevanti per il PEI (diagnosi, obiettivi, strategie, codici ICF) e applica la pseudonimizzazione dei nomi. Se lasci l'opzione disattivata i file di testo (PDF/DOCX) vengono salvati come testo grezzo, senza consumare crediti. Le foto e i PDF scansionati richiedono comunque la lettura ottica dell'AI (1 credito), perché non contengono testo selezionabile.
          </div>
        </div>
      </label>

      <div style={{ display:"flex", flexDirection:"column", gap:14, marginBottom:20 }}>
        {TIPI_WIZARD.map(tipo => {
          const caricato = docsCaricati.find(d => d.tipo === tipo.id);
          const loading  = docUploading[tipo.id];
          return (
            <div key={tipo.id} style={{
              border:`2px solid ${caricato ? hex(C.green,0.4) : C.border}`,
              borderRadius:12, overflow:"hidden",
              background: caricato ? hex(C.green,0.03) : C.surface,
              transition:"all .2s"
            }}>
              <div style={{ padding:"14px 16px",
                background: caricato ? hex(C.green,0.06) : hex(C.accent,0.03),
                borderBottom:`1px solid ${caricato ? hex(C.green,0.2) : C.border}`,
                display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ fontSize:24 }}>{tipo.icon}</span>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700,
                      color: caricato ? C.green : C.text }}>{tipo.label}</div>
                    <div style={{ fontSize:11, color:C.muted, marginTop:1 }}>{tipo.desc}</div>
                  </div>
                </div>
                {caricato ? (
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <Badge color={C.green}>✓ Caricato</Badge>
                    <Btn danger small onClick={() => rimuoviDoc(tipo.id)}>✕</Btn>
                  </div>
                ) : (
                  <Badge color={C.muted} small>Facoltativo</Badge>
                )}
              </div>

              {caricato ? (
                <div style={{ padding:"10px 16px", fontSize:12, color:C.muted }}>
                  📎 {caricato.nome}
                  {caricato.isImg && <Badge color={C.teal} small style={{ marginLeft:6 }}>OCR</Badge>}
                  {caricato.aiAnalizzato && <Badge color={C.accent} small style={{ marginLeft:6 }}>✦ AI</Badge>}
                </div>
              ) : (
                <div style={{ padding:"12px 16px" }}>
                  {loading ? (
                    <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8,
                        fontSize:12, color:C.accent }}>
                        <Spinner size={12} /> Elaborazione in corso…
                      </div>
                      <div className="indetTrack" role="progressbar" aria-label="Caricamento documento in corso">
                        <div className="indetBar" />
                      </div>
                    </div>
                  ) : (
                    <label style={{ cursor:"pointer", display:"inline-flex",
                      alignItems:"center", gap:8 }}>
                      <input type="file" accept=".pdf,.docx,image/jpeg,image/png,image/webp"
                        onChange={e => handleDocUpload(e, tipo.id)}
                        style={{ display:"none" }} />
                      <div style={{ padding:"7px 14px", borderRadius:7,
                        border:`1px dashed ${hex(C.accent,0.4)}`,
                        background:hex(C.accent,0.04),
                        color:C.accent, fontSize:12, fontWeight:600,
                        transition:"all .15s" }}
                        onMouseEnter={e => e.currentTarget.style.background = hex(C.accent,0.09)}
                        onMouseLeave={e => e.currentTarget.style.background = hex(C.accent,0.04)}>
                        + Carica documento <span style={{ fontSize:10, color:C.mutedLight }}>(PDF, DOCX, foto)</span>
                      </div>
                    </label>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      </>
      )}

      <div style={{ padding:"12px 16px", background:hex(C.accent,0.04),
        border:`1px solid ${hex(C.accent,0.15)}`, borderRadius:10,
        fontSize:12, color:C.muted, marginBottom:20 }}>
        📁 Hai relazioni cliniche, verbali GLHO o altri documenti? Potrai aggiungerli in qualsiasi momento dalla sezione <strong style={{ color:C.accent }}>Documenti</strong> nella barra laterale.
      </div>

      <div style={{ position:"sticky", bottom:0, background:C.surface,
        borderTop:`1px solid ${C.border}`, padding:"12px 0 4px", marginTop:16,
        display:"flex", gap:10, justifyContent:"space-between" }}>
        <Btn ghost onClick={() => setPasso(2)} icon="←">Indietro</Btn>
        <Btn primary onClick={salva} icon={docsCaricati.length > 0 ? "✓" : "→"}>
          {docsCaricati.length > 0
            ? `Crea profilo con ${docsCaricati.length} ${docsCaricati.length > 1 ? "documenti" : "documento"}`
            : "Salta questo passaggio"}
        </Btn>
      </div>
    </div>
  );

  // ── RENDER PRINCIPALE ─────────────────────────────────────────────────────
  return (
    <div className="fadeUp" style={{ maxWidth:680, margin:"0 auto", padding:"28px 24px" }}>
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:20, fontWeight:700, fontFamily:"'Fraunces',serif", marginBottom:4 }}>
          {inModifica ? `Modifica profilo di ${studenteIniziale.iniziali || studenteIniziale.iniziale}` : "Nuovo profilo studente"}
        </div>
        <div style={{ fontSize:13, color:C.muted }}>
          Passo {passo} di {PASSI.length} — {["Dati anagrafici", "Diagnosi e codici ICD-10", "Documentazione (facoltativo)"][passo-1]}
        </div>
      </div>

      <Card>
        <StepBar />
        {/* IMPORTANTE: invochiamo come funzioni (non come componenti) per evitare
            unmount/remount ad ogni keystroke che farebbe perdere il focus all'input */}
        {passo === 1 && Passo1()}
        {passo === 2 && Passo2()}
        {passo === 3 && !inModifica && UPLOAD_DOCUMENTI_ATTIVO && Passo3()}
      </Card>

      {onAnnulla && (
        <div style={{ textAlign:"center", marginTop:14 }}>
          <button onClick={onAnnulla}
            style={{ background:"none", border:"none", color:C.muted,
              fontSize:12, cursor:"pointer", textDecoration:"underline" }}>
            Annulla e torna indietro
          </button>
        </div>
      )}
    </div>
  );
}

// =============================================================================
//  STUDENTI TAB
// =============================================================================
function StudentiTab({ studenti, setStudenti, setTab, setStudenteAttivo, onNuovoStudente, onModificaStudente, eliminaStudente, documentiStudenti = {} }) {
  return (
    <div className="fadeUp" style={{ padding:28 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:22 }}>
        <div>
          <h1 style={{ fontSize:20, fontWeight:700, color:C.text }}>👤 Studenti</h1>
          <div style={{ fontSize:13, color:C.muted, marginTop:2 }}>{studenti.length} studenti registrati</div>
        </div>
        <Btn primary onClick={onNuovoStudente} icon="+">Nuovo studente</Btn>
      </div>
      {studenti.length === 0 ? (
        <div style={{ textAlign:"center", padding:"80px 0", color:C.muted }}>
          <div style={{ fontSize:48, marginBottom:14 }}>👤</div>
          <Btn primary onClick={onNuovoStudente} icon="✦">Crea il primo profilo</Btn>
        </div>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(290px,1fr))", gap:14 }}>
          {studenti.map(s => {
            const ord = ORDINI[s.ordine];
            return (
              <Card key={s.id} style={{ overflow:"hidden" }}>
                <div style={{ display:"flex", gap:12, alignItems:"center", marginBottom:12 }}>
                  <div style={{ width:46, height:46, borderRadius:"50%",
                    background:hex(s.colore,.12), color:s.colore,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontSize:14, fontWeight:700, flexShrink:0,
                    border:`2px solid ${hex(s.colore,.3)}` }}>{s.avatar}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:700, fontSize:15, color:C.text }}>{s.iniziali}</div>
                    <div style={{ fontSize:12, color:C.muted }}>{ord?.icon} {ord?.label} · Cl. {s.classe}</div>
                    {s.eta && <div style={{ fontSize:11, color:C.mutedLight }}>{s.eta} anni</div>}
                  </div>
                </div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:5, marginBottom:12, minWidth:0 }}>
                  <Badge color={ord?.color}>{ord?.allegato}</Badge>
                  {s.diagnosi && (
                    <Badge color={C.orange} small>
                      {s.diagnosi.length > 40 ? s.diagnosi.slice(0,40) + "…" : s.diagnosi}
                    </Badge>
                  )}
                  {s.icd10?.map(c => <Badge key={c.code} color={C.teal} small>{c.code}</Badge>)}
                </div>
                <div style={{ fontSize:11, color:C.muted, marginBottom:8 }}>
                  📁 {(documentiStudenti[s.id]?.length || 0)} documenti caricati
                </div>
                <Divider />
                <div style={{ display:"flex", gap:7, justifyContent:"space-between" }}>
                  <div style={{ display:"flex", gap:6 }}>
                    <Btn primary small onClick={() => { setStudenteAttivo(s); setTab("pei"); }} icon="📄">PEI</Btn>
                    <Btn small onClick={() => onModificaStudente(s)} icon="✎">Modifica</Btn>
                  </div>
                  <Btn danger small onClick={() => { if (window.confirm(`Eliminare lo studente ${s.iniziali}?`)) eliminaStudente(s.id); }}>✕</Btn>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// =============================================================================
//  DASHBOARD
// =============================================================================
function Dashboard({ studenti, scadenze, peiDatiMap, documentiStudenti, setTab, setStudenteAttivo, onNuovoStudente }) {
  const { user, balance } = useCreditsCtx();

  const peiCompletati = studenti.filter(s => {
    const d = peiDatiMap?.[s.id] || {};
    return Object.values(d).some(v => v && String(v).trim().length > 0);
  }).length;

  const totaleDocumenti = studenti.reduce((acc, s) => acc + (documentiStudenti?.[s.id]?.length || 0), 0);

  // ─── Saluto dinamico ─────────────────────────────────────────────────────
  // Convenzione italiana: 5-12 buongiorno, 12-17 buon pomeriggio, 17+ buonasera
  const ora = new Date().getHours();
  const { saluto, emoji } =
    ora < 5            ? { saluto: "Buonasera",      emoji: "🌙" } :
    ora < 12           ? { saluto: "Buongiorno",     emoji: "👋" } :
    ora < 17           ? { saluto: "Buon pomeriggio", emoji: "✨" } :
    ora < 22           ? { saluto: "Buonasera",      emoji: "🌙" } :
                          { saluto: "Buonasera",      emoji: "🌙" };

  // Nome utente: prima il first_name dal metadata signup, poi fallback al nome
  // intero, poi alla parte locale dell'email come ultima risorsa
  const nomeUtente =
    user?.user_metadata?.first_name ||
    (user?.user_metadata?.full_name || "").split(" ")[0] ||
    user?.email?.split("@")[0] || "";

  // Data di oggi in italiano
  const dataOggi = new Date().toLocaleDateString("it-IT", {
    weekday: "long", day: "numeric", month: "long",
  });
  // capitalizza la prima lettera (es. "sabato" → "Sabato")
  const dataOggiCap = dataOggi.charAt(0).toUpperCase() + dataOggi.slice(1);

  // Prossima scadenza imminente (entro 30gg, non passata)
  const oggi = new Date(); oggi.setHours(0,0,0,0);
  const prossima = (scadenze || [])
    .filter(s => s.data && new Date(s.data) >= oggi)
    .sort((a, b) => new Date(a.data) - new Date(b.data))[0];
  const giorniProssima = prossima
    ? Math.ceil((new Date(prossima.data) - oggi) / (1000 * 60 * 60 * 24))
    : null;

  // Sottotitolo: data + (studenti / crediti / scadenza), pezzo per pezzo
  const sottotitoloPezzi = [dataOggiCap];
  if (studenti.length > 0) {
    sottotitoloPezzi.push(`${studenti.length} ${studenti.length === 1 ? "studente" : "studenti"}`);
  }
  if (balance !== null) {
    sottotitoloPezzi.push(`${balance} ${balance === 1 ? "credito" : "crediti"}`);
  }
  if (prossima && giorniProssima !== null && giorniProssima <= 30) {
    const t =
      giorniProssima === 0 ? `${prossima.testo} oggi` :
      giorniProssima === 1 ? `${prossima.testo} domani` :
                             `${prossima.testo} tra ${giorniProssima} gg`;
    sottotitoloPezzi.push(t);
  }
  const sottotitolo = sottotitoloPezzi.join(" · ");

  return (
    <div className="fadeUp" style={{ padding:28 }}>
      <div style={{ background:grad(hex(C.accent,.07),hex(C.accent2,.04),150),
        border:`1px solid ${hex(C.accent,.15)}`,borderRadius:14,
        padding:"20px 24px",marginBottom:22,
        display:"flex",alignItems:"center",justifyContent:"space-between", gap: 16, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <div className="serif" style={{
            fontSize: 22, fontWeight: 700, marginBottom: 4, color: C.text,
            letterSpacing: "-0.02em", lineHeight: 1.2
          }}>
            {saluto}{nomeUtente && `, ${nomeUtente}`} <span style={{ display: "inline-block" }}>{emoji}</span>
          </div>
          <div style={{ fontSize: 13, color: C.muted }}>
            {sottotitolo}
            {studenti.length === 0 && " · Inizia creando il primo profilo studente"}
          </div>
        </div>
        {studenti.length === 0 && (
          <Btn primary onClick={onNuovoStudente} icon="✦">Crea primo PEI</Btn>
        )}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:22 }}>
        {[
          {v:studenti.length, l:"Studenti", icon:"👤", c:C.accent},
          {v:peiCompletati,   l:"PEI compilati", icon:"📄", c:C.green},
          {v:totaleDocumenti, l:"Documenti caricati", icon:"📁", c:C.teal},
          {v:"v1", l:"Versione", icon:"✦", c:C.accent2},
        ].map(k => (
          <Card key={k.l}>
            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              <div style={{ width:42,height:42,borderRadius:10,
                background:hex(k.c,.1),display:"flex",
                alignItems:"center",justifyContent:"center",fontSize:18 }}>{k.icon}</div>
              <div>
                <div style={{ fontSize:22, fontWeight:700, color:k.c, lineHeight:1 }}>{k.v}</div>
                <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{k.l}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
        <Card>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <div style={{ fontSize:14, fontWeight:600, color:C.text }}>📋 Studenti</div>
            <Btn ghost small onClick={onNuovoStudente}>+ Aggiungi</Btn>
          </div>
          {studenti.length === 0 ? (
            <div style={{ textAlign:"center", padding:"24px 0", color:C.muted }}>
              <div style={{ fontSize:32, marginBottom:8 }}>👤</div>
              <div style={{ fontSize:13 }}>Nessuno studente ancora</div>
            </div>
          ) : studenti.map(s => {
            const ord = ORDINI[s.ordine];
            return (
              <div key={s.id} onClick={() => { setStudenteAttivo(s); setTab("pei"); }}
                style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8,
                  padding:"8px 10px", borderRadius:8, cursor:"pointer",
                  background:C.bg, border:`1px solid ${C.border}`, transition:"all .2s" }}>
                <div style={{ width:34,height:34,borderRadius:"50%",
                  background:hex(s.colore,.12), color:s.colore,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:11, fontWeight:700, flexShrink:0 }}>{s.avatar}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{s.iniziali}</div>
                  <div style={{ fontSize:11, color:C.muted,
                    overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {ord?.icon} {ord?.label} · {s.classe}{s.diagnosi ? ` · ${s.diagnosi}` : ""}
                  </div>
                </div>
                <Badge color={ord?.color} small>{ord?.allegato}</Badge>
              </div>
            );
          })}
        </Card>

        <Card>
          <div style={{ fontSize:14, fontWeight:600, color:C.text, marginBottom:14 }}>🔔 Scadenze</div>
          {scadenze.slice(0,5).map(s => (
            <div key={s.id} style={{ marginBottom:7, padding:"8px 10px",
              background:C.bg, borderRadius:8,
              borderLeft:`3px solid ${s.urgente?C.red:C.border}` }}>
              <div style={{ fontSize:12, fontWeight:500, color:C.text }}>{s.testo}</div>
              <div style={{ fontSize:11, color:C.muted, marginTop:1 }}>{isoToIt(s.data)}</div>
            </div>
          ))}
          <Btn ghost small onClick={() => setTab("scadenze")} style={{ marginTop:6 }}>Gestisci →</Btn>
        </Card>
      </div>

      <Card style={{ marginTop:16, borderColor:hex(C.accent,.15), background:hex(C.accent,.02) }}>
        <div style={{ fontSize:13, fontWeight:600, color:C.accent, marginBottom:10 }}>📜 Normativa vigente — giugno 2026</div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8 }}>
          {[["D.I. 153/2023","Modelli PEI A1–A4"],["D.Lgs 62/2024","Riforma disabilità"],
            ["D.Lgs 66/2017","Inclusione scolastica"],["D.L. 71/2024","Disposizioni urgenti"],
            ["D.L. 202/2024","Riforma disabilità rinviata al 2027"],["INPS (2026)","Valutazione unica (sperim.)"]
          ].map(([d,n]) => (
            <div key={d} style={{ padding:"7px 10px", background:hex(C.accent,.05), borderRadius:7 }}>
              <div style={{ fontSize:11, fontWeight:700, color:C.accent }}>{d}</div>
              <div style={{ fontSize:11, color:C.muted, marginTop:1 }}>{n}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// =============================================================================
//  LOGOUT BUTTON (sidebar)
// =============================================================================
function LogoutButton() {
  const { signOut } = useCreditsCtx();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSignOut = async () => {
    setLoading(true);
    try {
      await signOut();
    } catch (e) {
      console.error("Logout error:", e);
    } finally {
      // Fallback: forza reload per garantire uscita anche se il listener non si aggiorna
      window.location.href = '/';
    }
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        style={{
          marginTop: 10, width: "100%",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          background: "none", border: `1px solid ${hex("#ffffff", 0.15)}`,
          borderRadius: R.sm, padding: "7px 12px",
          color: hex("#ffffff", 0.55), fontSize: 11, fontWeight: 500,
          fontFamily: "'Inter',sans-serif",
          cursor: "pointer", transition: "all .2s"
        }}
        onMouseEnter={e => { e.currentTarget.style.background = hex("#ffffff", 0.08); e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = hex("#ffffff", 0.3); }}
        onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = hex("#ffffff", 0.55); e.currentTarget.style.borderColor = hex("#ffffff", 0.15); }}
      >
        <span style={{ fontSize: 13 }}>↪</span> Logout
      </button>
      {showModal && createPortal(
        <div className="fadeIn" style={{
          position: "fixed", inset: 0, zIndex: 9000,
          background: "rgba(28,25,23,0.55)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 24
        }}>
          <div style={{
            background: C.surface, borderRadius: R.lg, padding: "32px 36px",
            maxWidth: 420, width: "100%",
            boxShadow: "0 24px 64px rgba(28,25,23,0.18)",
            border: `1px solid ${C.border}`
          }}>
            <div style={{ fontSize: 32, marginBottom: 12, textAlign: "center" }}>👋</div>
            <h2 className="serif" style={{
              fontSize: 22, fontWeight: 700, color: C.text,
              letterSpacing: "-0.02em", marginBottom: 10, textAlign: "center"
            }}>Vuoi uscire?</h2>
            <p style={{ fontSize: 14, color: C.muted, textAlign: "center", marginBottom: 24, lineHeight: 1.6 }}>
              I tuoi dati sono salvati in modo sicuro e saranno disponibili al prossimo accesso, da qualsiasi dispositivo.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <Btn primary onClick={handleSignOut} style={{ justifyContent: "center" }} disabled={loading}>
                {loading ? "Uscita in corso…" : "Esci"}
              </Btn>
              <Btn ghost onClick={() => setShowModal(false)} style={{ justifyContent: "center", color: C.muted }}>Annulla</Btn>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

// =============================================================================
//  SIDEBAR (con icona Impostazioni)
// =============================================================================
function Sidebar({ tab, setTab, studenti, studenteAttivo, setStudenteAttivo, onNuovoStudente, salvataggioOk, eliminaStudente }) {
  const { includeICFCodes, toggleIncludeICFCodes, guidedMode, setGuidedMode } = useSettings();
  const { balance, plan, loading: creditiLoading } = useCreditsCtx();
  const [showSettings, setShowSettings] = useState(false);

  // Colore card crediti in base al saldo
  const creditiColor =
    balance === null      ? C.muted :
    balance <= 0           ? C.red :
    balance <= SOGLIA_AVVISO ? C.orange :
                              C.green;
  const nav = [
    {id:"dashboard", icon:"⊞", label:"Dashboard"},
    {id:"studenti",  icon:"👤", label:"Studenti"},
    {id:"pei",       icon:"📄", label:"Editor PEI"},
    ...(UPLOAD_DOCUMENTI_ATTIVO ? [{id:"documenti", icon:"📁", label:"Documenti"}] : []),
    {id:"scadenze",  icon:"🔔", label:"Scadenze"},
    {id:"account",   icon:"💎", label:"Account & Crediti"},
  ];
  return (
    <div style={{
      width: 230, height: "100vh",
      background: C.accentDeep,
      display: "flex", flexDirection: "column",
      position: "sticky", top: 0, flexShrink: 0,
      color: "#fff",
      overflow: "hidden"
    }}>
      {/* glow decorativo */}
      <div style={{ position: "absolute", top: -80, left: -60,
        width: 240, height: 240, borderRadius: "50%",
        background: `radial-gradient(circle, ${hex(C.accent2, 0.4)} 0%, transparent 70%)`,
        pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -100, right: -60,
        width: 220, height: 220, borderRadius: "50%",
        background: `radial-gradient(circle, ${hex(C.accent, 0.25)} 0%, transparent 70%)`,
        pointerEvents: "none" }} />

      <div style={{ padding: "22px 18px 16px", position: "relative", zIndex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {/* Logo con rilievo: anello luminoso + sfondo bianco + drop shadow */}
          <div style={{
            position: "relative",
            width: 48, height: 48,
            borderRadius: 14,
            padding: 2,
            background: `linear-gradient(135deg, ${hex("#ffffff", 0.4)} 0%, ${hex("#ffffff", 0.10)} 50%, ${hex(C.accent, 0.5)} 100%)`,
            boxShadow: `0 10px 24px rgba(0,0,0,0.35), 0 2px 6px rgba(0,0,0,0.2), inset 0 1px 0 ${hex("#ffffff", 0.4)}`,
            flexShrink: 0
          }}>
            <div style={{
              width: "100%", height: "100%",
              borderRadius: 12,
              background: "linear-gradient(160deg, #ffffff 0%, #f6f5ef 100%)",
              display: "flex", alignItems: "center", justifyContent: "center",
              overflow: "hidden",
              boxShadow: `inset 0 -2px 4px ${hex("#000000", 0.06)}, inset 0 1px 0 ${hex("#ffffff", 0.9)}`
            }}>
              <img src={`${process.env.PUBLIC_URL || ""}/logo.jpg`}
                alt="Logo IEP Assistant"
                style={{
                  width: 42, height: 42,
                  objectFit: "cover",
                  borderRadius: 10,
                  filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.15))",
                  display: "block"
                }} />
            </div>
            {/* riflesso lucido in alto a sinistra */}
            <div style={{
              position: "absolute", top: 4, left: 4,
              width: 14, height: 14, borderRadius: "50%",
              background: `radial-gradient(circle at 30% 30%, ${hex("#ffffff", 0.8)} 0%, transparent 60%)`,
              pointerEvents: "none"
            }} />
          </div>
          <div>
            <div className="serif" style={{
              fontSize: 17, fontWeight: 700, color: "#fff",
              letterSpacing: "-0.02em", lineHeight: 1.1
            }}>IEP Assistant</div>
            <div style={{ fontSize: 10, color: hex("#ffffff", 0.55), letterSpacing: 0.5, marginTop: 2 }}>v1.0</div>
          </div>
        </div>
      </div>

      {studenti.length > 0 && (
        <div style={{ padding: "8px 12px 12px", position: "relative", zIndex: 1 }}>
          <div style={{ fontSize: 10, color: hex("#ffffff", 0.45), fontWeight: 700,
            textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8, paddingLeft: 8 }}>Studenti</div>
          {studenti.map(s => {
            const act = studenteAttivo?.id === s.id;
            return (
              <div key={s.id} style={{ position: "relative" }}>
                <button onClick={() => { setStudenteAttivo(s); setTab("pei"); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, width: "100%",
                    padding: "8px 10px", borderRadius: R.sm,
                    border: "none",
                    background: act ? hex("#ffffff", 0.10) : "transparent",
                    cursor: "pointer", textAlign: "left", marginBottom: 4,
                    transition: "all .2s cubic-bezier(.16,1,.3,1)"
                  }}
                  onMouseEnter={e => { if (!act) e.currentTarget.style.background = hex("#ffffff", 0.05); }}
                  onMouseLeave={e => { if (!act) e.currentTarget.style.background = "transparent"; }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: "50%",
                    background: hex(s.colore, 0.25),
                    color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 700, flexShrink: 0,
                    border: `1.5px solid ${hex(s.colore, 0.6)}`
                  }}>{s.avatar}</div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600,
                      color: "#fff",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.iniziali}</div>
                    <div style={{ fontSize: 10, color: hex("#ffffff", 0.5) }}>
                      {ORDINI[s.ordine]?.icon} {s.classe}
                    </div>
                  </div>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); if (window.confirm(`Eliminare lo studente ${s.iniziali}?`)) eliminaStudente(s.id); }}
                  style={{ position: "absolute", right: 6, top: 10,
                    background: "none", border: "none",
                    color: hex("#ffffff", 0.4), cursor: "pointer",
                    fontSize: 13, zIndex: 1, padding: 2, borderRadius: 4 }}
                  title="Elimina studente"
                >🗑️</button>
              </div>
            );
          })}
          <button onClick={onNuovoStudente}
            style={{
              display: "flex", alignItems: "center", gap: 8, width: "100%",
              padding: "8px 10px", borderRadius: R.sm,
              border: `1px dashed ${hex("#ffffff", 0.20)}`,
              background: "transparent",
              color: hex("#ffffff", 0.65),
              cursor: "pointer", fontSize: 12, fontWeight: 500,
              fontFamily: "'Inter',sans-serif", marginTop: 6,
              transition: "all .2s cubic-bezier(.16,1,.3,1)"
            }}
            onMouseEnter={e => { e.currentTarget.style.background = hex("#ffffff", 0.08); e.currentTarget.style.borderColor = hex("#ffffff", 0.4); e.currentTarget.style.color = "#fff"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = hex("#ffffff", 0.20); e.currentTarget.style.color = hex("#ffffff", 0.65); }}>
            <span style={{ fontSize: 14 }}>+</span> Nuovo studente
          </button>
        </div>
      )}

      <div style={{ flex: 1, overflowY: "auto", padding: "10px 12px", position: "relative", zIndex: 1 }}>
        <div style={{ fontSize: 10, color: hex("#ffffff", 0.45), fontWeight: 700,
          textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8, paddingLeft: 8 }}>Navigazione</div>
        {nav.map(it => {
          const act = tab === it.id;
          return (
            <button key={it.id} onClick={() => setTab(it.id)}
              style={{
                display: "flex", alignItems: "center", gap: 12, width: "100%",
                padding: "10px 12px", borderRadius: R.sm,
                border: "none",
                background: act ? grad(C.accent, C.accent2) : "transparent",
                color: act ? "#fff" : hex("#ffffff", 0.7),
                fontSize: 13, fontWeight: act ? 600 : 500,
                fontFamily: "'Inter',sans-serif",
                transition: "all .2s cubic-bezier(.16,1,.3,1)",
                textAlign: "left", marginBottom: 3,
                boxShadow: act ? `0 6px 18px ${hex(C.accent, 0.4)}` : "none",
                position: "relative"
              }}
              onMouseEnter={e => { if (!act) e.currentTarget.style.background = hex("#ffffff", 0.06); }}
              onMouseLeave={e => { if (!act) e.currentTarget.style.background = "transparent"; }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>{it.icon}</span>
              <span>{it.label}</span>
            </button>
          );
        })}
        <button onClick={() => setShowSettings(!showSettings)}
          style={{
            display: "flex", alignItems: "center", gap: 12, width: "100%",
            padding: "10px 12px", borderRadius: R.sm, border: "none",
            background: showSettings ? hex("#ffffff", 0.06) : "transparent",
            color: showSettings ? "#fff" : hex("#ffffff", 0.7),
            fontSize: 13, fontWeight: 500,
            fontFamily: "'Inter',sans-serif",
            transition: "all .2s cubic-bezier(.16,1,.3,1)",
            textAlign: "left", marginBottom: 3, marginTop: 4
          }}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>⚙️</span>
          <span>Impostazioni</span>
        </button>
        {showSettings && (
          <div className="fadeIn" style={{
            marginTop: 6, padding: "14px 16px",
            background: hex("#ffffff", 0.06),
            borderRadius: R.sm,
            border: `1px solid ${hex("#ffffff", 0.10)}`,
            display: "flex", flexDirection: "column", gap: 12,
            color: "#fff"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Toggle checked={includeICFCodes} onChange={toggleIncludeICFCodes} color={C.accent2} />
              <span style={{ fontSize: 12, color: "#fff" }}>Includi codici ICF</span>
            </div>
            <div style={{ fontSize: 10, color: hex("#ffffff", 0.5), lineHeight: 1.4 }}>
              Il modello ministeriale chiede una descrizione discorsiva: i codici non sono richiesti e restano nel Profilo di Funzionamento redatto dall'ASL. Attivalo se la tua scuola li vuole nel testo del PEI.
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Toggle checked={guidedMode} onChange={setGuidedMode} color={C.accent2} />
              <span style={{ fontSize: 12, color: "#fff" }}>Modalità guidata</span>
            </div>
            <div style={{ fontSize: 10, color: hex("#ffffff", 0.5), lineHeight: 1.4 }}>
              Compilazione passo-passo del PEI con validazione dei campi essenziali.
            </div>
          </div>
        )}
      </div>

      {/* ─── CARD CREDITI sempre visibile ───────────────────────────── */}
      <div style={{ padding: "12px 14px",
        borderTop: `1px solid ${hex("#ffffff", 0.10)}`,
        position: "relative", zIndex: 1 }}>
        <button
          onClick={() => setTab("account")}
          title="Vai a Account & Crediti"
          style={{
            width: "100%", display: "block",
            padding: "12px 14px",
            borderRadius: R.md,
            background: balance !== null
              ? `linear-gradient(135deg, ${hex(creditiColor, 0.22)} 0%, ${hex(creditiColor, 0.10)} 100%)`
              : hex("#ffffff", 0.06),
            cursor: "pointer", textAlign: "left",
            transition: "all .25s cubic-bezier(.16,1,.3,1)",
            border: `1px solid ${balance !== null ? hex(creditiColor, 0.40) : hex("#ffffff", 0.10)}`,
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 18 }}>💎</span>
              <span style={{ fontSize: 10, color: hex("#ffffff", 0.65), fontWeight: 700,
                textTransform: "uppercase", letterSpacing: 0.6 }}>Saldo</span>
            </div>
            <Badge color={plan === "premium" ? C.accent2 : plan === "base" ? C.accent : C.green}
              bg={plan === "premium" ? hex(C.accent2, 0.25) : plan === "base" ? hex(C.accent, 0.25) : hex(C.green, 0.22)}
              small>
              {plan === "free" ? "FREE" : plan.toUpperCase()}
            </Badge>
          </div>
          <div className="serif" style={{
            fontSize: 32, fontWeight: 700, color: "#fff",
            lineHeight: 1.05, letterSpacing: "-0.03em", marginTop: 6
          }}>
            {balance === null ? (creditiLoading ? "…" : "—") : balance}
            <span style={{ fontSize: 12, color: hex("#ffffff", 0.65),
              fontFamily: "'Inter', sans-serif", fontWeight: 500, marginLeft: 6 }}>
              {balance === 1 ? "credito" : "crediti"}
            </span>
          </div>
          {balance !== null && balance <= SOGLIA_AVVISO && (
            <div style={{ marginTop: 8, fontSize: 11, color: "#fff",
              padding: "4px 8px", borderRadius: R.sm,
              background: hex(creditiColor, 0.4),
              fontWeight: 600 }}>
              {balance <= 0 ? "🔒 Esauriti — acquista" : "⚠️ Quasi esauriti"}
            </div>
          )}
        </button>

        <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 6,
          fontSize: 10, fontWeight: 500,
          color: salvataggioOk ? C.green : hex("#ffffff", 0.4),
          transition: "color .3s" }}>
          <span style={{ fontSize: 11 }}>{salvataggioOk ? "✓" : "💾"}</span>
          <span>{salvataggioOk ? "Salvato in locale" : "Auto-salvataggio"}</span>
          <span style={{ marginLeft: "auto", color: hex("#ffffff", 0.4) }}>
            {studenti.length}/{plan === "free" ? "1" : "∞"}
          </span>
        </div>

        {SUPABASE_ENABLED && (
          <LogoutButton />
        )}

      </div>
    </div>
  );
}

// =============================================================================
//  SCADENZE
// =============================================================================
function Scadenze({ scadenze, setScadenze }) {
  const [form, setForm] = useState({testo:"",data:"",tipo:"glo",urgente:false});
  const [open, setOpen] = useState(false);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));
  const aggiungi = () => {
    if(!form.testo.trim()||!form.data) return;
    setScadenze(p=>[...p,{id:Date.now(),...form}]);
    setForm({testo:"",data:"",tipo:"glo",urgente:false}); setOpen(false);
  };

  const ripristinaPredefinite = () => {
    if (window.confirm('Ripristinare le scadenze predefinite? Le scadenze attuali verranno sostituite.')) {
      setScadenze(scadenzeDefault());
    }
  };

  const eliminaScadenza = (id) => {
    if (window.confirm('Eliminare questa scadenza?')) {
      setScadenze(p => p.filter(x => x.id !== id));
    }
  };

  const TIPI = {glo:{l:"GLO",c:C.accent,i:"🗓"},verifica:{l:"Verifica",c:C.orange,i:"📝"},pei:{l:"PEI",c:C.green,i:"📄"},altro:{l:"Altro",c:C.muted,i:"📌"}};
  const sorted = [...scadenze].sort((a,b)=>{if(a.urgente!==b.urgente)return a.urgente?-1:1;return a.data<b.data?-1:1;});
  
  return (
    <div className="fadeUp" style={{padding:28}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:22}}>
        <h1 style={{fontSize:20,fontWeight:700,color:C.text}}>🔔 Scadenze</h1>
        <div style={{display:"flex", gap:10}}>
          <Btn ghost small onClick={ripristinaPredefinite} icon="🔄">Ripristina predefinite</Btn>
          <Btn primary onClick={()=>setOpen(o=>!o)} icon="+">Nuova scadenza</Btn>
        </div>
      </div>
      {open && (
        <Card style={{marginBottom:16}}>
          <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr",gap:12,marginBottom:10}}>
            <Field label="Descrizione"><Input value={form.testo} onChange={e=>set("testo",e.target.value)} placeholder="Es. Riunione GLO…"/></Field>
            <Field label="Data"><Input type="date" value={form.data} onChange={e=>set("data",e.target.value)}/></Field>
            <Field label="Tipo">
              <Select value={form.tipo} onChange={e=>set("tipo",e.target.value)}>
                {Object.entries(TIPI).map(([k,v])=><option key={k} value={k}>{v.i} {v.l}</option>)}
              </Select>
            </Field>
          </div>
          <div style={{display:"flex",gap:10,alignItems:"center"}}>
            <Btn primary small onClick={aggiungi} disabled={!form.testo.trim()||!form.data}>Salva</Btn>
            <Btn ghost small onClick={()=>setOpen(false)}>Annulla</Btn>
            <label style={{fontSize:12,color:C.muted,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
              <input type="checkbox" checked={form.urgente} onChange={e=>set("urgente",e.target.checked)}/> Urgente
            </label>
          </div>
        </Card>
      )}
      <InfoBox color={C.accent} icon="📜">PEI definitivo entro ottobre · Verifica intermedia · Verifica finale + PEI provvisorio entro 30 giugno</InfoBox>
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {sorted.map(sc=>{const m=TIPI[sc.tipo]||TIPI.altro;return(
          <Card key={sc.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px"}}>
            <div style={{fontSize:22,flexShrink:0}}>{m.i}</div>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:600,color:C.text}}>{sc.testo}</div>
              <div style={{fontSize:11,color:C.muted,marginTop:1}}>{isoToIt(sc.data)}</div>
            </div>
            <div style={{display:"flex",gap:6,alignItems:"center",flexShrink:0}}>
              <Badge color={m.c}>{m.l}</Badge>
              {sc.urgente&&<Badge color={C.red}>URGENTE</Badge>}
              <Btn danger small onClick={()=>eliminaScadenza(sc.id)}>✕</Btn>
            </div>
          </Card>
        );})}
        {sorted.length===0&&
          <div style={{textAlign:"center",color:C.muted,padding:"40px 0",fontSize:13}}>
            Nessuna scadenza presente.<br/>
            <Btn ghost small onClick={ripristinaPredefinite} style={{marginTop:12}}>Ripristina scadenze predefinite</Btn>
          </div>
        }
      </div>
    </div>
  );
}

// =============================================================================
//  PERSISTENZA LOCALE E APP ROOT
// =============================================================================
const LS_KEY = "iep_assistant_v9";

function caricaDaLS() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function salvaSuLS(dati, ownerId) {
  try {
    // _ownerId marca a quale utente appartengono i dati in cache, così un altro
    // account sullo stesso browser non li legge/migra (isolamento dati). null = demo.
    localStorage.setItem(LS_KEY, JSON.stringify({ ...dati, _ownerId: ownerId ?? null }));
  } catch (e) { console.warn(e); }
}

// ─── Banner migrazione localStorage → Supabase ───────────────────────────────
function BannerMigrazione({ onConferma, onIgnora, dataBackup }) {
  return (
    <div style={{
      position:'fixed', top:0, left:0, right:0, zIndex:9999,
      background:'linear-gradient(135deg,#6366f1,#8b5cf6)',
      color:'#fff', padding:'14px 22px',
      display:'flex', alignItems:'center', gap:14, flexWrap:'wrap',
      boxShadow:'0 2px 12px rgba(0,0,0,.18)',
    }}>
      <span style={{ fontSize:20 }}>☁️</span>
      <div style={{ flex:1, fontSize:14, lineHeight:1.4 }}>
        <strong>Trovati dati locali da sincronizzare</strong>
        {dataBackup && <span style={{ opacity:.85 }}> (salvati il {dataBackup})</span>}
        <br/>
        <span style={{ opacity:.9 }}>Vuoi caricarli su Supabase per accedervi da qualsiasi dispositivo?</span>
      </div>
      <div style={{ display:'flex', gap:10 }}>
        <button onClick={onConferma} style={{
          background:'#fff', color:'#6366f1', border:'none', borderRadius:8,
          padding:'7px 16px', fontWeight:700, cursor:'pointer', fontSize:13,
        }}>
          ✓ Sincronizza
        </button>
        <button onClick={onIgnora} style={{
          background:'rgba(255,255,255,.2)', color:'#fff', border:'none', borderRadius:8,
          padding:'7px 16px', fontWeight:600, cursor:'pointer', fontSize:13,
        }}>
          Ignora
        </button>
      </div>
    </div>
  );
}

function codiceErroreSync(err) {
  const testo = [err?.message, err?.details, err?.hint, err?.code].filter(Boolean).join(' ');
  return testo.includes('ordine_non_consentito_per_band') ? 'band' : 'rete';
}

function AppContent() {
  const [tab,            setTab]            = useState("dashboard");
  const [studenti,       setStudenti]       = useState([]);
  const [scadenze,       setScadenze]       = useState(() => scadenzeDefault());
  const [studenteAttivo, setStudenteAttivo] = useState(null);
  const [peiDatiMap,     setPeiDatiMap]     = useState({});
  const [documentiStudenti, setDocumentiStudenti] = useState({});
  const [salvataggioOk,  setSalvataggioOk]  = useState(false);
  const [schermata,      setSchermata]      = useState("app");
  const [studenteInModifica, setStudenteInModifica] = useState(null);
  const [syncError,      setSyncError]      = useState(false);  // false | 'rete' | 'band' — esito dell'ultimo sync Supabase
  const [syncRetrying,   setSyncRetrying]   = useState(false);  // true durante il retry manuale

  // ─── Stato caricamento dati ─────────────────────────────────────────────────
  const [dataLoading,    setDataLoading]    = useState(true);   // true finché il primo caricamento non finisce
  const [dataMigrazione, setDataMigrazione] = useState(null);   // dati LS in attesa di migrazione
  const [migrazioneInCorso, setMigrazioneInCorso] = useState(false);
  const [paymentBanner,  setPaymentBanner]  = useState(null);   // 'success' | 'cancelled' dopo ritorno da Stripe

  const mountedRoot      = useRef(true);
  const saveDebounceRef  = useRef(null);
  const firstLoadDone    = useRef(false);

  useEffect(() => { mountedRoot.current = true; return () => { mountedRoot.current = false; }; }, []);

  // ─── user dal context ────────────────────────────────────────────────────────
  const { user, refresh } = useCreditsCtx();

  // ─── Ritorno dal Checkout Stripe (gestito a livello app, su QUALSIASI tab) ────
  // Stripe redirige a `/?payment=success&session_id=…` e l'utente atterra sulla
  // Dashboard. Mostriamo conferma, aggiorniamo il saldo (il webhook accredita in
  // modo asincrono → ripetiamo qualche refresh come rete di sicurezza oltre al
  // realtime), e ripuliamo l'URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('payment');
    if (status !== 'success' && status !== 'cancelled') return;
    window.history.replaceState({}, '', window.location.pathname);
    setPaymentBanner(status);
    if (status === 'success') {
      refresh?.();
      let n = 0;
      const id = setInterval(() => { n += 1; refresh?.(); if (n >= 5) clearInterval(id); }, 2000);
      const hide = setTimeout(() => setPaymentBanner(null), 9000);
      return () => { clearInterval(id); clearTimeout(hide); };
    }
  }, [refresh]);

  // ─── Caricamento iniziale dati (da Supabase o localStorage) ─────────────────
  useEffect(() => {
    if (firstLoadDone.current) return;  // evita doppio caricamento in StrictMode

    async function carica() {
      // 1. Prova Supabase (se disponibile e utente loggato)
      if (SUPABASE_ENABLED && user?.id) {
        const datiRemoti = await caricaDaSupabase(user.id);

        if (datiRemoti) {
          if (datiRemoti.studenti.length > 0) {
            // Supabase ha dati → usali, ignora localStorage
            if (!mountedRoot.current) return;
            setStudenti(datiRemoti.studenti);
            setPeiDatiMap(datiRemoti.peiDatiMap);
            setScadenze(datiRemoti.scadenze.length > 0 ? datiRemoti.scadenze : scadenzeDefault());
            setDocumentiStudenti(datiRemoti.documentiStudenti);
            setDataLoading(false);
            firstLoadDone.current = true;
            return;
          }

          // Supabase è vuoto → controlla se c'è roba in localStorage da migrare.
          // Solo dati "demo" (_ownerId null) o del MEDESIMO utente sono migrabili:
          // i dati di un ALTRO account loggato sullo stesso browser vanno ignorati
          // (isolamento dati tra account) e ripuliti — niente banner di sincronizzazione.
          const datiLS = leggiDatiLocalStorage(LS_KEY);
          const lsOwner = datiLS?._ownerId ?? null;
          const datiLSpropri = lsOwner === null || lsOwner === user.id;
          if (datiLS && datiLS.studenti?.length > 0 && datiLSpropri) {
            // Carica i dati LS in stato e mostra banner di migrazione
            if (!mountedRoot.current) return;
            setStudenti(datiLS.studenti);
            setPeiDatiMap(datiLS.peiDatiMap || {});
            setScadenze(datiLS.scadenze?.length > 0 ? datiLS.scadenze : scadenzeDefault());
            setDocumentiStudenti(datiLS.documentiStudenti || {});
            setDataMigrazione(datiLS);
            setDataLoading(false);
            firstLoadDone.current = true;
            return;
          }
          // Dati locali appartenenti a un altro account → ripuliscili, non mostrarli
          if (datiLS && !datiLSpropri) { try { localStorage.removeItem(LS_KEY); } catch (_) {} }

          // Supabase vuoto, localStorage vuoto → app vergine
          if (!mountedRoot.current) return;
          setDataLoading(false);
          firstLoadDone.current = true;
          return;
        }
        // Supabase ha restituito null (errore di rete) → fallback su localStorage
      }

      // 2. Fallback: localStorage (modalità demo o offline).
      // Carica solo dati demo (_ownerId null) o dell'utente corrente, mai di un altro account.
      const datiLS = caricaDaLS();
      const lsOwnerFb = datiLS?._ownerId ?? null;
      if (!mountedRoot.current) return;
      if (datiLS && (lsOwnerFb === null || lsOwnerFb === user?.id)) {
        setStudenti(datiLS.studenti || []);
        setPeiDatiMap(datiLS.peiDatiMap || {});
        setScadenze(datiLS.scadenze?.length > 0 ? datiLS.scadenze : scadenzeDefault());
        setDocumentiStudenti(datiLS.documentiStudenti || {});
      }
      setDataLoading(false);
      firstLoadDone.current = true;
    }

    carica();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // ─── Selezione studenteAttivo al caricamento dei dati ───────────────────────
  useEffect(() => {
    if (studenti.length > 0 && !studenteAttivo) {
      const saved = caricaDaLS();
      const id = saved?.studenteAttivoId;
      const trovato = id ? studenti.find(s => s.id === id) : null;
      setStudenteAttivo(trovato || studenti[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studenti]);

  // ─── Auto-salvataggio (localStorage SEMPRE + Supabase debounced) ─────────────
  useEffect(() => {
    if (dataLoading) return;   // non salvare prima del caricamento

    // 1. Salva SUBITO su localStorage (cache locale, istantaneo)
    salvaSuLS({ studenti, scadenze, peiDatiMap, documentiStudenti }, user?.id);
    setSalvataggioOk(true);
    const tBadge = setTimeout(() => { if (mountedRoot.current) setSalvataggioOk(false); }, 1800);

    // 2. Salva su Supabase con debounce 1.5s
    if (SUPABASE_ENABLED && user?.id) {
      if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
      saveDebounceRef.current = setTimeout(async () => {
        if (!mountedRoot.current) return;
        try {
          await Promise.all([
            salvaStudenti(user.id, studenti, UPLOAD_DOCUMENTI_ATTIVO ? documentiStudenti : {}),
            salvaScadenze(user.id, scadenze),
            salvaTuttaPeiDatiMap(user.id, peiDatiMap),
          ]);
          if (mountedRoot.current) setSyncError(false);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[AppContent] sync Supabase error:', err);
          if (mountedRoot.current) setSyncError(codiceErroreSync(err));
        }
      }, 1500);
    }

    return () => clearTimeout(tBadge);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studenti, scadenze, peiDatiMap, documentiStudenti]);

  // ─── Salvataggio immediato documenti su Supabase ──────────────────────────
  // I documenti vengono salvati subito (senza debounce) per evitare perdita
  // dati in caso di ricarica rapida della pagina dopo un upload.
  const prevDocumentiRef = useRef({});
  useEffect(() => {
    if (dataLoading || !SUPABASE_ENABLED || !user?.id) return;
    const prev = prevDocumentiRef.current;
    // Trova gli studenti i cui documenti sono cambiati
    const studentiCambiati = Object.keys(documentiStudenti).filter(studentId => {
      const prevDocs = prev[studentId];
      const currDocs = documentiStudenti[studentId];
      return JSON.stringify(prevDocs) !== JSON.stringify(currDocs);
    });
    prevDocumentiRef.current = documentiStudenti;
    if (studentiCambiati.length === 0) return;
    // Salva immediatamente i documenti degli studenti cambiati
    studentiCambiati.forEach(studentId => {
      salvaDocumentiStudente(studentId, user.id, UPLOAD_DOCUMENTI_ATTIVO ? (documentiStudenti[studentId] || []) : [])
        .catch(err => console.warn('[docs] save error:', err));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentiStudenti]);

  // ─── Retry sync manuale (chiamato dal banner di errore) ─────────────────────
  const retrySyncSupabase = async () => {
    if (!SUPABASE_ENABLED || !user?.id) return;
    setSyncRetrying(true);
    try {
      await Promise.all([
        salvaStudenti(user.id, studenti, UPLOAD_DOCUMENTI_ATTIVO ? documentiStudenti : {}),
        salvaScadenze(user.id, scadenze),
        salvaTuttaPeiDatiMap(user.id, peiDatiMap),
      ]);
      if (mountedRoot.current) setSyncError(false);
    } catch (err) {
      console.warn('[AppContent] retry sync error:', err);
      if (mountedRoot.current) setSyncError(codiceErroreSync(err));
    } finally {
      if (mountedRoot.current) setSyncRetrying(false);
    }
  };

  const onNuovoStudente = () => setSchermata("nuovo_studente");

  const onModificaStudente = (s) => {
    setStudenteInModifica(s);
    setSchermata("modifica_studente");
  };

  const chiudiModifica = () => {
    setStudenteInModifica(null);
    setSchermata("app");
  };

  const onAggiornaStudente = (s) => {
    setStudenti(prev => prev.map(x => (x.id === s.id ? s : x)));
    if (studenteAttivo?.id === s.id) setStudenteAttivo(s);
    setStudenteInModifica(null);
    setSchermata("app");
    setTab("studenti");
  };

  const eliminaStudente = (id) => {
    setStudenti(prev => prev.filter(s => s.id !== id));
    setPeiDatiMap(prev => { const newMap = { ...prev }; delete newMap[id]; return newMap; });
    setDocumentiStudenti(prev => { const newMap = { ...prev }; delete newMap[id]; return newMap; });
    if (studenteAttivo?.id === id) setStudenteAttivo(null);
    // Elimina immediatamente da Supabase (non aspettare il debounce)
    if (SUPABASE_ENABLED && user?.id) {
      eliminaStudenteDaSupabase(id, user.id).catch(err =>
        console.warn('[eliminaStudente] Supabase error:', err)
      );
    }
  };

  const onSalvaStudente = (s) => {
    setStudenti(p => [...p, s]);
    setStudenteAttivo(s);
    setSchermata("app");
    setTab("pei");
  };

  // ─── Handler migrazione localStorage → Supabase ─────────────────────────────
  const onConfermaMigrazione = async () => {
    if (!dataMigrazione || !user?.id) return;
    setMigrazioneInCorso(true);
    const ok = await migraLocalStorageASupabase(user.id, dataMigrazione);
    setMigrazioneInCorso(false);
    setDataMigrazione(null);
    if (ok) {
      alert("✅ Dati sincronizzati su Supabase! Ora puoi accedere dal qualsiasi dispositivo.");
    } else {
      alert("⚠️ Sincronizzazione non riuscita. I tuoi dati sono comunque al sicuro localmente.");
    }
  };

  const onIgnoraMigrazione = () => setDataMigrazione(null);

  // ─── Loading screen ──────────────────────────────────────────────────────────
  if (dataLoading) {
    return (
      <>
        <style>{STYLES}</style>
        <div style={{
          minHeight:"100vh", display:"flex", flexDirection:"column",
          alignItems:"center", justifyContent:"center",
          background:C.bg, gap:16, fontFamily:"'Inter',sans-serif",
        }}>
          <div className="spin" style={{
            width:32, height:32, border:`3px solid ${C.border}`,
            borderTop:`3px solid ${C.accent}`, borderRadius:"50%",
          }} />
          <div style={{ fontSize:14, color:C.muted }}>Caricamento dati in corso…</div>
        </div>
      </>
    );
  }

  if (schermata === "modifica_studente" && studenteInModifica) {
    return (
      <>
        <style>{STYLES}</style>
        <div style={{ minHeight:"100vh", background:C.bg }}>
          <div style={{ padding:"11px 22px", background:C.surface, borderBottom:`1px solid ${C.border}` }}>
            <Btn ghost onClick={chiudiModifica}>← Indietro</Btn>
          </div>
          <SchedaStudente studenteIniziale={studenteInModifica} onSalva={onAggiornaStudente}
            onAnnulla={chiudiModifica} studentiEsistenti={studenti} />
        </div>
      </>
    );
  }

  if (schermata === "nuovo_studente") {
    return (
      <>
        <style>{STYLES}</style>
        <div style={{ minHeight:"100vh", background:C.bg }}>
          <div style={{ padding:"11px 22px", background:C.surface, borderBottom:`1px solid ${C.border}` }}>
            <Btn ghost onClick={() => setSchermata("app")}>← Indietro</Btn>
          </div>
          <SchedaStudente onSalva={onSalvaStudente} onAnnulla={() => setSchermata("app")} studentiEsistenti={studenti} setDocumentiStudenti={setDocumentiStudenti} setPeiDatiMap={setPeiDatiMap} />
        </div>
      </>
    );
  }

  return (
    <>
      <style>{STYLES}</style>
      {/* Banner migrazione localStorage → Supabase */}
      {dataMigrazione && (
        <BannerMigrazione
          onConferma={onConfermaMigrazione}
          onIgnora={onIgnoraMigrazione}
          dataBackup={
            dataMigrazione.data
              ? new Date(dataMigrazione.data).toLocaleDateString("it-IT")
              : null
          }
        />
      )}
      {migrazioneInCorso && (
        <div style={{
          position:'fixed', top:0, left:0, right:0, zIndex:10000,
          background:'rgba(99,102,241,.92)', color:'#fff',
          padding:'14px 22px', textAlign:'center', fontSize:14,
        }}>
          ⏳ Sincronizzazione in corso…
        </div>
      )}
      {/* ─── Banner errore sync Supabase ─────────────────────────────────────── */}
      {syncError && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9500,
          background: `linear-gradient(135deg, ${C.orange}, #e07000)`,
          color: '#fff', padding: '11px 22px',
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          boxShadow: '0 2px 12px rgba(0,0,0,.20)',
        }}>
          <span style={{ fontSize: 18, flexShrink: 0 }}>⚠️</span>
          <div style={{ flex: 1, fontSize: 13, lineHeight: 1.4 }}>
            {syncError === 'band' ? (
              <><strong>Elenco studenti non salvato sul cloud.</strong> Uno degli studenti appartiene a un ordine di scuola diverso da quello del tuo pacchetto. I dati restano al sicuro su questo dispositivo: scrivi all'assistenza per allineare la fascia del tuo account.</>
            ) : (
              <><strong>Sincronizzazione non riuscita.</strong> I tuoi dati sono al sicuro in locale, ma non sono stati salvati sul cloud. Verifica la connessione e riprova.</>
            )}
          </div>
          {syncError !== 'band' && (
          <button
            onClick={retrySyncSupabase}
            disabled={syncRetrying}
            style={{
              background: 'rgba(255,255,255,0.25)', border: '1.5px solid rgba(255,255,255,0.6)',
              color: '#fff', borderRadius: 8, padding: '7px 18px',
              fontWeight: 700, cursor: syncRetrying ? 'not-allowed' : 'pointer',
              fontSize: 13, fontFamily: "'Inter',sans-serif",
              display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
              opacity: syncRetrying ? 0.7 : 1,
            }}>
            {syncRetrying && (
              <span className="spin" style={{
                width: 12, height: 12, border: '2px solid rgba(255,255,255,0.4)',
                borderTop: '2px solid #fff', borderRadius: '50%', display: 'inline-block'
              }} />
            )}
            {syncRetrying ? 'Salvataggio…' : 'Riprova'}
          </button>
          )}
        </div>
      )}

      {/* ─── Banner ritorno pagamento Stripe (success / cancelled) ────────────── */}
      {paymentBanner && (
        <div style={{
          position:'fixed', top:0, left:0, right:0, zIndex:9600,
          background: paymentBanner === 'success'
            ? `linear-gradient(135deg, ${C.green}, #047857)`
            : `linear-gradient(135deg, ${C.muted}, #4b5563)`,
          color:'#fff', padding:'12px 22px',
          display:'flex', alignItems:'center', gap:12, flexWrap:'wrap',
          boxShadow:'0 2px 12px rgba(0,0,0,.18)',
        }}>
          <span style={{ fontSize:18, flexShrink:0 }}>{paymentBanner === 'success' ? '🎉' : 'ℹ️'}</span>
          <div style={{ flex:1, fontSize:13, lineHeight:1.4 }}>
            {paymentBanner === 'success'
              ? <><strong>Pagamento ricevuto!</strong> I crediti vengono accreditati: il saldo si aggiorna tra pochi istanti.</>
              : <>Pagamento annullato. Puoi riprovare quando vuoi da <strong>Account &amp; Crediti</strong>.</>}
          </div>
          <button onClick={() => setPaymentBanner(null)} style={{
            background:'rgba(255,255,255,.2)', color:'#fff', border:'none', borderRadius:8,
            padding:'6px 14px', fontWeight:700, cursor:'pointer', fontSize:13, flexShrink:0,
          }}>OK</button>
        </div>
      )}

      <div style={{
        display:"flex", minHeight:"100vh", background:C.bg,
        // Se un banner fisso in cima è visibile, aggiungi spazio per non coprire il contenuto
        paddingTop: (dataMigrazione || syncError || paymentBanner) ? 72 : 0,
      }}>
        <Sidebar tab={tab} setTab={setTab} studenti={studenti} studenteAttivo={studenteAttivo}
          setStudenteAttivo={setStudenteAttivo} onNuovoStudente={onNuovoStudente}
          salvataggioOk={salvataggioOk} eliminaStudente={eliminaStudente} />
        <div style={{ flex:1, overflowY:"auto", minWidth:0, display:"flex", flexDirection:"column" }}>
          <CreditsBanner setTab={setTab} />
          {tab === "dashboard" && <Dashboard studenti={studenti} scadenze={scadenze} peiDatiMap={peiDatiMap} documentiStudenti={documentiStudenti} setTab={setTab} setStudenteAttivo={setStudenteAttivo} onNuovoStudente={onNuovoStudente} />}
          {tab === "studenti" && <StudentiTab studenti={studenti} setStudenti={setStudenti} setTab={setTab} setStudenteAttivo={setStudenteAttivo} onNuovoStudente={onNuovoStudente} onModificaStudente={onModificaStudente} eliminaStudente={eliminaStudente} documentiStudenti={documentiStudenti} />}
          {tab === "pei" && <PeiEditor studente={studenteAttivo} peiDatiMap={peiDatiMap} setPeiDatiMap={setPeiDatiMap} documentiStudenti={documentiStudenti} salvaRef={null} setTab={setTab} />}
          {tab === "documenti" && UPLOAD_DOCUMENTI_ATTIVO && <DocumentiStudente studente={studenteAttivo} documenti={documentiStudenti} setDocumenti={setDocumentiStudenti} setTab={setTab} />}
          {tab === "scadenze" && <Scadenze scadenze={scadenze} setScadenze={setScadenze} />}
          {tab === "account" && <AccountPage setTab={setTab} />}
        </div>
      </div>
    </>
  );
}

// ─── PAGINA ACCOUNT — saldo, storico, acquisto pacchetti ────────────────────
// Pacchetti per fascia (band): i prezzi mostrati devono combaciare con quelli
// che addebita il server (create-checkout deriva il prezzo dalla band del profilo).
// Sec I e Sec II hanno lo stesso prezzo. band null → fallback inf_pri.
const DESC_BASE    = "Pensato per circa 1 PEI completo. Quanti PEI riesci a completare dipende da quanti campi compili con l'AI.";
const DESC_PREMIUM = "Pensato per circa 2 PEI completi. Quanti PEI riesci a completare dipende da quanti campi compili con l'AI.";
const PACCHETTI_PER_BAND = {
  inf_pri: [
    { id: "base",    nome: "Base",    prezzo: "€15,00", crediti: 60,  descrizione: DESC_BASE,    color: "#6366f1" },
    { id: "premium", nome: "Premium", prezzo: "€28,00", crediti: 120, descrizione: DESC_PREMIUM, color: "#8b5cf6" },
  ],
  sec1: [
    { id: "base",    nome: "Base",    prezzo: "€20,00", crediti: 100, descrizione: DESC_BASE,    color: "#6366f1" },
    { id: "premium", nome: "Premium", prezzo: "€38,00", crediti: 200, descrizione: DESC_PREMIUM, color: "#8b5cf6" },
  ],
  sec2: [
    { id: "base",    nome: "Base",    prezzo: "€20,00", crediti: 100, descrizione: DESC_BASE,    color: "#6366f1" },
    { id: "premium", nome: "Premium", prezzo: "€38,00", crediti: 200, descrizione: DESC_PREMIUM, color: "#8b5cf6" },
  ],
};
// Fasce sospese dalla vendita — deve combaciare con BANDS_NON_IN_VENDITA in
// supabase/functions/create-checkout/index.ts (là c'è l'enforcement vero, qui
// solo la coerenza dell'interfaccia). Riabilitare = svuotare entrambe le liste.
const BANDS_NON_IN_VENDITA_UI = ["sec2"];

// Fase di prelancio: nessun acquisto possibile. Per riaprire le vendite serve
// mettere true QUI e in supabase/functions/create-checkout/index.ts, poi
// `supabase functions deploy create-checkout` (il blocco vero è quello lato server).
const VENDITA_ATTIVA = false;

const REASON_LABEL = {
  signup_bonus:        { icona: "🎁", testo: "Bonus registrazione",  colore: "green" },
  ai_field:            { icona: "✦",  testo: "Campo AI",              colore: "indigo" },
  verifica_coerenza:   { icona: "🔍", testo: "Verifica coerenza",     colore: "indigo" },
  purchase_base:       { icona: "💎", testo: "Acquisto pacchetto Base",    colore: "green" },
  purchase_premium:    { icona: "💎", testo: "Acquisto pacchetto Premium", colore: "green" },
  admin_adjust:        { icona: "🛠", testo: "Aggiustamento manuale", colore: "muted" },
};

function AccountPage({ setTab }) {
  const { user, balance, plan, band, transactions, loading, error, refresh, refreshTransactions, signOut } = useCreditsCtx();
  // Fascia sospesa dalla vendita: niente listino, solo la spiegazione del programma
  // tester. Mostrare prezzi per qualcosa che il server rifiuta sarebbe una promessa
  // falsa (create-checkout risponde `band_non_in_vendita`).
  const bandInVendita = !BANDS_NON_IN_VENDITA_UI.includes(band);
  const PACCHETTI = PACCHETTI_PER_BAND[band] || PACCHETTI_PER_BAND.inf_pri;
  const [acquistando, setAcquistando] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [paymentNotice, setPaymentNotice] = useState(null); // { type: 'success'|'error'|'cancelled', msg }

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([refresh(), refreshTransactions()]);
    setRefreshing(false);
  };

  const acquista = async (pacchetto) => {
    if (!VENDITA_ATTIVA) return;
    if (!SUPABASE_ENABLED) {
      alert('Acquisto non disponibile in modalità demo. Configura Supabase per abilitare i pagamenti.');
      return;
    }
    setAcquistando(pacchetto.id);
    try {
      // Chiama Edge Function `create-checkout` che genera URL Stripe Checkout
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { package: pacchetto.id },
      });
      if (error) throw new Error(error.message);
      if (!data?.url) throw new Error('URL Checkout mancante');
      // Redirect alla pagina di pagamento Stripe (hosted, sicura, PCI-compliant)
      window.location.href = data.url;
    } catch (e) {
      setPaymentNotice({ type: 'error', msg: `Errore durante l'avvio del pagamento: ${e.message}` });
      setAcquistando(null);
    }
  };

  // Quando torno dal Checkout: ?payment=success o ?payment=cancelled
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('payment');
    if (status === 'success') {
      setPaymentNotice({ type: 'success', msg: '🎉 Pagamento ricevuto! I crediti sono stati accreditati sul tuo account.' });
      refresh(); // ricarica il saldo aggiornato
      window.history.replaceState({}, '', window.location.pathname);
    } else if (status === 'cancelled') {
      setPaymentNotice({ type: 'cancelled', msg: 'Pagamento annullato. Puoi riprovare quando vuoi.' });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const balanceVisualizzato = balance === null ? (loading ? "…" : "—") : balance;

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "32px 40px" }}>
      <div className="fadeUp" style={{ maxWidth: 1000, margin: "0 auto" }}>
        {/* HEADER */}
        <div style={{ marginBottom: 28, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <h1 className="serif" style={{
              fontSize: 32, fontWeight: 700, color: C.text,
              letterSpacing: "-0.02em", marginBottom: 6
            }}>Account & Crediti</h1>
            <div style={{ fontSize: 14, color: C.muted }}>
              {user?.email && <>Loggato come <strong style={{ color: C.text }}>{user.email}</strong> · </>}
              Piano <Badge color={plan === "premium" ? C.accent2 : plan === "base" ? C.accent : C.muted} small>
                {plan === "free" ? "FREE" : plan.toUpperCase()}
              </Badge>
            </div>
          </div>
          <Btn ghost small onClick={handleRefresh} loading={refreshing} icon="🔄"
            title="Ricarica saldo dal server">
            Aggiorna saldo
          </Btn>
        </div>

        {/* Banner feedback pagamento Stripe (success / cancelled / error) */}
        {paymentNotice && (
          <div className="fadeIn" style={{
            padding: "14px 18px", marginBottom: 20,
            background: paymentNotice.type === 'success' ? hex(C.green, 0.08) : hex(C.red, 0.08),
            border: `1px solid ${paymentNotice.type === 'success' ? hex(C.green, 0.30) : hex(C.red, 0.30)}`,
            borderRadius: R.md, fontSize: 13,
            color: paymentNotice.type === 'success' ? C.green : C.red,
            lineHeight: 1.6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          }}>
            <span style={{ fontWeight: 500 }}>{paymentNotice.msg}</span>
            <button onClick={() => setPaymentNotice(null)} style={{
              background: 'none', border: 'none', cursor: 'pointer', fontSize: 16,
              color: 'inherit', opacity: 0.6, flexShrink: 0, lineHeight: 1,
            }}>✕</button>
          </div>
        )}

        {/* Errore di diagnosi (mostrato solo se c'è un problema) */}
        {error && (
          <div className="fadeIn" style={{
            padding: "14px 18px", marginBottom: 20,
            background: hex(C.red, 0.08),
            border: `1px solid ${hex(C.red, 0.30)}`,
            borderRadius: R.md, fontSize: 13, color: C.red, lineHeight: 1.6
          }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>⚠ Problema di sincronizzazione saldo</div>
            <div style={{ fontWeight: 500 }}>
              Non è stato possibile caricare il saldo. Riprova tra qualche minuto.
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>
              Se il problema persiste, contatta il supporto a{" "}
              <a href="mailto:supporto@iep-assistant.it" style={{ color: C.accent, fontWeight: 600 }}>
                supporto@iep-assistant.it
              </a>
            </div>
          </div>
        )}

        {/* SALDO + ACCOUNT INFO */}
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16, marginBottom: 28 }}>
          <div style={{
            padding: "28px 32px",
            background: `linear-gradient(135deg, ${hex(C.accent, 0.12)} 0%, ${hex(C.accent2, 0.06)} 100%)`,
            border: `1px solid ${hex(C.accent, 0.20)}`,
            borderRadius: R.lg
          }}>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 700,
              textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>
              Saldo crediti
            </div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
              <div className="serif" style={{
                fontSize: 72, fontWeight: 700, color: C.accent,
                lineHeight: 1, letterSpacing: "-0.04em",
                minWidth: 60
              }}>{balanceVisualizzato}</div>
              <div style={{ fontSize: 14, color: C.muted, marginBottom: 14 }}>
                {balance === 1 ? "credito disponibile" : "crediti disponibili"}
              </div>
            </div>
            <div style={{ marginTop: 16, fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
              1 credito per ogni campo generato/corretto con AI · {COSTI_CREDITI.VERIFICA_COERENZA} crediti per "Verifica coerenza"
            </div>
          </div>

          <div style={{
            padding: "20px 22px", background: C.surface,
            border: `1px solid ${C.border}`, borderRadius: R.lg,
            display: "flex", flexDirection: "column", justifyContent: "space-between"
          }}>
            <div>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700,
                textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Account</div>
              <div style={{ fontSize: 13, color: C.text, fontWeight: 500, marginBottom: 4,
                wordBreak: "break-all" }}>
                {user?.email || "Modalità demo (locale)"}
              </div>
              {user?.phone && (
                <div style={{ fontSize: 12, color: C.muted }}>📱 {user.phone}</div>
              )}
            </div>
            {SUPABASE_ENABLED && (
              <Btn ghost small onClick={() => setShowLogoutModal(true)} icon="↪" style={{ marginTop: 16 }}>Logout</Btn>
            )}
            {showLogoutModal && (
              <div className="fadeIn" style={{
                position: "fixed", inset: 0, zIndex: 1000,
                background: "rgba(28,25,23,0.55)",
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: 24
              }}>
                <div style={{
                  background: C.surface, borderRadius: R.lg, padding: "32px 36px",
                  maxWidth: 420, width: "100%",
                  boxShadow: "0 24px 64px rgba(28,25,23,0.18)",
                  border: `1px solid ${C.border}`
                }}>
                  <div style={{ fontSize: 32, marginBottom: 12, textAlign: "center" }}>👋</div>
                  <h2 className="serif" style={{
                    fontSize: 22, fontWeight: 700, color: C.text,
                    letterSpacing: "-0.02em", marginBottom: 10, textAlign: "center"
                  }}>Vuoi uscire?</h2>
                  <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.6, marginBottom: 24, textAlign: "center" }}>
                    I tuoi dati sono salvati in modo sicuro e saranno disponibili al prossimo accesso, da qualsiasi dispositivo.
                  </p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <Btn primary onClick={signOut} style={{ justifyContent: "center" }}>
                      Esci
                    </Btn>
                    <Btn ghost onClick={() => setShowLogoutModal(false)}
                      style={{ justifyContent: "center", color: C.muted }}>
                      Annulla
                    </Btn>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* PACCHETTI — nascosti per le fasce non in vendita (programma tester) */}
        {!bandInVendita ? (
          <div style={{ marginBottom: 28 }}>
            <h2 className="serif" style={{
              fontSize: 22, fontWeight: 700, color: C.text,
              letterSpacing: "-0.02em", marginBottom: 14
            }}>Programma di sperimentazione</h2>
            <div style={{
              padding: "22px 24px", background: C.surface,
              border: `2px solid ${hex(C.accent, 0.28)}`, borderRadius: 16,
              lineHeight: 1.65, fontSize: 14, color: C.text,
            }}>
              <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 15 }}>
                🧪 Secondaria di II grado — versione in prova
              </div>
              <p style={{ margin: "0 0 10px", color: C.muted }}>
                Il supporto per la secondaria di II grado è in fase di messa a punto e
                <strong> non è ancora in vendita</strong>: stiamo lavorando perché la progettazione
                disciplinare sia all'altezza degli altri ordini, tenendo conto della varietà degli
                indirizzi. Per questo non trovi qui i pacchetti di crediti.
              </p>
              <p style={{ margin: "0 0 10px", color: C.muted }}>
                I crediti del tuo account sono assegnati manualmente nell'ambito della
                sperimentazione. Quando sono in esaurimento, o se qualcosa non ti convince nei
                testi generati, scrivici: il tuo riscontro è esattamente ciò che serve.
              </p>
              <a href="mailto:supporto@iep-assistant.it?subject=Sperimentazione%20secondaria%20di%20II%20grado"
                style={{ color: C.accent, fontWeight: 700, textDecoration: "none" }}>
                supporto@iep-assistant.it →
              </a>
            </div>
          </div>
        ) : (
        <div style={{ marginBottom: 28 }}>
          <h2 className="serif" style={{
            fontSize: 22, fontWeight: 700, color: C.text,
            letterSpacing: "-0.02em", marginBottom: 14
          }}>Acquista crediti</h2>
          {!VENDITA_ATTIVA && (
            <div style={{
              padding: "20px 22px", marginBottom: 16, background: C.surface,
              border: `2px solid ${hex(C.orange, 0.3)}`, borderRadius: 16,
              lineHeight: 1.65, fontSize: 14, color: C.text,
            }}>
              <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 15 }}>
                ⏳ Acquisti temporaneamente non disponibili
              </div>
              <p style={{ margin: "0 0 10px", color: C.muted }}>
                IEP Assistant è nella fase conclusiva delle verifiche che precedono il rilascio
                ufficiale. Fino al termine di questa fase i pacchetti di crediti non sono
                acquistabili: preferiamo aprire le vendite soltanto quando ogni funzione sarà
                stata collaudata in condizioni d'uso reali.
              </p>
              <p style={{ margin: 0, color: C.muted }}>
                Nel frattempo puoi usare l'applicazione con i 2 crediti gratuiti della registrazione.
              </p>
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {PACCHETTI.map(p => (
              <div key={p.id} className="hoverLift" style={{
                position: "relative", padding: "28px 28px 24px",
                background: C.surface,
                border: `2px solid ${p.id === "premium" ? p.color : C.border}`,
                borderRadius: R.lg,
                boxShadow: p.id === "premium" ? `0 12px 32px ${hex(p.color, 0.18)}` : C.shadow,
                transition: "all .25s cubic-bezier(.16,1,.3,1)"
              }}>
                {p.id === "premium" && (
                  <div style={{
                    position: "absolute", top: -10, right: 18,
                    padding: "3px 12px", borderRadius: R.pill,
                    background: grad(C.accent, p.color),
                    color: "#fff", fontSize: 10, fontWeight: 700,
                    textTransform: "uppercase", letterSpacing: 0.8,
                    boxShadow: `0 4px 12px ${hex(p.color, 0.4)}`
                  }}>Più scelto</div>
                )}
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700,
                  textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>
                  Pacchetto {p.nome}
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 4 }}>
                  <div className="serif" style={{
                    fontSize: 38, fontWeight: 700, color: p.color,
                    lineHeight: 1, letterSpacing: "-0.03em"
                  }}>{p.prezzo}</div>
                </div>
                <div style={{ fontSize: 13, color: C.text, fontWeight: 600, marginBottom: 10 }}>
                  {p.crediti} crediti
                </div>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 18, lineHeight: 1.5 }}>
                  {p.descrizione}
                </div>
                <Btn primary onClick={() => acquista(p)} loading={acquistando === p.id}
                  disabled={!VENDITA_ATTIVA}
                  title={VENDITA_ATTIVA ? undefined : "Acquisti non disponibili durante la fase di verifica che precede il rilascio"}
                  style={{ width: "100%", justifyContent: "center",
                    ...(VENDITA_ATTIVA ? {} : { opacity: 0.45, cursor: "not-allowed", boxShadow: "none" }) }}>
                  {VENDITA_ATTIVA ? "Acquista" : "Non disponibile"}
                </Btn>
                <div style={{ marginTop: 12, fontSize: 11, color: C.mutedLight, lineHeight: 1.5 }}>
                  ✓ Crediti permanenti, non scadono mai<br/>
                  ✓ Pagamento sicuro tramite Stripe
                </div>
              </div>
            ))}
          </div>
        </div>
        )}

        {/* STORICO TRANSAZIONI */}
        <div>
          <h2 className="serif" style={{
            fontSize: 22, fontWeight: 700, color: C.text,
            letterSpacing: "-0.02em", marginBottom: 14
          }}>Storico transazioni</h2>
          <div style={{
            background: C.surface, border: `1px solid ${C.border}`,
            borderRadius: R.lg, overflow: "hidden"
          }}>
            {transactions.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 13 }}>
                {SUPABASE_ENABLED ? "Nessuna transazione ancora" : "Modalità demo: storico non disponibile"}
              </div>
            ) : (
              transactions.map(tx => {
                const meta = REASON_LABEL[tx.reason] || { icona: "•", testo: tx.reason, colore: "muted" };
                const positivo = tx.delta > 0;
                const colore = meta.colore === "green" ? C.green
                  : meta.colore === "indigo" ? C.accent : C.muted;
                return (
                  <div key={tx.id} style={{
                    display: "flex", alignItems: "center", gap: 14,
                    padding: "14px 20px", borderBottom: `1px solid ${C.border}`
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: R.sm,
                      background: hex(colore, 0.10),
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 16
                    }}>{meta.icona}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>
                        {meta.testo}
                      </div>
                      <div style={{ fontSize: 11, color: C.mutedLight, marginTop: 2 }}>
                        {new Date(tx.created_at).toLocaleString("it-IT", {
                          day: "2-digit", month: "short", year: "numeric",
                          hour: "2-digit", minute: "2-digit"
                        })}
                      </div>
                    </div>
                    <div style={{
                      fontSize: 14, fontWeight: 700,
                      color: positivo ? C.green : C.text,
                      fontFamily: "'Fraunces', Georgia, serif"
                    }}>
                      {positivo ? "+" : ""}{tx.delta}
                    </div>
                    <div style={{ fontSize: 11, color: C.muted, minWidth: 50, textAlign: "right" }}>
                      {tx.balance_after} rimanenti
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Banner crediti in esaurimento (≤ SOGLIA_AVVISO o saldo = 0) ───────────
function CreditsBanner({ setTab }) {
  const { balance } = useCreditsCtx();
  if (balance === null || balance > SOGLIA_AVVISO) return null;
  const esauriti = balance <= 0;
  return (
    <div className="fadeIn" style={{
      padding: "10px 32px",
      background: esauriti
        ? `linear-gradient(135deg, ${hex(C.red, 0.10)}, ${hex(C.red, 0.05)})`
        : `linear-gradient(135deg, ${hex(C.orange, 0.12)}, ${hex(C.orange, 0.06)})`,
      borderBottom: `1px solid ${esauriti ? hex(C.red, 0.25) : hex(C.orange, 0.30)}`,
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap"
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13,
        color: esauriti ? C.red : C.orange, fontWeight: 600 }}>
        <span style={{ fontSize: 16 }}>{esauriti ? "🔒" : "⚠️"}</span>
        {esauriti
          ? "Crediti esauriti. Acquista un pacchetto per continuare a usare l'AI."
          : `Restano solo ${balance} ${balance === 1 ? 'credito' : 'crediti'}. Ricarica per non rimanere senza.`}
      </div>
      <Btn primary small onClick={() => setTab('account')} icon="✦">Acquista crediti</Btn>
    </div>
  );
}

// ─── AuthGate — gestisce stato di login/logout e mostra le schermate auth ────
// Se Supabase NON è configurato, salta del tutto l'auth e va dritto all'app
// (modalità demo locale, comoda per sviluppo).
function AuthGate() {
  // Approccio affidabile: il link di reset reindirizza SEMPRE a /reset-password.
  // Controlliamo il pathname in modo sincrono — non dipende da eventi Supabase.
  const isResetPath = window.location.pathname.includes('reset-password');

  const [authReady, setAuthReady]   = useState(false);
  const [user, setUser]             = useState(null);
  const [recoveryMode, setRecovery] = useState(isResetPath || _recoveryPending);
  const recoveryRef                 = useRef(isResetPath || _recoveryPending);

  useEffect(() => {
    if (!SUPABASE_ENABLED) { setAuthReady(true); return; }

    _earlyAuthSub?.unsubscribe();
    _earlyAuthSub = null;

    // Sessione iniziale — non mostriamo l'app se siamo in recovery mode
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!recoveryRef.current) {
        setUser(session?.user || null);
      }
      setAuthReady(true);
    });

    // Listener per eventi successivi
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        recoveryRef.current = true;
        setRecovery(true);
        setUser(null);
      } else if (event === 'SIGNED_IN' && recoveryRef.current) {
        // Ignora: Supabase spara SIGNED_IN subito dopo PASSWORD_RECOVERY
      } else if (event === 'USER_UPDATED') {
        recoveryRef.current = false;
        setRecovery(false);
        setUser(null);
      } else {
        if (!recoveryRef.current) {
          setRecovery(false);
          setUser(session?.user || null);
        }
      }
    });
    return () => subscription?.unsubscribe();
  }, []);

  if (!authReady) {
    return (
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        background: "#f5f3ee", fontFamily: "'Inter',sans-serif"
      }}>
        <div className="spin" style={{
          width: 28, height: 28, border: "3px solid #ebe7df",
          borderTop: "3px solid #6366f1", borderRadius: "50%"
        }} />
      </div>
    );
  }

  // Modalità recupero password: mostra solo la schermata di reset
  if (SUPABASE_ENABLED && recoveryMode) {
    return <>
      <style>{STYLES}</style>
      <ResetPasswordScreen onSuccess={() => {
        window.history.replaceState({}, document.title, window.location.pathname);
        setRecovery(false);
        setUser(null); // forza il login manuale dopo il reset
      }} />
    </>;
  }

  // Se Supabase non configurato → modalità demo (no auth)
  // Se Supabase configurato e nessuno loggato → mostra schermate auth
  if (SUPABASE_ENABLED && !user) {
    return <>
      <style>{STYLES}</style>
      <AuthFlow onAuthenticated={(u) => setUser(u)} />
    </>;
  }

  return (
    <CreditsProvider user={user}>
      <AppContent />
    </CreditsProvider>
  );
}

// ─── Error Boundary globale ───────────────────────────────────────────────────
// Cattura qualsiasi errore React non gestito e mostra un messaggio utile
// invece di una schermata bianca. Usa una class component perché è l'unico
// modo supportato da React per implementare componentDidCatch.
class AppErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMsg: "" };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, errorMsg: error?.message || "Errore sconosciuto" };
  }
  componentDidCatch(error, info) {
    console.error("[AppErrorBoundary]", error, info);
  }
  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <>
        <style>{STYLES}</style>
        <div style={{
          minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
          background: C.bg, fontFamily: "'Inter', sans-serif", padding: 24
        }}>
          <div style={{
            background: C.surface, borderRadius: 20, padding: "40px 36px",
            maxWidth: 480, width: "100%", textAlign: "center",
            boxShadow: C.shadowLg, border: `1px solid ${C.border}`
          }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
            <h2 className="serif" style={{
              fontSize: 22, fontWeight: 700, color: C.text,
              letterSpacing: "-0.02em", marginBottom: 10
            }}>Qualcosa è andato storto</h2>
            <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.6, marginBottom: 8 }}>
              Si è verificato un errore imprevisto. I tuoi dati sono al sicuro — sono stati salvati automaticamente.
            </p>
            <p style={{
              fontSize: 11, color: C.mutedLight, fontFamily: "monospace",
              background: C.bg, padding: "6px 10px", borderRadius: 6,
              marginBottom: 24, wordBreak: "break-word"
            }}>{this.state.errorMsg}</p>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: `linear-gradient(135deg, ${C.accent}, ${C.accent2})`,
                color: "#fff", border: "none", borderRadius: 99,
                padding: "12px 28px", fontSize: 14, fontWeight: 600,
                fontFamily: "'Inter', sans-serif", cursor: "pointer"
              }}>
              Ricarica l'app
            </button>
          </div>
        </div>
      </>
    );
  }
}

export default function App() {
  return (
    <AppErrorBoundary>
      <SettingsProvider>
        <AuthGate />
      </SettingsProvider>
    </AppErrorBoundary>
  );
}